/**
 * Location Map Editor - Skylit Studio
 * Mapbox GL JS powered location map customiser
 * v2.1 - Natural Earth borders
 */
(function() {
  'use strict';

  // Fade out the static LCP poster (locationmap.liquid #lm-lcp-poster) once
  // the map is ready (called wherever the .lm-loader overlay is dismissed).
  function lmHidePoster() {
    var p = document.getElementById('lm-lcp-poster');
    if (p) { p.style.opacity = '0'; setTimeout(function(){ p.remove(); }, 600); }
  }

  var API_BASE = 'https://admin.skylitstudio.co.uk/';
  var ADMIN_BASE = 'https://admin.skylitstudio.co.uk/';
  var MAPBOX_TOKEN = 'pk.eyJ1Ijoic2t5bGl0c3R1ZGlvIiwiYSI6ImNtbnV4cWNyeDB4dDAycnM2c241ODBsZXAifQ.1wevFm2BCFZq5M1rr8GXfA';

  // Named preset slugs → admin order IDs used as visual templates. Homepage
  // bestselling cards link to the product with ?preset=<slug>; the wizard
  // hydrates the template's visuals and keeps a fresh orderId so the
  // customer's add-to-cart creates a new order.
  var LM_PRESETS = {
    classic:  '4aojw3o3nmz',
    boxed:    'mfpuaui63b',
    header:   '5hp5a0b1sj7',
    panel:    'syywiw0w0qi',
    circle:   '7f0ezsfr573',
    gradient: 'xx2ibsg31pc',
    // 3D-landmark variant of order dcolz5ao9w (Hyde Park, pitch 71°,
    // bearing 78°, patina theme, 50x70). Stripped placed icons; the
    // saved row lives at order_id=preset-hyde-park-3d in D1.
    'hyde-park-3d': 'preset-hyde-park-3d',
    // Big Ben — framed from the south-west across Parliament. Same
    // pattern as Hyde Park (D1 row + landing template + product-page
    // context block). See LANDMARK_PRESETS below for the canonical
    // camera params we cloned for this preset.
    'big-ben-3d':           'preset-big-ben-3d',
    // Additional London landmark presets — only landmarks verified to
    // have a custom 3D mesh in Mapbox Standard (mapbox.mapbox-
    // 3dbuildings-v1 / building-models layer) are included. Tower
    // Bridge for example renders as a flat road bridge with generic
    // blocks around it, so it's NOT in this list. Each preset needs
    // a matching D1 row at order_id=preset-<slug> with seed
    // camera/theme. Landing page template lives at templates/
    // page.preset-<slug>.json and is wired through the page's
    // template_suffix in Shopify Admin → Pages.
    'the-shard-3d':         'preset-the-shard-3d',
    'st-pauls-3d':          'preset-st-pauls-3d',
    'tower-of-london-3d':   'preset-tower-of-london-3d',
    'london-eye-3d':        'preset-london-eye-3d',
    'buckingham-palace-3d': 'preset-buckingham-palace-3d',
    'westminster-abbey-3d': 'preset-westminster-abbey-3d',
    'the-gherkin-3d':       'preset-the-gherkin-3d',
    'bt-tower-3d':          'preset-bt-tower-3d',
    'royal-albert-hall-3d': 'preset-royal-albert-hall-3d',
    'wembley-stadium-3d':   'preset-wembley-stadium-3d'
  };

  // Theme definitions
  // Blue-water variant. Mirrors BLUE_WATER_LIGHT/DARK + blue_water_for() in
  // skylit-admin renderer/scripts/render_locationmap.py — change both together.
  var LM_BLUE_WATER = { light: '#8fb8d6', dark: '#2d5c8a' };
  // Themes whose water is already blue: re-tapping them does nothing and
  // their tiles show no water dots. Keep in step with lm_blue_themes in
  // sections/locationmap.liquid.
  var LM_WATER_ALREADY_BLUE = ['ivory', 'ocean', 'nautical', 'blueprint', 'bauhaus', 'aurora', 'cyberpunk', 'pastel'];
  function lmBlueWater(t) {
    var h = String((t && t.bg) || '#ffffff').replace('#', '');
    if (h.length === 3) h = h.replace(/(.)/g, '$1$1');
    var r = parseInt(h.substr(0, 2), 16), g = parseInt(h.substr(2, 2), 16), b = parseInt(h.substr(4, 2), 16);
    if (isNaN(r + g + b)) return LM_BLUE_WATER.light;
    return (0.299 * r + 0.587 * g + 0.114 * b) / 255 > 0.45 ? LM_BLUE_WATER.light : LM_BLUE_WATER.dark;
  }
  // The palette every map/overlay path draws with: the theme, plus the blue
  // water swap when it is on. Use this, not THEMES[...] directly.
  function lmThemeData(name) {
    var t = THEMES[name || state.theme] || THEMES.classic;
    if (state.waterTint === 'blue' && LM_WATER_ALREADY_BLUE.indexOf(name || state.theme) === -1) {
      return Object.assign({}, t, { water: lmBlueWater(t) });
    }
    return t;
  }

  var THEMES = {
    classic:    { bg:'#1a1a2e', road:'#ffffff', roadMinor:'#cccccc', water:'#2a4a7a', land:'#1a1a2e', park:'#1e2a1e', building:'#222244', text:'#ffffff' },
    ivory:      { bg:'#f5f0e1', road:'#c9a96e', roadMinor:'#d4be8e', water:'#9fbdd3', land:'#f5f0e1', park:'#c9dab2', building:'#e8dcc8', text:'#3a2e1e' },
    midnight:   { bg:'#0a0e27', road:'#c9a96e', roadMinor:'#8a7a5a', water:'#1c2f5c', land:'#0a0e27', park:'#0e1a0e', building:'#111533', text:'#c9a96e' },
    monochrome: { bg:'#ffffff', road:'#333333', roadMinor:'#888888', water:'#dddddd', land:'#ffffff', park:'#eeeeee', building:'#f5f5f5', text:'#111111' },
    rosegold:   { bg:'#fdf0f0', road:'#b76e79', roadMinor:'#d4a0a8', water:'#e8d5d5', land:'#fdf0f0', park:'#e8ddd5', building:'#f0e0e0', text:'#6b3a42' },
    celestial:  { bg:'#0f0f23', road:'#4a90d9', roadMinor:'#2a5580', water:'#1e3a7a', land:'#0f0f23', park:'#0f1a0f', building:'#151530', text:'#e0e8ff' },
    ocean:      { bg:'#e8f1f5', road:'#5a8a9a', roadMinor:'#8ab5c4', water:'#3d8ea6', land:'#e8f1f5', park:'#c4ddd0', building:'#d5e5ea', text:'#2a4f5f' },
    terracotta: { bg:'#c0755a', road:'#f5ede8', roadMinor:'#ddc8bb', water:'#8a5040', land:'#c0755a', park:'#a06848', building:'#b06a52', text:'#f5ede8' },
    pastel:     { bg:'#fce4ec', road:'#80cbc4', roadMinor:'#a5d6d0', water:'#b2ebf2', land:'#fce4ec', park:'#c8e6c9', building:'#f8d1da', text:'#5d4037' },
    nautical:   { bg:'#f0e6d3', road:'#8b4513', roadMinor:'#a0724e', water:'#6fa8b8', land:'#f0e6d3', park:'#c5cfa0', building:'#e2d5c0', text:'#3e2a14' },
    noir:       { bg:'#000000', road:'#ffffff', roadMinor:'#888888', water:'#222222', land:'#000000', park:'#0e0e0e', building:'#161616', text:'#ffffff' },
    blueprint:  { bg:'#0d3b66', road:'#e0f2ff', roadMinor:'#7fb3d5', water:'#1d5a8a', land:'#0d3b66', park:'#114a7a', building:'#114a7a', text:'#e0f2ff' },
    sepia:      { bg:'#e8d9b8', road:'#7a4e2a', roadMinor:'#a87a52', water:'#c4a87a', land:'#e8d9b8', park:'#d4c19a', building:'#dcc9a0', text:'#3a2410' },
    forest:     { bg:'#1c2e1f', road:'#d4c19a', roadMinor:'#8a7a5a', water:'#466a55', land:'#1c2e1f', park:'#243a2a', building:'#2e4032', text:'#e8dcc0' },
    sandstone:  { bg:'#e6d5b8', road:'#8a6240', roadMinor:'#b08a5e', water:'#b8c8c0', land:'#e6d5b8', park:'#cfc0a0', building:'#dccaae', text:'#3a2814' },
    bordeaux:   { bg:'#3a0f1a', road:'#e8d4c0', roadMinor:'#a8826e', water:'#8a4555', land:'#3a0f1a', park:'#421422', building:'#5a2230', text:'#f0dcc8' },
    bauhaus:    { bg:'#f5e9d4', road:'#c0392b', roadMinor:'#1a1a1a', water:'#2c5f8d', land:'#f5e9d4', park:'#e8dcc0', building:'#ecdfc8', text:'#1a1a1a' },
    champagne:  { bg:'#f4ebd8', road:'#a37a3a', roadMinor:'#c2a16a', water:'#dccdab', land:'#f4ebd8', park:'#e8dcc0', building:'#ece0c4', text:'#3a2a14' },
    neonlime:   { bg:'#0a0e1a', road:'#b8ff3d', roadMinor:'#7c3aed', water:'#2a1a5a', land:'#0a0e1a', park:'#0e1428', building:'#1a2540', text:'#b8ff3d' },
    cyberpunk:  { bg:'#0a0a14', road:'#00e5ff', roadMinor:'#ff10a0', water:'#1a3a6e', land:'#0a0a14', park:'#0e0e1c', building:'#1a1a3a', text:'#00e5ff' },
    neonsunset: { bg:'#1a0a1a', road:'#ffb380', roadMinor:'#c084fc', water:'#6a2858', land:'#1a0a1a', park:'#1e0e22', building:'#3a1a40', text:'#ffd5c2' },
    aurora:     { bg:'#0a1830', road:'#7fffd4', roadMinor:'#4a90c8', water:'#1f4a7e', land:'#0a1830', park:'#0e1c38', building:'#1c2e55', text:'#c8f4e8' },
    plum:       { bg:'#2a1535', road:'#f0d878', roadMinor:'#9a6dba', water:'#7c5891', land:'#2a1535', park:'#2e1838', building:'#4a2356', text:'#f4e8a0' },
    emerald:    { bg:'#1f3d33', road:'#a9c9b5', roadMinor:'#6f9181', water:'#d4824a', land:'#1f3d33', park:'#22402f', building:'#244338', text:'#f0e0d0' },
    ruby:       { bg:'#2a0a14', road:'#ffb098', roadMinor:'#8a3548', water:'#9c4f60', land:'#2a0a14', park:'#2e0e18', building:'#4d1828', text:'#f4d0c8' },
    patina:     { bg:'#0e2a2a', road:'#d4a574', roadMinor:'#5a8a82', water:'#235e5e', land:'#0e2a2a', park:'#102e2e', building:'#123232', text:'#e8d4b0' }
  };

  // White-matt option for the Border (passepartout) layout. Re-clicking the
  // active Border tile toggles the mat between the theme bg and white; the mat
  // colour rides on state.customBg exactly like the (removed) bg picker did, so
  // the renderer, cart thumbnail and 3D mockups pick it up with no extra field.
  var LM_MATT_WHITE = '#ffffff';
  // A theme whose bg is already white (Mono) would flip white -> white, so its
  // alternate mat is black instead (the mirror of Noir, black -> white).
  var LM_MATT_BLACK = '#000000';
  function lmIsWhiteBg(hex) {
    var m = /^#?([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})/i.exec(String(hex || ''));
    return !!m && [m[1], m[2], m[3]].every(function (c) { return parseInt(c, 16) >= 0xf8; });
  }
  // The mat the tile's second dot / re-click switches to for a theme.
  function lmAltMat(themeName) {
    var t = THEMES[themeName || state.theme] || THEMES.classic;
    return lmIsWhiteBg(t.bg) ? LM_MATT_BLACK : LM_MATT_WHITE;
  }
  // Layouts with a mat/canvas behind an inset map — these support the theme↔white
  // mat flip (Border/passepartout, Circle, Heart). Full is edge-to-edge and Fade
  // blends into the bg, so both are intentionally excluded.
  var LM_MAT_LAYOUTS = ['passepartout', 'circle', 'heart'];

  // Frame texture URLs (same as starmap.liquid)
  var FRAME_TEXTURES = {
    Black:      { color:'rgba(33,33,33,0.75)', image:'url(https://cdn.shopify.com/s/files/1/0710/5278/7987/t/104/assets/frame-black-v3.webp) 100 / 1 / 0 stretch' },
    White:      { color:'rgb(255,255,255)',     image:'url(https://cdn.shopify.com/s/files/1/0710/5278/7987/t/104/assets/frame-white-v3.webp) 115 / 1 / 0 stretch' },
    Natural:    { color:'rgba(0,0,0,0)',        image:'url(https://cdn.shopify.com/s/files/1/0710/5278/7987/t/104/assets/frame-natural-v3.webp) 110 / 1 / 0 stretch' },
    'Dark Brown': { color:'rgba(0,0,0,0)',      image:'url(https://cdn.shopify.com/s/files/1/0710/5278/7987/t/104/assets/frame-dark-brown-v3.webp) 110 / 1 / 0 stretch' }
  };

  // State
  var map = null;
  var state = {
    lat: 51.5074, lng: -0.1278, zoom: 11, bearing: 0, pitch: 0,
    theme: 'nautical', layout: 'full',
    // Message font: Josefin Sans by default (was Prata until 2026-10-22). The
    // reopen path keeps Prata as ITS fallback, for saved designs that predate
    // a stored font and were made under the old default.
    title: 'Where It All Began', subtitle: 'London, UK', showCoordinates: true, font: 'Josefin Sans',
    // Optional date of the moment ("14 February 2019"), printed as its own line
    // between the place and the coordinates. Free text, empty by default. It is
    // stored inside the saved subtitle as "place\ndate" (see lmJoinSubtitle):
    // render_locationmap.py already stacks a multi-line subtitle, so the print,
    // D1 and the admin need nothing new.
    dateLine: '',
    // The coordinates line is an editable text field. null = automatic: the
    // lat/lng of the map (fmtCoords), following it as the map moves. A string
    // is the customer's own line in that slot; '' removes the line.
    // showCoordinates is kept in step (false only for '') for the save
    // payload and the older consumers that still read it.
    coordsText: null,
    // 'blue' swaps the theme's water colour for blue (tap the active theme tile
    // again, like Border's mat). Resets to 'theme' on any theme change.
    waterTint: 'theme',
    detailsFont: 'Josefin Sans', fontScope: 'message',
    locationName: 'London, UK',
    placeCity: '', placeCountry: '',  // structured city/country from last Google place select (Place composition)
    orientation: 'portrait', showLabels: false, showRoadLabels: false, showPOILabels: false, showWaterLabels: false, showLandBorders: true, showMaritimeBorders: true,
    show3dBuildings: false, show3dTerrain: false, buildingDetail: 'high', show3dLandmarks: false, lightPreset: 'day',
    textStyle: 'place', passepartout: false,
    handwritingEnabled: false,
    // Strokes captured in a fixed viewBox (HW_VBW × HW_VBH) so they're
    // resolution-independent. Renderer rescales into the boxed plaque.
    handwritingStrokes: [],
    // Print-size scale factor (1.0 = full plaque width). Customer-controlled
    // because handwriting at full size often dwarfs nearby print text.
    handwritingSize: 0.75,
    handwritingHeight: 1.0,
    selectedType: 'Print', selectedSize: 'A4', selectedFrame: 'Dark Brown', customBg: 'theme',
    // Mirror of the active theme's bg, kept fresh in updateLayout() purely so the
    // Border-tile mat badge can render a live theme-colour swatch via Alpine.
    themeBg: '#f0e6d3',
    // The alternate mat for the active theme (white, or black on Mono) — the
    // colour of the mat tiles' second dot.
    altMat: '#ffffff',
    locked: false,  // map frozen: pan/drag/zoom/rotate gestures + controls disabled
    orderId: Math.random().toString(36).substring(2, 15),
    editMode: false  // set true in loadExistingOrder when ?order_id= is an edit
  };
  // Edit mode: the Framed selection came from the cart's frame add-on line
  // (not the unified Framed variant) — confirm must keep the two-line
  // structure. Set by the pairedFrame hydration in the ?order_id= boot branch.
  var lmFrameFromAddon = false;
  // ── Deduplicate place name parts (e.g. "Cardiff, Cardiff, Wales" → "Cardiff, Wales") ──
  function dedupePlace(name) {
    if (!name) return name;
    var parts = name.split(',').map(function(s) { return s.trim(); });
    var seen = {};
    var result = [];
    parts.forEach(function(p) {
      var key = p.toLowerCase();
      if (!seen[key]) { seen[key] = true; result.push(p); }
    });
    return result.join(', ');
  }

  // The saved subtitle carries the optional date line after a newline.
  function lmJoinSubtitle() {
    var sub = String(state.subtitle || '').replace(/\n/g, ' ').trim();
    var date = String(state.dateLine || '').replace(/\n/g, ' ').trim();
    return date ? (sub + '\n' + date) : sub;
  }
  function lmCoordsLine() {
    return state.coordsText === null || state.coordsText === undefined ? (state.showCoordinates ? fmtCoords() : '') : String(state.coordsText);
  }
  function lmSetCoordsText(v) {
    state.coordsText = v;
    state.showCoordinates = v !== '';
  }
  // Keep the coordinates field showing what will print: the live lat/lng while
  // it is automatic. Never rewrite it under the customer's cursor.
  function lmSyncCoordsInput() {
    var i = document.getElementById('lm-coords-text');
    if (!i || document.activeElement === i) return;
    var v = lmCoordsLine();
    if (i.value !== v) i.value = v;
  }
  function lmSplitSubtitle(raw) {
    var parts = String(raw || '').split('\n');
    return { subtitle: parts[0] || '', dateLine: parts.slice(1).join(' ').trim() };
  }

  // ── Frame Aspect Ratio (driven by selected size) ──
  var sizeRatios = {
    'a4': 210 / 297,
    '30 x 40 cm': 30 / 40,
    '40 x 50 cm': 40 / 50,
    '50 x 70 cm': 50 / 70,
    '60 x 80 cm': 60 / 80
  };

  function applyFrameRatio() {
    var frame = document.getElementById('lm-print-frame');
    if (!frame) return;
    var key = (state.selectedSize || '').toLowerCase().trim();
    var portraitRatio = sizeRatios[key] || (4 / 5);
    var ratio = (state.orientation === 'landscape') ? (1 / portraitRatio) : portraitRatio;
    frame.style.setProperty('--frame-ratio', ratio.toFixed(5));

    // Clear any previously-baked inline height before measuring. Without
    // this, resizing the browser through the 991px / 768px breakpoints
    // gets stuck: a small pixel height set while the @media narrow rule
    // was active stays on the element after the rule drops, and base CSS
    // (width:auto + aspect-ratio) then derives offsetWidth from that
    // small height — locking the frame at its narrow size when the user
    // resizes back to a wide viewport.
    frame.style.height = '';
    // Clear any toolbar-clamp width too (see below) so the frame can grow
    // back to its natural width before we re-measure.
    frame.style.width = '';

    // Safari/WebKit: height:100% on children doesn't resolve against
    // aspect-ratio parents. Force an explicit pixel height after layout.
    requestAnimationFrame(function() {
      var w = frame.offsetWidth;
      if (w > 0) {
        var h = w / ratio;
        // ── Toolbar-aware vertical clamp ──
        // In landscape the floating top toolbar and the bottom zoom strip
        // are centred over .lm-main's top/bottom edges (CSS :has(.landscape)
        // rules). The frame is width-bound there, so without a cap it grows
        // tall enough to slide under both pills. .lm-main centres the frame,
        // so capping its height leaves symmetric clearance bands for the
        // toolbars. We re-derive width from the capped height to keep the
        // print aspect ratio exact. Portrait keeps the toolbars on the right
        // edge (no top/bottom overlap), and phones use a different stacked
        // layout — so this only applies to landscape on tablet/desktop.
        var main = frame.closest('.lm-main');
        if (main && window.innerWidth > 768 && state.orientation === 'landscape') {
          var mr = main.getBoundingClientRect();
          var topTb = main.querySelector('.lm-floating-toolbar');
          var botTb = main.querySelector('.lm-mobile-zoom');
          var MARGIN = 12;
          var topClear = (topTb && topTb.offsetHeight)
            ? (topTb.getBoundingClientRect().bottom - mr.top + MARGIN) : 60;
          var botClear = (botTb && botTb.offsetHeight)
            ? (mr.bottom - botTb.getBoundingClientRect().top + MARGIN) : 64;
          var maxH = mr.height - 2 * Math.max(topClear, botClear);
          if (maxH > 160 && h > maxH) {
            h = maxH;
            frame.style.width = (h * ratio) + 'px';
          }
        }
        frame.style.height = h + 'px';
      }
      if (map) setTimeout(function() { map.resize(); }, 30);
    });
  }

  // Expose state to Alpine.js (Phase 2 reactivity refactor).
  // Alpine.store wraps the value in a Proxy. JS writes that go directly
  // to the original `state` object bypass the proxy and don't fire
  // reactivity, so our side-effect helpers below write through both —
  // the proxy (for Alpine `:class` / `x-show` bindings) AND the plain
  // object (so the rest of the codebase, which still reads `state.x`,
  // sees the same value).
  document.addEventListener('alpine:init', function () {
    if (window.Alpine) window.Alpine.store('lm', state);
  });
  function lmStoreSet(key, value) {
    // Write THROUGH the Alpine proxy so the reactive system fires its
    // change notifications. The proxy wraps `state` so writes here also
    // update state[key] under the hood — no double-write needed.
    //
    // BUT: loadExistingOrder (and other hydration paths) writes
    // `state.x = data.x` directly before calling lmStoreSet to push the
    // value through Alpine. Those direct writes bypass the proxy and
    // mutate the underlying target; by the time lmStoreSet runs, the
    // proxy sees `old === new` and skips the notification, leaving every
    // `:class="$store.lm.X === '...' && 'active'"` binding stuck on its
    // pre-hydration value. To force re-evaluation, write a distinct
    // sentinel first when the proxy's current value already matches —
    // Alpine batches both writes within the same task, so reactive
    // consumers only see the final value.
    if (window.Alpine && window.Alpine.store) {
      var s = window.Alpine.store('lm');
      if (s) {
        if (s[key] === value) {
          var sentinel = (typeof value === 'string') ? '\u0000__lm_flush__'
                       : (typeof value === 'number') ? Number.NaN
                       : (typeof value === 'boolean') ? !value
                       : null;
          if (sentinel !== value) s[key] = sentinel;
        }
        s[key] = value;
        return;
      }
    }
    // Fallback before Alpine has loaded (very early during boot)
    state[key] = value;
  }

  // Side-effect functions called from Alpine click handlers. Setting the
  // value via these (rather than direct `state.x = ...` in the click) keeps
  // existing downstream sync (map.setStyle, applyFrameRatio, etc.) firing.
  window.lmSetOrientation = function (orientation) {
    if (state.orientation === orientation) return;
    lmStoreSet('orientation', orientation);
    var pf = document.getElementById('lm-print-frame');
    if (pf) pf.classList.toggle('landscape', orientation === 'landscape');
    if (typeof applyFrameRatio === 'function') applyFrameRatio();
    if (typeof applyRibbonImageForOrientation === 'function') applyRibbonImageForOrientation();
    if (typeof updateFrame === 'function') updateFrame(); // orientation-specific frame texture
    if (typeof scheduleLM3DRefresh === 'function') scheduleLM3DRefresh(500);
    if (typeof lmIconCardSync === 'function') lmIconCardSync();
  };

  // Tint the Layout/Composition mini-map tiles to the active theme by pushing
  // its palette into CSS vars on the wrapper (the tiles' SVG glyphs read these
  // vars). Cheap — 4 var writes recolour all tiles at once. Called from
  // updateLayout (covers every load path) and lmSetTheme (instant on tap).
  function lmApplyIconTheme() {
    var t = lmThemeData();
    var w = document.querySelector('.lm-wrapper');
    if (!w || !t) return;
    w.style.setProperty('--lm-ic-bg', t.bg);
    w.style.setProperty('--lm-ic-road', t.road);
    w.style.setProperty('--lm-ic-water', t.water);
    w.style.setProperty('--lm-ic-ink', t.text);
  }

  window.lmSetTheme = function (theme) {
    if (state.theme === theme) {
      // Re-tapping the active theme flips its water between the theme's own
      // colour and blue (the tile dots show which), like Border's mat.
      if (LM_WATER_ALREADY_BLUE.indexOf(theme) !== -1) return;
      window.lmSetWaterTint(state.waterTint === 'blue' ? 'theme' : 'blue');
      return;
    }
    lmStoreSet('theme', theme);
    // Blue water is per-theme: a new theme starts on its own water.
    lmStoreSet('waterTint', 'theme');
    lmRestyleForTheme();
  };

  // Direct pick from the tile's water dots. mode = 'theme' | 'blue'.
  window.lmSetWaterTint = function (mode) {
    var v = mode === 'blue' ? 'blue' : 'theme';
    if (state.waterTint === v || LM_WATER_ALREADY_BLUE.indexOf(state.theme) !== -1) return;
    lmStoreSet('waterTint', v);
    lmRestyleForTheme();
    if (typeof scheduleLmAutosave === 'function') scheduleLmAutosave();
  };
  window.lmBlueWaterFor = function (id) { return lmBlueWater(THEMES[id] || THEMES.classic); };

  function lmRestyleForTheme() {
    var theme = state.theme;
    // Keep the Border-tile mat badge's theme swatch in step immediately, without
    // waiting on the map's styledata → updateLayout path.
    lmStoreSet('themeBg', (THEMES[theme] || THEMES.classic).bg);
    lmStoreSet('altMat', lmAltMat(theme));
    // An alternate mat carries over to the new theme as THAT theme's alternate
    // (Mono's black mat must not survive onto Noir, where it is the theme bg).
    if (state.customBg !== 'theme' && state.customBg !== lmAltMat(theme)) {
      lmStoreSet('customBg', lmAltMat(theme));
      if (typeof updateTextOverlay === 'function') updateTextOverlay();
    }
    lmApplyIconTheme();
    if (!map) return;
    map.setStyle(buildStyle(theme));
    map.once('styledata', function () {
      updateTextOverlay();
      updateLayout();
      if (typeof window.lmHandwritingRedraw === 'function') window.lmHandwritingRedraw();
      setTimeout(addWaterTexture, 300);
      if (state.showLabels) setTimeout(addLabelLayers, 500);
      if (state.showWaterLabels) setTimeout(addWaterLabels, 500);
      if (state.showRoadLabels) setTimeout(addRoadLabels, 500);
      if (state.show3dBuildings && !state.show3dLandmarks) setTimeout(add3dBuildings, 500);
      if (state.show3dTerrain) setTimeout(add3dTerrain, 500);
      if (state.show3dLandmarks) {
        setTimeout(function () {
          try { applyLandmarksConfig(); } catch (e) {}
          applyNightStreetGlow();
          if (typeof addParkOverlay === 'function') addParkOverlay();
        }, 600);
      }
    });
  };

  window.lmSetLayout = function (layout) {
    if (state.layout === layout) {
      // Re-clicking an already-active mat layout (Border/Circle/Heart) toggles
      // its mat between the theme colour and white (the tile dots show which is
      // active). Non-mat layouts are a no-op on re-click as before.
      if (LM_MAT_LAYOUTS.indexOf(layout) !== -1) {
        lmStoreSet('customBg', state.customBg === 'theme' ? lmAltMat() : 'theme');
        if (typeof updateLayout === 'function') updateLayout();
        if (typeof updateTextOverlay === 'function') updateTextOverlay();
        if (typeof scheduleLM3DRefresh === 'function') scheduleLM3DRefresh(300);
      }
      return;
    }
    lmStoreSet('layout', layout);
    // The mat flip is per-layout: any layout change resets the mat to the theme
    // colour, so white never bleeds across layouts / into the cart thumbnail /
    // 3D mockup. (Full has no mat; resetting is harmless there.)
    lmStoreSet('customBg', 'theme');
    if (typeof updateLayout === 'function') updateLayout();
  };

  // Direct mat pick from the tile dots. mode = 'theme' | 'white'. Only meaningful
  // on a mat layout (Border/Circle/Heart — the only tiles that show the dots).
  window.lmSetMat = function (mode) {
    if (LM_MAT_LAYOUTS.indexOf(state.layout) === -1) return;
    // 'white' is the markup's historic name for the alternate mat.
    var v = (mode === 'theme') ? 'theme' : lmAltMat();
    if (state.customBg === v) return;
    lmStoreSet('customBg', v);
    if (typeof updateLayout === 'function') updateLayout();
    if (typeof updateTextOverlay === 'function') updateTextOverlay();
    if (typeof scheduleLM3DRefresh === 'function') scheduleLM3DRefresh(300);
  };

  window.lmSetTextStyle = function (textStyle) {
    if (state.textStyle === textStyle) return;
    var wasPlace = state.textStyle === 'place';
    lmStoreSet('textStyle', textStyle);
    // The "place" composition derives its two lines from the searched
    // location: title = city (large), subtitle = country (small). Populate
    // them on switch-in so the customer sees the place immediately; both
    // remain editable in the Title/Subtitle fields afterwards. Switching
    // away restores the pre-Place text (unless the customer edited the
    // fields while in Place — their edits win over the stash).
    if (textStyle === 'place') lmApplyPlaceComposition();
    else if (wasPlace) lmRestorePrePlaceText();
    if (typeof updateTextOverlay === 'function') updateTextOverlay();
  };

  // Split the current locationName into city + country. Cheap comma-split:
  // first segment = city/POI (Google's most specific part), last = country.
  // "Rome, Metropolitan City of Rome, Italy" → {Rome, Italy};
  // "London, UK" → {London, UK}. Customer can override either field.
  // Google returns a handful of countries abbreviated ("UK", "USA"); expand
  // them so the Place caption reads "United Kingdom" rather than "UK". Most
  // other countries already come through in full ("Italy", "France").
  var LM_COUNTRY_EXPAND = {
    'UK': 'United Kingdom',
    'USA': 'United States',
    'US': 'United States',
    'UAE': 'United Arab Emirates'
  };
  function lmDerivePlaceParts() {
    var name = (state.locationName || '').trim();
    if (!name) return null;
    var parts = name.split(',').map(function (s) { return s.trim(); }).filter(Boolean);
    if (!parts.length) return null;
    var country = parts.length > 1 ? parts[parts.length - 1] : '';
    country = LM_COUNTRY_EXPAND[country.toUpperCase()] || country;
    return { city: parts[0], country: country };
  }

  // Title/subtitle as they stood before Place overwrote them (captured the
  // first time lmApplyPlaceComposition writes), plus the exact values Place
  // wrote — so switch-out can tell the customer's own edits from Place text.
  var lmPrePlaceText = null;
  var lmPlaceAppliedText = null;

  function lmApplyPlaceComposition() {
    var parts = lmDerivePlaceParts() || {};
    // Prefer Google's structured city/country (set on the last place select);
    // fall back to the comma-split of locationName for the default/no-search case.
    var city = state.placeCity || parts.city || '';
    var country = state.placeCountry || parts.country || '';
    if (!city && !country) return;
    if (lmPrePlaceText === null) {
      lmPrePlaceText = { title: state.title, subtitle: state.subtitle };
    } else {
      // Re-applied by a new location search while already in Place: the
      // search handler just synced state.subtitle to the new location (the
      // value every other composition would show), so refresh the stash —
      // otherwise switch-out would restore the previous location's subtitle.
      lmPrePlaceText.subtitle = state.subtitle;
    }
    lmPlaceAppliedText = { title: city, subtitle: country };
    lmStoreSet('title', city);
    lmStoreSet('subtitle', country);
    var ti = document.getElementById('lm-title'); if (ti) ti.value = city;
    var si = document.getElementById('lm-subtitle'); if (si) si.value = country;
  }

  function lmRestorePrePlaceText() {
    if (lmPrePlaceText && lmPlaceAppliedText) {
      var title = (state.title === lmPlaceAppliedText.title) ? lmPrePlaceText.title : state.title;
      var subtitle = (state.subtitle === lmPlaceAppliedText.subtitle) ? lmPrePlaceText.subtitle : state.subtitle;
      lmStoreSet('title', title);
      lmStoreSet('subtitle', subtitle);
      var ti = document.getElementById('lm-title'); if (ti) ti.value = title;
      var si = document.getElementById('lm-subtitle'); if (si) si.value = subtitle;
    }
    lmPrePlaceText = null;
    lmPlaceAppliedText = null;
  }

  window.lmSetLightPreset = function (preset) {
    if (state.lightPreset === preset) return;
    lmStoreSet('lightPreset', preset);
    if (map && state.show3dLandmarks) {
      try { applyLandmarksConfig(); }
      catch (e) { map.setStyle(buildStyle(state.theme)); }
      applyNightStreetGlow();
      if (typeof addParkOverlay === 'function') addParkOverlay();
    }
    // Keep an open 3D view in step (style swap needs settle time)
    scheduleLM3DRefresh(600);
  };

  window.lmSetBuildingDetail = function (bd) {
    if (state.buildingDetail === bd) return;
    lmStoreSet('buildingDetail', bd);
    if (typeof refresh3dBuildings === 'function') refresh3dBuildings();
    // Keep an open 3D view in step
    scheduleLM3DRefresh(600);
  };

  // Font picker honours the active scope (Both / Message / Details), mirroring
  // the star map. 'message' → state.font (the .lm-preview-title inscription),
  // 'details' → state.detailsFont (subtitle + coords), 'both' → both.
  window.lmSetFont = function (font) {
    var scope = state.fontScope || 'both';
    var changed = false;
    if (scope === 'message' || scope === 'both') {
      if (state.font !== font) { lmStoreSet('font', font); changed = true; }
    }
    if (scope === 'details' || scope === 'both') {
      if (state.detailsFont !== font) { lmStoreSet('detailsFont', font); changed = true; }
    }
    if (changed && typeof updateTextOverlay === 'function') updateTextOverlay();
  };

  window.lmSetFontScope = function (scope) {
    if (state.fontScope === scope) return;
    lmStoreSet('fontScope', scope);  // only changes which font the picker edits + the active highlight
  };

  // Active-state for the font grid: reflects the font of the currently scoped
  // target ('details' → detailsFont, otherwise message font). Reads the Alpine
  // store proxy so the :class binding stays reactive.
  window.lmFontActive = function (font) {
    var s = (window.Alpine && window.Alpine.store) ? window.Alpine.store('lm') : state;
    var cur = (s.fontScope === 'details') ? s.detailsFont : s.font;
    return cur === font;
  };

  window.lmSetCustomBg = function (bg) {
    if (state.customBg === bg) return;
    lmStoreSet('customBg', bg);
    if (typeof updateLayout === 'function') updateLayout();
  };

  // ── Post-purchase claim lock ──────────────────────────────────────────
  // The single choke point every type/size/frame change funnels through.
  // Unlike the JS-generated <button> pickers in name-landsat.js, this
  // section's variant tiles are Alpine-driven <div>s rendered server-side by
  // Liquid (never destroyed/rebuilt by JS), and Alpine's @click handlers
  // call window.lmSetSelectedType/Size/Frame directly — no other code path
  // changes a customer's selection. Guarding the three setters themselves
  // (rather than trying to `.disabled` a <div>, which has no such attribute)
  // is therefore the actual lock: it holds regardless of boot timing, since
  // it re-checks window.SkylitClaim.active on every call rather than
  // snapshotting it once. The `opts.claimForce` escape hatch is how the
  // 'skylit:claim-ready' handler below is still allowed to pre-select the
  // PAID variant once, before the lock closes behind it.
  function lmClaimActive() {
    return !!(window.SkylitClaim && window.SkylitClaim.active);
  }

  // Cosmetic/accessibility layer only — the real block is the guard inside
  // each setter above. Applied to every [data-claim-locked] tile: real
  // <button> semantics don't exist on these <div>s, so aria-disabled +
  // pointer-events:none + tabIndex=-1 is the closest equivalent, and is
  // idempotent/safe to call repeatedly (boot, hydration, claim-ready).
  function lmApplyClaimVisualLock() {
    if (!lmClaimActive()) return;
    document.querySelectorAll('[data-claim-locked] .lm-variant-btn').forEach(function (btn) {
      btn.style.pointerEvents = 'none';
      btn.style.opacity = '0.55';
      btn.style.cursor = 'default';
      btn.setAttribute('aria-disabled', 'true');
      btn.tabIndex = -1;
    });
  }

  window.lmSetSelectedType = function (type, opts) {
    if (lmClaimActive() && !(opts && opts.claimForce)) return;
    if (state.selectedType === type) return;
    // A finish sold out at the chosen size (see updateTypeAvailability).
    if (!(opts && opts.claimForce)) {
      var typeBtn = document.querySelector('.lm-type-picker [data-variant-option="' + type + '"]');
      if (typeBtn && typeBtn.classList.contains('lm-soldout')) return;
    }
    lmStoreSet('selectedType', type);
    if (typeof applyFinishAttr === 'function') applyFinishAttr();
    var fp = document.querySelector('.lm-frame-picker-wrap');
    if (fp) fp.style.display = (type === 'Framed') ? 'block' : 'none';
    if (typeof updateFrame === 'function') updateFrame();
    if (typeof filterSizesForType === 'function') filterSizesForType();
    if (typeof updateVariantAndPrice === 'function') updateVariantAndPrice();
    if (typeof updateGiftVisibility === 'function') updateGiftVisibility();
    // 3D mockup swap (Framed ↔ Canvas) when a 3D preview is currently visible
    var is3DActive = (window.LM3DFramedMockup && window.LM3DFramedMockup.isActive && window.LM3DFramedMockup.isActive()) ||
                     (window.LM3DMockup && window.LM3DMockup.isActive && window.LM3DMockup.isActive());
    if (is3DActive) {
      var isCanvas = type.toLowerCase().indexOf('canvas') !== -1;
      (async function () {
        if (window.LM3DFramedMockup && window.LM3DFramedMockup.isActive()) await window.LM3DFramedMockup.hide();
        if (window.LM3DMockup && window.LM3DMockup.isActive()) await window.LM3DMockup.hide();
        if (isCanvas) {
          if (!window.LM3DMockup) {
            var s = document.createElement('script'); s.src = window.lm3DMockupUrl;
            s.onload = async function () { await new Promise(function (r) { setTimeout(r, 100); }); if (window.LM3DMockup) { await window.LM3DMockup.init(); await window.LM3DMockup.show(); } };
            document.head.appendChild(s);
          } else { await window.LM3DMockup.show(); }
        } else {
          if (!window.LM3DFramedMockup) {
            var s2 = document.createElement('script'); s2.src = window.lm3DFramedMockupUrl;
            s2.onload = async function () { await new Promise(function (r) { setTimeout(r, 100); }); if (window.LM3DFramedMockup) { await window.LM3DFramedMockup.init(); await window.LM3DFramedMockup.show(); } };
            document.head.appendChild(s2);
          } else { await window.LM3DFramedMockup.show(); }
        }
      })();
    }
  };

  window.lmSetSelectedSize = function (size, btnEl, opts) {
    if (lmClaimActive() && !(opts && opts.claimForce)) return;
    // Sold-out sizes can be clicked even with pointer-events:none in CSS
    // when JS triggers .click(); guard explicitly.
    if (btnEl && btnEl.classList && btnEl.classList.contains('sold-out')) return;
    if (state.selectedSize === size) return;
    lmStoreSet('selectedSize', size);
    if (typeof applyFrameRatio === 'function') applyFrameRatio();
    if (typeof updateFrameAvailability === 'function') updateFrameAvailability();
    if (typeof updateVariantAndPrice === 'function') updateVariantAndPrice();
  };

  window.lmSetSelectedFrame = function (frame, btnEl, opts) {
    if (lmClaimActive() && !(opts && opts.claimForce)) return;
    if (btnEl && btnEl.classList && btnEl.classList.contains('sold-out')) return;
    if (state.selectedFrame === frame) return;
    lmStoreSet('selectedFrame', frame);
    if (typeof updateFrame === 'function') updateFrame();
    if (typeof updateVariantAndPrice === 'function') updateVariantAndPrice();
  };

  // design-claim.js dispatches this on `document` once a pending claim
  // resolves (detail = the claim row: {type, size, frame, ...} — the exact
  // Shopify option1/option2/option3 strings, since the admin API derives
  // them by splitting the paid line item's variant_title "Type / Size /
  // Frame" the same way Shopify generated it from these three options in
  // the first place, so no translation table is needed here).
  //
  // {claimForce:true} is required to get past the guards just added to the
  // three setters above — this is the one legitimate caller allowed through
  // while a claim is active. Runs type → size → frame in that order because
  // each setter's side effects gate the next: lmSetSelectedType re-filters
  // which sizes are visible/sold-out, lmSetSelectedSize re-filters frame
  // availability, so selecting out of order could clobber a size/frame that
  // becomes unavailable a moment later.
  //
  // No pendingClaimVariant queue (unlike name-landsat.js): this section's
  // variant tiles are rendered server-side by Liquid, not created by an
  // async boot sequence, so they already exist in the DOM by the time any
  // JS runs — there is nothing for this listener to race against.
  document.addEventListener('skylit:claim-ready', function (e) {
    var claim = e.detail || {};
    if (claim.type) window.lmSetSelectedType(claim.type, { claimForce: true });
    if (claim.size) window.lmSetSelectedSize(claim.size, null, { claimForce: true });
    // Print/Canvas have no frame option — only apply when the paid type is
    // Framed, mirroring updateFrameAvailability()'s own guard.
    if (claim.type === 'Framed' && claim.frame) {
      window.lmSetSelectedFrame(claim.frame, null, { claimForce: true });
    }
    lmApplyClaimVisualLock();
  });

  // Expose order ID and state for save-for-later toolbar
  window.lmOrderId = state.orderId;
  // Expose theme bg + text colour so the 3D mockup can match the composition,
  // regardless of the current layout (full layout clears #lm-layout-bg).
  window.lmGetThemeBg = function() { return lmThemeData().bg; };
  window.lmGetThemeText = function() { return lmThemeData().text; };
  window.lmGetSaveData = function() {
    return {
      order: state.orderId, lat: state.lat, lng: state.lng, zoom: state.zoom,
      bearing: state.bearing, pitch: state.pitch,
      theme: state.theme, layout: state.layout, text_style: state.textStyle, passepartout: state.passepartout,
      title: state.title, subtitle: lmJoinSubtitle(),
      handwriting_enabled: state.handwritingEnabled ? 1 : 0,
      handwriting_strokes: state.handwritingEnabled && state.handwritingStrokes && state.handwritingStrokes.length
        ? JSON.stringify({ vbw: 1000, vbh: 240, strokes: state.handwritingStrokes })
        : null,
      handwriting_size: state.handwritingSize,
      handwriting_height: state.handwritingHeight,
      show_coordinates: !!lmCoordsLine(), coords_text: (typeof state.coordsText === 'string' && state.coordsText.trim()) ? state.coordsText.trim() : null, water_tint: state.waterTint === 'blue' ? 'blue' : null, show_labels: state.showLabels, show_water_labels: state.showWaterLabels, show_road_labels: state.showRoadLabels, show_poi_labels: state.showPOILabels, show_land_borders: state.showLandBorders, show_maritime_borders: state.showMaritimeBorders, show_3d_buildings: state.show3dBuildings, show_3d_terrain: state.show3dTerrain, building_detail: state.buildingDetail, show_3d_landmarks: state.show3dLandmarks, lm_mode: (window.SKYLIT_LM_MODE === 'landmarks' ? 'landmarks' : 'flat'), light_preset: state.lightPreset, font: state.font, details_font: state.detailsFont,
      text_color: lmEffectiveTextColor(),
      location_name: state.locationName, size: state.selectedSize,
      selected_type: state.selectedType, selected_frame: state.selectedFrame, gift_option: state.selectedGift || 'none',
      orientation: state.orientation, custom_bg: state.customBg,
      icons_json: JSON.stringify(getIconsData()), icon_size: iconSize,
      bounds: map ? JSON.stringify(map.getBounds().toArray()) : null,
      // Use the visible slot, not map.getContainer() — when supersample is
      // active the map div's clientWidth is the SS-inflated value (e.g. 4×),
      // which would throw off the renderer's framing math. state.zoom is also
      // saved un-offset, so the (viewport, zoom) pair sent here describes
      // what the customer would have seen at 1× without supersample.
      preview_viewport_w: lmViewportW(),
      preview_viewport_h: lmViewportH()
    };
  };

  // Visible preview-slot size, with a last-known-good cache. The live read
  // returns 0 whenever #lm-print-frame is display:none at save time (e.g.
  // .sm-gallery-viewing while browsing product photos — common on mobile),
  // and a saved preview_viewport_w of 0 disables the cross-device zoom
  // compensation on every later restore/render of the order (real case:
  // order 2l0tsa803n7, pvw=0, phone vs desktop framed different areas).
  var lmLastVW = 0, lmLastVH = 0;
  function lmViewportW() {
    var i = document.getElementById('lm-map-inner');
    var w = i ? i.clientWidth : (map && map.getContainer ? map.getContainer().clientWidth : 0);
    if (w > 0) { lmLastVW = w; return w; }
    return lmLastVW > 0 ? lmLastVW : null;
  }
  function lmViewportH() {
    var i = document.getElementById('lm-map-inner');
    var h = i ? i.clientHeight : (map && map.getContainer ? map.getContainer().clientHeight : 0);
    if (h > 0) { lmLastVH = h; return h; }
    return lmLastVH > 0 ? lmLastVH : null;
  }

  // Re-apply a saved-camera restore on every map resize until the customer
  // first interacts with the map (or 30s). Hydration keeps resizing the slot
  // well past any fixed settle window on slow landmark loads, and a restore
  // computed against a mid-boot width leaves the wrong zoom. Resize-only
  // re-apply can't fight pans/zooms; detaching on first interaction stops a
  // late window resize from reverting the customer's edits.
  function _lmAttachRestoreOnResize(restoreFn) {
    var detached = false;
    var onResize = function() { if (!detached) restoreFn(); };
    var detach = function() {
      if (detached) return;
      detached = true;
      try { map.off('resize', onResize); } catch (e) {}
    };
    map.on('resize', onResize);
    var mapEl = document.getElementById('lm-map');
    if (mapEl) {
      ['pointerdown', 'wheel', 'touchstart'].forEach(function(ev) {
        mapEl.addEventListener(ev, detach, { once: true, passive: true });
      });
    }
    setTimeout(detach, 30000);
  }

  // Higher pixelRatio when 3D Landmarks is on so Mapbox renders denser building
  // / landmark detail in the preview, closer to what the print produces. Mobile
  // is one step lower to keep pan/tilt smooth on mid-range Android.
  // 3D Landmarks (Standard import) and our own 3D Buildings/Terrain are
  // mutually exclusive — they belong to different rendering paths and
  // combining them produces z-fighting / no visible benefit. Disable the other
  // two rows when Landmarks is on so customers don't think they're broken.
  function updateOther3dRows() {
    var disabled = !!state.show3dLandmarks;
    ['lm-3d-buildings-row', 'lm-3d-terrain-row'].forEach(function(id){
      var row = document.getElementById(id);
      if (!row) return;
      row.style.opacity = disabled ? '0.4' : '';
      row.style.pointerEvents = disabled ? 'none' : '';
    });
    var bdRow = document.getElementById('lm-bd-detail');
    if (bdRow && disabled) bdRow.style.display = 'none';
  }

  // ── Supersample mode ──
  // When on, the #lm-map div is sized 200%×200% and CSS-scaled back to fit
  // its slot. Mapbox sees a logical viewport 2× wider/taller, which raises
  // its tile-LOD budget — more distant buildings render before sub-pixel
  // culling. We pair that with a +1 zoom internally so the angled 3D camera
  // still frames the same composition at the same on-screen size.
  // state.zoom is kept in user-facing (un-offset) units so the value saved
  // on the order is what the renderer expects.
  var ZOOM_SS_OFFSET = 0;
  // Icons (DOM markers) and Mapbox place labels both live inside the
  // supersampled #lm-map (sized SS_SCALE× and CSS-scaled 1/SS_SCALE). When
  // 3D buildings bump SS_SCALE 2→4 for extra footprint LOD when zoomed out,
  // their on-screen size would otherwise halve. lmIconSSMult (= SS_SCALE /
  // SS_BASE) is the factor we grow marker font-size + label text-size by to
  // hold their size constant.
  var lmIconSSMult = 1;

  function applyZoom(z) { return z + ZOOM_SS_OFFSET; }
  function reportZoom(z) { return z - ZOOM_SS_OFFSET; }

  // First-visit map controls hint. Stored in localStorage so it only shows once.
  // Force-replay with ?lm_help=1, suppress with ?lm_help=0.
  function maybeShowOnboarding() {
    var overlay = document.getElementById('lm-onboard');
    if (!overlay) return;
    var KEY = 'lm_onboarded_v1';
    var force = false, suppress = false;
    try {
      var qs = new URLSearchParams(location.search);
      force = qs.get('lm_help') === '1';
      suppress = qs.get('lm_help') === '0';
      if (suppress) { localStorage.setItem(KEY, '1'); return; }
      if (!force && localStorage.getItem(KEY) === '1') return;
    } catch(e) {}
    var isTouch = window.matchMedia && window.matchMedia('(pointer: coarse)').matches;
    var rows = document.getElementById('lm-onboard-rows');
    var sub  = document.getElementById('lm-onboard-sub');
    if (sub) sub.textContent = isTouch ? 'Touch controls' : 'Mouse controls';
    var html;
    if (isTouch) {
      html =
        '<div class="lm-onboard-row">' +
          '<div class="lm-onboard-icon">☝️</div>' +
          '<div class="lm-onboard-text"><b>One finger</b> drags the map around.</div>' +
        '</div>' +
        '<div class="lm-onboard-row">' +
          '<div class="lm-onboard-icon">✌️</div>' +
          '<div class="lm-onboard-text"><b>Two fingers</b> tilt and rotate. Pinch to zoom.</div>' +
        '</div>';
    } else {
      html =
        '<div class="lm-onboard-row">' +
          '<div class="lm-onboard-icon">🖱️</div>' +
          '<div class="lm-onboard-text"><b>Left-click + drag</b> to pan around the map.</div>' +
        '</div>' +
        '<div class="lm-onboard-row">' +
          '<div class="lm-onboard-icon">🎯</div>' +
          '<div class="lm-onboard-text"><b>Right-click + drag</b> to tilt and rotate. Scroll to zoom.</div>' +
        '</div>';
    }
    if (rows) rows.innerHTML = html;
    overlay.classList.add('visible');
    var dismissed = false;
    function dismiss() {
      if (dismissed) return;
      dismissed = true;
      overlay.classList.remove('visible');
      try { localStorage.setItem(KEY, '1'); } catch(e) {}
    }
    var btn = document.getElementById('lm-onboard-dismiss');
    if (btn) btn.addEventListener('click', dismiss);
    overlay.addEventListener('click', function(e){ if (e.target === overlay) dismiss(); });

    // The hint is now a passive toast that does not cover the map, so the
    // visitor can start dragging straight away — clear it the moment they
    // do rather than making them tap "Got it" first. Passive listeners so
    // they never delay a map gesture.
    var mapEl = document.getElementById('lm-map');
    if (mapEl) {
      ['pointerdown', 'touchstart', 'wheel'].forEach(function (ev) {
        mapEl.addEventListener(ev, dismiss, { once: true, passive: true });
      });
    }
  }

  function maybeEnableSupersample() {
    // Headless CF cart-thumbnail render: skip supersample entirely. It renders
    // the map at SS_BASE× (2× desktop), and SwiftShader leaves the top of a
    // SHORT canvas unpainted (white band) — seen on A4 LANDSCAPE (1200×848
    // supersampled). The thumbnail is composited small in the wall mockup, so
    // the extra tile detail is wasted, and the 1× canvas paints fully. The real
    // 300dpi print is rendered separately by the Mac daemon, unaffected.
    if (window.__LM_HEADLESS === true) return;
    try {
      var qs = new URLSearchParams(location.search);
      if (qs.get('lm_ss') === '1') localStorage.removeItem('lm_supersample');
      if (qs.get('lm_ss') === '0') localStorage.setItem('lm_supersample', '0');
    } catch(e) {}
    // On by default for both modes; opt out via ?lm_ss=0 (sticky in localStorage).
    var disabled = false;
    try { disabled = localStorage.getItem('lm_supersample') === '0'; } catch(e) {}
    if (disabled) return;
    var inner = document.getElementById('lm-map-inner');
    if (!inner) return;
    var isLandmarks = (window.SKYLIT_LM_MODE || 'flat') === 'landmarks';
    var isTouch = window.matchMedia && window.matchMedia('(pointer: coarse)').matches;
    var isPhone = isTouch && window.innerWidth < 768;
    // Phones break 3D landmarks under SS (Standard import + supersample
    // canvas exceeds the mobile GPU budget and the basemap drops out).
    // Flat maps don't render 3D buildings so phones are fine there — the
    // pixel buffer is well within mobile limits and surfaces road tiles
    // a zoom level earlier, which is the whole point. Mobile flat uses
    // a higher multiplier (3×) than desktop because the mobile customizer
    // sits at a wider zoom by default and benefits more from the extra
    // tile LOD; desktop already has a comfortable starting zoom.
    if (isLandmarks && isPhone) return;
    inner.classList.add('lm-supersample');
    var SS_BASE = isLandmarks ? (isTouch ? 3 : 4) : (isTouch ? 3 : 2);
    var SS_SCALE = SS_BASE;
    ZOOM_SS_OFFSET = Math.log2(SS_SCALE);

    // Inline pixel sizing — Mapbox writes its own inline width/height to the
    // map container after init, which defeats CSS percentages. Fix it in JS:
    // size #lm-map to 4× the visible slot in absolute px, scale it back, and
    // keep it in sync when the slot resizes (orientation, panel toggles).
    var mapDiv = document.getElementById('lm-map');
    function syncSupersample() {
      var w = inner.clientWidth, h = inner.clientHeight;
      if (!w || !h) return;
      mapDiv.style.position = 'absolute';
      mapDiv.style.top = '0';
      mapDiv.style.left = '0';
      mapDiv.style.width  = (w * SS_SCALE) + 'px';
      mapDiv.style.height = (h * SS_SCALE) + 'px';
      mapDiv.style.transform = 'scale(' + (1 / SS_SCALE) + ')';
      mapDiv.style.transformOrigin = '0 0';
      if (window.__lmMapInst) {
        try { window.__lmMapInst.resize(); } catch(e) {}
      }
    }
    syncSupersample();
    window.__lmSyncSS = syncSupersample;
    if (window.ResizeObserver) {
      try { new ResizeObserver(syncSupersample).observe(inner); } catch(e) {}
    } else {
      window.addEventListener('resize', syncSupersample);
    }

    // Dynamic scale for 3D buildings on flat maps (desktop only). The OMT
    // building source-layer only carries the full footprint set at higher
    // tile zooms, so at the flat-map base SS of 2 the preview shows far
    // fewer buildings than the print render (whose large canvas sits ~2
    // zoom levels higher). Bumping to 4 while buildings are on gives the
    // preview the same tile LOD the landmarks mode already runs at.
    // Touch keeps its base scale — the mobile GPU budget comment above.
    window.__lmSetSSForBuildings = function(on) {
      if (isLandmarks || isTouch) return;
      var target = on ? 4 : SS_BASE;
      if (target === SS_SCALE) return;
      var oldOffset = ZOOM_SS_OFFSET;
      SS_SCALE = target;
      ZOOM_SS_OFFSET = Math.log2(SS_SCALE);
      syncSupersample();
      if (window.__lmMapInst) {
        // Keep the same geographic view: the bigger canvas would otherwise
        // zoom the visible map out by the offset delta.
        try { window.__lmMapInst.setZoom(window.__lmMapInst.getZoom() + (ZOOM_SS_OFFSET - oldOffset)); } catch(e) {}
      }
      // The SS scale just changed, and icons/labels live inside the scaled
      // #lm-map — grow their size by the new factor so they stay put visually.
      lmIconSSMult = SS_SCALE / SS_BASE;
      lmApplySSSizes();
    };
  }

  // iOS Safari hits its WebGL memory cap on Mapbox Standard's 3D landmarks
  // far sooner than Chromium. Detect it once for use across the renderer.
  // Once-per-session, soft notice for iOS Safari users: the 3D landmarks
  // page is functional but degraded there (Safari's WebGL/VRAM limits).
  // Suggest a desktop/laptop session for the smoothest customisation.
  function maybeShowIOSNotice() {
    if (!isIOSSafari()) return;
    try { if (sessionStorage.getItem('lm_ios_notice_v1')) return; } catch(e) {}
    // Anchor to the wrap (full customiser width) rather than the print
    // frame so the notice has horizontal room and doesn't overlap the
    // map artwork.
    var wrap = document.querySelector('.lm-wrapper') || document.body;
    if (!wrap) return;
    var n = document.createElement('div');
    n.setAttribute('role', 'status');
    n.style.cssText = [
      'position:fixed','left:12px','right:12px','bottom:12px',
      'margin:0 auto','max-width:520px',
      'background:rgba(28,22,16,0.94)','color:#f4ead8',
      'font-size:13px','line-height:1.35','font-family:inherit',
      'padding:10px 12px','border-radius:10px',
      'box-shadow:0 6px 24px rgba(0,0,0,0.30)',
      'z-index:9999','display:flex','align-items:center','gap:10px',
      '-webkit-backdrop-filter:blur(6px)','backdrop-filter:blur(6px)'
    ].join(';');
    n.innerHTML =
      '<span style="flex:1;min-width:0">For the smoothest 3D experience, design your map on a laptop or desktop.</span>' +
      '<button type="button" aria-label="Dismiss" style="flex:none;background:transparent;border:0;color:#f4ead8;font-size:20px;line-height:1;padding:2px 6px;cursor:pointer;opacity:.85">×</button>';
    wrap.appendChild(n);
    n.querySelector('button').addEventListener('click', function(){
      try { sessionStorage.setItem('lm_ios_notice_v1', '1'); } catch(e) {}
      n.remove();
    });
    setTimeout(function(){
      try { if (n && n.parentNode) n.style.opacity = '0.85'; } catch(e) {}
    }, 8000);
  }

  function isIOSSafari() {
    var ua = (navigator && navigator.userAgent) || '';
    var isIOS = /iPad|iPhone|iPod/.test(ua) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
    var isSafari = /Safari/.test(ua) && !/CriOS|FxiOS|EdgiOS|OPiOS/.test(ua);
    return isIOS && isSafari;
  }
  // Tag <html> with a class on iOS Safari so CSS can hide features that
  // Mapbox doesn't actually render there (3D Terrain via raster-dem
  // silently no-ops on iOS Safari WebGL2 — setTerrain neither throws
  // nor sticks). Done once at module init.
  try {
    if (isIOSSafari() && document.documentElement) {
      document.documentElement.classList.add('lm-ios-safari');
    }
  } catch (e) {}

  function desiredPixelRatio() {
    if (!state.show3dLandmarks) return 2;
    // iOS Safari runs WebGL on Metal with much tighter texture/VRAM
    // budgets than Chromium. Drop further to keep landmarks rendering.
    if (isIOSSafari()) return 1.5;
    // Phones can't handle the higher pixelRatio with landmarks on — the 3D
    // meshes silently fail to load. Keep mobile at 2.
    var isPhone = window.matchMedia && window.matchMedia('(max-width: 768px)').matches;
    if (isPhone) return 2;
    var isTouch = window.matchMedia && window.matchMedia('(pointer: coarse)').matches;
    return isTouch ? 3 : 4;
  }

  // ── Runaway-pan guard ──────────────────────────────────────────────────
  //
  // At high pitch the top of the viewport approaches the horizon, where one
  // screen pixel covers an unbounded amount of ground. Mapbox's drag handler
  // unprojects the grabbed point onto the ground plane, so a drag that starts
  // up there translates the camera hundreds of kilometres in a single gesture.
  //
  // Order 9kxq8pn53bk (1 Sep 2026) searched "Raskelf, York", tilted to 76°,
  // and two drags later was framed on open Atlantic ~300km west of Ireland:
  // 56 screen widths of travel across two gestures, with the zoom barely
  // moving. It printed as a blank navy rectangle, and the customer confirmed
  // it three times — a blank map on the midnight theme reads as a map that
  // has not finished loading, not as a map of the wrong place.
  //
  // Framing a print means nudging by a screen width or two; the horizon
  // artifact moves tens. A gesture that travels further than the cap snaps
  // the CENTRE back to where the gesture started, keeping whatever zoom,
  // bearing and pitch the same gesture changed — so a two-finger tilt that
  // also flung the map keeps the tilt and loses only the fling.
  //
  // Only user gestures are guarded: geocoder flyTo, preset loads, the mobile
  // nudge buttons and the saved-camera restore all move the camera on
  // purpose and arrive without an originalEvent.
  // ── Horizon-grab guard ─────────────────────────────────────────────────
  //
  // Mapbox pans by moving the ground point under the cursor to wherever the
  // cursor goes. Near the horizon that ground point is hundreds of kilometres
  // away and its distance per screen pixel is unbounded, so a short drag high
  // in a tilted frame translates the camera across a country.
  //
  // Order 9kxq8pn53bk (1 Sep 2026) searched "Raskelf, York", stayed at the
  // 3D-landmark default tilt of 76 degrees, and two drags later was framed on
  // open Atlantic ~300km west of Ireland. It printed as a blank navy rectangle
  // and the customer confirmed it three times: a blank map on the midnight
  // theme reads as a map still loading, not as a map of the wrong place.
  //
  // Measured on the live editor at zoom 11.7, as the grab point's ground
  // distance from the camera centre in multiples of the frame's own near-field
  // span (centre to bottom edge) — a ratio, so it holds at any zoom, latitude
  // or viewport size:
  //
  //   pitch 0    1.5x        pitch 60   2.7x
  //   pitch 45   2.0x        pitch 70   3.2x - 8.3x  (top of frame)
  //                          pitch 76   3.7x         (top half, saturated)
  //
  // Everything Mapbox handles sanely sits under 3x; the runaway region starts
  // above it. Grabs beyond that ratio do not pan at all, so the camera never
  // moves wrongly and there is nothing to undo. At pitch 85 the near field is
  // itself past the horizon clamp and every pixel is degenerate, which the
  // absolute near-field test catches — panning is refused until the tilt comes
  // down, which is the only honest answer at that camera.
  var LM_MAX_GRAB_SPANS = 3;
  var LM_MAX_NEARFIELD_SCREENS = 30;

  /** Metres per CSS pixel at a given latitude and Mapbox (512px-tile) zoom. */
  function lmMetresPerPixel(lat, zoom) {
    return 78271.516 * Math.cos(lat * Math.PI / 180) / Math.pow(2, zoom);
  }

  function lmDistanceMetres(a, b) {
    var R = 6371000;
    var dLat = (b.lat - a.lat) * Math.PI / 180;
    var dLng = (b.lng - a.lng) * Math.PI / 180;
    var lat1 = a.lat * Math.PI / 180, lat2 = b.lat * Math.PI / 180;
    var h = Math.sin(dLat / 2) * Math.sin(dLat / 2) +
            Math.sin(dLng / 2) * Math.sin(dLng / 2) * Math.cos(lat1) * Math.cos(lat2);
    return 2 * R * Math.asin(Math.min(1, Math.sqrt(h)));
  }

  /** True when a drag starting at this canvas pixel would fly the camera. */
  function lmGrabIsDegenerate(x, y) {
    if (!map) return false;
    var el = document.getElementById('lm-map-inner');
    var w = (el && el.clientWidth) || 360;
    var h = (el && el.clientHeight) || 360;
    try {
      var c = map.getCenter();
      var span = lmDistanceMetres(c, map.unproject([w / 2, h - 1]));
      if (!(span > 0)) return false;
      // Whole camera past the horizon clamp: no pixel in the frame is safe.
      if (span > LM_MAX_NEARFIELD_SCREENS * lmMetresPerPixel(c.lat, map.getZoom()) * w) return true;
      return lmDistanceMetres(c, map.unproject([x, y])) > LM_MAX_GRAB_SPANS * span;
    } catch (e) {
      return false; // never let the guard itself break panning
    }
  }

  function lmShowPanNotice() {
    var host = document.getElementById('lm-map-inner') || document.getElementById('lm-map');
    if (!host) return;
    var el = document.getElementById('lm-pan-notice');
    if (!el) {
      el = document.createElement('div');
      el.id = 'lm-pan-notice';
      el.setAttribute('role', 'status');
      el.style.cssText = 'position:absolute;left:50%;bottom:14px;transform:translateX(-50%);' +
        'z-index:5;pointer-events:none;max-width:88%;text-align:center;' +
        'background:rgba(28,20,12,0.88);color:#f6ecdd;font-size:12px;line-height:1.35;' +
        'padding:7px 12px;border-radius:8px;opacity:0;transition:opacity .2s;';
      if (getComputedStyle(host).position === 'static') host.style.position = 'relative';
      host.appendChild(el);
    }
    el.textContent = 'That part of the map is near the horizon \u2014 drag from lower down, or reduce the tilt, to move around.';
    requestAnimationFrame(function() { el.style.opacity = '1'; });
    clearTimeout(el.__t);
    el.__t = setTimeout(function() { el.style.opacity = '0'; }, 3600);
  }

  /**
   * Bound once on the map container rather than per map instance: the
   * container outlives every map (the landmark toggle rebuilds the map inside
   * the same div), and `map` is read at event time so the listener always
   * addresses the live instance.
   *
   * Capture phase so this runs before Mapbox's own pointer handling, which is
   * what makes disabling dragPan here take effect for the gesture in progress.
   */
  function bindPanGuard() {
    var host = document.getElementById('lm-map');
    if (!host || host.__lmPanGuardBound) return;
    host.__lmPanGuardBound = true;
    var suppressed = false;

    host.addEventListener('pointerdown', function(e) {
      if (!map || state.locked || suppressed) return;
      var canvas = map.getCanvas();
      if (!canvas) return;
      var r = canvas.getBoundingClientRect();
      if (!lmGrabIsDegenerate(e.clientX - r.left, e.clientY - r.top)) return;
      try { map.dragPan.disable(); } catch (err) { return; }
      suppressed = true;
      lmShowPanNotice();
    }, { capture: true, passive: true });

    ['pointerup', 'pointercancel', 'touchend', 'touchcancel', 'mouseup', 'blur'].forEach(function(ev) {
      window.addEventListener(ev, function() {
        if (!suppressed) return;
        suppressed = false;
        // Restore through applyMapLock rather than a bare enable(), so a frame
        // locked mid-gesture stays locked.
        applyMapLock();
      }, { capture: true, passive: true });
    });
  }

  function attachMapListeners() {
    map.on('moveend', function() {
      var c = map.getCenter();
      state.lat = c.lat; state.lng = c.lng;
      state.zoom = reportZoom(map.getZoom());
      state.bearing = map.getBearing();
      state.pitch = map.getPitch();
      var rs = document.getElementById('lm-rotate'); if (rs) rs.value = state.bearing;
      var ps = document.getElementById('lm-pitch'); if (ps) ps.value = state.pitch;
      var rv = document.getElementById('lm-rotate-val'); if (rv) rv.textContent = Math.round(state.bearing) + '°';
      var pv = document.getElementById('lm-pitch-val'); if (pv) pv.textContent = Math.round(state.pitch) + '°';
      updateTextOverlay();
    });
    map.on('zoomend', function() {
      state.zoom = reportZoom(map.getZoom());
      var slider = document.getElementById('lm-zoom');
      if (slider) slider.value = state.zoom;
    });
    bindPanGuard();
    // Re-apply the lock to the freshly built map. attachMapListeners() runs
    // after every map (re)creation — initial boot, landmark toggle, preset
    // load, crash recovery — so this keeps a locked frame frozen across all
    // of them instead of silently re-enabling gestures.
    applyMapLock();
  }

  // ── Map lock (freeze framing) ──────────────────────────────────────────
  // Mapbox gesture handlers we toggle so a locked map can't be nudged by an
  // accidental drag, scroll, pinch or rotate. touchPitch only exists on some
  // GL versions, so every access is guard-checked below.
  var LM_LOCK_HANDLERS = ['dragPan', 'scrollZoom', 'dragRotate', 'touchZoomRotate', 'touchPitch', 'doubleClickZoom', 'keyboard', 'boxZoom'];
  // Deliberate controls disabled alongside gestures so "locked" means fully
  // frozen — no half-state where the sidebar slider still moves a locked map.
  var LM_LOCK_CONTROL_IDS = ['lm-zoom', 'lm-rotate', 'lm-pitch', 'lm-rotate-pitch-reset', 'lm-mobile-zoom-in', 'lm-mobile-zoom-out', 'lm-mobile-rotate-ccw', 'lm-mobile-rotate-cw', 'lm-mobile-tilt-up', 'lm-mobile-tilt-down'];

  // The preview stage (lm-stage.js) pauses gestures for its own reasons: the
  // phone preview is shrunk to a thumbnail, or Room view has the map in
  // perspective. Kept apart from state.locked, which is the customer's lock
  // (saved, shown on the lock button, and it also disables the sliders).
  var lmStageLocks = {};
  function lmStageLocked() {
    for (var k in lmStageLocks) if (lmStageLocks[k]) return true;
    return false;
  }
  window.lmStageLock = function (reason, on) {
    lmStageLocks[reason] = !!on;
    applyMapLock();
  };
  window.lmUserLocked = function () { return !!state.locked; };
  window.lmMapResize = function () { if (map) setTimeout(function () { map.resize(); }, 30); };

  // Mapbox sizes the map from its container's ON-SCREEN box
  // (getBoundingClientRect) and divides out only the first scale transform
  // it finds walking up, which is the supersample scale on #lm-map. Any other
  // transform above it, the preview stage's shrink (scale 0.76 while the phone
  // preview is scrolled) or Room view's matrix3d, then reads as a smaller
  // container, and the next resize (iOS fires one as the address bar hides)
  // re-fits the map to it: the print shows a zoomed-in crop of the framing.
  // The container's layout size is what that arithmetic reconstructs anyway,
  // and transforms don't touch it.
  function lmPinMapSizing() {
    var P = window.mapboxgl && window.mapboxgl.Map && window.mapboxgl.Map.prototype;
    if (!P || P.__lmLayoutSized || typeof P._updateContainerDimensions !== 'function') return;
    var measured = P._updateContainerDimensions;
    P._updateContainerDimensions = function () {
      var c = this._container;
      if (c && c.clientWidth && c.clientHeight) {
        this._containerWidth = c.clientWidth;
        this._containerHeight = c.clientHeight;
        return;
      }
      return measured.apply(this, arguments);
    };
    P.__lmLayoutSized = true;
  }

  function applyMapLock() {
    if (!map) return;
    var off = state.locked || lmStageLocked();
    LM_LOCK_HANDLERS.forEach(function(h) {
      var handler = map[h];
      if (!handler) return;
      try { off ? handler.disable() : handler.enable(); } catch (e) {}
    });
  }

  function syncLockUI() {
    LM_LOCK_CONTROL_IDS.forEach(function(id) {
      var el = document.getElementById(id);
      if (el) el.disabled = state.locked;
    });
    var inner = document.getElementById('lm-map-inner');
    if (inner) inner.classList.toggle('lm-locked', state.locked);
    var btns = document.querySelectorAll('.lm-lock-btn');
    for (var i = 0; i < btns.length; i++) {
      var label = state.locked ? 'Unlock map position' : 'Lock map position';
      btns[i].classList.toggle('locked', state.locked);
      btns[i].setAttribute('aria-pressed', state.locked ? 'true' : 'false');
      btns[i].setAttribute('title', label);
      // The cluster button's accessible name comes from aria-label, not title,
      // so flip it too — otherwise screen readers announce a stale state.
      if (btns[i].hasAttribute('aria-label')) btns[i].setAttribute('aria-label', label);
    }
  }

  function setMapLock(locked) {
    state.locked = !!locked;
    applyMapLock();
    syncLockUI();
  }

  function bindLock() {
    var btns = document.querySelectorAll('.lm-lock-btn');
    for (var i = 0; i < btns.length; i++) {
      btns[i].addEventListener('click', function() { setMapLock(!state.locked); });
    }
    syncLockUI();
  }

  // Recreate the map with the desired pixelRatio, preserving camera + UI state.
  // Used when toggling 3D Landmarks since pixelRatio is fixed at construction.
  function recreateMapForLandmarks() {
    if (!map) return;
    var center = map.getCenter();
    var zoom = map.getZoom();
    var bearing = map.getBearing();
    var pitch = map.getPitch();
    try { map.remove(); } catch(e) {}
    lmPinMapSizing();
    map = new mapboxgl.Map({
      container: 'lm-map',
      style: buildStyle(state.theme),
      center: [center.lng, center.lat],
      zoom: zoom,
      bearing: bearing,
      pitch: pitch,
      interactive: true,
      attributionControl: false,
      preserveDrawingBuffer: true,
      pixelRatio: desiredPixelRatio(),
      maxPitch: state.show3dLandmarks ? 85 : 45
    });
    document.getElementById('lm-map').__mapInstance = map;
    window.__lmMapInst = map;
    attachMapListeners();
    attachCrashRecovery();
    map.on('load', function() {
      addWaterTexture();
      if (state.show3dBuildings && !state.show3dLandmarks) add3dBuildings();
      if (state.show3dTerrain) add3dTerrain();
      if (state.show3dLandmarks) {
        try { applyLandmarksConfig(); } catch(e) {}
        applyNightStreetGlow(); addParkOverlay();
        hideStandardRoadArrows();
        attachArrowGuard();
      }
      if (state.showLabels) addLabelLayers();
      if (state.showWaterLabels) addWaterLabels();
      if (state.showRoadLabels) addRoadLabels();
      // Re-attach any placed icons. map.remove() above detached every
      // marker DOM element — without this rebind, saved icons stay in
      // placedIcons (and in the "Your icons" sidebar) but never render
      // on the new map. Same goes for icons placed before a recreate
      // triggered by toggling landmarks or clicking a preset.
      if (typeof window.__lmRebindIcons === 'function') {
        try { window.__lmRebindIcons(); } catch (e) { console.warn('[lm] rebind icons failed', e); }
      }
    });
  }

  // Self-healing: if the WebGL context is lost (mobile GPU eviction is the
  // common cause for this product) or Mapbox throws a fatal error, rebuild
  // the map preserving camera + state. Throttled so a render-loop crash
  // can't ping-pong us into infinite recreates.
  var _lmLastRecover = 0;
  // recreateMapForLandmarks() has no built-in retry limit, so a device whose
  // GPU keeps evicting the context would recreate forever with nothing ever
  // telling the customer. Count consecutive recoveries and, once they're
  // genuinely exhausted, fall back to the shared message instead of another
  // recreate. A successful 'load' resets the count, so an otherwise-healthy
  // session with an occasional isolated blip never gets flagged as failed.
  var _lmRecoverAttempts = 0;
  var LM_MAX_RECOVER_ATTEMPTS = 3;
  function attachCrashRecovery() {
    if (!map) return;
    var canvas = map.getCanvas && map.getCanvas();
    function recover(reason) {
      var now = Date.now();
      if (now - _lmLastRecover < 4000) return;
      _lmLastRecover = now;
      _lmRecoverAttempts++;
      if (_lmRecoverAttempts > LM_MAX_RECOVER_ATTEMPTS) {
        try { console.warn('[lm] map recovery exhausted after', _lmRecoverAttempts, 'attempts:', reason); } catch(e) {}
        // Shared failure message once Mapbox recovery has genuinely given up.
        if (window.SkylitWebGL) {
          var host = document.querySelector('.lm-map-wrap') || document.querySelector('#lm-print-frame');
          if (host) window.SkylitWebGL.showFallback(host.parentNode || host, { label: 'location map' });
        }
        return;
      }
      try { console.warn('[lm] map recover:', reason); } catch(e) {}
      try { recreateMapForLandmarks(); } catch(e) {
        try { console.error('[lm] recover failed', e); } catch(_) {}
      }
    }
    if (canvas) {
      canvas.addEventListener('webglcontextlost', function(e) {
        e.preventDefault();
        recover('webglcontextlost');
      }, false);
    }
    map.on('error', function(ev) {
      var msg = (ev && ev.error && ev.error.message) || '';
      if (/WebGL|context|out of memory|GPU/i.test(msg)) recover('error:' + msg);
    });
    map.on('load', function() { _lmRecoverAttempts = 0; });
  }

  function init() {
    if (typeof mapboxgl === 'undefined') return;
    mapboxgl.accessToken = MAPBOX_TOKEN;

    var mapContainer = document.getElementById('lm-map');
    if (!mapContainer) return;

    // Block accidental ATC submission via Enter on any text input inside
    // the product form. Add-to-cart should only fire from an explicit
    // click (or Enter while the submit button itself is focused).
    var lmForm = document.getElementById('lm-product-form');
    if (lmForm) {
      lmForm.addEventListener('keydown', function(e) {
        if (e.key !== 'Enter') return;
        var t = e.target;
        if (!t) return;
        var tag = t.tagName;
        if (tag === 'TEXTAREA') return;
        if (tag === 'BUTTON' && (!t.type || t.type === 'submit')) return;
        e.preventDefault();
      });
    }

    maybeEnableSupersample();

    // ── Landmarks-mode defaults ──
    // When the section is in `landmarks` mode (set by product tag or
    // ?lm_mode=landmarks), pre-enable 3D landmarks + supporting toggles
    // and tilt the camera so the photoreal models are visible immediately.
    var lmMode = window.SKYLIT_LM_MODE || 'flat';
    if (lmMode === 'landmarks') {
      // Standard import handles buildings/terrain when landmarks is on, so
      // we leave those toggles off in state (the rows are hidden anyway).
      state.show3dLandmarks = true;
      state.pitch = 76;
      state.bearing = 64;
      // Phones default to portrait (matches the natural device orientation
      // and gives more vertical room for the customiser controls below).
      // Tablets and desktops default to landscape to show off the skyline.
      var _isPhoneWidth = window.matchMedia && window.matchMedia('(max-width: 768px)').matches;
      state.orientation = _isPhoneWidth ? 'portrait' : 'landscape';
      state.lightPreset = 'dawn';
      // Default to a known landmark city so the product instantly demos value.
      state.lat = 51.5033;   // London Eye
      state.lng = -0.1196;
      state.zoom = 16.5;
      state.locationName = 'London, UK';
    }

    // Headless boot: start the map on the order's own theme and camera. Created
    // on the defaults, CF Browser Rendering loaded a full London map only for
    // bootHeadless to throw it away (~13s of a ~26s cart-thumbnail budget).
    // lmHydrateFromData still applies the whole design; this just means its
    // setStyle diffs against the same sources instead of starting over.
    var _hs = window.__LM_HEADLESS === true && window.__LM_HEADLESS_STATE;
    if (_hs) {
      if (_hs.theme) state.theme = _hs.theme;
      ['lat', 'lng', 'zoom', 'pitch', 'bearing'].forEach(function (k) {
        var v = parseFloat(_hs[k]);
        if (isFinite(v)) state[k] = v;
      });
    }

    lmPinMapSizing();
    map = new mapboxgl.Map({
      container: 'lm-map',
      style: buildStyle(state.theme),
      center: [state.lng, state.lat],
      zoom: applyZoom(state.zoom),
      interactive: true,
      attributionControl: false,
      preserveDrawingBuffer: true,
      pixelRatio: desiredPixelRatio(),
      maxPitch: lmMode === 'landmarks' ? 85 : 45,
      pitch: state.pitch || 0,
      bearing: state.bearing || 0
    });

    // Store map reference on DOM for 3D capture access
    document.getElementById('lm-map').__mapInstance = map;
    window.__lmMapInst = map;
    if (window.__lmSyncSS) {
      // Re-apply inline supersample sizing (Mapbox may have set its own
      // inline width/height during construction) and resize the map.
      window.__lmSyncSS();
      map.once('load', window.__lmSyncSS);
    }

    // No built-in nav controls — zoom slider in sidebar handles this

    // Headless boot: map is created above; skip all wizard/UI wiring and the
    // ?order_id= fetch path — the preview-host will call window.LM.bootHeadless.
    if (window.__LM_HEADLESS === true) return;

    attachMapListeners();
    attachCrashRecovery();
    maybeShowOnboarding();
    if (lmMode === 'landmarks') maybeShowIOSNotice();

    bindAll();
    bindStepNav();
    // One pill slides between the step tabs (skylit-motion.js).
    if (window.SkylitTabPill) {
      window.SkylitTabPill.attach(document.getElementById('lm-steps-nav'), {
        tab: '.lm-step-tab',
        className: 'lm-steps-indicator'
      });
    }

    // Reorder Step 1 for everyone: Orientation → Layout → Composition → Theme.
    // The Theme grid is bulky (25 swatches) and pushes Layout/Composition
    // off-screen on mobile; users hit those more often than swapping themes.
    (function reorderDesignStep(){
      var themeGrid = document.getElementById('lm-themes');
      var themeSection = themeGrid ? themeGrid.closest('.lm-section') : null;
      var layoutSection = (function(){ var n = document.getElementById('lm-layouts'); return n ? n.closest('.lm-section') : null; })();
      var compSection = (function(){ var n = document.getElementById('lm-text-styles'); return n ? n.closest('.lm-section') : null; })();
      if (!themeSection || !themeSection.parentNode) return;
      var parent = themeSection.parentNode;
      if (layoutSection) parent.insertBefore(layoutSection, themeSection);
      if (compSection) parent.insertBefore(compSection, themeSection);
    })();

    map.on('load', function() {
      addWaterTexture();
      if (state.show3dBuildings) add3dBuildings();
      if (state.show3dTerrain) add3dTerrain();
      if (state.show3dLandmarks) {
        hideStandardRoadArrows();
        attachArrowGuard();
      }
      // Landmarks-mode: reflect pre-enabled state in the UI and open the
      // 3D accordion so the lighting/preset chips are visible immediately.
      if (lmMode === 'landmarks') {
        try { applyLandmarksConfig(); } catch(e) {}
        applyNightStreetGlow(); addParkOverlay();
        var sw = document.getElementById('lm-3d-landmarks-switch');
        if (sw) sw.classList.add('active');
        // Reflect landscape + rotate/tilt defaults in the UI controls so
        // the slider values + frame ratio match what the map is showing.
        try {
          document.querySelectorAll('#lm-orientations [data-orientation]').forEach(function(b){
            b.classList.toggle('active', b.dataset.orientation === state.orientation);
          });
          var pf = document.getElementById('lm-print-frame');
          if (pf) pf.classList.toggle('landscape', state.orientation === 'landscape');
          if (typeof applyFrameRatio === 'function') applyFrameRatio();
          if (typeof applyRibbonImageForOrientation === 'function') applyRibbonImageForOrientation();
          var rs = document.getElementById('lm-rotate'); if (rs) rs.value = state.bearing;
          var rv = document.getElementById('lm-rotate-val'); if (rv) rv.textContent = Math.round(state.bearing) + '°';
          var ts = document.getElementById('lm-pitch'); if (ts) ts.value = state.pitch;
          var tv = document.getElementById('lm-pitch-val'); if (tv) tv.textContent = Math.round(state.pitch) + '°';
        } catch(e) {}
        var ps = document.getElementById('lm-pitch'); if (ps) ps.max = '85';
        // Relabel "3D Effects" → "Popular POIs" and drop the "Jump to landmark"
        // sublabel inside the presets panel (the section title now says it).
        var head3d = document.querySelector('#lm-3d-accordion .lm-section-title');
        if (head3d) {
          var iconHTML = head3d.querySelector('svg') ? head3d.querySelector('svg').outerHTML : '';
          head3d.innerHTML = iconHTML + 'Popular POIs';
        }
        var presetsHint = document.querySelector('#lm-landmark-presets .lm-icons-sublabel');
        if (presetsHint) presetsHint.style.display = 'none';
        var presets = document.getElementById('lm-landmark-presets');
        if (presets) presets.style.display = 'block';
        var lp = document.getElementById('lm-light-preset');
        if (lp) lp.style.display = 'block';
        // Restructure Step 1 (Design) for landmarks mode:
        // Orientation → Lighting → Layout → Composition → Theme.
        // Theme is bulky (25 swatches) so push it to the bottom.
        var themeGrid = document.getElementById('lm-themes');
        var themeSection = themeGrid ? themeGrid.closest('.lm-section') : null;
        var layoutSection = (function(){ var n = document.getElementById('lm-layouts'); return n ? n.closest('.lm-section') : null; })();
        var compSection = (function(){ var n = document.getElementById('lm-text-styles'); return n ? n.closest('.lm-section') : null; })();
        if (themeSection && themeSection.parentNode) {
          var parent = themeSection.parentNode;
          if (lp && lp.parentNode !== parent) {
            parent.insertBefore(lp, themeSection);
            lp.style.margin = '0 0 14px';
          } else if (lp) {
            parent.insertBefore(lp, themeSection);
          }
          if (layoutSection) parent.insertBefore(layoutSection, themeSection);
          if (compSection) parent.insertBefore(compSection, themeSection);
        }
        // Leave the "Popular POIs" accordion CLOSED by default in landmarks
        // mode (it was previously force-opened here, but the panel is bulky
        // and most customers want to focus on framing first). The accordion
        // head + body stay at their HTML defaults (aria-expanded="false",
        // body[hidden]) — clicking opens them through bindAccordions.
        // Reflect dusk in the lighting buttons.
        document.querySelectorAll('#lm-light-preset .lm-bd-btn').forEach(function(b) {
          b.classList.toggle('active', b.getAttribute('data-light') === 'dawn');
        });
        // Sync the icon-size slider's visible position to the landmark
        // default (48). iconSize itself was already initialised to 48 at
        // var-decl time when SKYLIT_LM_MODE='landmarks', but the <input>
        // is rendered server-side with value="28" so its thumb starts at
        // the flat-map default until we explicitly bump it here.
        // Size UI is the XS-XL button group now; it reads iconSize at
        // selection-sync time (lmSyncIconSizeButtons), nothing to seed here.
      }
      // Hillshade renders chunky/pixelated on mobile (raster-dem + high DPR).
      // Hide it below 768px UNLESS the user has 3D Terrain on — in which case
      // hillshade is what makes the elevation visually read.
      if (window.matchMedia && window.matchMedia('(max-width: 768px)').matches && !state.show3dTerrain) {
        try { map.setLayoutProperty('hillshade', 'visibility', 'none'); } catch(e) {}
      }
      // Check for edit mode (?order_id=xxx) or preset mode (?preset=<slug>)
      var urlParams = new URLSearchParams(window.location.search);
      var editOrderId = urlParams.get('order_id');
      var presetKey = urlParams.get('preset');

      // Per-product default preset (set on themed products like
      // Wedding Location Map via the customizer.default_preset metafield).
      // URL ?preset= still wins; only fires on a clean direct landing.
      if (!editOrderId && !presetKey
          && typeof window.SKYLIT_PRODUCT_DEFAULT_PRESET === 'string'
          && window.SKYLIT_PRODUCT_DEFAULT_PRESET) {
        presetKey = window.SKYLIT_PRODUCT_DEFAULT_PRESET;
        console.log('[lm.cart] product default preset=' + presetKey);
      }

      // Pre-select variant from ?variant= URL param (used by Google Merchant Center)
      var variantParam = urlParams.get('variant');
      // Apply the URL ?variant= (Google Merchant Center links carry the
      // advertised offer's variant id). Used on plain landings AND after
      // preset hydration — occasion products all have a default preset, and
      // the advertised variant must still win on type/size/frame so the
      // displayed price matches the ad (Merchant Center's automated-discounts
      // review checks the rendered sale price).
      function lmApplyUrlVariant() {
        if (!variantParam) return false;
        var variants = window.lmProductVariants || [];
        var hit = false;
        for (var i = 0; i < variants.length; i++) {
          if (String(variants[i].id) === variantParam) {
            state.selectedType = variants[i].option1 || state.selectedType;
            state.selectedSize = variants[i].option2 || state.selectedSize;
            if (variants[i].option3 && variants[i].option3 !== 'No Frame') {
              state.selectedFrame = variants[i].option3;
            }
            hit = true;
            break;
          }
        }
        if (!hit) return false;
        // Type/size/frame picker .active classes are now Alpine-driven via
        // :class="$store.lm.selectedX === '...'" — but we still need to push
        // the hydrated values into the Alpine store so the bindings
        // re-evaluate. lmStoreSet writes through the proxy and updates the
        // underlying state object atomically.
        if (state.selectedType !== undefined) lmStoreSet('selectedType', state.selectedType);
        if (state.selectedSize !== undefined) lmStoreSet('selectedSize', state.selectedSize);
        if (state.selectedFrame !== undefined) lmStoreSet('selectedFrame', state.selectedFrame);
        // Show/hide frame picker based on type
        var fp = document.querySelector('.lm-frame-picker-wrap');
        if (fp) fp.style.display = (state.selectedType === 'Framed') ? 'block' : 'none';
        console.log('[lm.cart] url variant=' + variantParam + ' applied');
        return true;
      }
      if (variantParam && !editOrderId && !presetKey) {
        lmApplyUrlVariant();
      }

      // Gallery-wall companion handoff. Reuse the exact place carried by a
      // star/moon cart line instead of geocoding its label again.
      var prefillLat = parseFloat(urlParams.get('prefill_lat'));
      var prefillLng = parseFloat(urlParams.get('prefill_lng'));
      if (!editOrderId && !presetKey && isFinite(prefillLat) && isFinite(prefillLng) &&
          prefillLat >= -90 && prefillLat <= 90 && prefillLng >= -180 && prefillLng <= 180) {
        var prefillPlace = (urlParams.get('prefill_place') || '').slice(0, 80);
        state.lat = prefillLat; state.lng = prefillLng;
        // The cart's cross-sell rail also passes the star/moon line's night as
        // ?prefill_date=YYYY-MM-DD; it fills the optional date line.
        var pdm = /^(\d{4})-(\d{2})-(\d{2})$/.exec(urlParams.get('prefill_date') || '');
        if (pdm && +pdm[2] >= 1 && +pdm[2] <= 12) {
          var pdMonths = ['January','February','March','April','May','June','July','August','September','October','November','December'];
          lmStoreSet('dateLine', (+pdm[3]) + ' ' + pdMonths[+pdm[2] - 1] + ' ' + pdm[1]);
          var prefillDateInput = document.getElementById('lm-date');
          if (prefillDateInput) prefillDateInput.value = state.dateLine;
        }
        if (prefillPlace) {
          state.locationName = dedupePlace(prefillPlace);
          state.subtitle = state.locationName.toUpperCase();
          var prefillSubtitle = document.getElementById('lm-subtitle');
          if (prefillSubtitle) prefillSubtitle.value = state.subtitle;
          setLocationInputDisplay(state.locationName);
        }
        if (map) map.jumpTo({ center: [prefillLng, prefillLat], zoom: applyZoom(state.zoom) });
        updateTextOverlay();
        // Star/moon places are often the city alone ("Riga", "Bath"), which
        // leaves Place's country line empty. Look the country up from the
        // coordinates and fill it in, unless the customer has moved on to
        // another place or retyped the line by then.
        var prefillParts = lmDerivePlaceParts();
        if (prefillPlace && prefillParts && !prefillParts.country && window.fetch) {
          var prefillSub = state.subtitle;
          fetch('https://api.mapbox.com/search/geocode/v6/reverse?longitude=' + prefillLng +
                '&latitude=' + prefillLat + '&types=country&language=en&access_token=' + MAPBOX_TOKEN)
            .then(function (r) { return r.ok ? r.json() : null; })
            .then(function (d) {
              var country = d && d.features && d.features[0] && d.features[0].properties && d.features[0].properties.name;
              if (!country || state.lat !== prefillLat || state.lng !== prefillLng) return;
              state.placeCountry = country;
              state.locationName = dedupePlace(prefillPlace + ', ' + country);
              setLocationInputDisplay(state.locationName);
              var subIn = document.getElementById('lm-subtitle');
              if (state.textStyle === 'place') {
                // Only while the country line is still what Place wrote (empty).
                if (!state.subtitle) lmApplyPlaceComposition();
              } else if (state.subtitle === prefillSub) {
                state.subtitle = state.locationName.toUpperCase();
                if (subIn) subIn.value = state.subtitle;
              }
              updateTextOverlay();
            })
            .catch(function () {});
        }
        console.log('[lm.cart] gallery prefill=' + prefillLat + ',' + prefillLng + ' ' + prefillPlace);
      }

      if (editOrderId) {
        // Refuse to hydrate a paid order — see isPaidLocked. Drop the URL
        // param and reload as a fresh customizer session. Cart ids are
        // fetched alongside so lmHydrateFromData can decide edit mode from
        // actual cart membership (save-for-later links arrive as ?order_id=
        // too, and must NOT get "Confirm edits" / autosave-off).
        Promise.all([lmFetchOrder(editOrderId), lmFetchCartOrderIds()]).then(function(res) {
          var row = res[0];
          if (isPaidLocked(row, editOrderId)) {
            console.log('[lm.cart] paid order revisited, starting fresh', editOrderId);
            try { localStorage.removeItem(LM_DRAFT_KEY); } catch (e) {}
            var p = new URLSearchParams(window.location.search);
            p.delete('order_id');
            var qs = p.toString();
            window.location.replace(window.location.pathname + (qs ? '?' + qs : ''));
            return;
          }
          loadExistingOrder(editOrderId, function() {
            applyFrameRatio();
            updateTextOverlay();
            updateFrame();
            updateLayout();
            updateVariantAndPrice();
            updateGiftVisibility();
            applyRibbonImageForOrientation();
            applyFinishAttr();
            // Belt-and-braces: lmHydrateFromData (inside loadExistingOrder)
            // writes state.selectedType/Size/Frame directly rather than
            // through the guarded setters (it's restoring a saved design, not
            // a customer pick), so a claim's locked variant survives this
            // path only because the design was never savable as anything
            // else while the claim was active. Re-applying the visual lock
            // here is defence in depth, matching the pattern that closed the
            // equivalent name-landsat.js hydration regression.
            lmApplyClaimVisualLock();
            document.querySelector('.lm-loader').style.display = 'none'; lmHidePoster();
            // In the cart, "autosave" only notes what they try (cart-edit
            // log, see scheduleLmCartEditLog), so arming it here never writes
            // to the design. Outside the cart this path stays as it was.
            if (state.editMode) lmArmAutosaveOnFirstInteraction();
            // A frame bought from the cart's "Complete the look" upsell:
            // show it as the Framed selection so the customer sees the frame
            // they own. The submit handler keeps the two-line structure —
            // the print re-adds as Print and the add-on line is synced.
            if (state.editMode && window.SkylitPairing && window.SkylitPairing.pairedFrame) {
              window.SkylitPairing.pairedFrame(state.orderId).then(function (f) {
                if (!f || state.selectedType !== 'Print') return;
                lmFrameFromAddon = true;
                window.lmSetSelectedType('Framed');
                window.lmSetSelectedFrame(f.colour, null);
              });
            }
          }, null, row); // row already fetched for the paid-lock check
        });
      } else if (presetKey) {
        // Preset mode: clone a template order's visuals but keep the fresh
        // orderId so the customer's add-to-cart creates a new order.
        var presetOrderId = LM_PRESETS[presetKey.toLowerCase()] || presetKey;
        var freshOrderId = state.orderId;
        console.log('[lm.cart] url preset=' + presetKey + ' → ' + presetOrderId);
        // Preset URL = new working design — abandon any prior draft pointer.
        // Do NOT write a pointer to the fresh orderId here: autosave only
        // POSTs after a real interaction, so a bounce would leave a pointer
        // to an orderId that never existed in D1 (and the next plain visit
        // would offer to "continue" nothing). The first successful autosave
        // writes the real pointer instead (lmAutosavePost).
        try { localStorage.removeItem(LM_DRAFT_KEY); } catch (e) {}
        loadExistingOrder(presetOrderId, function() {
          // Advertised variant beats the preset's saved type/size/frame —
          // see lmApplyUrlVariant above. Runs before the re-render calls so
          // the price/pickers reflect it in this same pass.
          lmApplyUrlVariant();
          applyFrameRatio();
          updateTextOverlay();
          updateFrame();
          updateLayout();
          updateVariantAndPrice();
          updateGiftVisibility();
          applyRibbonImageForOrientation();
          applyFinishAttr();
          lmApplyClaimVisualLock();
          document.querySelector('.lm-loader').style.display = 'none'; lmHidePoster();
          lmArmAutosaveOnFirstInteraction();
        }, freshOrderId, null, true);
      } else {
        filterSizesForType();
        // Frame-picker visibility for a plain fresh load. The variant/order/
        // preset branches each sync this, but the fresh path didn't — so with
        // the default selectedType 'Print' the Frame Colour picker stayed
        // visible (markup has no default-hidden state). Hide it unless Framed.
        var fpInit = document.querySelector('.lm-frame-picker-wrap');
        if (fpInit) fpInit.style.display = (state.selectedType === 'Framed') ? 'block' : 'none';
        // Default composition is "place": auto-fill title=city / subtitle=country
        // from the default London location so the fresh preview reads the place
        // straight away (matches the switch-in behaviour in lmSetTextStyle).
        if (state.textStyle === 'place' && typeof lmApplyPlaceComposition === 'function') lmApplyPlaceComposition();
        // Set data-finish="print" on the form so the CSS frame-strip rule
        // (.lm-wrapper:has([data-finish="print"]) #lm-print-frame) applies on
        // load. Without it the default decorative frame (grey border) survives
        // on mobile, where a media-query border-width:14px !important beats the
        // inline reset from updateFrame(). Edit/preset paths already do this;
        // the fresh-load path didn't, so a first-time Print preview showed the
        // frame until the customer toggled the type.
        applyFinishAttr();
        applyFrameRatio();
        updateTextOverlay();
        updateFrame();
        updateLayout();
        updateVariantAndPrice();
        updateGiftVisibility();
        lmApplyClaimVisualLock();
        document.querySelector('.lm-loader').style.display = 'none'; lmHidePoster();
        // Plain URL: offer to resume previous draft (if any). Only prime autosave
        // if there's no draft to resume — otherwise we'd create a stub server-side
        // order before the customer decides between Continue and Start fresh.
        if (!lmReadDraftPointer()) lmArmAutosaveOnFirstInteraction();
        setTimeout(maybeShowLmResumeModal, 800);
      }
    });
  }

  // ── Mapbox Style Builder ──
  // ── 3D Preview Refresh Helper ──
  function scheduleLM3DRefresh(delay) {
    var framedActive = window.LM3DFramedMockup && window.LM3DFramedMockup.isActive && window.LM3DFramedMockup.isActive();
    var canvasActive = window.LM3DMockup && window.LM3DMockup.isActive && window.LM3DMockup.isActive();
    if (!framedActive && !canvasActive) return;
    clearTimeout(window._lm3dRefreshTimer);
    window._lm3dRefreshTimer = setTimeout(function() {
      if (window.LM3DFramedMockup && window.LM3DFramedMockup.isActive()) {
        window.LM3DFramedMockup.refresh();
      }
      if (window.LM3DMockup && window.LM3DMockup.isActive()) {
        window.LM3DMockup.refresh();
      }
    }, delay || 600);
  }

  // Blend two hex colours: mix('#ff0000','#0000ff',0.5) → '#800080'
  // Tint Mapbox Standard's basemap to follow the active theme + lighting.
  // Standard's default water is too close to its land tone under several
  // light presets, especially dawn/day, which made rivers/coast hard to
  // read in landmarks mode. Pushing the water config to a darker, more
  // saturated version of the theme's water colour gives strong contrast
  // across all presets without changing the rest of the basemap palette.
  function applyLandmarksConfig() {
    if (!map) return;
    try { map.setConfigProperty('basemap', 'lightPreset', state.lightPreset || 'day'); } catch(e) {}
    // iOS Safari: skip the extra colorWater Standard restyle. Mutating
    // import config repeatedly during landmark tile load has been observed
    // to leave the glTF meshes in a half-loaded state.
    if (isIOSSafari()) return;
    var t = (typeof THEMES !== 'undefined') && lmThemeData();
    if (!t) return;
    // Darken Standard's water toward black for contrast — but for themes
    // whose water is already dark (luminance < ~0.2), darkening further
    // collapses the river into the bg. In those cases, mix LIGHTER instead
    // so the water still reads distinct from the surrounding land.
    var wHex = t.water || '#000000';
    var wLum = (parseInt(wHex.slice(1,3),16) + parseInt(wHex.slice(3,5),16) + parseInt(wHex.slice(5,7),16)) / (3 * 255);
    var tinted = wLum < 0.2
      ? mixColor(t.water, '#ffffff', 0.18)
      : mixColor(t.water, '#000000', 0.30);
    try { map.setConfigProperty('basemap', 'colorWater', tinted); } catch(e) {}
    // Sync clone-bridge-primary colour to whatever the basemap config
    // resolves for colorRoads under the current light preset, so the
    // bridge body stays continuous with land roads at dawn/dusk/night.
    try {
      var roadsColor = map.getConfigProperty('basemap','colorRoads');
      if (roadsColor && map.getLayer('clone-bridge-primary')) {
        map.setPaintProperty('clone-bridge-primary','line-color', roadsColor);
      }
    } catch(e) {}
  }

  function mixColor(a, b, t) {
    var pa = [parseInt(a.slice(1,3),16), parseInt(a.slice(3,5),16), parseInt(a.slice(5,7),16)];
    var pb = [parseInt(b.slice(1,3),16), parseInt(b.slice(3,5),16), parseInt(b.slice(5,7),16)];
    var r = Math.round(pa[0]+(pb[0]-pa[0])*t), g = Math.round(pa[1]+(pb[1]-pa[1])*t), bl = Math.round(pa[2]+(pb[2]-pa[2])*t);
    return '#'+((1<<24)+(r<<16)+(g<<8)+bl).toString(16).slice(1);
  }

  // ── Country + maritime borders (loaded dynamically) ──
  function ensureBorderSource() {
    if (!map) return false;
    var url = (window.__neBordersUrl || '').replace(/^\/\//, 'https://');
    if (!url) return false;
    if (!map.getSource('ne-borders')) {
      map.addSource('ne-borders', { type: 'geojson', data: url });
    }
    try { map.removeLayer('admin-boundary-placeholder'); } catch(e) {}
    return true;
  }

  function addLandBorders() {
    if (!map || !state.showLandBorders) return;
    if (!ensureBorderSource()) return;
    var t = lmThemeData();
    try { map.removeLayer('ne-land-border'); } catch(e) {}
    map.addLayer({
      id: 'ne-land-border', type: 'line', source: 'ne-borders',
      filter: ['==',['get','type'],'land'],
      minzoom: 1,
      layout: { 'line-join': 'round', 'line-cap': 'round' },
      paint: { 'line-color': t.text, 'line-width': ['interpolate',['linear'],['zoom'],2,0.5,5,0.9,8,1.2,12,1.6,14,1.8], 'line-opacity': 0.75, 'line-dasharray': [5,3] }
    });
  }

  function removeLandBorders() {
    if (!map) return;
    try { map.removeLayer('ne-land-border'); } catch(e) {}
  }

  function addMaritimeBorders() {
    if (!map || !state.showMaritimeBorders) return;
    if (!ensureBorderSource()) return;
    var t = lmThemeData();
    try { map.removeLayer('ne-maritime-border'); } catch(e) {}
    map.addLayer({
      id: 'ne-maritime-border', type: 'line', source: 'ne-borders',
      filter: ['==',['get','type'],'maritime'],
      minzoom: 1,
      layout: { 'line-join': 'round', 'line-cap': 'round' },
      paint: { 'line-color': t.text, 'line-width': ['interpolate',['linear'],['zoom'],2,0.5,5,0.8,8,1.1,12,1.4,14,1.6], 'line-opacity': 0.65, 'line-dasharray': [4,3] }
    });
  }

  function removeMaritimeBorders() {
    if (!map) return;
    try { map.removeLayer('ne-maritime-border'); } catch(e) {}
  }

  function addNeBorders() {
    addLandBorders();
    addMaritimeBorders();
  }

  // ── Water texture — pencil/crosshatch noise over water fills ──
  function addWaterTexture() {
    if (!map) return;
    var t = lmThemeData();
    var size = 128;
    var canvas = document.createElement('canvas');
    canvas.width = size; canvas.height = size;
    var ctx = canvas.getContext('2d');

    var wc = t.water;
    var wr = parseInt(wc.slice(1,3),16), wg = parseInt(wc.slice(3,5),16), wb = parseInt(wc.slice(5,7),16);

    // 1. Dense grain noise
    var imageData = ctx.createImageData(size, size);
    var d = imageData.data;
    for (var i = 0; i < d.length; i += 4) {
      var noise = (Math.random() - 0.5) * 80;
      d[i]     = Math.max(0, Math.min(255, wr + noise));
      d[i + 1] = Math.max(0, Math.min(255, wg + noise));
      d[i + 2] = Math.max(0, Math.min(255, wb + noise));
      d[i + 3] = 120;
    }
    ctx.putImageData(imageData, 0, 0);

    // 2. Pencil hatching — primary direction
    ctx.globalAlpha = 0.35;
    ctx.strokeStyle = 'rgba(' + Math.max(wr-60,0) + ',' + Math.max(wg-60,0) + ',' + Math.max(wb-60,0) + ',1)';
    ctx.lineWidth = 0.6;
    for (var j = -size; j < size * 2; j += 3) {
      ctx.beginPath();
      var jitter1 = (Math.random() - 0.5) * 1.5;
      var jitter2 = (Math.random() - 0.5) * 1.5;
      ctx.moveTo(j + jitter1, 0);
      ctx.lineTo(j + size * 0.6 + jitter2, size);
      ctx.stroke();
    }
    // Cross direction
    ctx.globalAlpha = 0.2;
    ctx.lineWidth = 0.5;
    for (var k = -size; k < size * 2; k += 5) {
      ctx.beginPath();
      ctx.moveTo(k + size, 0);
      ctx.lineTo(k + (Math.random() - 0.5) * 2, size);
      ctx.stroke();
    }

    // 3. Highlight flecks
    ctx.globalAlpha = 0.2;
    ctx.fillStyle = 'rgba(' + Math.min(wr+70,255) + ',' + Math.min(wg+70,255) + ',' + Math.min(wb+70,255) + ',1)';
    for (var f = 0; f < 120; f++) {
      var fx = Math.random() * size, fy = Math.random() * size;
      ctx.fillRect(fx, fy, Math.random() * 2.5 + 0.5, Math.random() * 1 + 0.3);
    }

    // Register and apply
    if (map.hasImage('water-pattern')) map.removeImage('water-pattern');
    map.addImage('water-pattern', ctx.getImageData(0, 0, size, size), { pixelRatio: 2, sdf: false });

    try { map.removeLayer('water-texture'); } catch(e) {}
    try { map.removeLayer('water-base'); } catch(e) {}
    var aboveLayer = null;
    var layers = map.getStyle().layers;
    for (var i = 0; i < layers.length; i++) {
      if (layers[i].id === 'water') { aboveLayer = layers[i+1] ? layers[i+1].id : null; break; }
    }
    // In landmarks mode the texture sits above Mapbox Standard's bright
    // basemap water (which can match the land tone under day/dawn).
    // Add a solid theme-tinted base underneath so contrast survives.
    // iOS Safari: skip the extra fill layer — it pushed the WebGL context
    // over its memory budget and landmarks stopped rendering entirely.
    // Slot ground fills below Standard's 3D extrusions when landmarks are
    // on (otherwise the water polygons paint OVER Big Ben/Westminster's
    // tower mesh on iOS — the addLayer beforeId arg doesn't override slot
    // ordering for imports). For flat (non-landmarks) maps no import is
    // active, so the slot is a no-op.
    var groundSlot = state.show3dLandmarks ? 'bottom' : undefined;
    // Mapbox silently rejects a layer if `slot` is present but undefined —
    // it has to be a real slot name OR the property must be omitted entirely.
    // Build the layer objects conditionally so flat mode (no slot) renders.
    if (state.show3dLandmarks && !isIOSSafari()) {
      var waterBaseLayer = {
        id: 'water-base', type: 'fill', source: 'openmaptiles', 'source-layer': 'water',
        paint: { 'fill-color': mixColor(t.water, '#000000', 0.15), 'fill-opacity': 0.85 }
      };
      if (groundSlot) waterBaseLayer.slot = groundSlot;
      map.addLayer(waterBaseLayer, aboveLayer);
    }
    var waterTextureLayer = {
      id: 'water-texture', type: 'fill', source: 'openmaptiles', 'source-layer': 'water',
      paint: { 'fill-pattern': 'water-pattern', 'fill-opacity': 1 }
    };
    if (groundSlot) waterTextureLayer.slot = groundSlot;
    map.addLayer(waterTextureLayer, aboveLayer);

    // Re-add Natural Earth borders on top so they show over water texture
    addNeBorders();
  }

  // Land road layers that get a bridge-only twin in buildStyle(). Their filters
  // are legacy-syntax, as is the structure test added to them, so the combined
  // 'all' stays valid. Casings are left out: a 20-25% casing over the fills
  // leaves pale round-cap arcs on the road at both ends of every bridge.
  var LM_BRIDGE_ROAD_LAYERS = ['road-path', 'road-minor', 'road-tertiary', 'road-secondary',
    'road-link', 'road-primary', 'road-motorway', 'railway'];
  function buildStyle(themeName) {
    var t = lmThemeData(themeName);
    // Computed intermediate road colours for hierarchy
    var roadMid = mixColor(t.roadMinor, t.road, 0.5);     // secondary/tertiary
    var roadFaint = mixColor(t.bg, t.roadMinor, 0.4);     // very subtle residential fill
    // Skylit building layers: tinted from the ROAD colour (not t.building,
    // which most themes keep near-bg) so footprints read clearly against the
    // background while staying quieter than the road lines themselves.
    var buildingLow = mixColor(t.bg, t.road, 0.45);        // own low-zoom footprints
    var fabricTint = mixColor(t.bg, t.road, 0.20);         // built-up landuse wash
    var casingColor = mixColor(t.bg, t.road, 0.35);        // subtle casing behind major roads
    // Hairline outline around building footprints (2026-09-29): separates
    // adjoining buildings the way a cadastral city map does (cf. Momenterie).
    // Between the fill and the road ink, so every theme gets its own tone.
    var buildingEdge = mixColor(t.building, t.road, 0.55);
    var buildingLowEdge = mixColor(buildingLow, t.road, 0.5);
    var landFill = mixColor(t.bg, t.park, 0.3);            // subtle landuse tint
    var waterEdge = mixColor(t.water, t.road, 0.2);        // faint coastline/shore
    var style = {
      version: 8, name: 'skylit-' + themeName,
      sources: {
        openmaptiles: { type: 'vector', url: 'mapbox://mapbox.mapbox-streets-v8' },
        terrain: { type: 'raster-dem', url: 'mapbox://mapbox.mapbox-terrain-dem-v1', tileSize: 512, maxzoom: 14 },
        bathymetry: { type: 'vector', url: 'mapbox://mapbox.mapbox-bathymetry-v2', maxzoom: 7 },
        // Skylit's own generalized OSM building footprints (Planetiler PMTiles
        // on R2, served by the skylit-tiles worker). streets-v8 carries no
        // small buildings below ~z13 tiles; this source fills z10-13 so
        // zoomed-out maps with 3D Buildings on show real footprints. No layer
        // reads it unless the buildings toggle is on, so it costs nothing
        // otherwise; uncovered regions return empty 204 tiles harmlessly.
        skylit_buildings: { type: 'vector', tiles: ['https://tiles.skylitstudio.co.uk/skylit-buildings-planet/{z}/{x}/{y}.mvt'], minzoom: 10, maxzoom: 14 }
      },
      // terrain: { source: 'terrain', exaggeration: 0 }, // disabled — causes blur on mobile via framebuffer rendering
      glyphs: 'mapbox://fonts/mapbox/{fontstack}/{range}.pbf',
      layers: [
        { id:'background', type:'background', paint:{'background-color':t.bg} },
        // ── Hillshade for terrain texture at all zoom levels ──
        { id:'hillshade', type:'hillshade', source:'terrain',
          paint:{'hillshade-shadow-color':mixColor(t.bg,'#000000',0.3),'hillshade-highlight-color':mixColor(t.bg,'#ffffff',0.15),
            'hillshade-accent-color':mixColor(t.bg,'#000000',0.2),
            'hillshade-exaggeration':['interpolate',['linear'],['zoom'],2,0.3,8,0.35,14,0.25],'hillshade-illumination-direction':315} },
        // ── Natural / landcover fills for visual richness at low zoom ──
        { id:'landcover', type:'fill', source:'openmaptiles', 'source-layer':'landuse',
          filter:['in','class','wood','scrub','agriculture','crop'],
          paint:{'fill-color':t.park,'fill-opacity':['interpolate',['linear'],['zoom'],2,0.2,5,0.25,10,0.35,14,0.4]} },
        { id:'landuse-residential', type:'fill', source:'openmaptiles', 'source-layer':'landuse',
          filter:['in','class','residential','neighbourhood'],
          paint:{'fill-color':landFill,'fill-opacity':['interpolate',['linear'],['zoom'],8,0.1,12,0.2,16,0.15]} },
        { id:'landuse-commercial', type:'fill', source:'openmaptiles', 'source-layer':'landuse',
          filter:['in','class','commercial','retail','industrial'],
          paint:{'fill-color':landFill,'fill-opacity':['interpolate',['linear'],['zoom'],10,0.08,14,0.15]} },
        // ── Bathymetry — ocean depth shading ──
        { id:'bathymetry-fill', type:'fill', source:'bathymetry', 'source-layer':'depth',
          maxzoom: 8,
          paint:{'fill-color':mixColor(t.water,'#000000',0.35),
            'fill-opacity':['interpolate',['linear'],['get','min_depth'],0,0,50,0.12,200,0.25,1000,0.4,3000,0.55,6000,0.7]} },
        { id:'bathymetry-line', type:'line', source:'bathymetry', 'source-layer':'depth',
          maxzoom: 8,
          paint:{'line-color':mixColor(t.water,'#000000',0.4),
            'line-width':['interpolate',['linear'],['zoom'],2,0.4,5,0.8,7,1],'line-opacity':0.4} },
        { id:'water', type:'fill', source:'openmaptiles', 'source-layer':'water',
          paint:{'fill-color':t.water,'fill-opacity':0.6} },
        // Waterways (rivers, streams, canals) — visible from low zoom
        { id:'waterway', type:'line', source:'openmaptiles', 'source-layer':'waterway',
          paint:{'line-color':t.water,'line-width':['interpolate',['linear'],['zoom'],3,0.2,6,0.5,8,1,12,2.5,14,3.5,18,5],'line-opacity':0.9} },
        { id:'landuse-park', type:'fill', source:'openmaptiles', 'source-layer':'landuse',
          filter:['in','class','park','grass','cemetery','glacier','pitch','sand'],
          paint:{'fill-color':t.park,'fill-opacity':['interpolate',['linear'],['zoom'],6,0.3,10,0.5,14,0.6]} },
        { id:'building', type:'fill', source:'openmaptiles', 'source-layer':'building',
          paint:{'fill-color':t.building,'fill-opacity':['interpolate',['linear'],['zoom'],11,0,12,0.4,14,0.85,16,1]} },
        // Urban fabric + Skylit low-zoom footprints — ALWAYS on (2026-07-10;
        // previously gated behind the 3D Buildings toggle, but they are flat
        // 2D content that completes the map at zooms where streets-v8 has no
        // building data). Fabric shades built-up landuse blocks below the
        // footprint zooms; lm-lowzoom-buildings draws real generalized
        // footprints from Skylit's own tileset at z10-14 (overzoomed past 14),
        // fading out as the streets-v8 'building' fill above reaches full
        // strength. streets-v8 z13-14 tiles carry LARGE buildings only — the
        // full residential detail only lands in its z15 tiles — so the Skylit
        // footprints must hold until z15 and crossfade out by 15.8; fading at
        // 14.5 (pre-2026-07-11) left z13.5-15 with no visible buildings at
        // all outside big-building city centres. Both sit under the road
        // stack. The 3D Buildings toggle now adds ONLY the extrusions +
        // supersample LOD bump.
        { id:'lm-urban-fabric', type:'fill', source:'openmaptiles', 'source-layer':'landuse',
          maxzoom: 14,
          filter:['in','class','residential','neighbourhood','suburb','commercial','retail','industrial'],
          paint:{'fill-color':fabricTint,'fill-opacity':['interpolate',['linear'],['zoom'],8,0.10,10,0.22,12,0.28,13,0.18,14,0]} },
        { id:'lm-lowzoom-buildings', type:'fill', source:'skylit_buildings', 'source-layer':'building',
          paint:{'fill-color':buildingLow,'fill-opacity':['interpolate',['linear'],['zoom'],10,0.40,12,0.65,15,0.65,15.8,0]} },
        // Building outlines. The low-zoom one fades in from z10.5 and
        // crossfades out with its fill; the streets-v8 one takes over from
        // z14.5 as the full footprint set arrives. Mirrored in
        // render_locationmap.py generate_mapbox_style (same ids and stops).
        { id:'lm-lowzoom-buildings-outline', type:'line', source:'skylit_buildings', 'source-layer':'building',
          paint:{'line-color':buildingLowEdge,'line-width':['interpolate',['linear'],['zoom'],10.5,0.12,12.5,0.2,15,0.4],
                 'line-opacity':['interpolate',['linear'],['zoom'],10.5,0,11.5,0.3,12.5,0.45,15,0.55,15.8,0]} },
        { id:'building-outline', type:'line', source:'openmaptiles', 'source-layer':'building',
          paint:{'line-color':buildingEdge,'line-width':['interpolate',['linear'],['zoom'],14,0.3,16,0.5,18,0.9],
                 'line-opacity':['interpolate',['linear'],['zoom'],14,0,15,0.55,16,0.7]} },
        // ── Bridge deck ────────────────────────────────────────────────
        //   bridge-deck-poly  : structure-layer polygon coloured bg, so the
        //                       bridge body blends with land where it overhangs
        //                       the shore and only reads over water.
        //   clone-bridge-primary(-case) : Mapbox Standard's bridge body, in the
        //                       sheet colour on flat maps.
        // The deck sits BELOW every road layer. It used to sit above them, so
        // past z13 it erased any road passing under or along a bridge that the
        // old road-bridge overlays didn't redraw: slip roads went missing at
        // interchanges and flyovers cut sheet-coloured bands across motorways
        // (order kyb1ppm3stf, Malaga). Landmarks mode already had this order via
        // slots (deck in 'middle', roads unslotted above). Bridge roads are
        // per-class copies of the land layers appended after `railway` (see
        // LM_BRIDGE_ROAD_LAYERS). Mirrored in render_locationmap.py.
        { id:'bridge-deck-poly', type:'fill', source:'openmaptiles', 'source-layer':'structure',
          minzoom:13,
          filter:['all',['==','class','land'],['==','type','bridge']],
          paint:{'fill-color':t.bg,'fill-opacity':1} },
        // clone-bridge-primary mirrors Mapbox Standard's `bridge-roads` layer
        // (which sources `colorRoads` from the basemap config) so the bridge
        // body reads as a continuous extension of land roads. Hardcoded to
        // hsl(224, 25%, 80%) for the day light preset; applyLandmarksConfig
        // syncs the resolved colorRoads at runtime for non-day presets.
        { id:'clone-bridge-primary-case', type:'line', source:'openmaptiles', 'source-layer':'road',
          minzoom:15,
          filter:['all',['==',['get','structure'],'bridge'],['==',['get','class'],'primary'],['==',['geometry-type'],'LineString']],
          layout:{'line-cap':'butt','line-join':'miter'},
          // Theme casing, not Mapbox's hsl(224,15%,70%) — see clone-bridge-primary.
          paint:{'line-color':casingColor,
            'line-width':['interpolate',['exponential',1.5],['zoom'],14,1,22,2],
            'line-gap-width':['interpolate',['exponential',1.5],['zoom'],3,0.8,18,28,22,280],
            'line-opacity':1} },
        { id:'clone-bridge-primary', type:'line', source:'openmaptiles', 'source-layer':'road',
          minzoom:13,
          filter:['all',['==',['get','structure'],'bridge'],['==',['get','class'],'primary'],['==',['geometry-type'],'LineString']],
          layout:{'line-cap':'butt','line-join':'miter'},
          // Sheet colour, not Mapbox Standard's hsl(224,25%,80%). This body is
          // ~1.8% of the print wide, so on a flat map that grey read as a navy
          // slab across the design (Axel spotted it as #c2c7db on a pastel
          // print). applyLandmarksConfig() already retints it to the basemap's
          // colorRoads, but `basemap` is the Standard import and does not exist
          // on flat maps, so nothing ever themed it here — and the print
          // renderer never ran that call at all. Drawing the deck in the sheet
          // colour makes it vanish and lets the themed overlays below carry the
          // road across, matching bridge-deck-poly. Landmarks mode still
          // overrides this at runtime, which is correct there.
          paint:{'line-color':t.bg,
            'line-width':['interpolate',['exponential',1.5],['zoom'],3,0.8,18,28,22,280],
            'line-opacity':1} },
        // ── Road casings (subtle outlines behind major roads) ──
        { id:'road-motorway-casing', type:'line', source:'openmaptiles', 'source-layer':'road',
          filter:['==','class','motorway'],
          layout:{'line-cap':'round','line-join':'round'},
          paint:{'line-color':casingColor,'line-width':['interpolate',['linear'],['zoom'],5,1,8,2,10,3,14,5,18,9],'line-opacity':0.25} },
        { id:'road-primary-casing', type:'line', source:'openmaptiles', 'source-layer':'road',
          filter:['in','class','primary','trunk'],
          layout:{'line-cap':'round','line-join':'round'},
          paint:{'line-color':casingColor,'line-width':['interpolate',['linear'],['zoom'],6,0.8,8,1.5,10,2.5,14,4,18,7],'line-opacity':0.2} },
        // ── Road fills ──
        { id:'road-path', type:'line', source:'openmaptiles', 'source-layer':'road',
          filter:['in','class','path','pedestrian','track'],
          layout:{'line-cap':'round','line-join':'round'},
          paint:{'line-color':t.roadMinor,'line-width':['interpolate',['linear'],['zoom'],11,0.2,13,0.5,16,1,18,1.5],'line-opacity':0.5} },
        { id:'road-minor', type:'line', source:'openmaptiles', 'source-layer':'road',
          filter:['in','class','street','street_limited','service'],
          layout:{'line-cap':'round','line-join':'round'},
          paint:{'line-color':t.roadMinor,'line-width':['interpolate',['linear'],['zoom'],10,0.3,11,0.5,12,0.8,14,1.5,18,3.5],'line-opacity':0.9} },
        { id:'road-tertiary', type:'line', source:'openmaptiles', 'source-layer':'road',
          filter:['==','class','tertiary'],
          layout:{'line-cap':'round','line-join':'round'},
          paint:{'line-color':roadMid,'line-width':['interpolate',['linear'],['zoom'],7,0.15,9,0.4,10,0.7,12,1.2,14,2,18,4],'line-opacity':1} },
        { id:'road-secondary', type:'line', source:'openmaptiles', 'source-layer':'road',
          filter:['==','class','secondary'],
          layout:{'line-cap':'round','line-join':'round'},
          paint:{'line-color':roadMid,'line-width':['interpolate',['linear'],['zoom'],6,0.2,8,0.5,10,0.9,12,1.4,14,2.2,18,5],'line-opacity':1} },
        { id:'road-link', type:'line', source:'openmaptiles', 'source-layer':'road',
          filter:['in','class','motorway_link','trunk_link','primary_link','secondary_link','tertiary_link'],
          layout:{'line-cap':'round','line-join':'round'},
          paint:{'line-color':roadMid,'line-width':['interpolate',['linear'],['zoom'],8,0.3,10,0.6,14,1.4,18,3],'line-opacity':0.8} },
        { id:'road-primary', type:'line', source:'openmaptiles', 'source-layer':'road',
          filter:['in','class','primary','trunk'],
          // Round, like every other road. Butt caps ended the road flat against
          // a visible deck; with the deck under the roads they only left an
          // anti-aliased hairline across the road wherever the way splits.
          layout:{'line-cap':'round','line-join':'round'},
          paint:{'line-color':t.road,'line-width':['interpolate',['linear'],['zoom'],5,0.3,7,0.6,10,1.2,12,1.8,14,2.8,18,6],'line-opacity':1} },
        { id:'road-motorway', type:'line', source:'openmaptiles', 'source-layer':'road',
          filter:['==','class','motorway'],
          layout:{'line-cap':'round','line-join':'round'},
          paint:{'line-color':t.road,'line-width':['interpolate',['linear'],['zoom'],4,0.4,6,0.8,8,1.4,10,2,14,3.5,18,8],'line-opacity':1} },
        { id:'railway', type:'line', source:'openmaptiles', 'source-layer':'road',
          filter:['in','class','major_rail','minor_rail'],
          paint:{'line-color':t.roadMinor,'line-width':['interpolate',['linear'],['zoom'],8,0.3,10,0.6,14,1.2],'line-opacity':0.3,'line-dasharray':[4,4]} },
        { id:'clone-bridge-path-case', type:'line', source:'openmaptiles', 'source-layer':'road',
          minzoom:15,
          filter:['all',['==',['get','structure'],'bridge'],['==',['get','class'],'path'],
            ['step',['zoom'],['!',['match',['get','type'],['steps','sidewalk','crossing'],true,false]],16,['!=',['get','type'],'steps']],
            ['==',['geometry-type'],'LineString']],
          layout:{'line-cap':'butt','line-join':'miter'},
          paint:{'line-color':'hsl(0, 10%, 80%)',
            'line-width':['interpolate',['exponential',1.5],['zoom'],14,0.5,18,1,22,2],
            'line-gap-width':['interpolate',['exponential',1.5],['zoom'],12,0,18,6,22,80],
            'line-opacity':1} },
        { id:'clone-bridge-path', type:'line', source:'openmaptiles', 'source-layer':'road',
          minzoom:14,
          filter:['all',['==',['get','structure'],'bridge'],['==',['get','class'],'path'],['!=',['get','type'],'steps'],['==',['geometry-type'],'LineString']],
          layout:{'line-cap':'butt','line-join':'miter'},
          paint:{'line-color':['interpolate',['linear'],['zoom'],15,'hsl(295, 10%, 97%)',16,'hsl(295, 10%, 93%)'],
            'line-width':['interpolate',['exponential',1.5],['zoom'],12,0,18,6,22,80],
            'line-opacity':1} },
        { id:'clone-bridge-path-cycleway-piste', type:'line', source:'openmaptiles', 'source-layer':'road',
          minzoom:13,
          filter:['all',['==',['get','structure'],'bridge'],
            ['match',['get','class'],['path','track'],true,false],
            ['match',['get','type'],'cycleway',['step',['zoom'],false,15,true],'piste',true,false],
            ['==',['geometry-type'],'LineString']],
          layout:{'line-cap':'butt','line-join':'miter'},
          paint:{'line-color':'hsl(125, 50%, 60%)',
            'line-width':['interpolate',['linear'],['zoom'],12,['match',['get','type'],['piste'],0.5,0],18,['match',['get','type'],['piste'],4,2],22,['match',['get','type'],['piste'],40,20]],
            'line-offset':['interpolate',['linear'],['zoom'],12,0,18,['match',['get','type'],['piste'],0,-2],22,['match',['get','type'],['piste'],0,-20]],
            'line-opacity':['interpolate',['linear'],['zoom'],15,['match',['get','type'],['piste'],1,0],16,1],
            'line-dasharray':['step',['zoom'],['literal',[1]],16,['literal',[1,1]]]} },
        // Land cycleway/piste — the same treatment off the bridge.
        //
        // The clone-bridge-* stack (4937619) was built for landmarks mode, where
        // Standard is imported underneath and draws land cycleways itself, so
        // only the BRIDGE span needed cloning. Flat maps have no Standard import,
        // which left the bridge clone as the only cycleway layer in the style:
        // the green dashes appeared over the bridge and stopped dead at the bank.
        // Identical paint to the bridge layer above so the two are
        // indistinguishable where they meet; filter inverted to non-bridge.
        { id:'clone-path-cycleway-piste', type:'line', source:'openmaptiles', 'source-layer':'road',
          minzoom:13,
          filter:['all',['!=',['get','structure'],'bridge'],
            ['match',['get','class'],['path','track'],true,false],
            ['match',['get','type'],'cycleway',['step',['zoom'],false,15,true],'piste',true,false],
            ['==',['geometry-type'],'LineString']],
          layout:{'line-cap':'butt','line-join':'miter'},
          paint:{'line-color':'hsl(125, 50%, 60%)',
            'line-width':['interpolate',['linear'],['zoom'],12,['match',['get','type'],['piste'],0.5,0],18,['match',['get','type'],['piste'],4,2],22,['match',['get','type'],['piste'],40,20]],
            'line-offset':['interpolate',['linear'],['zoom'],12,0,18,['match',['get','type'],['piste'],0,-2],22,['match',['get','type'],['piste'],0,-20]],
            'line-opacity':['interpolate',['linear'],['zoom'],15,['match',['get','type'],['piste'],1,0],16,1],
            'line-dasharray':['step',['zoom'],['literal',[1]],16,['literal',[1,1]]]} },
        // ── Country borders added dynamically via addNeBorders() after load ──
        // No streets-v8 `admin` layer here. It used to sit here as a
        // placeholder that drew EVERY admin level (London boroughs, counties)
        // as dashed lines, and only ensureBorderSource() removed it, which runs
        // only when a border toggle is ON. With both toggles off it stayed:
        // after any theme change (setStyle rebuild) on the storefront, and on
        // every ACP preview whose agent turned borders off. The print
        // (render_locationmap.py) always removes it, so it never printed.
      ]
    };
    // Bridge roads: split each land road layer into a non-bridge half (left in
    // place) and a bridge-only twin drawn after ALL land roads, so a flyover
    // sits on top of whatever passes beneath it. The twin is an exact copy of
    // the land paint — the old road-bridge overlays used the primary width and
    // road colour for every class. Splitting (not overdrawing) keeps the
    // semi-transparent classes from doubling their opacity on the span.
    // Mirrored in render_locationmap.py (_BRIDGE_ROAD_LAYERS).
    var _bridgeTwins = [];
    style.layers.forEach(function(l) {
      if (LM_BRIDGE_ROAD_LAYERS.indexOf(l.id) === -1) return;
      var b = JSON.parse(JSON.stringify(l));
      b.id = l.id + '-bridge';
      b.filter = ['all', l.filter, ['==', 'structure', 'bridge']];
      l.filter = ['all', l.filter, ['!=', 'structure', 'bridge']];
      _bridgeTwins.push(b);
    });
    var _railAt = style.layers.findIndex(function(l) { return l.id === 'railway'; }) + 1;
    Array.prototype.splice.apply(style.layers, [_railAt, 0].concat(_bridgeTwins));
    // Bridge sidewalks are Mapbox Standard's, in its untinted pink-grey and
    // near-white. Under landmarks they sit on Standard's grey decks; on a flat
    // themed map they read as white dashes, and road-path-bridge already draws
    // these ways in the theme colour.
    if (!state.show3dLandmarks) {
      style.layers = style.layers.filter(function(l) {
        return l.id !== 'clone-bridge-path-case' && l.id !== 'clone-bridge-path';
      });
    }
    // Add label layers if enabled
    if (state.showLabels) {
      var labelColor = t.text || '#333';
      var haloColor = t.bg || '#fff';
      style.layers.push(
        { id:'place-label', type:'symbol', source:'openmaptiles', 'source-layer':'place_label',
          filter:['==',['get','class'],'settlement'],
          layout:{'text-field':['get','name'],'text-font':['DIN Pro Medium','Arial Unicode MS Regular'],
            'text-size':lmTS(['interpolate',['linear'],['zoom'],6,8,10,14,14,18]),'text-max-width':8},
          paint:{'text-color':labelColor,'text-opacity':0.6,'text-halo-color':haloColor,'text-halo-width':1.5} },
        { id:'place-label-sub', type:'symbol', source:'openmaptiles', 'source-layer':'place_label',
          filter:['==',['get','class'],'settlement_subdivision'],
          layout:{'text-field':['get','name'],'text-font':['DIN Pro Regular','Arial Unicode MS Regular'],
            'text-size':lmTS(['interpolate',['linear'],['zoom'],10,9,14,12]),'text-max-width':7},
          paint:{'text-color':labelColor,'text-opacity':0.4,'text-halo-color':haloColor,'text-halo-width':1} },
        { id:'country-label', type:'symbol', source:'openmaptiles', 'source-layer':'place_label',
          filter:['==',['get','class'],'country'],
          layout:{'text-field':['get','name'],'text-font':['DIN Pro Medium','Arial Unicode MS Regular'],
            'text-size':lmTS(['interpolate',['linear'],['zoom'],3,10,6,16]),'text-transform':'uppercase','text-letter-spacing':0.15,'text-max-width':10},
          paint:{'text-color':labelColor,'text-opacity':0.35,'text-halo-color':haloColor,'text-halo-width':1.5} }
      );
    }
    // Hillshade can render chunky/pixelated on mobile (raster-DEM at high DPR),
    // so default it hidden on small screens. add3dTerrain re-enables it when
    // the user explicitly turns 3D Terrain on (which is what makes the elevation
    // visually read).
    var _hideHillshadeMobile = window.matchMedia && window.matchMedia('(max-width: 768px)').matches && !state.show3dTerrain;
    if (_hideHillshadeMobile) {
      style.layers.forEach(function(l) {
        if (l.id === 'hillshade') {
          l.layout = l.layout || {};
          l.layout.visibility = 'none';
        }
      });
    }
    // 3D Landmarks: import Mapbox Standard (which bundles glTF landmark meshes
    // for London Eye, Eiffel Tower, etc.) and place all our themed layers in
    // the 'bottom' slot so Standard's 3D objects render above them.
    if (state.show3dLandmarks) {
      style.imports = [{
        id: 'basemap',
        url: 'mapbox://styles/mapbox/standard',
        config: {
          lightPreset: state.lightPreset || 'day',
          showPlaceLabels: false,
          showRoadLabels: false,
          showPointOfInterestLabels: false,
          showTransitLabels: false,
          showLandmarkIcons: false,
          show3dObjects: true
        }
      }];
      // Slot strategy when Standard import is active:
      //   middle — fills (water, park, landuse) so they sit above Standard's
      //            basemap polygons but below 3D buildings/landmarks
      //   no slot (default = above all imports) — roads render on top of
      //            everything from Standard, guaranteeing visibility
      style.layers.forEach(function(l) {
        // The base style's `background` layer (theme bg colour) needs to
        // sit BELOW Standard's basemap polygons — otherwise on iOS Safari
        // it covers Standard's ground/water/landuse and only the 3D
        // building extrusions (which rise above z=0) remain visible at
        // the horizon. Slotting it to "bottom" puts it under Standard's
        // basemap so the ground re-appears, while still serving as a
        // theme-coloured backdrop when Standard is mid-load. Hillshade
        // stays unslotted (renders above imports for terrain shading).
        if (l.type === 'background') {
          l.slot = 'bottom';
          return;
        }
        if (l.type === 'hillshade') return;
        // Bridge stack — both clone-* (Mapbox-style wide body, path fills,
        // cycleway) and bridge-deck-poly (structure polygon) live at slot
        // 'middle' so they sit above Standard's basemap water but below
        // Standard's 3D objects.
        if (l.id === 'bridge-deck-poly' ||
            l.id === 'clone-bridge-primary' ||
            l.id === 'clone-bridge-primary-case' ||
            l.id === 'clone-bridge-path' ||
            l.id === 'clone-bridge-path-case' ||
            l.id === 'clone-bridge-path-cycleway-piste' ||
            l.id === 'clone-path-cycleway-piste') {
          l.slot = 'middle';
          return;
        }
        if (l.id && (l.id.indexOf('label') === 0 || l.id.indexOf('text') === 0)) {
          // Labels stay above all imports (must be readable over buildings).
          delete l.slot;
        } else if (l.id && l.id.indexOf('road') === 0) {
          // Roads sit at "middle" — Standard's 3D building/landmark
          // extrusions then render OVER them via depth-test. Before this,
          // roads were slot-less (above all imports) which painted the
          // tarmac straight through Big Ben/Westminster towers.
          l.slot = 'middle';
        } else {
          // Ground fills (water, parks, landuse, building footprints,
          // bathymetry, etc) all sit at "bottom" — under Standard's 3D
          // extrusions. Previously slotted to "middle", which on iOS
          // Safari painted the water polygons OVER nearby foreground
          // 3D landmarks (Big Ben's base looked submerged behind a
          // teal water band). With ground fills at "bottom", they
          // render below 3D objects but still above Standard's basemap
          // basics, so the theme colour comes through correctly.
          l.slot = 'bottom';
        }
      });
    }
    return style;
  }

  // ── Mobile zoom strip flip-front wrapper ──
  // Landscape print flips #lm-mobile-zoom 180deg to reveal #lm-icon-card as
  // its .lm-flip-back face (see lmIconCardPlace). assets/locationmap-card.css
  // hides whichever face isn't showing via backface-visibility, but that
  // only works if BOTH faces exist — and nothing ever gave the buttons a
  // .lm-flip-front wrapper, so on-device they stayed visible (upside down)
  // through the flip instead of being hidden. Wrap the strip's original
  // button children into one .lm-flip-front div, once, at bind time,
  // regardless of orientation, so the DOM is stable before any flip ever
  // happens and portrait behaviour (buttons are still direct descendants of
  // the flex layout, just one level deeper) is unaffected. Idempotent: a
  // second call (there isn't one today, but bindAll only ever runs once
  // per page load) no-ops instead of re-wrapping. Never wraps #lm-icon-card
  // itself — that must stay a direct child of #lm-mobile-zoom as the
  // .lm-flip-back face (lmIconCardPlace appends it there directly).
  function bindMobileZoomFlipFront() {
    var zoom = document.getElementById('lm-mobile-zoom');
    if (!zoom) return;
    if (zoom.querySelector('.lm-flip-front')) return; // already wrapped
    var front = document.createElement('div');
    front.className = 'lm-flip-front';
    // Snapshot current children into a plain array first — zoom.children is
    // a live HTMLCollection that would mutate mid-loop as each node moves
    // into front, skipping every other child.
    var kids = Array.prototype.slice.call(zoom.children);
    for (var i = 0; i < kids.length; i++) {
      var kid = kids[i];
      if (kid.id === 'lm-icon-card') continue;
      front.appendChild(kid);
    }
    zoom.appendChild(front);
  }

  // ── Bind All UI ──
  function bindAll() {
    bindMobileZoomFlipFront();
    bindSearch();
    bindLabelsToggle();
    bindZoom();
    bindRotatePitch();
    enhanceSlider(document.getElementById('lm-zoom'));
    enhanceSlider(document.getElementById('lm-rotate'), { centre: true });
    enhanceSlider(document.getElementById('lm-pitch'));
    ['lm-hw-width', 'lm-hw-modal-width', 'lm-hw-size', 'lm-hw-height'].forEach(function (id) {
      enhanceSlider(document.getElementById(id));
    });
    bindMobileTilt();
    bindLock();
    bindAccordions();
    // Theme/Layout/TextStyle/Orientation/BgColor/Font/Type/Size/Frame/Gift
    // pickers are Alpine-driven (Phase 2); their click handling lives in
    // declarative `@click` attributes calling lmSet*() helpers.
    bindThemeDisclosure();
    bindTextInputs();
    bindIcons();
    bindToolbarFab();
    bindAddToCart();
    bindLmAutosave();

    window.addEventListener('resize', function() { applyFrameRatio(); });
  }

  // ── Theme picker disclosure ──
  // The Theme section shows six curated swatches and hides the other groups
  // behind "Show all 25 themes" (markup in sections/locationmap.liquid, styling
  // under "Theme picker disclosure" in assets/locationmap-section.css).
  //
  // Deliberately a class toggle rather than Alpine local state: hydration paths
  // (a resumed draft, a ?order_id= edit, a preset) settle state.theme long
  // after Alpine initialises the component, and if that theme is not one of the
  // six its tile would sit inside a collapsed container with nothing on screen
  // marked active. lmSyncThemeDisclosure() is called from those paths to open
  // the list when that happens. It never CLOSES the list, so a customer who
  // opened it manually keeps it open when the theme changes underneath them.
  //
  // Not persisted: the picker starts collapsed on every fresh load, otherwise
  // the simplification only lands once per customer.
  var LM_STARTER_THEMES = ['nautical', 'midnight', 'monochrome', 'rosegold', 'noir', 'forest'];

  function lmSetThemeDisclosure(open) {
    var wrap = document.getElementById('lm-themes');
    var btn = document.getElementById('lm-theme-toggle');
    if (!wrap) return;
    wrap.classList.toggle('is-open', !!open);
    if (btn) {
      btn.setAttribute('aria-expanded', open ? 'true' : 'false');
      var label = btn.querySelector('.lm-theme-toggle-label');
      if (label) label.textContent = open ? 'Show fewer themes' : 'Show all 25 themes';
    }
  }

  function lmSyncThemeDisclosure() {
    if (LM_STARTER_THEMES.indexOf(state.theme) === -1) lmSetThemeDisclosure(true);
  }
  window.lmSyncThemeDisclosure = lmSyncThemeDisclosure;

  function bindThemeDisclosure() {
    var btn = document.getElementById('lm-theme-toggle');
    if (!btn) return;
    btn.addEventListener('click', function () {
      var wrap = document.getElementById('lm-themes');
      lmSetThemeDisclosure(!(wrap && wrap.classList.contains('is-open')));
    });
    lmSyncThemeDisclosure();
  }

  // ── Location Search (Google Places PlaceAutocompleteElement) ──
  // Ported from starmap-v2.js — Mapbox Search Box had weak POI recall
  // (e.g. "Hyde Park" wouldn't surface the actual park), so we mount
  // google.maps.places.PlaceAutocompleteElement instead. The web component
  // owns its own input + dropdown (and a fullscreen dialog on mobile).
  var _lmPacBound = false;
  var _lmPacElement = null;
  // Holds the desired placeholder text when an order/preset load runs
  // BEFORE Google's async script has fired the lmInitPlaces callback. The
  // pending value is consumed inside bindPlacesAutocomplete when the
  // element finally mounts. Without this, the visible search field stays
  // empty/default after reloading a saved order.
  var _lmPendingDisplay = null;
  function bindPlacesAutocomplete() {
    if (_lmPacBound) return;
    if (typeof google === 'undefined' || !google.maps || !google.maps.places) return;
    if (!google.maps.places.PlaceAutocompleteElement) return;
    var wrapper = document.getElementById('lm-location-wrapper');
    if (!wrapper) return;
    _lmPacBound = true;
    // UK bias — same rationale as starmap-v2: Skylit is a UK store, audience
    // ~95% UK. requestedRegion shapes ranking; locationBias is a UK bbox.
    // Neither RESTRICTS, so international customers can still pick non-UK
    // places — they just appear lower in the dropdown.
    // SkylitPlaces swaps in our look-alike (assets/skylit-place-autocomplete.js),
    // which debounces typing and bills picks at the Essentials Places tier.
    var pacOptions = {
      requestedRegion: 'gb',
      locationBias: { north: 60.9, south: 49.5, east: 1.8, west: -8.7 }
    };
    var pac = window.SkylitPlaces
      ? window.SkylitPlaces.createAutocomplete(pacOptions)
      : new google.maps.places.PlaceAutocompleteElement(pacOptions);
    pac.id = 'lm-location-input';
    pac.className = 'lm-pac-element';
    // Pending display from an early order-load wins over state.locationName
    // (they should be in sync, but the pending value is the explicit signal
    // that a reload-display has been requested).
    var initialDisplay = _lmPendingDisplay || state.locationName || 'Search for a place...';
    try { pac.placeholder = initialDisplay; } catch (e) {}
    try { pac.setAttribute && pac.setAttribute('placeholder', initialDisplay); } catch (e) {}
    _lmPendingDisplay = null;
    wrapper.insertBefore(pac, wrapper.firstChild);
    _lmPacElement = pac;

    pac.addEventListener('gmp-select', async function(event) {
      try {
        var place = event.placePrediction.toPlace();
        await place.fetchFields({ fields: ['displayName', 'formattedAddress', 'location', 'addressComponents', 'viewport', 'types'] });
        var lat = place.location.lat();
        var lng = place.location.lng();
        // Google returns POIs/landmarks as { displayName: 'Hyde Park',
        // formattedAddress: 'London' } — preferring formattedAddress alone
        // throws away the landmark name. For typed addresses both fields
        // typically match, in which case combining is a no-op via dedupe.
        var disp = place.displayName || '';
        var addr = place.formattedAddress || '';
        var raw;
        if (disp && addr && disp.toLowerCase() !== addr.toLowerCase() &&
            addr.toLowerCase().indexOf(disp.toLowerCase()) === -1) {
          raw = disp + ', ' + addr;
        } else {
          raw = addr || disp;
        }
        var cleanName = dedupePlace(raw);
        state.lat = lat;
        state.lng = lng;
        state.locationName = cleanName;
        // Structured city + country from Google's addressComponents — reliable
        // even when formattedAddress drops the country for UK-region results
        // (e.g. "Manchester" with no ", United Kingdom"). The "place"
        // composition reads these; comma-split is the fallback.
        try {
          var comps = place.addressComponents || [];
          var pick = function (type) {
            for (var i = 0; i < comps.length; i++) {
              var types = comps[i].types || [];
              if (types.indexOf(type) !== -1) return comps[i].longText || comps[i].shortText || '';
            }
            return '';
          };
          state.placeCountry = pick('country');
          state.placeCity = pick('locality') || pick('postal_town') || pick('administrative_area_level_2') || (place.displayName || '');
        } catch (e) { state.placeCountry = ''; state.placeCity = ''; }
        console.log('[lm.places] selected', { lat: lat, lng: lng, locationName: cleanName, formattedAddress: place.formattedAddress, displayName: place.displayName });
        // Sync subtitle with location (matches the legacy Mapbox behaviour:
        // first two comma-separated parts, uppercased downstream).
        var sub = document.getElementById('lm-subtitle');
        if (sub) {
          sub.value = cleanName.split(',').slice(0, 2).join(',').trim();
          state.subtitle = sub.value;
        }
        // In "place" composition, re-derive city/country from the new location.
        if (state.textStyle === 'place' && typeof lmApplyPlaceComposition === 'function') lmApplyPlaceComposition();
        // Framing: a fixed zoom 11 suits a city but is far too tight for a
        // country/region (it lands on a random interior point). For large-area
        // places, fit Google's recommended viewport so the whole thing is in
        // frame; cities/towns/POIs keep the well-tuned zoom 11. fitBounds runs
        // against the supersampled container, so reportZoom strips the SS
        // offset on save and the renderer stays in parity.
        if (map) {
          var placeTypes = place.types || [];
          var isLargeArea = placeTypes.indexOf('country') !== -1 ||
                            placeTypes.indexOf('administrative_area_level_1') !== -1;
          if (isLargeArea && place.viewport) {
            try {
              var _ne = place.viewport.getNorthEast();
              var _sw = place.viewport.getSouthWest();
              map.fitBounds(
                [[_sw.lng(), _sw.lat()], [_ne.lng(), _ne.lat()]],
                { padding: 40, duration: 1500 }
              );
            } catch (e) {
              map.flyTo({ center: [lng, lat], zoom: applyZoom(11), duration: 1500 });
            }
          } else {
            map.flyTo({ center: [lng, lat], zoom: applyZoom(11), duration: 1500 });
          }
        }
        if (typeof updateTextOverlay === 'function') updateTextOverlay();
        if (typeof scheduleLmAutosave === 'function') scheduleLmAutosave();
      } catch (e) { console.warn('[lm] place select failed', e); }
      _lmPacBlur();
    });

    // No iOS body-lock here. It used to pin the field to the top by fixing
    // <body> at -scrollY (lifted from starmap-v2, whose panel scrolls inside
    // its own box). On phones this page scrolls on the WINDOW and the preview
    // stage (lm-stage.js) collapses/expands with the scroll position, so the
    // lock's jump to a fixed top re-laid-out the stage under it and the
    // keyboard opened with the field scrolled off-screen (the view landed on
    // the icon grid). Same fix as the moon (commit eabe771): let iOS scroll the
    // focused field into view; interactive-widget=resizes-content in
    // layout/theme.liquid keeps it above the keyboard.
    function _lmPacBlur() {
      // After a pick, drop focus so the keyboard closes.
      try { pac.blur && pac.blur(); } catch (e) {}
    }
  }
  // Update the search field display (preset/saved load). PlaceAutocompleteElement
  // doesn't reliably accept .value writes across API versions, so we mirror
  // starmap-v2 and seed the placeholder — the user sees the loaded location
  // until they tap to enter a new query.
  function setLocationInputDisplay(text) {
    if (!text) return;
    if (!_lmPacElement) { _lmPendingDisplay = text; return; }
    try { _lmPacElement.placeholder = text; } catch (e) {}
    try { _lmPacElement.setAttribute && _lmPacElement.setAttribute('placeholder', text); } catch (e) {}
  }
  function bindSearch() { bindPlacesAutocomplete(); }
  window.lmInitPlaces = bindPlacesAutocomplete;
  window.lmInitPlaces.__real = true;

  // ── Labels Toggle ──
  var LABEL_LAYER_IDS = ['place-label','place-label-sub','country-label'];
  var WATER_LABEL_IDS = ['water-label','waterway-label'];
  var ROAD_LABEL_IDS = ['road-label-major','road-label-minor'];

  function getLabelColors() {
    var t = lmThemeData();
    return { color: t.text || '#333', halo: t.bg || '#fff' };
  }

  function addWaterLabels() {
    if (!map || !state.showWaterLabels) return;
    var c = getLabelColors();
    var layers = [
      { id:'water-label', type:'symbol', source:'openmaptiles', 'source-layer':'natural_label',
        filter:['match',['get','class'],['ocean','sea','bay','strait','continent'],true,false],
        minzoom:1,
        layout:{'text-field':['coalesce',['get','name_en'],['get','name']],'text-font':['Open Sans Italic','Arial Unicode MS Regular'],
          'text-size':lmTS(['interpolate',['linear'],['zoom'],1,10,4,12,8,14,12,16]),'text-letter-spacing':0.15,'text-max-width':8},
        paint:{'text-color':c.color,'text-opacity':0.45,'text-halo-color':c.halo,'text-halo-width':1.2} },
      { id:'waterway-label', type:'symbol', source:'openmaptiles', 'source-layer':'natural_label',
        filter:['match',['get','class'],['river','canal','water','water_feature','reservoir','stream','lake','dock','drain'],true,false],
        minzoom:3,
        layout:{'text-field':['coalesce',['get','name_en'],['get','name']],'text-font':['Open Sans Italic','Arial Unicode MS Regular'],
          'text-size':lmTS(['interpolate',['linear'],['zoom'],3,9,6,10,10,12,14,14]),'text-letter-spacing':0.12,'text-max-width':7},
        paint:{'text-color':c.color,'text-opacity':0.45,'text-halo-color':c.halo,'text-halo-width':1.2} }
    ];
    layers.forEach(function(l) {
      try { map.removeLayer(l.id); } catch(e) {}
      try { map.addLayer(l); } catch(e) { console.warn('Water label error:', l.id, e); }
    });
  }

  function removeWaterLabels() {
    WATER_LABEL_IDS.forEach(function(id) {
      try { map.removeLayer(id); } catch(e) {}
    });
  }

  function addLabelLayers() {
    var c = getLabelColors();
    var layers = [
      { id:'place-label', type:'symbol', source:'openmaptiles', 'source-layer':'place_label',
        filter:['==',['get','class'],'settlement'],
        layout:{'text-field':['get','name'],'text-font':['Open Sans Semibold','Arial Unicode MS Regular'],
          'text-size':lmTS(['interpolate',['linear'],['zoom'],6,8,10,14,14,18]),'text-max-width':8},
        paint:{'text-color':c.color,'text-opacity':0.6,'text-halo-color':c.halo,'text-halo-width':1.5} },
      { id:'place-label-sub', type:'symbol', source:'openmaptiles', 'source-layer':'place_label',
        filter:['==',['get','class'],'settlement_subdivision'],
        layout:{'text-field':['get','name'],'text-font':['Open Sans Regular','Arial Unicode MS Regular'],
          'text-size':lmTS(['interpolate',['linear'],['zoom'],10,9,14,12]),'text-max-width':7},
        paint:{'text-color':c.color,'text-opacity':0.4,'text-halo-color':c.halo,'text-halo-width':1} },
      { id:'country-label', type:'symbol', source:'openmaptiles', 'source-layer':'place_label',
        filter:['==',['get','class'],'country'],
        layout:{'text-field':['get','name'],'text-font':['Open Sans Bold','Arial Unicode MS Regular'],
          'text-size':lmTS(['interpolate',['linear'],['zoom'],3,10,6,16]),'text-transform':'uppercase','text-letter-spacing':0.15,'text-max-width':10},
        paint:{'text-color':c.color,'text-opacity':0.35,'text-halo-color':c.halo,'text-halo-width':1.5} }
    ];
    layers.forEach(function(l) {
      try { map.removeLayer(l.id); } catch(e) {}
      try { map.addLayer(l); } catch(e) { console.warn('Label layer error:', l.id, e); }
    });
  }

  function removeLabelLayers() {
    LABEL_LAYER_IDS.forEach(function(id) {
      try { map.removeLayer(id); } catch(e) {}
    });
  }

  function addRoadLabels() {
    var c = getLabelColors();
    var layers = [
      { id:'road-label-major', type:'symbol', source:'openmaptiles', 'source-layer':'road',
        filter:['match',['get','class'],['trunk','primary','secondary','tertiary'],true,false],
        layout:{'text-field':['get','name'],'text-font':['Open Sans Regular','Arial Unicode MS Regular'],
          'text-size':lmTS(['interpolate',['linear'],['zoom'],10,8,14,11,18,14]),
          'symbol-placement':'line','text-max-angle':30,'text-padding':10},
        paint:{'text-color':c.color,'text-opacity':0.4,'text-halo-color':c.halo,'text-halo-width':1} },
      { id:'road-label-minor', type:'symbol', source:'openmaptiles', 'source-layer':'road',
        filter:['match',['get','class'],['street','street_limited'],true,false],
        minzoom:14,
        layout:{'text-field':['get','name'],'text-font':['Open Sans Regular','Arial Unicode MS Regular'],
          'text-size':lmTS(['interpolate',['linear'],['zoom'],14,9,18,12]),
          'symbol-placement':'line','text-max-angle':30,'text-padding':15},
        paint:{'text-color':c.color,'text-opacity':0.3,'text-halo-color':c.halo,'text-halo-width':1} }
    ];
    layers.forEach(function(l) {
      try { map.removeLayer(l.id); } catch(e) {}
      try { map.addLayer(l); } catch(e) { console.warn('Road label error:', l.id, e); }
    });
  }

  function removeRoadLabels() {
    ROAD_LABEL_IDS.forEach(function(id) {
      try { map.removeLayer(id); } catch(e) {}
    });
  }


  function bindLabelToggle(rowId, switchId, stateKey, addFn, removeFn) {
    var row = document.getElementById(rowId);
    var toggle = document.getElementById(switchId);
    if (!row || !toggle) return;
    row.addEventListener('click', function() {
      state[stateKey] = !state[stateKey];
      toggle.classList.toggle('active', state[stateKey]);
      if (state[stateKey]) { addFn(); } else { removeFn(); }
      scheduleLM3DRefresh(1000);
    });
  }

  function bindLabelsToggle() {
    bindLabelToggle('lm-land-borders-row', 'lm-land-borders-switch', 'showLandBorders', addLandBorders, removeLandBorders);
    bindLabelToggle('lm-maritime-borders-row', 'lm-maritime-borders-switch', 'showMaritimeBorders', addMaritimeBorders, removeMaritimeBorders);
    bindLabelToggle('lm-labels-row', 'lm-labels-switch', 'showLabels', addLabelLayers, removeLabelLayers);
    bindLabelToggle('lm-water-labels-row', 'lm-water-labels-switch', 'showWaterLabels', addWaterLabels, removeWaterLabels);
    bindLabelToggle('lm-road-labels-row', 'lm-road-labels-switch', 'showRoadLabels', addRoadLabels, removeRoadLabels);
    bindLabelToggle('lm-3d-buildings-row', 'lm-3d-buildings-switch', 'show3dBuildings',
      add3dBuildings, remove3dBuildings);
    bindLabelToggle('lm-3d-terrain-row', 'lm-3d-terrain-switch', 'show3dTerrain', add3dTerrain, remove3dTerrain);
    bindLabelToggle('lm-3d-landmarks-row', 'lm-3d-landmarks-switch', 'show3dLandmarks',
      function(){
        if (!map) return;
        var ps = document.getElementById('lm-pitch'); if (ps) ps.max = '85';
        var lp = document.getElementById('lm-light-preset'); if (lp) lp.style.display = 'block';
        var lmp = document.getElementById('lm-landmark-presets'); if (lmp) lmp.style.display = 'block';
        updateOther3dRows();
        // pixelRatio is fixed at construction — recreate to bump it for denser
        // 3D detail in preview.
        recreateMapForLandmarks();
      },
      function(){
        if (!map) return;
        if (state.pitch > 45) {
          state.pitch = 45;
          var ps = document.getElementById('lm-pitch'); if (ps) ps.value = '45';
          var pv = document.getElementById('lm-pitch-val'); if (pv) pv.textContent = '45°';
        }
        var ps2 = document.getElementById('lm-pitch'); if (ps2) ps2.max = '45';
        var lp = document.getElementById('lm-light-preset'); if (lp) lp.style.display = 'none';
        var lmp = document.getElementById('lm-landmark-presets'); if (lmp) lmp.style.display = 'none';
        updateOther3dRows();
        // Drop pixelRatio back to 2 by recreating without landmarks.
        recreateMapForLandmarks();
      });
    // Light + building-detail click handlers are Alpine-driven — see
    // window.lmSetLightPreset / lmSetBuildingDetail. The markup uses
    // :class + @click against $store.lm.lightPreset / buildingDetail.
    renderLandmarkPresets();
  }

  // POIs confirmed present in Mapbox Standard's curated 3D Landmarks set
  // (verified against the mapbox-landmark-pois-v1 tileset). Each entry
  // pre-configures a cinematic camera and the title/place text that
  // auto-fills when the chip is clicked. Only includes landmarks that
  // actually render with the high-detail glTF model.
  var LANDMARK_PRESETS = [
    // London
    { label: 'Big Ben',            title: 'Where It All Began', place: 'Big Ben, London',             lat: 51.5007, lng: -0.1246, zoom: 16.8, bearing: 30,  pitch: 65 },
    { label: 'London Eye',         title: 'Where It All Began', place: 'London Eye, London',          lat: 51.5033, lng: -0.1196, zoom: 16.6, bearing: -50, pitch: 65 },
    { label: 'Tower of London',    title: 'Where It All Began', place: 'Tower of London',             lat: 51.5086, lng: -0.0760, zoom: 16.4, bearing: -30, pitch: 60 },
    { label: 'Buckingham Palace',  title: 'Where It All Began', place: 'Buckingham Palace, London',   lat: 51.5008, lng: -0.1429, zoom: 16.8, bearing: 90,  pitch: 60 },
    { label: 'St Paul\u2019s',     title: 'Where It All Began', place: 'St Paul\u2019s, London',      lat: 51.5138, lng: -0.0984, zoom: 16.8, bearing: 40,  pitch: 60 },
    { label: 'The Shard',          title: 'Where It All Began', place: 'The Shard, London',           lat: 51.5045, lng: -0.0865, zoom: 16.6, bearing: 20,  pitch: 65 },
    { label: 'Battersea Power',    title: 'Where It All Began', place: 'Battersea, London',           lat: 51.4816, lng: -0.1442, zoom: 16.6, bearing: -20, pitch: 60 },
    // Paris
    { label: 'Eiffel Tower',       title: 'Where It All Began', place: 'Eiffel Tower, Paris',         lat: 48.8584, lng:  2.2945, zoom: 16.6, bearing: 45,  pitch: 70 },
    { label: 'Arc de Triomphe',    title: 'Where It All Began', place: 'Arc de Triomphe, Paris',      lat: 48.8738, lng:  2.2950, zoom: 16.8, bearing: 60,  pitch: 60 },
    { label: 'Notre-Dame',         title: 'Where It All Began', place: 'Notre-Dame, Paris',           lat: 48.8530, lng:  2.3499, zoom: 17.0, bearing: 30,  pitch: 60 },
    { label: 'Louvre',             title: 'Where It All Began', place: 'Louvre Museum, Paris',        lat: 48.8615, lng:  2.3352, zoom: 16.8, bearing: 0,   pitch: 60 },
    // NYC
    { label: 'Empire State',       title: 'Where It All Began', place: 'Empire State, NYC',           lat: 40.7484, lng: -73.9857, zoom: 16.6, bearing: 30,  pitch: 65 },
    { label: 'Statue of Liberty',  title: 'Where It All Began', place: 'Statue of Liberty, NYC',      lat: 40.6892, lng: -74.0445, zoom: 16.4, bearing: 0,   pitch: 60 },
    { label: 'Chrysler',           title: 'Where It All Began', place: 'Chrysler Building, NYC',      lat: 40.7516, lng: -73.9755, zoom: 16.8, bearing: 30,  pitch: 65 },
    { label: 'Flatiron',           title: 'Where It All Began', place: 'Flatiron, NYC',               lat: 40.7411, lng: -73.9897, zoom: 17.0, bearing: 0,   pitch: 60 },
    // USA other
    { label: 'US Capitol',         title: 'Where It All Began', place: 'US Capitol, Washington',      lat: 38.8898, lng: -77.0090, zoom: 16.8, bearing: 270, pitch: 60 },
    { label: 'Lincoln Memorial',   title: 'Where It All Began', place: 'Lincoln Memorial, Washington', lat: 38.8893, lng: -77.0502, zoom: 17.0, bearing: 90,  pitch: 60 },
    { label: 'Willis Tower',       title: 'Where It All Began', place: 'Willis Tower, Chicago',       lat: 41.8788, lng: -87.6360, zoom: 16.4, bearing: 30,  pitch: 70 },
    // Italy / Vatican
    { label: 'Colosseum',          title: 'Where It All Began', place: 'Colosseum, Rome',             lat: 41.8902, lng: 12.4922, zoom: 16.8, bearing: 30,  pitch: 65 },
    { label: 'St Peter\u2019s',    title: 'Where It All Began', place: 'St Peter\u2019s, Vatican',    lat: 41.9022, lng: 12.4539, zoom: 16.6, bearing: 90,  pitch: 60 },
    { label: 'Tower of Pisa',      title: 'Where It All Began', place: 'Tower of Pisa',               lat: 43.7230, lng: 10.3967, zoom: 17.0, bearing: 0,   pitch: 60 },
    // Europe other
    { label: 'Sagrada Fam\u00edlia', title: 'Where It All Began', place: 'Sagrada Fam\u00edlia, Barcelona', lat: 41.4036, lng: 2.1744, zoom: 16.8, bearing: 30, pitch: 65 },
    { label: 'Brandenburg Gate',   title: 'Where It All Began', place: 'Brandenburg Gate, Berlin',    lat: 52.5163, lng: 13.3777, zoom: 17.0, bearing: 90,  pitch: 60 },
    { label: 'Neuschwanstein',     title: 'Where It All Began', place: 'Neuschwanstein Castle',       lat: 47.5576, lng: 10.7498, zoom: 16.8, bearing: 30,  pitch: 65 },
    { label: 'Parthenon',          title: 'Where It All Began', place: 'Parthenon, Athens',           lat: 37.9715, lng: 23.7266, zoom: 17.0, bearing: 0,   pitch: 60 },
    // Africa / Middle East
    { label: 'Great Pyramid',      title: 'Where It All Began', place: 'Great Pyramid, Giza',         lat: 29.9792, lng: 31.1342, zoom: 16.4, bearing: 30,  pitch: 65 },
    // Americas other
    { label: 'Christ Redeemer',    title: 'Where It All Began', place: 'Christ the Redeemer, Rio',    lat: -22.9519, lng: -43.2105, zoom: 16.6, bearing: 0,   pitch: 60 },
    // Asia / Oceania
    { label: 'Tokyo Skytree',      title: 'Where It All Began', place: 'Tokyo Skytree',               lat: 35.7101, lng: 139.8107, zoom: 16.0, bearing: 30,  pitch: 70 },
    { label: 'Tokyo Tower',        title: 'Where It All Began', place: 'Tokyo Tower',                 lat: 35.6586, lng: 139.7455, zoom: 16.4, bearing: 30,  pitch: 70 },
    { label: 'Petronas Towers',    title: 'Where It All Began', place: 'Petronas Towers, KL',         lat: 3.1579,  lng: 101.7115, zoom: 16.4, bearing: 30,  pitch: 70 },
    { label: 'Sydney Opera',       title: 'Where It All Began', place: 'Sydney Opera House',          lat: -33.8568, lng: 151.2152, zoom: 16.8, bearing: 200, pitch: 65 }
  ];

  function renderLandmarkPresets() {
    var wrap = document.getElementById('lm-landmark-chips');
    if (!wrap || wrap.dataset.rendered) return;
    wrap.dataset.rendered = '1';
    LANDMARK_PRESETS.forEach(function(p){
      var btn = document.createElement('button');
      btn.type = 'button';
      btn.className = "lm-landmark-chip px-[10px] py-[5px] text-[11px] rounded-full border border-black/[.12] bg-white text-[#444] cursor-pointer transition-all whitespace-nowrap leading-[1.2] hover:border-[#c39c71] hover:text-[#333] hover:bg-[rgba(195,156,113,0.08)]";
      btn.textContent = p.label;
      btn.addEventListener('click', function(){ jumpToLandmark(p); });
      wrap.appendChild(btn);
    });
  }

  // Preset zooms are calibrated for the ~640px-wide desktop map slot.
  // On mobile the slot is roughly 360px so the same zoom shows ~half the area
  // → looks far too zoomed in. Compensate by log2(currentWidth / reference).
  function presetZoomForViewport(z) {
    var inner = document.getElementById('lm-map-inner');
    var w = inner ? inner.clientWidth : (map && map.getContainer ? map.getContainer().clientWidth : 640);
    if (!w || w <= 0) return z;
    var REF = 640;
    return z + Math.log2(w / REF);
  }

  function jumpToLandmark(p) {
    var adjustedZoom = presetZoomForViewport(p.zoom);
    // Make sure landmarks mode is on (recreates the map at higher pixelRatio)
    if (!state.show3dLandmarks) {
      state.show3dLandmarks = true;
      var sw = document.getElementById('lm-3d-landmarks-switch');
      if (sw) sw.classList.add('active');
      var ps = document.getElementById('lm-pitch'); if (ps) ps.max = '85';
      var lp = document.getElementById('lm-light-preset'); if (lp) lp.style.display = 'block';
      var lmp = document.getElementById('lm-landmark-presets'); if (lmp) lmp.style.display = 'block';
      updateOther3dRows();
      // Update camera target before recreate so the new map opens at the landmark
      state.lat = p.lat; state.lng = p.lng; state.zoom = adjustedZoom;
      state.bearing = p.bearing; state.pitch = p.pitch;
      recreateMapForLandmarks();
    } else if (map) {
      map.jumpTo({ center: [p.lng, p.lat], zoom: applyZoom(adjustedZoom), bearing: p.bearing, pitch: p.pitch });
    }
    // Auto-fill title, subtitle (the place caption shown on the print) and the
    // location search field.
    state.title = p.title;
    state.subtitle = p.place.toUpperCase();
    state.locationName = p.place;
    var titleInput = document.getElementById('lm-title');
    if (titleInput) titleInput.value = p.title;
    var subInput = document.getElementById('lm-subtitle');
    if (subInput) subInput.value = state.subtitle;
    setLocationInputDisplay(p.place);
    if (typeof updateTextOverlay === 'function') updateTextOverlay();
    // Sync sliders
    var rs = document.getElementById('lm-rotate'); if (rs) rs.value = p.bearing;
    var psR = document.getElementById('lm-pitch'); if (psR) psR.value = p.pitch;
    var rv = document.getElementById('lm-rotate-val'); if (rv) rv.textContent = Math.round(p.bearing) + '\u00b0';
    var pv = document.getElementById('lm-pitch-val'); if (pv) pv.textContent = Math.round(p.pitch) + '\u00b0';
    var zs = document.getElementById('lm-zoom'); if (zs) zs.value = p.zoom;
  }

  // ── 3D Buildings ──
  function buildingMinHeight() {
    return state.buildingDetail === 'low' ? 50 : state.buildingDetail === 'high' ? 0 : 20;
  }
  function add3dBuildings() {
    if (!map || map.getLayer('building-3d')) return;
    // When 3D Landmarks (Standard import) is on, Standard renders buildings
    // itself — adding ours would z-fight.
    if (state.show3dLandmarks) return;
    if (window.__lmSetSSForBuildings) window.__lmSetSSForBuildings(true);
    var t = lmThemeData();
    var minH = buildingMinHeight();
    map.addLayer({
      id: 'building-3d', type: 'fill-extrusion', source: 'openmaptiles', 'source-layer': 'building',
      minzoom: 13,
      filter: ['all',
        // Umbrella outlines of buildings that have building:part children
        // carry extrude="false" (OSM Simple 3D spec) -- extruding them
        // renders a giant slab over the parts (the London Eye square).
        // BUT elevated parts (min_height > 0: the Eye's rim segments,
        // spokes, gondolas) are ALSO marked extrude="false" by Mapbox, so
        // building:part features are kept unconditionally; only non-part
        // features must be extrude="true".
        ['any', ['==', ['get', 'type'], 'building:part'], ['==', ['get', 'extrude'], 'true']],
        ['!=', ['get', 'hide_3d'], true],
        ['>=', ['coalesce', ['get','height'], ['get','render_height'], 0], minH]
      ],
      paint: {
        'fill-extrusion-color': t.building,
        'fill-extrusion-height': ['interpolate', ['linear'], ['zoom'], 13, 0, 14.5, ['coalesce', ['get','height'], ['get','render_height'], 5]],
        'fill-extrusion-base': ['coalesce', ['get','min_height'], ['get','render_min_height'], 0],
        'fill-extrusion-opacity': 0.9,
        'fill-extrusion-ambient-occlusion-intensity': 0.4,
        'fill-extrusion-ambient-occlusion-radius': 4
      }
    });
    // building-3d was just added to the TOP of the stack, so it now covers the
    // place/water labels. Re-add the active label layers so they sit above the
    // buildings (addLayer with no beforeId re-inserts them on top).
    lmReaddLabels();
  }
  // (Urban fabric + lm-lowzoom-buildings moved into buildStyle 2026-07-10 —
  // always on for flat maps too, not gated behind the 3D Buildings toggle.)
  function remove3dBuildings() {
    if (map && map.getLayer('building-3d')) map.removeLayer('building-3d');
    if (window.__lmSetSSForBuildings) window.__lmSetSSForBuildings(false);
  }

  // Adds a warm emissive line layer above the road network so streets glow
  // at night. Only active when lightPreset === 'night' (otherwise removed).
  // Mapbox Standard's night preset only lights building windows, not roads.
  // Mapbox Standard renders white arrows along roads (one-way / direction-of-
  // travel). They look out of place on a printed poster. Imported layers are
  // not exposed by getStyle().layers in v3, so we both:
  //   1. try-set known Standard arrow layer ids directly
  //   2. walk the merged style if mapbox-gl exposes it
// Persistent listener that re-hides arrows whenever the style settles —
  // theme switches and lighting changes call setStyle/setConfigProperty which
  // restore the import's default layer visibility. Attach once per map.
  function attachArrowGuard() {
    if (!map || map.__arrowGuard) return;
    map.__arrowGuard = true;
    map.on('idle', function() {
      if (state.show3dLandmarks) hideStandardRoadArrows();
    });
  }

  function hideStandardRoadArrows() {
    if (!map) return;
    // Mapbox v3 silently ignores setLayoutProperty for imported layer ids
    // (and the qualified `<id>\u001fbasemap` form is internal). The reliable
    // path is to mutate the import's layer data in-place and re-apply via
    // setStyle({diff: true}) — confirmed via probe.
    try {
      var style = map.getStyle();
      var imports = style && style.imports;
      if (!imports || !imports.length) return;
      var changed = 0;
      imports.forEach(function(imp) {
        var layers = imp && imp.data && imp.data.layers;
        if (!layers) return;
        layers.forEach(function(l) {
          if (!l || !l.id) return;
          var id = String(l.id).toLowerCase();
          // Also hide Standard's bridge-primary (+case/shadow) and bridge-
          // motorway-trunk/secondary-tertiary equivalents — our clone-bridge-
          // primary replaces them with butt-cap layers that don't show a
          // rounded peek at bridge boundaries.
          var hideBridge = id === 'bridge-primary' || id === 'bridge-primary-case' || id === 'bridge-primary-shadow' ||
                           id === 'bridge-motorway-trunk' || id === 'bridge-motorway-trunk-case' || id === 'bridge-motorway-trunk-shadow' ||
                           id === 'bridge-secondary-tertiary' || id === 'bridge-secondary-tertiary-case' || id === 'bridge-secondary-tertiary-shadow';
          if (id.indexOf('arrow') !== -1 || id.indexOf('oneway') !== -1 || hideBridge) {
            l.layout = l.layout || {};
            if (l.layout.visibility !== 'none') {
              l.layout.visibility = 'none';
              changed++;
            }
          }
        });
      });
      if (changed) map.setStyle(style, { diff: true });
    } catch(e) {}
  }

  function applyNightStreetGlow() {
    if (!map) return;
    // Pre-Mapbox-GL-3.13 this had an `if (isIOSSafari()) return;` bail-out
    // because the extra streets-v8 source + glow line layers tipped iOS
    // Safari over its WebGL VRAM budget and 3D landmarks would silently
    // stop rendering. With the GL JS upgrade + proper slot ordering, iOS
    // now handles the full effect cleanly — verified live on iPhone via
    // the CDP bridge.
    var on = state.show3dLandmarks && state.lightPreset === 'night';
    var ids = ['lm-night-glow-major', 'lm-night-glow-minor'];
    ids.forEach(function(id){ if (map.getLayer(id)) map.removeLayer(id); });
    if (!on) {
      if (map.getSource('lm-night-streets')) {
        try { map.removeSource('lm-night-streets'); } catch(e){}
      }
      return;
    }
    if (!map.getSource('lm-night-streets')) {
      map.addSource('lm-night-streets', {
        type: 'vector',
        url: 'mapbox://mapbox.mapbox-streets-v8'
      });
    }
    // Major arteries: stronger amber glow
    map.addLayer({
      id: 'lm-night-glow-major',
      type: 'line',
      slot: 'middle',
      source: 'lm-night-streets',
      'source-layer': 'road',
      minzoom: 12,
      filter: ['all',
        ['==', ['geometry-type'], 'LineString'],
        ['in', ['get', 'class'],
          ['literal', ['motorway','trunk','primary','secondary','motorway_link','trunk_link','primary_link']]
        ]
      ],
      layout: { 'line-cap': 'round', 'line-join': 'round' },
      paint: {
        'line-color': '#ffb05a',
        'line-emissive-strength': 1.4,
        'line-blur': ['interpolate', ['linear'], ['zoom'], 12, 1.2, 16, 2.4, 19, 4],
        'line-width': ['interpolate', ['linear'], ['zoom'], 12, 0.8, 14, 1.6, 16, 3.2, 18, 6, 20, 10],
        'line-opacity': 0.85
      }
    });
    // Side streets: subtler glow so the whole grid lights up gently
    map.addLayer({
      id: 'lm-night-glow-minor',
      type: 'line',
      slot: 'middle',
      source: 'lm-night-streets',
      'source-layer': 'road',
      minzoom: 14,
      filter: ['all',
        ['==', ['geometry-type'], 'LineString'],
        ['in', ['get', 'class'],
          ['literal', ['tertiary','street','street_limited','tertiary_link','service','residential']]
        ]
      ],
      layout: { 'line-cap': 'round', 'line-join': 'round' },
      paint: {
        'line-color': '#ffc77a',
        'line-emissive-strength': 0.9,
        'line-blur': ['interpolate', ['linear'], ['zoom'], 14, 0.8, 17, 1.6, 19, 2.4],
        'line-width': ['interpolate', ['linear'], ['zoom'], 14, 0.4, 16, 1, 18, 2.4, 20, 4],
        'line-opacity': 0.55
      }
    });
  }
  function _ensureTreePattern() {
    if (!map || map.hasImage('lm-tree-pattern')) return;
    var s = 32;
    var c = document.createElement('canvas');
    c.width = s; c.height = s;
    var ctx = c.getContext('2d');
    // Pseudo-random but deterministic tree positions inside the tile
    var trees = [
      [6, 8, 3.0], [20, 5, 2.6], [14, 14, 3.2],
      [26, 17, 2.4], [4, 22, 2.8], [22, 26, 3.0]
    ];
    trees.forEach(function(t){
      var x = t[0], y = t[1], r = t[2];
      // shadow
      ctx.fillStyle = 'rgba(0,0,0,0.18)';
      ctx.beginPath(); ctx.ellipse(x, y + r*0.85, r*0.9, r*0.28, 0, 0, Math.PI*2); ctx.fill();
      // canopy
      ctx.fillStyle = '#3d6b3a';
      ctx.beginPath(); ctx.arc(x, y, r, 0, Math.PI*2); ctx.fill();
      // highlight
      ctx.fillStyle = 'rgba(255,255,255,0.22)';
      ctx.beginPath(); ctx.arc(x - r*0.35, y - r*0.4, r*0.35, 0, Math.PI*2); ctx.fill();
    });
    try {
      map.addImage('lm-tree-pattern', { width: s, height: s, data: ctx.getImageData(0, 0, s, s).data });
    } catch(e) {}
  }

  function addParkOverlay() {
    if (!map) return;
    var ids = ['lm-park-fill', 'lm-park-trees'];
    ids.forEach(function(id){ if (map.getLayer(id)) map.removeLayer(id); });
    // Pre-Mapbox-GL-3.13 the extra fill + tile pattern pushed iOS Safari
    // over its WebGL VRAM ceiling and landmark meshes silently dropped
    // out. With the GL JS upgrade + proper slot:'bottom' on both layers,
    // iOS Safari now renders the overlay without breaking landmarks.
    if (!state.show3dLandmarks) {
      // keep source around — applyNightStreetGlow may also use it
      return;
    }
    if (!map.getSource('lm-night-streets')) {
      map.addSource('lm-night-streets', {
        type: 'vector',
        url: 'mapbox://mapbox.mapbox-streets-v8'
      });
    }
    var night = state.lightPreset === 'night';
    var fillColor = night ? '#1f3a23' : '#4a7a44';
    var fillOpacity = night ? 0.55 : 0.45;
    map.addLayer({
      id: 'lm-park-fill',
      type: 'fill',
      slot: 'bottom',
      source: 'lm-night-streets',
      'source-layer': 'landuse',
      filter: ['in', ['get', 'class'], ['literal', ['park','cemetery','wood','grass','pitch','garden','playground']]],
      paint: {
        'fill-color': fillColor,
        'fill-opacity': ['interpolate', ['linear'], ['zoom'], 10, fillOpacity * 0.5, 14, fillOpacity, 18, fillOpacity * 0.85],
        'fill-emissive-strength': night ? 0.15 : 0.0
      }
    });
    _ensureTreePattern();
    map.addLayer({
      id: 'lm-park-trees',
      type: 'fill',
      slot: 'bottom',
      source: 'lm-night-streets',
      'source-layer': 'landuse',
      minzoom: 15,
      filter: ['in', ['get', 'class'], ['literal', ['park','wood','garden','cemetery']]],
      paint: {
        'fill-pattern': 'lm-tree-pattern',
        'fill-opacity': ['interpolate', ['linear'], ['zoom'], 15, 0, 16, 0.7, 19, 0.95]
      }
    });
  }

  function refresh3dBuildings() {
    if (!state.show3dBuildings) return;
    remove3dBuildings();
    add3dBuildings();
  }

  // ── 3D Terrain ──
  // Shaded-relief constants for the 3D Terrain toggle.
  //
  // These used to be iOS-only. setTerrain() silently no-ops on iOS Safari
  // (see add3dTerrain below), so the hillshade had to carry the entire effect
  // there and was boosted accordingly, while desktop — and, critically, the
  // CF cart thumbnail and the print renderer, which are both "not iOS" — ran a
  // much weaker 0.55/0.70/0.55/0.18. A map designed on an iPhone therefore
  // printed with roughly half the relief the customer approved.
  //
  // Unified upward 2026-08-14: the boosted values are the standard on every
  // device and in every output. At pitch 0 (how nearly every terrain map is
  // composed) the real terrain mesh contributes almost nothing anyway — the
  // hillshade IS the effect, so it should be identical everywhere.
  //
  // MIRRORED in renderer/scripts/render_locationmap.py (TERRAIN_HILLSHADE) and
  // consumed by app/_internal/loc-preview-host/route.ts via the window export
  // below. Change all three together.
  var TERRAIN_HILLSHADE = { shadow: 0.85, highlight: 0.30, accent: 0.70, exaggeration: 1.0 };
  // How far the LOD-delta compensation may push the shading, and over how many
  // zoom levels it ramps to full strength. Mirrors _TERRAIN_LOD_SPAN /
  // _TERRAIN_LOD_GAIN in render_locationmap.py.
  var TERRAIN_LOD_SPAN = 3.0;
  var TERRAIN_LOD_GAIN = { shadow: 0.10, highlight: 0.05, accent: 0.10 };

  function mixHex(a, b, k) {
    var pa = [parseInt(a.slice(1,3),16), parseInt(a.slice(3,5),16), parseInt(a.slice(5,7),16)];
    var pb = [parseInt(b.slice(1,3),16), parseInt(b.slice(3,5),16), parseInt(b.slice(5,7),16)];
    return '#' + pa.map(function(c,i){ return Math.round(c+(pb[i]-c)*k).toString(16).padStart(2,'0'); }).join('');
  }

  // Apply the shaded-relief paint, optionally compensated for a camera sitting
  // `lodDelta` zoom levels deeper than the one the customer composed at.
  //
  // Mapbox derives hillshade slope from the elevation delta between ADJACENT
  // DEM pixels, so zooming in shrinks that delta and washes the relief out.
  // hillshade-exaggeration can't buy it back (the style spec clamps it to 0..1
  // and TERRAIN_HILLSHADE is already at the 1.0 ceiling), so the compensation
  // runs through the shadow/accent colours instead.
  //
  // The live customiser calls this with 0 — its camera IS the reference. The
  // headless cart-thumbnail host (loc-preview-host/route.ts) reframes deeper
  // and passes its own delta. render_locationmap.py does the same for print.
  function applyTerrainHillshade(lodDelta) {
    if (!map) return;
    var k = Math.max(0, Math.min(1, (lodDelta || 0) / TERRAIN_LOD_SPAN));
    var t = lmThemeData();
    try {
      map.setLayoutProperty('hillshade', 'visibility', 'visible');
      map.setPaintProperty('hillshade', 'hillshade-shadow-color',
        mixHex(t.bg, '#000000', TERRAIN_HILLSHADE.shadow + TERRAIN_LOD_GAIN.shadow * k));
      map.setPaintProperty('hillshade', 'hillshade-highlight-color',
        mixHex(t.bg, '#ffffff', TERRAIN_HILLSHADE.highlight + TERRAIN_LOD_GAIN.highlight * k));
      map.setPaintProperty('hillshade', 'hillshade-accent-color',
        mixHex(t.bg, '#000000', TERRAIN_HILLSHADE.accent + TERRAIN_LOD_GAIN.accent * k));
      map.setPaintProperty('hillshade', 'hillshade-exaggeration', TERRAIN_HILLSHADE.exaggeration);
      map.setPaintProperty('hillshade', 'hillshade-illumination-direction', 315);
    } catch(e) {}
  }
  try { window.__lmApplyTerrainHillshade = applyTerrainHillshade; } catch (e) {}

  function add3dTerrain() {
    if (!map) return;
    // setTerrain() is broken on iOS Safari Mapbox GL JS 3.13 — it parses
    // into style.terrain but never materialises the runtime terrain
    // object (verified live: getTerrain()/queryTerrainElevation() both
    // return null even after setStyle({terrain: ...})). Standard skips
    // the call there and falls back to the boosted hillshade below,
    // which iOS DOES render correctly and gives the same "shaded relief"
    // dimensional feel customers expect from a 3D Terrain toggle.
    var iOS = isIOSSafari();
    if (!iOS) {
      // Re-use the existing 'terrain' source from buildStyle rather than
      // adding a duplicate `terrain-3d` source pointing at the same DEM.
      // The shared `terrain` source is already loaded by hillshade.
      var sourceId = map.getSource('terrain') ? 'terrain' : 'terrain-3d';
      if (sourceId === 'terrain-3d' && !map.getSource('terrain-3d')) {
        map.addSource('terrain-3d', { type: 'raster-dem', url: 'mapbox://mapbox.mapbox-terrain-dem-v1', tileSize: 512, maxzoom: 14 });
      }
      map.setTerrain({ source: sourceId, exaggeration: 1.8 });
    }
    // Boosted hillshade: deeper shadows + warmer highlights. On iOS this
    // is the entire effect; on desktop/Android it sits on top of the
    // real terrain extrusion for a richer "shaded relief" look.
    // The live customiser is the reference camera, so no LOD compensation.
    applyTerrainHillshade(0);
  }
  function remove3dTerrain() {
    if (!map) return;
    map.setTerrain(null);
    // Restore hillshade defaults derived from theme
    var t = lmThemeData();
    function mix(a, b, k) {
      var pa = [parseInt(a.slice(1,3),16), parseInt(a.slice(3,5),16), parseInt(a.slice(5,7),16)];
      var pb = [parseInt(b.slice(1,3),16), parseInt(b.slice(3,5),16), parseInt(b.slice(5,7),16)];
      return '#' + pa.map(function(c,i){ return Math.round(c+(pb[i]-c)*k).toString(16).padStart(2,'0'); }).join('');
    }
    try {
      map.setPaintProperty('hillshade', 'hillshade-shadow-color', mix(t.bg, '#000000', 0.3));
      map.setPaintProperty('hillshade', 'hillshade-highlight-color', mix(t.bg, '#ffffff', 0.15));
      map.setPaintProperty('hillshade', 'hillshade-accent-color', mix(t.bg, '#000000', 0.2));
      map.setPaintProperty('hillshade', 'hillshade-exaggeration', 0.35);
      // Re-hide hillshade on mobile when terrain is off (matches initial mobile default).
      if (window.matchMedia && window.matchMedia('(max-width: 768px)').matches) {
        map.setLayoutProperty('hillshade', 'visibility', 'none');
      }
    } catch(e) {}
  }

  // ── Accordions (collapsible sections) ──
  function bindAccordions() {
    document.querySelectorAll('.lm-accordion-head').forEach(function(head) {
      head.addEventListener('click', function() {
        var expanded = head.getAttribute('aria-expanded') === 'true';
        head.setAttribute('aria-expanded', expanded ? 'false' : 'true');
        var bodyId = head.getAttribute('aria-controls');
        var body = bodyId && document.getElementById(bodyId);
        if (body) body.hidden = expanded;
      });
    });
  }

  // ── Double-click / double-tap a slider to zero it ──────────────────────
  //
  // Rotate and tilt are corrections: a customer nudges them, decides the
  // straight-on version was better, and wants back to 0 without dragging for
  // the exact centre. The Reset button already does both at once; this is the
  // per-slider version, on the control itself.
  //
  // Zoom is deliberately NOT wired up. It has no neutral value (its range is
  // 8-18) and the default 11 is a wide regional view, so snapping to it on a
  // stray double-click would throw away the composition with no undo.

  // 350ms and 30px are inline rather than named constants because the test
  // harness slices this function out between the markers and evaluates it on
  // its own — anything it referenced from the enclosing scope would be
  // undefined there. See tests/harness/extract.mjs.
  /* @lm-test-export:start isDoubleTap */
  function isDoubleTap(prev, next) {
    if (!prev) return false;
    if (next.t - prev.t > 350) return false;
    return Math.abs(next.x - prev.x) <= 30 && Math.abs(next.y - prev.y) <= 30;
  }
  /* @lm-test-export:end isDoubleTap */

  function bindZeroOnDoubleTap(el, apply) {
    if (!el) return;

    el.addEventListener('dblclick', function (e) {
      if (el.disabled) return;      // map is locked
      e.preventDefault();
      apply();
    });

    // Touch has no dblclick. Track the last tap ourselves, and clear it after
    // firing so a third tap cannot chain into a second reset.
    var lastTap = null;
    el.addEventListener('touchend', function (e) {
      if (el.disabled) return;
      var touch = (e.changedTouches && e.changedTouches[0]) || null;
      if (!touch) return;
      var now = { t: e.timeStamp, x: touch.clientX, y: touch.clientY };
      if (isDoubleTap(lastTap, now)) {
        // Also stops Safari treating it as double-tap-to-zoom on the page.
        e.preventDefault();
        lastTap = null;
        apply();
        return;
      }
      lastTap = now;
    }, { passive: false });
  }

  // ── Slider polish (zoom, rotate, tilt, handwriting) ──
  // Fill, grab, rubber-band ends, and gliding back with the map: shared with
  // the other customisers (skylit-motion.js). Without it they stay plain.
  function enhanceSlider(input, opts) {
    if (window.SkylitSlider) window.SkylitSlider.enhance(input, opts);
  }
  function glideSlider(input, label, to, ms) {
    if (!input) return;
    if (window.SkylitSlider) { window.SkylitSlider.glide(input, label, to, ms); return; }
    input.value = to;
    if (label) label.textContent = Math.round(to) + '°';
  }

  // ── Rotate & Tilt Sliders ──
  function bindRotatePitch() {
    var rot = document.getElementById('lm-rotate');
    var pit = document.getElementById('lm-pitch');
    var rotVal = document.getElementById('lm-rotate-val');
    var pitVal = document.getElementById('lm-pitch-val');
    var reset = document.getElementById('lm-rotate-pitch-reset');
    if (rot) rot.addEventListener('input', function() {
      var v = parseFloat(rot.value); state.bearing = v;
      if (rotVal) rotVal.textContent = Math.round(v) + '°';
      map.setBearing(v);
    });
    if (pit) pit.addEventListener('input', function() {
      var v = parseFloat(pit.value); state.pitch = v;
      if (pitVal) pitVal.textContent = Math.round(v) + '°';
      map.setPitch(v);
    });
    if (reset) reset.addEventListener('click', function() {
      state.bearing = 0; state.pitch = 0;
      glideSlider(rot, rotVal, 0, 400);
      glideSlider(pit, pitVal, 0, 400);
      map.easeTo({ bearing: 0, pitch: 0, duration: 400 });
    });

    // Same easeTo duration as the Reset button, so zeroing one slider and
    // zeroing both look like the same gesture rather than two features.
    bindZeroOnDoubleTap(rot, function () {
      state.bearing = 0;
      glideSlider(rot, rotVal, 0, 400);
      map.easeTo({ bearing: 0, duration: 400 });
    });
    bindZeroOnDoubleTap(pit, function () {
      state.pitch = 0;
      glideSlider(pit, pitVal, 0, 400);
      map.easeTo({ pitch: 0, duration: 400 });
    });
  }

  // ── Mobile tilt buttons (next to rotate/zoom in toolbar) ─────────────
  // Adjust standard pitch by ±10° per tap, clamped 0–85.
  // Button → pitch direction was inverted in user testing — tilt-up
  // chevron reads as "lower the tilt" (more top-down) for the kind of
  // map customer we have, so we go: up=-pitch, down=+pitch.
  function bindMobileTilt() {
    var up = document.getElementById('lm-mobile-tilt-up');
    var down = document.getElementById('lm-mobile-tilt-down');
    var TILT_STEP = 10;
    function adjust(delta) {
      if (!map) return;
      var newPitch = Math.max(0, Math.min(85, map.getPitch() + delta));
      state.pitch = newPitch;
      var pit = document.getElementById('lm-pitch');
      var pitVal = document.getElementById('lm-pitch-val');
      glideSlider(pit, pitVal, newPitch, 300);
      map.easeTo({ pitch: newPitch, duration: 300 });
    }
    if (up) up.addEventListener('click', function() { adjust(-TILT_STEP); });
    if (down) down.addEventListener('click', function() { adjust(+TILT_STEP); });
  }

  // ── Zoom Slider ──
  function bindZoom() {
    var slider = document.getElementById('lm-zoom');
    if (!slider) return;
    slider.addEventListener('input', function() { map.setZoom(applyZoom(parseFloat(slider.value))); });

    // Mobile zoom + rotation buttons. Rotation step is 15° per click
    // — fine enough to nudge a landmark off-axis, coarse enough that
    // four taps gets you a quarter-turn. easeTo animates the change so
    // it feels continuous rather than snappy.
    var ROT_STEP = 15;
    var zoomIn = document.getElementById('lm-mobile-zoom-in');
    var zoomOut = document.getElementById('lm-mobile-zoom-out');
    var rotCcw = document.getElementById('lm-mobile-rotate-ccw');
    var rotCw  = document.getElementById('lm-mobile-rotate-cw');
    if (zoomIn) zoomIn.addEventListener('click', function() {
      map.easeTo({ zoom: map.getZoom() + 0.5, duration: 300 });
    });
    if (zoomOut) zoomOut.addEventListener('click', function() {
      map.easeTo({ zoom: map.getZoom() - 0.5, duration: 300 });
    });
    // Mapbox semantics: increasing `bearing` rotates the camera clockwise,
    // which makes the MAP appear to spin counter-clockwise. So to make the
    // "rotate clockwise / right" button visually rotate the map right, we
    // DECREASE bearing on the CW button and INCREASE it on the CCW button.
    if (rotCcw) rotCcw.addEventListener('click', function() {
      map.easeTo({ bearing: map.getBearing() + ROT_STEP, duration: 300 });
    });
    if (rotCw) rotCw.addEventListener('click', function() {
      map.easeTo({ bearing: map.getBearing() - ROT_STEP, duration: 300 });
    });
  }

  // ── Text Inputs ──
  function bindTextInputs() {
    var title = document.getElementById('lm-title');
    var subtitle = document.getElementById('lm-subtitle');
    if (title) title.addEventListener('input', function() { state.title = title.value; updateTextOverlay(); });
    if (subtitle) subtitle.addEventListener('input', function() { state.subtitle = subtitle.value; updateTextOverlay(); });
    var dateLineInput = document.getElementById('lm-date');
    if (dateLineInput) dateLineInput.addEventListener('input', function() { lmStoreSet('dateLine', dateLineInput.value); updateTextOverlay(); });

    // Coordinates line: typing the exact live coordinates back puts it on
    // automatic again, anything else is the customer's own line.
    var coordsTextInput = document.getElementById('lm-coords-text');
    if (coordsTextInput) {
      coordsTextInput.value = lmCoordsLine();
      coordsTextInput.addEventListener('input', function() {
        var v = coordsTextInput.value;
        lmSetCoordsText(v.trim() === fmtCoords() ? null : v);
        updateTextOverlay();
      });
      coordsTextInput.addEventListener('blur', lmSyncCoordsInput);
    }

    // Coordinates toggle switch (removed from the markup; kept for old caches)
    var coordsRow = document.getElementById('lm-coords-row');
    var coordsSwitch = document.getElementById('lm-coords-switch');
    if (coordsRow && coordsSwitch) {
      coordsRow.addEventListener('click', function() {
        lmSetCoordsText(state.showCoordinates ? '' : null);
        coordsSwitch.classList.toggle('active', state.showCoordinates);
        updateTextOverlay();
      });
    }

    bindHandwriting();
  }

  // ── Handwriting capture ──
  function bindHandwriting() {
    var row = document.getElementById('lm-hw-row');
    // Typed/Handwritten segmented pill (ported from star map). Replaces the
    // old single toggle switch (#lm-hw-switch) — state still flows through
    // state.handwritingEnabled + syncToggleUI().
    var modeBtns = row ? row.querySelectorAll('[data-hw-mode]') : [];
    var panel = document.getElementById('lm-hw-panel');
    var canvas = document.getElementById('lm-hw-canvas');
    var widthInput = document.getElementById('lm-hw-width');
    var widthInputModal = document.getElementById('lm-hw-modal-width');
    var sizeInput = document.getElementById('lm-hw-size');
    var sizeVal = document.getElementById('lm-hw-size-val');
    var heightInput = document.getElementById('lm-hw-height');
    var heightVal = document.getElementById('lm-hw-height-val');
    var undoBtn = document.getElementById('lm-hw-undo');
    var clearBtn = document.getElementById('lm-hw-clear');
    var titleInput = document.getElementById('lm-title');
    if (!row || !panel || !canvas) return;

    var ctx = canvas.getContext('2d');
    var current = null;
    var eraserOn = false;
    // Move mode -- drag the whole message around inside the pad. moveBase is
    // an immutable clone taken at drag start; each move applies the total
    // drag delta to that base (never cumulatively to live state), so pointer
    // jitter cannot drift the artwork. Ported from moon-handwriting.js.
    var moveOn = false;
    var moveBase = null;
    var moveStartVB = null;
    var hwHistory = [];
    function snapshotHwHistory() {
      var snap = state.handwritingStrokes.map(function(s) { return { w: s.w, pts: s.pts.slice() }; });
      hwHistory.push(snap);
      if (hwHistory.length > 50) hwHistory.shift();
    }
    function undoHw() {
      if (!hwHistory.length) {
        state.handwritingStrokes = [];
      } else {
        state.handwritingStrokes = hwHistory.pop();
      }
      redraw(); updateTextOverlay();
      scheduleLmAutosave();
    }
    function brushSliderVal() {
      var v = parseFloat((widthInput && widthInput.value) || (widthInputModal && widthInputModal.value));
      return isFinite(v) && v > 0 ? v : 6;
    }
    function eraseRadiusVB() { return brushSliderVal() * 4; }
    var ERASE_RADIUS_VB = 24;
    function syncBrushInputs(src) {
      var v = src.value;
      if (widthInput && widthInput !== src) widthInput.value = v;
      if (widthInputModal && widthInputModal !== src) widthInputModal.value = v;
    }
    if (widthInput) widthInput.addEventListener('input', function() { syncBrushInputs(widthInput); scheduleRedraw(); });
    if (widthInputModal) widthInputModal.addEventListener('input', function() { syncBrushInputs(widthInputModal); scheduleRedraw(); });
    function eraseAtVB(vbx, vby) {
      var r = eraseRadiusVB();
      var r2 = r * r;
      var changed = false;
      var out = [];
      for (var i = 0; i < state.handwritingStrokes.length; i++) {
        var s = state.handwritingStrokes[i];
        if (!s.pts || !s.pts.length) continue;
        var run = [];
        var sawErase = false;
        for (var j = 0; j < s.pts.length; j++) {
          var dx = s.pts[j][0] - vbx, dy = s.pts[j][1] - vby;
          if (dx * dx + dy * dy <= r2) {
            sawErase = true;
            if (run.length >= 2) out.push({ w: s.w, pts: run });
            run = [];
          } else {
            run.push(s.pts[j]);
          }
        }
        if (!sawErase) {
          out.push(s);
        } else {
          changed = true;
          if (run.length >= 2) out.push({ w: s.w, pts: run });
        }
      }
      if (changed) {
        state.handwritingStrokes = out;
        redraw(); updateTextOverlay();
        scheduleLmAutosave();
      }
    }
    var activePointerId = null;
    var redrawScheduled = false;
    var dpr = Math.max(1, window.devicePixelRatio || 1);
    var defaultWidth = 4;
    // Rotate-90 writing mode (modal only): CSS-rotates the canvas so the long
    // 1000-unit axis runs down the portrait screen, giving far more writing
    // length when the phone's orientation lock keeps it in portrait. The
    // canvas layout box is rotation-invariant, so bitmap + drawing coords use
    // canvasBox() in this mode while pointer coords are un-rotated in rectXYToVB.
    var rotated = false;
    function canvasBox() {
      var w = canvas.offsetWidth, h = canvas.offsetHeight;
      if (!w || !h) { var r = canvas.getBoundingClientRect(); w = w || r.width; h = h || r.height; }
      return { width: w, height: h };
    }
    // Palm rejection (modal only): user-controlled toggle. When on, only
    // pointerType==='pen' is accepted; touch (finger/palm) is ignored.
    var modalOpen = false;
    var palmRejectOn = false;
    function scheduleRedraw() {
      if (redrawScheduled) return;
      redrawScheduled = true;
      requestAnimationFrame(function() { redrawScheduled = false; redraw(); });
    }

    function sizeCanvas() {
      // Under rotation the bounding rect is the rotated (swapped) footprint, so
      // use the rotation-invariant layout box for the bitmap; identical to the
      // bounding rect when not rotated.
      var rect = rotated ? canvasBox() : canvas.getBoundingClientRect();
      // Keep visual pixel ratio matched to DPR for crisp lines, but draw in
      // CSS-pixel coords for simplicity.
      canvas.width = Math.round(rect.width * dpr);
      canvas.height = Math.round(rect.height * dpr);
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      redraw();
    }
    function rectXYToVB(rect, clientX, clientY) {
      if (rotated) {
        // Canvas is rotate(90deg) clockwise about its centre. Map the screen
        // point into the canvas's local (pre-rotation) box, then to viewBox.
        var box = canvasBox();
        var cx = rect.left + rect.width / 2, cy = rect.top + rect.height / 2;
        var dx = clientX - cx, dy = clientY - cy;
        // inverse of a 90° clockwise rotation (screen y-down): localX = dy, localY = -dx
        var lx = dy + box.width / 2;
        var ly = -dx + box.height / 2;
        return [Math.max(0, Math.min(HW_VBW, lx / box.width * HW_VBW)),
                Math.max(0, Math.min(HW_VBH, ly / box.height * HW_VBH))];
      }
      var x = (clientX - rect.left) / rect.width * HW_VBW;
      var y = (clientY - rect.top) / rect.height * HW_VBH;
      return [Math.max(0, Math.min(HW_VBW, x)), Math.max(0, Math.min(HW_VBH, y))];
    }
    function vbToCss(rect, p) {
      return [p[0] / HW_VBW * rect.width, p[1] / HW_VBH * rect.height];
    }
    function redraw() {
      var rect = rotated ? canvasBox() : canvas.getBoundingClientRect();
      ctx.clearRect(0, 0, rect.width, rect.height);
      var wrap = canvas.parentElement;
      if (wrap) wrap.classList.toggle('has-strokes', state.handwritingStrokes.length > 0 || !!current);
      var t = lmThemeData();
      ctx.strokeStyle = t.text || '#222';
      ctx.lineCap = 'round'; ctx.lineJoin = 'round';
      var allStrokes = state.handwritingStrokes.concat(current ? [current] : []);
      for (var i = 0; i < allStrokes.length; i++) {
        var s = allStrokes[i];
        if (!s.pts || !s.pts.length) continue;
        // Draw the smoothed samples so the pad shows what actually ships.
        s = { pts: hwSmoothPts(s.pts), w: s.w };
        // Stroke width is given in viewBox units; convert to CSS px using
        // the smaller axis ratio so width feels consistent regardless of
        // canvas aspect.
        var widthPx = s.w * Math.min(rect.width / HW_VBW, rect.height / HW_VBH);
        ctx.lineWidth = Math.max(1, widthPx);
        ctx.beginPath();
        var p0 = vbToCss(rect, s.pts[0]);
        ctx.moveTo(p0[0], p0[1]);
        if (s.pts.length === 1) {
          // Single tap — render a dot.
          ctx.lineTo(p0[0] + 0.01, p0[1] + 0.01);
        } else if (s.pts.length === 2) {
          var pl = vbToCss(rect, s.pts[1]);
          ctx.lineTo(pl[0], pl[1]);
        } else {
          // Midpoint quadratic smoothing — same curve construction as the
          // preview SVG (hwStrokesToPathD), the cart thumbnail and the
          // print renderer, so the pad shows exactly what ships.
          for (var j = 1; j < s.pts.length - 1; j++) {
            var c = vbToCss(rect, s.pts[j]);
            var nx = vbToCss(rect, s.pts[j + 1]);
            ctx.quadraticCurveTo(c[0], c[1], (c[0] + nx[0]) / 2, (c[1] + nx[1]) / 2);
          }
          var pe = vbToCss(rect, s.pts[s.pts.length - 1]);
          ctx.lineTo(pe[0], pe[1]);
        }
        ctx.stroke();
      }
      if (eraserOn && lastEraserVB) {
        var c = vbToCss(rect, lastEraserVB);
        var rPx = eraseRadiusVB() * (rect.width / HW_VBW);
        ctx.save();
        ctx.beginPath();
        ctx.arc(c[0], c[1], rPx, 0, Math.PI * 2);
        ctx.fillStyle = 'rgba(180,60,60,0.18)';
        ctx.fill();
        ctx.lineWidth = 2;
        ctx.strokeStyle = 'rgba(180,60,60,0.95)';
        ctx.stroke();
        ctx.restore();
      }
    }
    var lastEraserVB = null;
    function commitCurrent() {
      if (current && current.pts.length) {
        snapshotHwHistory();
        state.handwritingStrokes.push(current);
      }
      current = null;
      updateTextOverlay();
      scheduleLmAutosave();
    }

    // -- Center / Move (ported from moon-handwriting.js) ------------------
    function strokesBBox(strokes) {
      var minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
      for (var i = 0; i < strokes.length; i++) {
        var pts = strokes[i].pts || [];
        for (var j = 0; j < pts.length; j++) {
          if (pts[j][0] < minX) minX = pts[j][0];
          if (pts[j][0] > maxX) maxX = pts[j][0];
          if (pts[j][1] < minY) minY = pts[j][1];
          if (pts[j][1] > maxY) maxY = pts[j][1];
        }
      }
      return minX === Infinity ? null : { minX: minX, minY: minY, maxX: maxX, maxY: maxY };
    }
    function translateStrokes(strokes, dx, dy) {
      return strokes.map(function(s) {
        return { w: s.w, pts: s.pts.map(function(p) { return [p[0] + dx, p[1] + dy]; }) };
      });
    }
    // Horizontal re-center: the vertical position (baseline relationship)
    // is the customer's own; only the left/right balance is corrected.
    function centerStrokes() {
      var box = strokesBBox(state.handwritingStrokes);
      if (!box) return;
      var dx = (HW_VBW - (box.maxX - box.minX)) / 2 - box.minX;
      if (Math.abs(dx) < 0.5) return;
      snapshotHwHistory();
      state.handwritingStrokes = translateStrokes(state.handwritingStrokes, dx, 0);
      redraw();
      updateTextOverlay();
      scheduleLmAutosave();
    }

    function onPointerDown(e) {
      if (!state.handwritingEnabled) return;
      // Only react to primary contact — secondary touches (e.g. accidental
      // palm rest while writing with Pencil) shouldn't start a new stroke.
      if (e.isPrimary === false) return;
      // Palm rejection: when toggled on in modal, ignore non-pen input.
      if (modalOpen && palmRejectOn && e.pointerType !== 'pen') return;
      e.preventDefault();
      var rectE = canvas.getBoundingClientRect();
      if (moveOn) {
        if (!state.handwritingStrokes.length) return;
        activePointerId = e.pointerId;
        try { canvas.setPointerCapture(e.pointerId); } catch (_) {}
        snapshotHwHistory();
        moveBase = state.handwritingStrokes.map(function(s) { return { w: s.w, pts: s.pts.slice() }; });
        moveStartVB = rectXYToVB(rectE, e.clientX, e.clientY);
        if (canvas.parentElement) canvas.parentElement.classList.add('move-dragging');
        return;
      }
      if (eraserOn) {
        activePointerId = e.pointerId;
        snapshotHwHistory();
        var pE = rectXYToVB(rectE, e.clientX, e.clientY);
        lastEraserVB = pE;
        eraseAtVB(pE[0], pE[1]);
        scheduleRedraw();
        return;
      }
      // If a previous stroke is still open (fast tap-tap, or a missed
      // pointerup that iPadOS Safari occasionally swallows), commit it
      // first so this new pointerdown starts a fresh stroke.
      if (current) commitCurrent();
      activePointerId = e.pointerId;
      // NOTE: deliberately NOT calling canvas.setPointerCapture — on iPadOS
      // Safari, capture from the previous gesture can still be active when
      // the user lifts and re-touches quickly, which suppresses the next
      // pointerdown. Listening on `window` instead reliably catches every
      // move/up regardless of where the gesture ends.
      var w = parseFloat(widthInput && widthInput.value) || defaultWidth;
      // Pencil pressure modulates width slightly so it feels natural.
      if (e.pointerType === 'pen' && e.pressure > 0) w = w * (0.6 + 0.8 * e.pressure);
      current = { w: w, pts: [rectXYToVB(rectE, e.clientX, e.clientY)] };
      scheduleRedraw();
    }
    function onPointerMove(e) {
      if (e.pointerId !== activePointerId) return;
      if (modalOpen && palmRejectOn && e.pointerType !== 'pen') return;
      if (moveOn) {
        if (!moveBase || !moveStartVB) return;
        if (e.cancelable) e.preventDefault();
        var rectMv = canvas.getBoundingClientRect();
        var curVB = rectXYToVB(rectMv, e.clientX, e.clientY);
        var dxm = curVB[0] - moveStartVB[0];
        var dym = curVB[1] - moveStartVB[1];
        // Clamp so the message's bounding box never leaves the pad.
        var boxM = strokesBBox(moveBase);
        if (boxM) {
          dxm = Math.max(-boxM.minX, Math.min(HW_VBW - boxM.maxX, dxm));
          dym = Math.max(-boxM.minY, Math.min(HW_VBH - boxM.maxY, dym));
        }
        state.handwritingStrokes = translateStrokes(moveBase, dxm, dym);
        scheduleRedraw();
        return;
      }
      if (eraserOn) {
        if (e.cancelable) e.preventDefault();
        var rectM = canvas.getBoundingClientRect();
        var evs = (typeof e.getCoalescedEvents === 'function') ? e.getCoalescedEvents() : [e];
        for (var k = 0; k < evs.length; k++) {
          var pM = rectXYToVB(rectM, evs[k].clientX, evs[k].clientY);
          lastEraserVB = pM;
          eraseAtVB(pM[0], pM[1]);
        }
        scheduleRedraw();
        return;
      }
      if (!current) return;
      // Suppress native text-selection drag during fast Pencil swipes —
      // safe because we only ever fire when an active stroke is in flight.
      if (e.cancelable) e.preventDefault();
      var rect = canvas.getBoundingClientRect();
      // Coalesced events smooth out pen + finger jitter on supporting browsers.
      var events = (typeof e.getCoalescedEvents === 'function') ? e.getCoalescedEvents() : [e];
      for (var i = 0; i < events.length; i++) {
        var np = rectXYToVB(rect, events[i].clientX, events[i].clientY);
        // Decimate: skip samples closer than ~1.5 viewBox units to the last
        // point. Coalesced pointer streams carry sensor jitter at sub-unit
        // spacing which reads as jagged ink; the midpoint-quad smoothing in
        // the renderers works best on sparser, cleaner points.
        var lp = current.pts[current.pts.length - 1];
        if (lp) {
          var ddx = np[0] - lp[0], ddy = np[1] - lp[1];
          if (ddx * ddx + ddy * ddy < 2.25) continue;
        }
        current.pts.push(np);
      }
      scheduleRedraw();
    }
    function onPointerUp(e) {
      if (moveOn) {
        if (moveBase) {
          moveBase = null; moveStartVB = null;
          activePointerId = null;
          document.querySelectorAll('.lm-hw-canvas-wrap').forEach(function(w) {
            w.classList.remove('move-dragging');
          });
          updateTextOverlay();
          scheduleLmAutosave();
        }
        return;
      }
      if (eraserOn) { activePointerId = null; lastEraserVB = null; scheduleRedraw(); return; }
      if (!current) return;
      // Accept any pointerup that arrives — on iPadOS the up event can come
      // through with a slightly different pointerId after Safari re-routes
      // the gesture, so being permissive here prevents stuck strokes.
      activePointerId = null;
      commitCurrent();
    }

    canvas.addEventListener('pointerdown', onPointerDown);
    // Non-passive so onPointerMove can preventDefault during a stroke —
    // otherwise fast Pencil drags initiate native text selection on the
    // surrounding modal. Move/up still on window to catch off-canvas ends.
    window.addEventListener('pointermove', onPointerMove, { passive: false });
    window.addEventListener('pointerup', onPointerUp);
    window.addEventListener('pointercancel', onPointerUp);
    // Stop the page from scrolling while drawing on touch devices.
    canvas.style.touchAction = 'none';
    // iOS native gesture blockers — only kick in mid-stroke so they don't
    // interfere with normal page interaction.
    function blockIfDrawing(e) { if ((current || moveBase || (eraserOn && activePointerId !== null)) && e.cancelable) e.preventDefault(); }
    document.addEventListener('selectstart', blockIfDrawing);
    document.addEventListener('gesturestart', blockIfDrawing);
    document.addEventListener('touchmove', blockIfDrawing, { passive: false });

    if (undoBtn) undoBtn.addEventListener('click', undoHw);
    if (clearBtn) clearBtn.addEventListener('click', function() {
      if (state.handwritingStrokes.length) snapshotHwHistory();
      state.handwritingStrokes = [];
      redraw(); updateTextOverlay();
      scheduleLmAutosave();
    });

    var eraserBtn = document.getElementById('lm-hw-eraser');
    var eraserBtnModal = document.getElementById('lm-hw-modal-eraser');
    function syncEraserUI() {
      var pressed = eraserOn ? 'true' : 'false';
      if (eraserBtn) eraserBtn.setAttribute('aria-pressed', pressed);
      if (eraserBtnModal) eraserBtnModal.setAttribute('aria-pressed', pressed);
      if (canvas.parentElement) canvas.parentElement.classList.toggle('eraser-on', eraserOn);
    }
    function toggleEraser() { eraserOn = !eraserOn; if (!eraserOn) lastEraserVB = null; if (eraserOn && moveOn) setMove(false); syncEraserUI(); updateEraserCursorVisibility(); scheduleRedraw(); }
    if (eraserBtn) eraserBtn.addEventListener('click', toggleEraser);
    if (eraserBtnModal) eraserBtnModal.addEventListener('click', toggleEraser);

    // Center + Move controls -- inline panel and expand modal share state,
    // mirroring the eraser pattern. Move and Eraser are mutually exclusive.
    var centerBtn = document.getElementById('lm-hw-center');
    var centerBtnModal = document.getElementById('lm-hw-modal-center');
    if (centerBtn) centerBtn.addEventListener('click', centerStrokes);
    if (centerBtnModal) centerBtnModal.addEventListener('click', centerStrokes);

    var moveBtn = document.getElementById('lm-hw-move');
    var moveBtnModal = document.getElementById('lm-hw-modal-move');
    function setMove(on) {
      moveOn = !!on;
      if (!moveOn) { moveBase = null; moveStartVB = null; }
      var pressedM = moveOn ? 'true' : 'false';
      if (moveBtn) moveBtn.setAttribute('aria-pressed', pressedM);
      if (moveBtnModal) moveBtnModal.setAttribute('aria-pressed', pressedM);
      document.querySelectorAll('.lm-hw-canvas-wrap').forEach(function(w) {
        w.classList.toggle('move-on', moveOn);
      });
    }
    function toggleMove() {
      if (!moveOn && eraserOn) toggleEraser();
      setMove(!moveOn);
    }
    if (moveBtn) moveBtn.addEventListener('click', toggleMove);
    if (moveBtnModal) moveBtnModal.addEventListener('click', toggleMove);


    var eraserCursorEl = document.createElement('div');
    eraserCursorEl.className = 'lm-hw-eraser-cursor';
    var pointerInside = false;
    function attachEraserCursor() {
      if (eraserCursorEl.parentElement !== document.body) document.body.appendChild(eraserCursorEl);
    }
    attachEraserCursor();
    function updateEraserCursorVisibility() {
      // Only show the radius ring while the pointer is actually over the
      // pad — otherwise toggling Eraser flashes it at a stale position.
      eraserCursorEl.classList.toggle('visible', !!eraserOn && pointerInside);
    }
    function moveEraserCursor(clientX, clientY) {
      var r = canvas.getBoundingClientRect();
      var diameter = Math.max(16, 2 * ERASE_RADIUS_VB * (r.width / HW_VBW));
      eraserCursorEl.style.width = diameter + 'px';
      eraserCursorEl.style.height = diameter + 'px';
      eraserCursorEl.style.left = clientX + 'px';
      eraserCursorEl.style.top = clientY + 'px';
    }
    function showEraserCursorAt(e) {
      if (!eraserOn) return;
      attachEraserCursor();
      moveEraserCursor(e.clientX, e.clientY);
      pointerInside = true;
      updateEraserCursorVisibility();
    }
    canvas.addEventListener('pointerdown', showEraserCursorAt, true);
    canvas.addEventListener('pointermove', showEraserCursorAt, true);
    canvas.addEventListener('pointerenter', showEraserCursorAt, true);
    canvas.addEventListener('pointerleave', function() {
      pointerInside = false;
      updateEraserCursorVisibility();
    }, true);
    function syncSizeUI() {
      if (!sizeInput) return;
      sizeInput.value = String(Math.round((state.handwritingSize || 0.75) * 100));
      if (sizeVal) sizeVal.textContent = sizeInput.value + '%';
    }
    if (sizeInput) sizeInput.addEventListener('input', function() {
      state.handwritingSize = Math.max(0.2, Math.min(1.1, parseInt(sizeInput.value, 10) / 100));
      if (sizeVal) sizeVal.textContent = sizeInput.value + '%';
      updateTextOverlay();
      scheduleLmAutosave();
    });
    if (heightInput) heightInput.addEventListener('input', function() {
      state.handwritingHeight = Math.max(0.4, Math.min(1.4, parseInt(heightInput.value, 10) / 100));
      if (heightVal) heightVal.textContent = heightInput.value + '%';
      updateTextOverlay();
      scheduleLmAutosave();
    });

    function syncToggleUI() {
      var hw = !!state.handwritingEnabled;
      // Pill switch: highlight the matching tab; show the typed title OR the
      // handwriting canvas, never both. The subtitle (place) stays visible.
      for (var _i = 0; _i < modeBtns.length; _i++) {
        modeBtns[_i].classList.toggle('active', (modeBtns[_i].getAttribute('data-hw-mode') === 'handwritten') === hw);
      }
      panel.style.display = hw ? '' : 'none';
      if (titleInput) {
        titleInput.disabled = hw;
        // Hide the labelled wrapper (label + box), not just the box.
        (titleInput.closest('.lm-field') || titleInput).style.display = hw ? 'none' : '';
      }
      // Inspiration writes into the typed title, so it hides in handwriting mode.
      var inspBtn = document.getElementById('lm-open-inspiration');
      if (inspBtn) inspBtn.style.display = hw ? 'none' : '';
      var fontSection = document.getElementById('lm-font-section');
      if (fontSection) fontSection.style.display = hw ? 'none' : '';
      if (hw) {
        // Defer until panel is visible so getBoundingClientRect returns real size.
        requestAnimationFrame(sizeCanvas);
      }
    }
    for (var _mb = 0; _mb < modeBtns.length; _mb++) {
      modeBtns[_mb].addEventListener('click', function() {
        var wantHw = this.getAttribute('data-hw-mode') === 'handwritten';
        if (wantHw === !!state.handwritingEnabled) return;
        state.handwritingEnabled = wantHw;
        syncToggleUI();
        updateTextOverlay();
        scheduleLmAutosave();
      });
    }

    // Re-render preview when stroke colour might change (theme switch)
    window.lmHandwritingRedraw = function() { redraw(); };

    // ── Expand-to-modal — moves the same canvas DOM node into a fullscreen
    // wrap so all event handlers and stroke state stay live without any
    // duplication. The 1000×240 viewBox is preserved so anything drawn in
    // the modal lands in the same place at print time.
    var modal = document.getElementById('lm-hw-modal');
    // The modal markup lives inside .lm-main, a stacking context (position:
    // relative; z-index:1), so a fixed child is capped there and the mobile-dock
    // (z:20) / customer-hub drawer (z:9999) paint over its lower edge. Re-parent
    // to <body> so it stacks at the page root. Canvas move logic uses getElement
    // ById + getBoundingClientRect, so the DOM relocation is transparent to it.
    if (modal && modal.parentElement !== document.body) document.body.appendChild(modal);
    var modalWrap = document.getElementById('lm-hw-modal-wrap');
    var modalBackdrop = document.getElementById('lm-hw-modal-backdrop');
    var modalDone = document.getElementById('lm-hw-modal-done');
    var modalUndo = document.getElementById('lm-hw-modal-undo');
    var modalClear = document.getElementById('lm-hw-modal-clear');
    var expandBtn = document.getElementById('lm-hw-expand');
    var inlineWrap = canvas.parentElement; // .lm-hw-canvas-wrap (inline)

    var palmToggleBtn = document.getElementById('lm-hw-palm-toggle');
    var palmToggleLabel = document.getElementById('lm-hw-palm-toggle-label');
    function updatePalmRejectionUI() {
      if (!modal) return;
      modal.classList.toggle('pen-locked', palmRejectOn);
      if (palmToggleBtn) palmToggleBtn.setAttribute('aria-pressed', palmRejectOn ? 'true' : 'false');
      if (palmToggleLabel) palmToggleLabel.textContent = 'Palm rejection: ' + (palmRejectOn ? 'on' : 'off');
    }
    if (palmToggleBtn) palmToggleBtn.addEventListener('click', function() {
      palmRejectOn = !palmRejectOn;
      updatePalmRejectionUI();
    });
    var rotateBtn = document.getElementById('lm-hw-modal-rotate');
    function applyRotation() {
      if (modal) modal.classList.toggle('lm-hw-rotated', rotated);
      if (rotateBtn) {
        rotateBtn.setAttribute('aria-pressed', rotated ? 'true' : 'false');
        var lbl = rotateBtn.querySelector('span');
        if (lbl) lbl.textContent = rotated ? 'Upright' : 'Rotate';
      }
      // Re-measure after the transform/layout settles (double rAF) so the
      // bitmap matches the new on-screen size and strokes stay put.
      requestAnimationFrame(function() { sizeCanvas(); requestAnimationFrame(sizeCanvas); });
    }
    if (rotateBtn) rotateBtn.addEventListener('click', function() {
      rotated = !rotated;
      applyRotation();
    });
    function openModal() {
      if (!modal || !modalWrap) return;
      if (canvas.parentElement) canvas.parentElement.classList.remove('eraser-on');
      modalWrap.appendChild(canvas);
      attachEraserCursor();
      syncEraserUI();
      modal.classList.add('active');
      modal.setAttribute('aria-hidden', 'false');
      modalOpen = true;
      updatePalmRejectionUI();
      document.body.style.overflow = 'hidden';
      requestAnimationFrame(function() { sizeCanvas(); });
    }
    function closeModal() {
      if (!modal || !inlineWrap) return;
      if (canvas.parentElement) canvas.parentElement.classList.remove('eraser-on');
      // Leave rotate mode before returning the canvas to the inline panel.
      if (rotated) { rotated = false; applyRotation(); }
      inlineWrap.appendChild(canvas);
      attachEraserCursor();
      syncEraserUI();
      modal.classList.remove('active');
      modal.setAttribute('aria-hidden', 'true');
      modalOpen = false;
      document.body.style.overflow = '';
      requestAnimationFrame(function() { sizeCanvas(); });
    }

    if (expandBtn) expandBtn.addEventListener('click', openModal);
    if (modalDone) modalDone.addEventListener('click', closeModal);
    if (modalBackdrop) modalBackdrop.addEventListener('click', closeModal);
    // Modal undo/clear mirror the inline ones (same state target).
    if (modalUndo) modalUndo.addEventListener('click', undoHw);
    if (modalClear) modalClear.addEventListener('click', function() {
      if (state.handwritingStrokes.length) snapshotHwHistory();
      state.handwritingStrokes = []; redraw(); updateTextOverlay();
      scheduleLmAutosave();
    });
    document.addEventListener('keydown', function(e) {
      if (e.key === 'Escape' && modal && modal.classList.contains('active')) closeModal();
    });

    syncToggleUI();
    syncSizeUI();
    // Expose so loadExistingOrder can refresh the slider after restoring state.
    window.lmHandwritingSyncUI = function() { syncToggleUI(); syncSizeUI(); };
    window.addEventListener('resize', function() { if (state.handwritingEnabled) sizeCanvas(); });
  }

  // A row is treated as "locked" when the order has progressed to paid AND
  // the orderId doesn't carry the DUP- prefix used by the admin-side
  // duplicate route. Locked orders must not be edited from a returning
  // customizer session — that would corrupt the rendered/shipped artwork.
  function isPaidLocked(row, orderId) {
    if (!row) return false;
    if (typeof orderId === 'string' && orderId.indexOf('DUP-') === 0) return false;
    if (row.funnel_status === 'paid') return true;
    return false;
  }

  function lmFetchOrder(orderId) {
    return fetch(API_BASE + 'api/location-map-data/?order_no=' + encodeURIComponent(orderId))
      .then(function(r) { return r.status === 200 ? r.json() : null; })
      .catch(function() { return null; });
  }

  // ── lmHydrateFromData ────────────────────────────────────────────────────
  // Applies a saved-order data object (lmGetSaveData shape / location-map-data
  // GET response) to all state fields and triggers the matching UI/map updates.
  // Extracted from the inline fetch callback in loadExistingOrder so that a
  // headless boot (Task 2) can call the exact same hydration path.
  //
  // Parameters:
  //   data            — the order data object returned by the API
  //   preserveOrderId — when provided (preset/bestseller flow), keep this ID on
  //                     state instead of overwriting with data.order. Matches the
  //                     original local variable from loadExistingOrder.
  //   callback        — optional function called after styledata/fallback fires
  //   orderId         — the original orderId passed to loadExistingOrder; used
  //                     only for lmLogEditPersonalisationsOpened
  function lmHydrateFromData(data, preserveOrderId, callback, orderId) {
        // Update state
        state.orderId = preserveOrderId || data.order;
        window.lmOrderId = state.orderId;
        // The paper design is remembered client-side (localStorage), keyed
        // by order id, it isn't part of the D1 print record `data`. The
        // real order id only exists from this point on (earlier code runs
        // against a throwaway random id), so re-derive it here rather than
        // trusting whatever state.giftPaper was set to at script boot.
        if (window.SkylitGifts) {
          state.giftPaper = window.SkylitGifts.recallPaperFor('lm_' + state.orderId) || state.giftPaper || 'dots';
          state.ribbonColor = window.SkylitGifts.recallRibbonFor('lm_' + state.orderId) || state.ribbonColor || 'Red';
        }
        state.lat = data.lat;
        state.lng = data.lng;
        state.zoom = data.zoom;
        state.bearing = data.bearing || 0;
        state.pitch = data.pitch || 0;
        state.theme = data.theme || 'classic';
        state.waterTint = data.water_tint === 'blue' ? 'blue' : 'theme';
        state.layout = data.layout || 'full';
        state.title = data.title || '';
        var lmSplit = lmSplitSubtitle(data.subtitle);
        state.subtitle = lmSplit.subtitle;
        state.dateLine = lmSplit.dateLine;
        state.showCoordinates = data.show_coordinates !== false && data.show_coordinates !== 0 && data.show_coordinates !== '0';
        state.coordsText = data.coords_text ? String(data.coords_text) : (state.showCoordinates ? null : '');
        state.showLabels = !!data.show_labels;
        state.showWaterLabels = !!data.show_water_labels;
        state.showRoadLabels = !!data.show_road_labels;
        state.showPOILabels = !!data.show_poi_labels;
        state.showLandBorders = data.show_land_borders !== false && data.show_land_borders !== 0 && data.show_land_borders !== '0';
        state.showMaritimeBorders = data.show_maritime_borders !== false && data.show_maritime_borders !== 0 && data.show_maritime_borders !== '0';
        state.show3dBuildings = !!data.show_3d_buildings;
        state.show3dTerrain = !!data.show_3d_terrain;
        state.buildingDetail = 'high'; // detail picker removed 2026-06-09 — always All, even for old drafts
        state.show3dLandmarks = !!data.show_3d_landmarks;
        state.lightPreset = data.light_preset || 'day';
        state.font = data.font || 'Prata';
        state.detailsFont = data.details_font || 'Josefin Sans';
        state.locationName = dedupePlace(data.location_name || '');
        state.selectedType = data.selected_type || 'Framed';
        state.selectedFrame = data.selected_frame || 'Dark Brown';
        // The customer's own design (reopened from the cart, a draft or a
        // save-for-later link) brings its wrapping back: editing from the
        // cart purges the print AND its gift line and re-adds both from this
        // state, so leaving it on 'none' dropped the wrap on Confirm Edits.
        // Never from a preset template (preserveOrderId = the fresh id).
        if (!preserveOrderId && ['none', 'ribbon', 'giftwrap', 'bundle'].indexOf(data.gift_option) !== -1) {
          state.selectedGift = data.gift_option;
        }
        state.orientation = data.orientation || 'portrait';
        // bg picker removed 2026-06-09 → default 'theme'. The mat-layout white
        // flip (2026-07-08) is the one sanctioned non-theme value, so restore it
        // when a mat-layout draft (Border/Circle/Heart) saved white; any other
        // legacy custom_bg is ignored.
        state.customBg = (LM_MAT_LAYOUTS.indexOf(state.layout) !== -1
                          && typeof data.custom_bg === 'string'
                          && data.custom_bg.toLowerCase() === lmAltMat(state.theme))
                         ? lmAltMat(state.theme) : 'theme';
        state.textStyle = data.text_style || 'classic';
        state.passepartout = !!data.passepartout;
        // Headless only (skylit-admin location-thumbnail-render.ts): the cart
        // print renders a Border order's map FULL-BLEED and mats it afterwards,
        // so it hands the real layout here so the overlay still insets and fits
        // its title to the mat window. Never set by the storefront.
        state.overlayInsetLayout = data.overlay_inset_layout || null;
        state.handwritingEnabled = !!data.handwriting_enabled;
        try {
          if (data.handwriting_strokes) {
            var hw = typeof data.handwriting_strokes === 'string' ? JSON.parse(data.handwriting_strokes) : data.handwriting_strokes;
            state.handwritingStrokes = (hw && hw.strokes) || [];
          }
        } catch (e) { state.handwritingStrokes = []; }
        if (data.handwriting_size != null) {
          var _hs = parseFloat(data.handwriting_size);
          if (!isNaN(_hs)) state.handwritingSize = Math.max(0.2, Math.min(1.1, _hs));
        }
        if (data.handwriting_height != null) {
          var _hh = parseFloat(data.handwriting_height);
          if (!isNaN(_hh)) state.handwritingHeight = Math.max(0.4, Math.min(1.4, _hh));
        }
        if (typeof window.lmHandwritingSyncUI === 'function') window.lmHandwritingSyncUI();
        // The size in D1 can come from any of several historical save paths:
        //   "30x40"        — sizeCode (cart submit)
        //   "30 x 40 cm"   — variantOption (auto-save)
        //   "30 x 40"      — variantOption with " cm" stripped (legacy)
        //   "A4"           — same in every path
        // Match by exact data-size-code or data-variant-option first, then fall
        // back to a whitespace/cm-suffix-insensitive match so the picker still
        // highlights the right tile regardless of which form was saved.
        // URL ?size= overrides the loaded order's size — bestselling cards
        // pass size=a4 so customers land on the entry-level size rather
        // than whatever the template was saved at.
        var _lmSizeOverride = null;
        try { _lmSizeOverride = new URLSearchParams(window.location.search).get('size'); } catch (e) {}
        var rawSize = _lmSizeOverride || data.size || 'A4';
        var _normSize = function(s){ return String(s||'').toLowerCase().replace(/\s+/g,'').replace(/cm$/,''); };
        var nRaw = _normSize(rawSize);
        var sizeMatch = null;
        var sizeBtns = document.querySelectorAll('.lm-size-picker [data-variant-option]');
        for (var _si = 0; _si < sizeBtns.length; _si++) {
          var _b = sizeBtns[_si];
          if (_b.dataset.sizeCode === rawSize || _b.dataset.variantOption === rawSize ||
              _normSize(_b.dataset.sizeCode) === nRaw || _normSize(_b.dataset.variantOption) === nRaw) {
            sizeMatch = _b; break;
          }
        }
        state.selectedSize = sizeMatch ? sizeMatch.dataset.variantOption : rawSize;

        // Move map — raise maxPitch first so saved tilt isn't clamped to 45
        if (state.show3dLandmarks) {
          try { map.setMaxPitch(85); } catch(e) {}
          var psR0 = document.getElementById('lm-pitch'); if (psR0) psR0.max = '85';
        }
        // Restore the customer's exact framing.
        //
        // fitBounds was previously preferred for parity with the renderer
        // (render_locationmap.py also fits to saved bounds). That works
        // fine for top-down flat maps, but at non-trivial pitch (and in
        // 3D-landmark mode where pitch is typically 45–80°) the visible
        // region is a screen-space trapezoid — the lat/lng "bounds"
        // rectangle returned by map.getBounds() is much wider than what
        // the camera actually sees. Calling fitBounds on that rectangle
        // re-computes a zoom that fits the WHOLE rectangle, which is
        // looser and at a different center than the saved camera, and
        // Mapbox sometimes silently clamps the requested pitch as well.
        // Result: reloaded orders showed visibly different framing.
        //
        // For tilted/3D views, jumpTo with the saved {center,zoom,bearing,
        // pitch} reproduces the exact camera the customer designed
        // against. Flat views still use fitBounds so the editor preview
        // matches the print pixel-for-pixel.
        var isTilted = Math.abs(state.pitch || 0) > 5 || !!state.show3dLandmarks;
        var _boundsRestored = false;
        // Preferred restore: saved {center, zoom} scaled by the width ratio
        // between the CURRENT map slot and the slot the order was authored
        // on (preview_viewport_w). This reproduces the exact camera the
        // customer designed — same maths as the print renderer's
        // zoom + log2(render_w / preview_viewport_w).
        //
        // fitBounds(saved_bounds) is only the fallback for legacy orders
        // without preview_viewport_w: with a non-zero bearing, getBounds()
        // is the axis-aligned bbox of the ROTATED view, and fitBounds must
        // zoom out to contain that whole bbox — reloaded orders showed
        // ~2.3x the designed area (order o7yv5sveh1, bearing -22), with
        // village-level place labels popping in below the tileset's ~z12
        // label cliff that the customer never saw while designing.
        // Applies to tilted/3D-landmark orders too: zoom is per-viewport, so
        // replaying the raw saved zoom on a different-width slot (phone ~390px
        // vs desktop ~640px) framed a visibly different area cross-device.
        // jumpTo keeps the saved bearing/pitch, so the width-scaled zoom is
        // correct for tilted cameras as well.
        if (window.__LM_HEADLESS !== true) {
          var _pvw = parseFloat(data.preview_viewport_w);
          var _savedZoom = parseFloat(data.zoom);
          var _savedCenter = [state.lng, state.lat];
          var _savedBearing = state.bearing, _savedPitch = state.pitch;
          if (_pvw > 0 && !isNaN(_savedZoom)) {
            var _restoreSavedCamera = function() {
              var el = document.getElementById('lm-map-inner');
              var w = el ? el.clientWidth : 0;
              if (!w) return;
              map.jumpTo({
                center: _savedCenter,
                zoom: applyZoom(_savedZoom + Math.log2(w / _pvw)),
                bearing: _savedBearing, pitch: _savedPitch,
              });
            };
            _restoreSavedCamera();
            // The slot keeps resizing AFTER this point (orientation/size
            // hydration below swaps the frame aspect; the supersample
            // ResizeObserver then resizes the map), and a restore computed
            // against the mid-boot slot is wrong once the layout settles.
            // Re-apply on those resize events, computed fresh from the
            // CAPTURED saved values (state.zoom mutates on zoomend, so
            // recomputing from state would double-adjust). A fixed time
            // window proved too short on slow landmark loads, so instead
            // stay attached until the customer first touches the map
            // (resize-only re-apply can't stomp pans/zooms, but detaching
            // on interaction keeps a late window resize from reverting
            // their edits), with a 30s hard cap.
            _lmAttachRestoreOnResize(_restoreSavedCamera);
            _boundsRestored = true;
          }
        }
        // Headless CF render: skip fitBounds and use jumpTo(center,zoom). In CF
        // SwiftShader, fitBounds on a short LANDSCAPE viewport left the top of
        // the canvas unpainted (a ~12% white band). jumpTo with the saved
        // center+zoom paints the full viewport and matches the Mac print
        // renderer (which also jumpTo's, not fitBounds).
        if (data.bounds && !isTilted && !_boundsRestored && window.__LM_HEADLESS !== true) {
          try {
            var _b = typeof data.bounds === 'string' ? JSON.parse(data.bounds) : data.bounds;
            if (Array.isArray(_b) && _b.length === 2) {
              map.fitBounds(_b, {
                padding: 0, animate: false, duration: 0,
                bearing: state.bearing, pitch: state.pitch,
              });
              _boundsRestored = true;
            }
          } catch (e) { /* fall through to jumpTo for malformed bounds */ }
        }
        if (!_boundsRestored) {
          map.jumpTo({ center: [state.lng, state.lat], zoom: applyZoom(state.zoom), bearing: state.bearing, pitch: state.pitch });
        }

        // Update theme style — if landmarks are on, recreate the map at higher
        // pixelRatio (init created it at the default 2).
        if (state.show3dLandmarks) {
          recreateMapForLandmarks();
          // The recreate copies the camera, but the resize re-apply above is
          // bound to the OLD map instance. Re-assert the restored camera on
          // the new instance while the slot is still settling.
          if (typeof _restoreSavedCamera === 'function') {
            _restoreSavedCamera();
            _lmAttachRestoreOnResize(_restoreSavedCamera);
          }
        } else {
          map.setStyle(buildStyle(state.theme));
        }

        // Update UI inputs
        setLocationInputDisplay(state.locationName);
        var titleInput = document.getElementById('lm-title');
        if (titleInput) titleInput.value = state.title;
        var subtitleInput = document.getElementById('lm-subtitle');
        if (subtitleInput) subtitleInput.value = state.subtitle;
        var dateInput = document.getElementById('lm-date');
        if (dateInput) dateInput.value = state.dateLine;
        var zoomSlider = document.getElementById('lm-zoom');
        if (zoomSlider) zoomSlider.value = state.zoom;
        var rotSlider = document.getElementById('lm-rotate');
        if (rotSlider) rotSlider.value = state.bearing;
        var pitSlider = document.getElementById('lm-pitch');
        if (pitSlider) pitSlider.value = state.pitch;
        var rotVal = document.getElementById('lm-rotate-val');
        if (rotVal) rotVal.textContent = Math.round(state.bearing) + '°';
        var pitVal = document.getElementById('lm-pitch-val');
        if (pitVal) pitVal.textContent = Math.round(state.pitch) + '°';

        // Update coords toggle
        var coordsSwitch = document.getElementById('lm-coords-switch');
        if (coordsSwitch) coordsSwitch.classList.toggle('active', state.showCoordinates);
        lmSyncCoordsInput();

        // Update labels toggles
        var labelsSwitch = document.getElementById('lm-labels-switch');
        if (labelsSwitch) labelsSwitch.classList.toggle('active', state.showLabels);
        var waterSwitch = document.getElementById('lm-water-labels-switch');
        if (waterSwitch) waterSwitch.classList.toggle('active', state.showWaterLabels);
        var roadSwitch = document.getElementById('lm-road-labels-switch');
        if (roadSwitch) roadSwitch.classList.toggle('active', state.showRoadLabels);
        var landBordersSwitch = document.getElementById('lm-land-borders-switch');
        if (landBordersSwitch) landBordersSwitch.classList.toggle('active', state.showLandBorders);
        var maritimeBordersSwitch = document.getElementById('lm-maritime-borders-switch');
        if (maritimeBordersSwitch) maritimeBordersSwitch.classList.toggle('active', state.showMaritimeBorders);
        var b3dSwitch = document.getElementById('lm-3d-buildings-switch');
        if (b3dSwitch) b3dSwitch.classList.toggle('active', state.show3dBuildings);
        var t3dSwitch = document.getElementById('lm-3d-terrain-switch');
        if (t3dSwitch) t3dSwitch.classList.toggle('active', state.show3dTerrain);
        var lmkSwitch = document.getElementById('lm-3d-landmarks-switch');
        if (lmkSwitch) lmkSwitch.classList.toggle('active', state.show3dLandmarks);
        if (state.show3dLandmarks) {
          map.setMaxPitch(85);
          var psR = document.getElementById('lm-pitch'); if (psR) psR.max = '85';
          var lpR = document.getElementById('lm-light-preset'); if (lpR) lpR.style.display = 'block';
        }
        updateOther3dRows();
        var bdRow = document.getElementById('lm-bd-detail');
        if (bdRow) bdRow.style.display = state.show3dBuildings ? 'block' : 'none';
        // Picker .active classes are now Alpine-driven via :class bindings
        // against $store.lm.X. Push every hydrated value through lmStoreSet
        // so the proxy fires reactivity and the bindings re-evaluate.
        // (Direct `state.x = y` writes from above bypass the proxy.)
        ['theme', 'layout', 'textStyle', 'font', 'detailsFont', 'fontScope', 'orientation',
         'selectedType', 'selectedSize', 'selectedFrame', 'selectedGift', 'giftPaper',
         'customBg', 'lightPreset', 'buildingDetail'].forEach(function (k) {
          if (state[k] !== undefined) lmStoreSet(k, state[k]);
        });
        // Reflect the recalled paper design onto the swatch picker and the
        // live overlay/3D parcel: the values above only update Alpine's
        // reactive store, not the picker's own .active class or the DOM
        // overlay src, which lmPaperPicker.set()/lmApplyGiftOverlays() own.
        if (typeof lmPaperPicker !== 'undefined' && lmPaperPicker) lmPaperPicker.set(state.giftPaper || 'dots');
        if (typeof lmRibbonPicker !== 'undefined' && lmRibbonPicker) lmRibbonPicker.set(state.ribbonColor || 'Red');
        if (typeof lmApplyGiftOverlays === 'function') lmApplyGiftOverlays(state.selectedGift);
        if (typeof lmApplyPeekTo3D === 'function') lmApplyPeekTo3D();
        var frame = document.getElementById('lm-print-frame');
        if (frame) frame.classList.toggle('landscape', state.orientation === 'landscape');
        applyFrameRatio();
        var fp = document.querySelector('.lm-frame-picker-wrap');
        if (fp) fp.style.display = (state.selectedType === 'Framed') ? 'block' : 'none';

        // Reload icons from saved data
        if (data.icons_json) {
          try {
            var savedIcons = (typeof data.icons_json === 'string') ? JSON.parse(data.icons_json) : data.icons_json;
            if (data.icon_size) {
              var defaultIconSize = (window.SKYLIT_LM_MODE === 'landmarks') ? 48 : 28;
              iconSize = parseInt(data.icon_size) || defaultIconSize;
              // (size buttons sync per-selection via lmSyncIconSizeButtons)
            }
            savedIcons.forEach(function(ic) {
              var iconObj = {
                id: ic.icon + '-' + Date.now() + Math.random(),
                icon: ic.icon, emoji: ic.emoji,
                lat: ic.lat, lng: ic.lng,
                // Per-icon size/rotation (2026-07). Legacy orders lack these:
                // fall back to the old global icon_size (now in `iconSize`) and 0.
                size: ic.size || iconSize,
                rotation: ic.rotation || 0,
                label: ic.label || ''
              };
              if (ic.photo && LM_PHOTO_URL_RE.test(ic.photo)) iconObj.photo = ic.photo;
              placedIcons.push(iconObj);
              _attachMarkerForIcon(iconObj);
            });
            updatePlacedIconsUI();
          } catch(e) { console.warn('[LocationMap] Failed to reload icons:', e); }
        }

        // Change ATC button text for edit mode (mirror starmap-v2 which
        // uses 'Confirm edits'). Skip when preserveOrderId is set — that
        // path is preset/bestseller hydration, not an edit of a cart line.
        // Set state.editMode so updateVariantAndPrice (which also writes to
        // the ATC span) preserves the label on subsequent picker changes.
        if (!preserveOrderId) {
          // Edit mode only when the design is actually in the live cart —
          // a resumed draft or save-for-later link is a plain editing
          // session (normal ATC copy, autosave stays on). When the cart
          // state is unknown (fetch failed), stay conservative with edit
          // mode ON so an in-cart design never duplicates its line.
          state.editMode = (lmCartOrderIds === null)
            ? true
            : lmCartOrderIds.indexOf(orderId) !== -1;
          if (state.editMode) {
            var btn = document.querySelector('.lm-add-to-cart-btn span');
            if (btn) btn.textContent = 'Confirm edits';
            // Log the revisit so admin can see customers who returned to
            // verify their design — even if they leave without changes.
            // Skip in headless CF preview renders — no orderId is present
            // and firing a real fetch leaks the ?state= URL in `detail`.
            if (window.__LM_HEADLESS !== true) {
              lmLogEditPersonalisationsOpened(orderId);
            }
          }
        }

        // styledata fires after setStyle — but may not fire if same style
        var styleFired = false;
        map.once('styledata', function() {
          if (styleFired) return;
          styleFired = true;
          updateTextOverlay();
          updateLayout();
          updateFrame();
          filterSizesForType();
          updateVariantAndPrice();
          setTimeout(addWaterTexture, 300);
          if (state.showLabels) setTimeout(addLabelLayers, 500); if (state.showWaterLabels) setTimeout(addWaterLabels, 500); if (state.showRoadLabels) setTimeout(addRoadLabels, 500);
          if (state.show3dBuildings && !state.show3dLandmarks) setTimeout(add3dBuildings, 500);
          if (state.show3dTerrain) setTimeout(add3dTerrain, 500);
          if (state.show3dLandmarks) {
            setTimeout(function(){
              try { applyLandmarksConfig(); } catch(e) {}
              applyNightStreetGlow(); addParkOverlay();
            }, 600);
          }
          if (map) setTimeout(function() { map.resize(); }, 100);
          if (callback) callback();
        });
        // Fallback: if styledata doesn't fire within 2s, run anyway
        setTimeout(function() {
          if (styleFired) return;
          styleFired = true;
          updateTextOverlay();
          updateLayout();
          updateFrame();
          filterSizesForType();
          updateVariantAndPrice();
          setTimeout(addWaterTexture, 300);
          if (state.showLabels) setTimeout(addLabelLayers, 500); if (state.showWaterLabels) setTimeout(addWaterLabels, 500); if (state.showRoadLabels) setTimeout(addRoadLabels, 500);
          if (state.show3dBuildings && !state.show3dLandmarks) setTimeout(add3dBuildings, 500);
          if (state.show3dTerrain) setTimeout(add3dTerrain, 500);
          if (state.show3dLandmarks) {
            setTimeout(function(){
              try { applyLandmarksConfig(); } catch(e) {}
              applyNightStreetGlow(); addParkOverlay();
            }, 600);
          }
          if (map) setTimeout(function() { map.resize(); }, 100);
          if (callback) callback();
        }, 2000);
  }

  // Fire-and-forget log of an "edit personalisations" revisit. Useful for
  // visibility into customers who returned to verify their design but
  // didn't change anything (those wouldn't produce a diff in autosave).
  function lmLogEditPersonalisationsOpened(orderId) {
    try {
      fetch(API_BASE + 'api/order-event', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          order_id: orderId,
          event: 'edit_personalisations_opened',
          detail: window.location.href.slice(0, 200)
        })
      }).catch(function() {});
    } catch (e) {}
  }

  // ── Load Existing Order (edit mode) ──
  // preserveOrderId: when provided (preset flow), keep that ID on state
  // instead of overwriting with the template's — so add-to-cart creates a
  // brand-new order instead of clobbering the template.
  function loadExistingOrder(orderId, callback, preserveOrderId, prefetched, asPreset) {
    // prefetched: callers that already hold the order row (e.g. the
    // paid-lock check via lmFetchOrder) pass it here to skip a duplicate
    // sequential round trip to the admin API.
    // asPreset appends &preset=1 so the backend knows this is a "start from
    // this template" load, not a customer returning to their own draft — the
    // route skips notifyDraftResumed for it. Without the flag the template row
    // (old + unpaid) trips isDraftReturnVisit and fires a bogus
    // "Returned to draft" push naming the TEMPLATE's size/location.
    // Mirrors starmap-v2.js:3281 and moon-myjs.js:608.
    (prefetched ? Promise.resolve(prefetched) : fetch(API_BASE + 'api/location-map-data/?order_no=' + orderId + (asPreset ? '&preset=1' : ''))
      .then(function(r) {
        // Treat 404 / 5xx as "stale pointer" — the order was GC'd or never
        // existed. Don't pollute state with undefined and let Mapbox propagate
        // NaN coords; clean up the stale id and bail to defaults.
        if (!r.ok) {
          throw new Error('lm.loadExistingOrder: HTTP ' + r.status + ' for ' + orderId);
        }
        return r.json();
      }))
      .then(function(data) {
        // Validate the response actually has usable coords. The admin API
        // can return 200 with an empty body in edge cases (mid-create stub
        // orders, race conditions), and undefined→NaN cascade through the
        // Mapbox setCenter call breaks the entire preview.
        if (!isFinite(data && data.lat) || !isFinite(data && data.lng)) {
          throw new Error('lm.loadExistingOrder: missing lat/lng for ' + orderId);
        }
        lmHydrateFromData(data, preserveOrderId, callback, orderId);
      })
      .catch(function(e) {
        console.warn('[LocationMap] Failed to load order, falling back to defaults:', e);
        // Clear the stale pointer so next nav doesn't re-hit the same 404.
        // - URL: drop ?order_id=…  (preserves the rest of the query string)
        // - localStorage draft pointer: only delete if it matches the bad id
        try {
          var p = new URLSearchParams(window.location.search);
          if (p.has('order_id')) {
            p.delete('order_id');
            var qs = p.toString();
            window.history.replaceState({}, '',
              window.location.pathname + (qs ? '?' + qs : '') + window.location.hash);
          }
        } catch (_) {}
        try {
          if (typeof LM_DRAFT_KEY !== 'undefined') {
            var raw = localStorage.getItem(LM_DRAFT_KEY);
            if (raw) {
              var parsed = null;
              try { parsed = JSON.parse(raw); } catch (_) {}
              // Pointer is written as {orderId, ts} — the old `parsed.id`
              // read here was always undefined, so stale pointers never
              // actually got removed and the doomed "Continue your design?"
              // modal returned on every visit.
              var draftId = parsed && parsed.orderId ? parsed.orderId : raw;
              if (draftId === orderId) localStorage.removeItem(LM_DRAFT_KEY);
            }
          }
        } catch (_) {}
        // Continue boot at default coords. The caller's callback typically
        // hides the loader and runs the standard updates, which now operate
        // on the un-mutated default state — no NaN cascade.
        if (callback) callback();
      });
  }

  // ── Step Navigation ──
  var _lmStepScrollRequest = 0;

  // Mobile uses normal document scrolling rather than the desktop sidebar
  // scroller. When a tall panel is replaced by a shorter one, Safari keeps the
  // old document position and can leave the customer at the bottom of the new
  // step. Re-align the start of the controls after customer navigation.
  function scrollLocationStepIntoView() {
    if (!window.matchMedia || !window.matchMedia('(max-width: 991px)').matches) return;
    var side = document.querySelector('.lm-side');
    if (!side) return;
    var request = ++_lmStepScrollRequest;
    window.requestAnimationFrame(function() {
      window.requestAnimationFrame(function() {
        if (request !== _lmStepScrollRequest) return;
        // With the sticky preview (lm-stage.js) the sidebar has to land under
        // where the preview rests once pinned, not under the viewport edge,
        // or the top of the step hides behind it. Its `top` is negative (it
        // collapses under the edge), so top + height is the compact bottom.
        var safeTop = 8;
        var main = document.querySelector('.lm-wrapper > .lm-main');
        if (main) {
          var ms = window.getComputedStyle(main);
          var pinnedBottom = (parseFloat(ms.top) || 0) + main.getBoundingClientRect().height;
          if (ms.position === 'sticky' && pinnedBottom < window.innerHeight * 0.8) safeTop = Math.ceil(pinnedBottom);
        }
        var targetY = Math.max(0, (window.scrollY || window.pageYOffset || 0) + side.getBoundingClientRect().top - safeTop);
        var reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        window.scrollTo({ top: targetY, behavior: reducedMotion ? 'auto' : 'smooth' });
      });
    });
  }

  function bindStepNav() {
    // Tab clicks
    document.querySelectorAll('.lm-step-tab').forEach(function(tab) {
      tab.addEventListener('click', function() { goToStep(parseInt(tab.dataset.step), { scroll: true }); });
    });
    // Next/Back buttons
    document.querySelectorAll('.lm-next-btn, .lm-back-btn').forEach(function(btn) {
      btn.addEventListener('click', function() { goToStep(parseInt(btn.dataset.next), { scroll: true }); });
    });
  }

  function goToStep(stepNum, options) {
    var activeTab = document.querySelector('.lm-step-tab.active');
    var previousStep = activeTab ? parseInt(activeTab.dataset.step) : 0;
    // Update tabs
    document.querySelectorAll('.lm-step-tab').forEach(function(tab) {
      var n = parseInt(tab.dataset.step);
      tab.classList.remove('active');
      if (n < stepNum) tab.classList.add('visited');
      if (n === stepNum) tab.classList.add('active');
    });
    // Update content
    document.querySelectorAll('.lm-step-content').forEach(function(panel) {
      panel.classList.toggle('active', parseInt(panel.dataset.stepContent) === stepNum);
    });
    if (stepNum !== previousStep && options && options.scroll) scrollLocationStepIntoView();
    lmSyncPhotoSample();
  }

  // ── Icons / Pins ──
  var placedIcons = []; // { id, icon, emoji, lat, lng, size, rotation, marker }
  var selectedIconId = null;

  // Default icon size in px. Bumped to 48 in landmarks mode (initialised
  // when window.SKYLIT_LM_MODE === 'landmarks') because tilted 3D views
  // foreshorten markers — 28px reads tiny against photoreal landmarks.
  // Now a PER-ICON value (each icon carries its own .size); this stays as the
  // default seed for new icons and the legacy fallback for old saved orders.
  var iconSize = (window.SKYLIT_LM_MODE === 'landmarks') ? 48 : 28;

  var LM_NUDGE_PX = 2;
  var _lmMapDeselectBound = false;

  // ── Selection ──
  function lmGetSelectedIcon() {
    return placedIcons.find(function(p){ return p.id === selectedIconId; }) || null;
  }
  function lmSetMarkerSelected(iconObj, on) {
    var el = iconObj && iconObj.marker && iconObj.marker.getElement();
    if (el) el.classList.toggle('lm-marker-selected', !!on);
  }
  window.lmSelectIcon = function (id) {
    var prev = lmGetSelectedIcon();
    if (prev) lmSetMarkerSelected(prev, false);
    selectedIconId = id;
    var cur = lmGetSelectedIcon();
    if (cur) lmSetMarkerSelected(cur, true);
    updatePlacedIconsUI();
    lmSyncIconEditor();
  };
  window.lmDeselectIcon = function () {
    var prev = lmGetSelectedIcon();
    if (prev) lmSetMarkerSelected(prev, false);
    selectedIconId = null;
    updatePlacedIconsUI();
    lmSyncIconEditor();
  };
  // Deselect when the empty map is tapped. Markers stopPropagation on click,
  // so this fires only for non-marker map clicks. Bound lazily (map must exist).
  function lmEnsureMapDeselect() {
    if (_lmMapDeselectBound || !map) return;
    _lmMapDeselectBound = true;
    map.on('click', function(){ if (selectedIconId) window.lmDeselectIcon(); });
  }

  // ── Per-icon editor panel ──
  function lmApplyRotationUI(deg) {
    var needle = document.querySelector('#lm-icon-rot-dial .lm-rot-needle');
    if (needle) needle.style.transform = 'translateX(-50%) rotate(' + deg + 'deg)';
    var out = document.getElementById('lm-icon-rot-readout');
    if (out) out.textContent = Math.round(((deg % 360) + 360) % 360) + '°';
  }
  // Selecting from the panel on mobile should not strand the customer looking
  // at a card for an icon they can't see. Only scrolls when the map is not
  // already substantially on screen, so it doesn't yank the page every tap.
  function lmRevealMapIfNeeded() {
    if (window.matchMedia && window.matchMedia('(min-width: 992px)').matches) return;
    var frame = document.getElementById('lm-print-frame');
    if (!frame) return;
    var r = frame.getBoundingClientRect();
    var vh = window.innerHeight || 0;
    var visible = Math.min(r.bottom, vh) - Math.max(r.top, 0);
    if (visible >= r.height * 0.5) return;
    try { frame.scrollIntoView({ behavior: 'smooth', block: 'center' }); }
    catch (e) { frame.scrollIntoView(); }
  }
  // Both "should the card be visible" inputs are class flags written by other
  // files — .sm-gallery-viewing on .lm-main by starmap-gallery.js, and
  // .lm-3d-active on the lazily-created 3D container by customizer-3d-core.js.
  // Watching class changes across the subtree catches both without reaching
  // into either file. rAF-coalesced: class churn here is frequent and cheap
  // to ignore.
  var _iconBarObsRaf = 0;
  function lmIconCardObserveChrome() {
    var main = document.querySelector('.lm-main');
    if (!main || !window.MutationObserver) return;
    new MutationObserver(function (records) {
      // This observer exists to catch two EXTERNAL class flags —
      // .sm-gallery-viewing on .lm-main and .lm-3d-active on the lazily
      // created 3D container — neither of which lives inside the card
      // itself. The card (and its picker) is appended as a SIBLING of
      // .lm-main in portrait (see lmIconCardPlace), and lives inside
      // #lm-mobile-zoom as its flip-back face in landscape. lmBuildPicker
      // toggles .active on its own tab/cell buttons every time it rebuilds,
      // which is itself a class mutation the observer would otherwise catch,
      // re-triggering the very sync that rebuilt it — a self-sustaining
      // rebuild loop, one iteration per animation frame, for as long as the
      // picker stays open. #lm-mobile-zoom itself is also written by this
      // same sync (lmIconCardPlace toggles lm-flip-host/lm-flip-flipped on
      // it), so its own class churn must be ignored too or the sync
      // retriggers itself every frame. Ignore any record whose target is
      // inside #lm-icon-card or is #lm-mobile-zoom itself so the card's own
      // (and its host's own) churn can never feed back into this.
      var card = lmIconCardEl();
      var zoomEl = document.getElementById('lm-mobile-zoom');
      var relevant = false;
      for (var i = 0; i < records.length; i++) {
        var t = records[i].target;
        if (card && card.contains && card.contains(t)) continue;
        if (t === zoomEl) continue;
        relevant = true;
        break;
      }
      if (!relevant) return;
      if (_iconBarObsRaf) return;
      _iconBarObsRaf = requestAnimationFrame(function () {
        _iconBarObsRaf = 0;
        lmIconCardSync();
      });
    }).observe(main, { attributes: true, attributeFilter: ['class'], subtree: true });
  }
  // ── Icon editor card ──
  // One component, two homes. Landscape print: the #lm-mobile-zoom strip
  // already reflows to a horizontal bar under the print, so the card becomes
  // its back face and the strip flips. Portrait print: that strip is a 40px
  // right-edge column with no room, so the card sits below the print instead
  // and pushes the panel down (overlaying would cover the step tabs).
  var _iconCard = null;
  // Tracks whether the portrait card was shown on the previous sync, so the
  // entry animation only plays on an actual closed->open transition.
  var _iconCardWasShown = false;
  function lmIconCardEl() {
    if (!_iconCard) _iconCard = document.getElementById('lm-icon-card');
    return _iconCard;
  }
  function lmIsLandscapePrint() {
    var f = document.getElementById('lm-print-frame');
    return !!(f && f.classList.contains('landscape'));
  }
  function lmIconCardShouldShow(sel) {
    if (!sel) return false;
    if (window.matchMedia && window.matchMedia('(min-width: 992px)').matches) return false;
    if (window.LM3DFramedMockup && window.LM3DFramedMockup.isActive && window.LM3DFramedMockup.isActive()) return false;
    if (window.LM3DMockup && window.LM3DMockup.isActive && window.LM3DMockup.isActive()) return false;
    var main = document.querySelector('.lm-main');
    if (main && main.classList.contains('sm-gallery-viewing')) return false;
    return true;
  }
  // The strip (#lm-mobile-zoom) is a ~44px row of round buttons; the card it
  // hosts as its flip-back face needs ~102px for its two rows. .lm-flip-host
  // has a `height` transition (assets/locationmap-card.css) but nothing ever
  // sets an explicit height, so height:auto -> auto never animates and the
  // back face (position:absolute; inset:0) is crushed/clipped to the strip's
  // own height. Measure the card's real content height and push it onto the
  // host explicitly; clear it to let the strip spring back to its natural
  // button-row size. Called both when the flip opens/closes and whenever the
  // picker (inside the card) opens/closes while already flipped, since the
  // picker grid changes the card's content height on its own.
  //
  // card.scrollHeight alone under-reports the requirement, the same flaw
  // lmSyncFlipHostWidth had to route around: the card is `position:absolute;
  // inset:0` inside the host, so its own box height is pinned to the host's
  // CURRENT (too small) height before this even runs, and an element with
  // the default `overflow: visible` reports scrollHeight == its own box
  // height, not the true extent of content spilling past its bottom edge.
  // Measure the same way the width path does: sum the card's direct-child
  // rows' own natural heights (each row is sized by its content, not
  // squeezed by the card's constrained box) plus the row gaps between them
  // plus the card's own vertical padding/borders, all read from computed
  // style so this stays correct if the CSS spacing changes again.
  function lmSyncFlipHostHeight(zoom, card, show) {
    if (!zoom) return;
    if (show) {
      var h = 0;
      if (card) {
        var kids = card.children;
        var rowHeights = [];
        var hasCS = !!window.getComputedStyle;
        for (var i = 0; i < kids.length; i++) {
          var kid = kids[i];
          if (kid.hidden) continue;
          if (hasCS && window.getComputedStyle(kid).display === 'none') continue;
          // Out-of-flow children contribute nothing to the card's flow height.
          // The emoji picker is one: it is position:absolute (it renders as a
          // popover ABOVE the card, see assets/locationmap-card.css), and
          // summing its height here inflated the flip host to roughly twice
          // the card's real size, leaving a large empty area under the last
          // row and throwing the popover far off the top of the card.
          if (hasCS) {
            var kidPos = window.getComputedStyle(kid).position;
            if (kidPos === 'absolute' || kidPos === 'fixed') continue;
          }
          var r = kid.getBoundingClientRect ? kid.getBoundingClientRect() : null;
          rowHeights.push(r ? r.height : (kid.offsetHeight || 0));
        }
        if (rowHeights.length) {
          for (var j = 0; j < rowHeights.length; j++) h += rowHeights[j];
          if (hasCS) {
            var cs = window.getComputedStyle(card);
            var gap = parseFloat(cs.rowGap) || parseFloat(cs.gap) || 0;
            h += gap * (rowHeights.length - 1);
            h += (parseFloat(cs.paddingTop) || 0) + (parseFloat(cs.paddingBottom) || 0) +
                 (parseFloat(cs.borderTopWidth) || 0) + (parseFloat(cs.borderBottomWidth) || 0);
          }
        }
        // Floor: never report less than the card's own natural box, in
        // case the row-summing above ever misses a child.
        var sh = card.scrollHeight || (card.getBoundingClientRect && card.getBoundingClientRect().height) || 0;
        if (sh > h) h = sh;
      }
      if (h) zoom.style.height = h + 'px';
    } else {
      zoom.style.height = '';
    }
  }
  // Mirrors lmSyncFlipHostHeight for width. The strip's own width is
  // content-driven by its 7 round buttons (~272px); the card's second row
  // (5 size pills + 2 rotate buttons + delete) needs more than that, so
  // without an explicit width the flipped host clips to the strip's
  // narrower natural size and the card's content (delete button included)
  // spills outside it. Clamped to the viewport so a tall/narrow phone
  // never pushes the strip off-screen; clear it on close so the strip
  // springs back to its natural button-row width. #lm-mobile-zoom is
  // centred via `right: 50%` + `transform: translateX(50%)` (see
  // locationmap-card.css) — both are relative to the element's own box, so
  // widening it here keeps it centred without any extra positioning math.
  //
  // card.scrollWidth alone under-reports the requirement: the card is
  // `display:flex; flex-direction:column`, so each `.lm-icon-card-row` is
  // stretched (align-items default) to the card's OWN current width before
  // its buttons are laid out. If that width is still the strip's old
  // ~272px when this runs, the row's buttons hit their `min-width` floors
  // and visually overflow the row — but that overflow never grows the
  // card's own scrollWidth, because the row (a block-level flex item) is
  // itself fixed at 272px; only the row's scrollWidth captures its
  // children's true min-content size. So measure the widest row instead
  // and add the card's own horizontal padding/border back on top.
  function lmSyncFlipHostWidth(zoom, card, show) {
    if (!zoom) return;
    if (show) {
      var w = 0;
      if (card) {
        var rows = card.querySelectorAll('.lm-icon-card-row');
        for (var i = 0; i < rows.length; i++) {
          if (rows[i].scrollWidth > w) w = rows[i].scrollWidth;
        }
        if (w && card.style && window.getComputedStyle) {
          var cs = window.getComputedStyle(card);
          w += (parseFloat(cs.paddingLeft) || 0) + (parseFloat(cs.paddingRight) || 0) +
               (parseFloat(cs.borderLeftWidth) || 0) + (parseFloat(cs.borderRightWidth) || 0);
        }
        // Only as a floor when the row scan found nothing — card.scrollWidth
        // counts the absolutely-positioned picker, which deliberately bleeds
        // past the card's sides in landscape and would widen the host with it.
        if (!w && card.scrollWidth > w) w = card.scrollWidth;
      }
      // Floor the card at the print's width. Sizing purely to the widest row
      // hugged the controls and left the card visibly narrower than the print
      // it sits on, which read as unfinished rather than deliberate. The print
      // is the widest thing on the stage, so matching it also gives the emoji
      // picker the room it needs for all six category chips on one line.
      var lmFrame = document.getElementById('lm-print-frame');
      if (lmFrame && lmFrame.getBoundingClientRect) {
        var fw = lmFrame.getBoundingClientRect().width;
        if (fw > w) w = fw;
      }
      if (w) {
        var maxW = window.innerWidth - 32;
        zoom.style.width = Math.min(w, maxW) + 'px';
      }
    } else {
      zoom.style.width = '';
    }
  }
  // Move the card into the right host and set the flip/entry state.
  function lmIconCardPlace(show) {
    var card = lmIconCardEl();
    if (!card) return;
    var zoom = document.getElementById('lm-mobile-zoom');
    var landscape = lmIsLandscapePrint();
    if (landscape && zoom) {
      // Back face of the zoom strip.
      if (card.parentNode !== zoom) {
        zoom.classList.add('lm-flip-host');
        card.classList.add('lm-flip-back');
        card.classList.remove('lm-card-entering');
        zoom.appendChild(card);
      }
      zoom.classList.toggle('lm-flip-flipped', !!show);
      // card.hidden must already reflect `show` by this point (lmIconCardSync
      // sets it before calling lmIconCardPlace) so the measurement below sees
      // real, laid-out content rather than a display:none box.
      lmSyncFlipHostHeight(zoom, card, show);
      lmSyncFlipHostWidth(zoom, card, show);
    } else {
      // Portrait: the card must NOT live inside .lm-main. .lm-main is
      // position:relative and hosts #lm-mobile-zoom as an absolutely
      // positioned bottom-right column (zoom/rotate/tilt controls) — growing
      // .lm-main by appending the card into it re-anchors that column to the
      // card's corner instead of the map's, dragging a shipped control off
      // the map and over the card's own buttons. .lm-main also has
      // overflow:hidden, so a card appended there risks being clipped
      // outright. Instead insert the card as a sibling of .lm-main, directly
      // after it, inside .lm-wrapper — at <=991px .lm-wrapper is
      // flex-direction:column (sections/locationmap.liquid:1363), so it
      // stacks below the print and above the side panel, and .lm-main keeps
      // its own height so the zoom column stays put.
      var main = document.querySelector('.lm-main');
      var wrapper = main ? main.parentNode : null;
      // Guard with contains() before remove(): DOMTokenList.remove() writes
      // the class attribute (and so emits a MutationRecord) even when the
      // token was never present. Called unconditionally on every sync this
      // fired 60x/sec against #lm-mobile-zoom, which the observer above
      // watches — observer -> rAF -> lmIconCardSync -> lmIconCardPlace ->
      // remove() -> observer, a self-sustaining loop. Only write when there
      // is an actual state change.
      if (zoom && (zoom.classList.contains('lm-flip-host') || zoom.classList.contains('lm-flip-flipped'))) {
        zoom.classList.remove('lm-flip-host', 'lm-flip-flipped');
      }
      // Defensive: a rotation from landscape(open) straight to portrait skips
      // the landscape branch's own show=false call, which is what normally
      // clears this. An explicit height/width left behind here would
      // otherwise pin #lm-mobile-zoom's landscape-strip dimensions onto the
      // unrelated portrait right-edge zoom column.
      if (zoom && zoom.style.height) { zoom.style.height = ''; }
      if (zoom && zoom.style.width) { zoom.style.width = ''; }
      if (card.classList.contains('lm-flip-back')) { card.classList.remove('lm-flip-back'); }
      // No-op when the card is already exactly where it belongs, so
      // repeated syncs (lmIconCardSync runs on every selection change and
      // resize) don't thrash the DOM.
      // On the phone preview stage (lm-stage.js) the preview is sticky and
      // paints over whatever follows it, so the card overlays the print
      // inside the panel instead (absolutely positioned, lm-stage.css): it no
      // longer grows .lm-main, which is what the note above guards against.
      var onStage = main && main.classList.contains('lm-stage') &&
        window.matchMedia && window.matchMedia('(max-width: 991px)').matches;
      if (onStage) {
        if (card.parentNode !== main) main.appendChild(card);
      } else if (wrapper && main && card.previousSibling !== main) {
        wrapper.insertBefore(card, main.nextSibling);
      }
      if (show) {
        // Only (re)play the entry animation on an actual closed->open
        // transition. Applying lm-card-entering on every sync — even while
        // already shown — forced a reflow and restarted the 200ms entry
        // animation every frame, so the card never advanced past its
        // opening frame (opacity stuck at 0).
        if (!_iconCardWasShown) {
          card.classList.remove('lm-card-entering');
          // Reflow so the animation restarts on every open.
          void card.offsetWidth;
          card.classList.add('lm-card-entering');
        }
      } else {
        card.classList.remove('lm-card-entering');
      }
      _iconCardWasShown = show;
    }
  }
  function lmIconCardSync() {
    var card = lmIconCardEl();
    if (!card) return;
    var sel = lmGetSelectedIcon();
    var show = lmIconCardShouldShow(sel);
    // Unhide/hide before lmIconCardPlace runs: in the landscape branch it
    // measures the card's real (laid-out) height to size the flip host, and
    // a still-[hidden] card (display:none) measures as 0.
    card.hidden = !show;
    lmIconCardPlace(show);
    if (!show) {
      card.hidden = true;
      var pk = document.getElementById('lm-icon-card-picker');
      if (pk) pk.hidden = true;
      var eb = document.getElementById('lm-icon-card-emoji');
      if (eb) eb.setAttribute('aria-expanded', 'false');
      return;
    }
    card.hidden = false;
    lmSetGlyphEl(document.getElementById('lm-icon-card-emoji'), sel);
    lmShowRotationControls(!sel.photo);
    var lab = document.getElementById('lm-icon-card-label');
    if (lab && document.activeElement !== lab) lab.value = sel.label || '';
    lmSyncCardSizeButtons(sel.size || iconSize);
    // If the picker is open while the selection changes (e.g. the customer
    // taps a different icon's marker without closing the picker first), it
    // is still showing the OLD icon's glyph highlighted. Rebuild it so the
    // active cell follows the new selection — the click itself already
    // switches the right icon via lmSwitchSelectedEmoji, this just keeps the
    // highlight honest. Rebuild only when the selection actually changed
    // since the last build (tracked via picker.dataset.builtFor) — this runs
    // on every resize and on every surviving chrome-observer tick, and a
    // full innerHTML rebuild is wasteful (and, combined with an unguarded
    // observer, was previously the source of a rebuild loop) when nothing
    // about the selection moved.
    var openPicker = document.getElementById('lm-icon-card-picker');
    if (openPicker && !openPicker.hidden && openPicker.dataset.builtFor !== sel.id) {
      lmBuildPicker(openPicker, 'switch');
      lmAddPickerClose(openPicker);
      openPicker.dataset.builtFor = sel.id;
    }
  }
  // Highlight the size step nearest the icon's px size — legacy orders carry
  // arbitrary slider values, so snap the highlight, not the stored size.
  function lmSyncCardSizeButtons(px) {
    var btns = document.querySelectorAll('#lm-icon-card-sizes button');
    var best = null, bestDiff = Infinity;
    btns.forEach(function (b) {
      var d = Math.abs(parseInt(b.dataset.size, 10) - px);
      if (d < bestDiff) { bestDiff = d; best = b; }
    });
    btns.forEach(function (b) { b.classList.toggle('active', b === best); });
  }
  function lmBindIconCard() {
    var card = lmIconCardEl();
    if (!card) return;

    // "Your photo" straight on the card (not only inside the emoji picker),
    // with a "New" badge until the customer has used it once on this device.
    // Built here, not in the section, which is close to Shopify's 256KB cap.
    var emTile = document.getElementById('lm-icon-card-emoji');
    if (emTile && !document.getElementById('lm-icon-card-photo')) {
      var camBtn = document.createElement('button');
      camBtn.type = 'button';
      camBtn.id = 'lm-icon-card-photo';
      camBtn.className = 'lm-icon-card-photo';
      camBtn.setAttribute('aria-label', 'Use your own photo');
      camBtn.innerHTML = '<svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><rect x="3" y="6" width="18" height="14" rx="2.5"/><circle cx="12" cy="13" r="3.6"/><path d="M8.5 6l1.4-2h4.2l1.4 2"/></svg>';
      var seen = false;
      try { seen = localStorage.getItem('lm_photo_marker_seen') === '1'; } catch (e) {}
      if (!seen) {
        var badge = document.createElement('span');
        badge.className = 'lm-icon-card-photo-new';
        badge.textContent = 'New';
        camBtn.appendChild(badge);
      }
      camBtn.addEventListener('click', function () {
        try { localStorage.setItem('lm_photo_marker_seen', '1'); } catch (e) {}
        var b = camBtn.querySelector('.lm-icon-card-photo-new'); if (b) b.remove();
        lmPhotoMarkerStart(lmGetSelectedIcon());
      });
      emTile.parentNode.insertBefore(camBtn, emTile.nextSibling);
    }

    var lab = document.getElementById('lm-icon-card-label');
    if (lab) lab.addEventListener('input', function () {
      var sel = lmGetSelectedIcon(); if (!sel) return;
      sel.label = (lab.value || '').slice(0, LM_ICON_LABEL_MAX);
      lmApplyLabelToMarker(sel);
      var panelInput = document.getElementById('lm-icon-label-input');
      if (panelInput) panelInput.value = sel.label;
      scheduleLM3DRefresh(600);
      if (typeof scheduleLmAutosave === 'function') scheduleLmAutosave();
    });

    var sizes = document.getElementById('lm-icon-card-sizes');
    if (sizes) sizes.addEventListener('click', function (ev) {
      var btn = ev.target.closest('button[data-size]'); if (!btn) return;
      var sel = lmGetSelectedIcon(); if (!sel) return;
      sel.size = parseInt(btn.dataset.size, 10);
      lmApplyIconSize(sel);
      lmSyncCardSizeButtons(sel.size);
      lmSyncIconSizeButtons(sel.size);
      scheduleLM3DRefresh(500);
      if (typeof scheduleLmAutosave === 'function') scheduleLmAutosave();
    });

    var ccw = document.getElementById('lm-icon-card-ccw');
    if (ccw) ccw.addEventListener('click', function () {
      var sel = lmGetSelectedIcon(); if (!sel) return;
      lmSetIconRotation((sel.rotation || 0) - 15);
    });
    var cw = document.getElementById('lm-icon-card-cw');
    if (cw) cw.addEventListener('click', function () {
      var sel = lmGetSelectedIcon(); if (!sel) return;
      lmSetIconRotation((sel.rotation || 0) + 15);
    });

    var del = document.getElementById('lm-icon-card-delete');
    if (del) del.addEventListener('click', function () {
      if (selectedIconId) removeIcon(selectedIconId);
    });

    var done = document.getElementById('lm-icon-card-done');
    if (done) done.addEventListener('click', function () { window.lmDeselectIcon(); });

    var emBtn = document.getElementById('lm-icon-card-emoji');
    var picker = document.getElementById('lm-icon-card-picker');
    if (emBtn && picker) emBtn.addEventListener('click', function () {
      var open = picker.hidden;
      if (open) {
        lmBuildPicker(picker, 'switch');
        lmAddPickerClose(picker);
        // Keep this in step with lmIconCardSync's own builtFor bookkeeping,
        // so opening the picker and a sync tick immediately after agree on
        // whether a rebuild is still owed.
        var sel = lmGetSelectedIcon();
        picker.dataset.builtFor = sel ? sel.id : '';
      }
      picker.hidden = !open;
      emBtn.setAttribute('aria-expanded', open ? 'true' : 'false');
      // Landscape only: the picker grid changes the card's content
      // height/width while the flip host is already open, so its explicit
      // height (set by lmSyncFlipHostHeight in lmIconCardPlace) and width
      // (lmSyncFlipHostWidth) need to grow/shrink with it too, or the grid
      // clips against the stale, smaller size.
      var lmZoom = document.getElementById('lm-mobile-zoom');
      if (lmZoom && lmZoom.classList.contains('lm-flip-flipped')) {
        lmSyncFlipHostHeight(lmZoom, card, true);
        lmSyncFlipHostWidth(lmZoom, card, true);
      }
    });

    // Print orientation decides which host the card lives in, and the
    // orientation buttons change it without touching the selection.
    // rAF-throttled: iOS fires resize repeatedly as the address bar
    // collapses/expands during scroll.
    var _cardResizeRaf = 0;
    window.addEventListener('resize', function () {
      if (_cardResizeRaf) return;
      _cardResizeRaf = requestAnimationFrame(function () {
        _cardResizeRaf = 0;
        lmIconCardSync();
      });
    });
    lmIconCardObserveChrome();
  }

  // ── Add-icon cloud (phone preview toolbar) ──
  // The toolbar's add-icon button used to drop a 📍 at the map centre on
  // the first tap. It now opens the picker as a cloud over the print, and
  // nothing is placed until the customer picks an icon or their photo; the
  // ✕ or a tap anywhere else closes it with nothing added. Positioned from
  // the button's live box, because lm-stage.css moves that button with the
  // stage (frame bottom, landscape row).
  function lmAddIconPickerOpen(open) {
    var pk = document.getElementById('lm-add-icon-picker');
    var btn = document.getElementById('lm-add-icon-btn');
    if (!pk || !btn) return;
    if (open) {
      lmBuildPicker(pk, 'add');
      lmAddPickerClose(pk);
      var main = pk.parentElement;
      var mW = main.clientWidth, mH = main.clientHeight;
      var w = Math.min(mW - 16, 400);
      var bx = btn.offsetLeft + btn.offsetWidth / 2;
      var left = Math.max(8, Math.min(bx - 30, mW - 8 - w));
      pk.style.width = w + 'px';
      pk.style.left = left + 'px';
      pk.style.bottom = (mH - btn.offsetTop + 10) + 'px';
      pk.style.setProperty('--lm-cloud-tail-x', Math.round(bx - left) + 'px');
    }
    pk.hidden = !open;
    btn.setAttribute('aria-expanded', open ? 'true' : 'false');
  }
  function lmBindAddIconPicker() {
    var pk = document.getElementById('lm-add-icon-picker');
    var btn = document.getElementById('lm-add-icon-btn');
    if (!pk || !btn) return;
    btn.setAttribute('aria-haspopup', 'true');
    btn.setAttribute('aria-expanded', 'false');
    // A pick (icon or photo) or the ✕ closes the cloud. The icon's own
    // handler runs first (it is on the button), so the new icon is already
    // placed and selected when this hides the cloud.
    pk.addEventListener('click', function (e) {
      if (e.target.closest('.lm-picker-grid button, .lm-photo-marker-btn, .lm-picker-close')) lmAddIconPickerOpen(false);
    });
    document.addEventListener('pointerdown', function (e) {
      if (pk.hidden) return;
      if (pk.contains(e.target) || btn.contains(e.target)) return;
      lmAddIconPickerOpen(false);
    }, true);
  }

  // ── Add-icon button animation ──
  // A glossy 3D honey pin on a little floating map tile: every few seconds it
  // hops and squashes on landing, a ripple crosses the map and the "+" badge
  // pops (assets/lm-add-icon-anim.js, which sets window.SkylitLmAddIconAnim to
  // the Lottie data: a .js asset because Shopify re-indents .json assets. The
  // 3D parts are three.js renders embedded as WebP, built by skylit-admin
  // scripts/lm-lottie: render_pin3d.mjs + gen_add_icon3d_lottie.py). Phones only, since that is the
  // only place the button shows. The player (the theme's lottie-svg, 62 KB
  // gzipped) loads once the page has gone idle after the map; the static SVG
  // pin shows until then. It plays the hop (frames 0-150; 150 looks the same
  // as 0), rests 3 s and goes again, skipping turns while the tab is hidden or
  // the button is faded out. Reduced motion gets the still first frame.
  var LM_ADD_ANIM_REST_MS = 3000;
  function lmAddIconAnimInit() {
    var btn = document.getElementById('lm-add-icon-btn');
    var box = btn && btn.querySelector('.lm-add-icon-anim');
    if (!box || !btn.dataset.animSrc || !window.matchMedia) return;
    if (!window.matchMedia('(max-width: 991px)').matches) return;
    var still = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    function loadScript(src, global) {
      if (window[global]) return Promise.resolve(window[global]);
      return new Promise(function (resolve, reject) {
        var sc = document.createElement('script');
        sc.src = src;
        sc.async = true;
        sc.onload = function () { window[global] ? resolve(window[global]) : reject(new Error('no ' + global)); };
        sc.onerror = reject;
        document.head.appendChild(sc);
      });
    }
    function start() {
      Promise.all([loadScript(btn.dataset.lottieSrc, 'lottie'), loadScript(btn.dataset.animSrc, 'SkylitLmAddIconAnim')])
        .then(function (res) {
          var anim = res[0].loadAnimation({ container: box, renderer: 'svg', loop: false, autoplay: false, animationData: JSON.parse(JSON.stringify(res[1])) });
          var timer = 0;
          function shown() {
            if (document.hidden || !btn.offsetParent) return false;
            var cs = getComputedStyle(btn);
            return cs.opacity !== '0' && cs.visibility !== 'hidden';
          }
          function hop(from) {
            clearTimeout(timer);
            if (still) return;
            anim.playSegments([from || 0, 150], true);
          }
          anim.addEventListener('DOMLoaded', function () {
            anim.goToAndStop(0, true);
            btn.classList.add('lm-add-icon-btn--anim');
            if (!still) timer = setTimeout(function () { hop(0); }, 1200);
          });
          anim.addEventListener('complete', function () {
            timer = setTimeout(function next() {
              if (shown()) hop(0); else timer = setTimeout(next, LM_ADD_ANIM_REST_MS);
            }, LM_ADD_ANIM_REST_MS);
          });
          // A tap gets the hop at once (from the crouch), as the cloud opens.
          btn.addEventListener('click', function () { hop(60); });
        })
        .catch(function (e) { console.warn('[lm] add-icon animation unavailable', e); });
    }
    var go = function () { (window.requestIdleCallback || function (f) { setTimeout(f, 1500); })(start, { timeout: 4000 }); };
    if (document.readyState === 'complete') go(); else window.addEventListener('load', go, { once: true });
  }

  // ── Emoji picker ──
  // Renders LM_EMOJI_CATEGORIES into `container`. mode 'place' adds a new icon
  // at the map centre; mode 'switch' changes the selected icon's glyph; mode
  // 'add' is 'place' for the toolbar's add-icon cloud, whose photo button goes
  // straight to the photo chooser (the customer has already asked to add
  // something). All modes show the SAME set — a customer must never be able
  // to switch to a glyph they cannot place.
  function lmBuildPicker(container, mode) {
    if (!container) return;
    container.innerHTML = '';
    var tabs = document.createElement('div');
    tabs.className = 'lm-picker-tabs';
    var grid = document.createElement('div');
    grid.className = 'lm-picker-grid';
    container.appendChild(tabs);
    container.appendChild(grid);

    // "Your photo" sits above the emoji tabs in the place picker: a customer
    // photo cropped to a circle becomes a marker (lmPhotoMarkerStart).
    // Both pickers: the desktop place palette adds a new photo marker; the
    // phone's quick card ('switch') turns the selected icon into one.
    if (mode === 'place' || mode === 'switch' || mode === 'add') {
      var photoBtn = document.createElement('button');
      photoBtn.type = 'button';
      photoBtn.className = 'lm-photo-marker-btn';
      if (mode === 'place') photoBtn.setAttribute('data-photo-place', '');
      photoBtn.innerHTML = '<svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><rect x="3" y="5" width="18" height="15" rx="2"/><circle cx="12" cy="12.5" r="3.5"/><path d="M8 5l1.5-2h5L16 5"/></svg><span>Add your photo</span>';
      photoBtn.addEventListener('click', function () {
        if (mode === 'switch') { lmPhotoMarkerStart(lmGetSelectedIcon()); return; }
        if (mode === 'add') { lmPhotoMarkerStart(null); return; }
        // First tap shows the sample pin on the map; the next (or a tap on
        // the pin) opens the photo picker.
        if (lmPhotoSampleOffer()) return;
        lmPhotoMarkerStart(null);
      });
      container.insertBefore(photoBtn, tabs);
    }

    function paint(catIndex) {
      var cat = LM_EMOJI_CATEGORIES[catIndex];
      tabs.querySelectorAll('.lm-picker-tab').forEach(function (t, i) {
        t.classList.toggle('active', i === catIndex);
      });
      grid.innerHTML = '';
      var sel = (mode === 'switch') ? lmGetSelectedIcon() : null;
      cat.icons.forEach(function (ic, i) {
        var b = document.createElement('button');
        b.type = 'button';
        b.textContent = ic.emoji;
        b.setAttribute('aria-label', ic.id);
        b.dataset.iconId = ic.id;
        // Cap the stagger so the last cell is never far behind the first.
        b.style.setProperty('--i', Math.min(i, 6));
        if (sel && sel.icon === ic.id) b.classList.add('active');
        b.addEventListener('click', function () {
          if (mode === 'switch') lmSwitchSelectedEmoji(ic.id, ic.emoji);
          else addIcon(ic.id, ic.emoji);
        });
        grid.appendChild(b);
      });
    }

    LM_EMOJI_CATEGORIES.forEach(function (cat, i) {
      var t = document.createElement('button');
      t.type = 'button';
      t.className = 'lm-picker-tab';
      t.textContent = cat.label;
      t.addEventListener('click', function () { paint(i); });
      tabs.appendChild(t);
    });
    paint(0);
  }

  // The card's picker doubles as a popover in portrait, so it gets its own
  // close affordance on top of re-tapping the emoji tile. Appended after each
  // build rather than living in the section's markup because lmBuildPicker
  // clears the container (innerHTML = '') every time it runs, which would
  // throw away anything static in there.
  function lmAddPickerClose(picker) {
    if (!picker || picker.querySelector('.lm-picker-close')) return;
    var x = document.createElement('button');
    x.type = 'button';
    x.className = 'lm-picker-close';
    x.setAttribute('aria-label', 'Close icon picker');
    x.innerHTML = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" aria-hidden="true"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>';
    x.addEventListener('click', function () {
      picker.hidden = true;
      var em = document.getElementById('lm-icon-card-emoji');
      if (em) em.setAttribute('aria-expanded', 'false');
    });
    picker.appendChild(x);
  }

  // Change the selected icon's glyph, keeping size, rotation, label and
  // position. Writes the same two fields the placement flow writes.
  function lmSwitchSelectedEmoji(id, emoji) {
    var sel = lmGetSelectedIcon(); if (!sel) return;
    var wasPhoto = !!sel.photo;
    sel.icon = id;
    sel.emoji = emoji;
    if (wasPhoto) {
      // A photo marker has no emoji span to rewrite: rebuild it as an emoji.
      delete sel.photo;
      try { sel.marker && sel.marker.remove(); } catch (e) {}
      _attachMarkerForIcon(sel);
    } else if (sel.marker && sel.marker.getElement) {
      var span = sel.marker.getElement().querySelector('.lm-marker-emoji');
      if (span) span.textContent = emoji;
    }
    lmSetGlyphEl(document.getElementById('lm-icon-card-emoji'), sel);
    lmSetGlyphEl(document.getElementById('lm-icon-editor-emoji'), sel);
    updatePlacedIconsUI();
    scheduleLM3DRefresh(300);
    if (typeof scheduleLmAutosave === 'function') scheduleLmAutosave();
  }
  function lmSyncIconEditor() {
    lmIconCardSync();
    var panel = document.getElementById('lm-icon-editor');
    if (!panel) return;
    var sel = lmGetSelectedIcon();
    if (!sel) { panel.style.display = 'none'; return; }
    panel.style.display = 'block';
    lmSetGlyphEl(document.getElementById('lm-icon-editor-emoji'), sel);
    lmShowRotationControls(!sel.photo);
    lmSyncIconSizeButtons(sel.size || iconSize);
    var labelInput = document.getElementById('lm-icon-label-input');
    if (labelInput && document.activeElement !== labelInput) labelInput.value = sel.label || '';
    lmApplyRotationUI(sel.rotation || 0);
  }
  // Highlight the size step nearest the icon's px size (legacy orders carry
  // arbitrary slider values — snap the highlight, not the stored size).
  function lmSyncIconSizeButtons(px) {
    var btns = document.querySelectorAll('#lm-icon-size-btns .lm-icon-size-btn');
    var best = null, bestDiff = Infinity;
    btns.forEach(function (b) {
      var d = Math.abs(parseInt(b.dataset.size, 10) - px);
      if (d < bestDiff) { bestDiff = d; best = b; }
    });
    btns.forEach(function (b) { b.classList.toggle('active', b === best); });
  }
  function lmSetIconRotation(deg) {
    var sel = lmGetSelectedIcon(); if (!sel || sel.photo) return;
    deg = ((deg % 360) + 360) % 360;
    sel.rotation = deg;
    if (sel.marker) sel.marker.setRotation(deg);
    lmApplyRotationUI(deg);
    scheduleLM3DRefresh(300);
  }
  // Single place that turns a per-icon size into a rendered marker size,
  // including the supersample multiplier. Both the panel buttons and the
  // mobile bar route through this.
  function lmApplyIconSize(sel) {
    if (!sel) return;
    var el = sel.marker && sel.marker.getElement && sel.marker.getElement();
    if (el) el.style.fontSize = ((sel.size || iconSize) * lmIconSSMult) + 'px';
    lmApplyLabelToMarker(sel);   // label scales with the icon
  }
  function lmNudgeSelected(dx, dy) {
    var sel = lmGetSelectedIcon(); if (!sel || !map || !sel.marker) return;
    var p = map.project(sel.marker.getLngLat());
    var ll = map.unproject([p.x + dx, p.y + dy]);
    sel.lat = ll.lat; sel.lng = ll.lng;
    sel.marker.setLngLat([ll.lng, ll.lat]);
    scheduleLM3DRefresh(300);
  }
  function lmBindNudge(id, dx, dy) {
    var b = document.getElementById(id); if (!b) return;
    var timer=null, iv=null;
    function start(ev){ ev.preventDefault(); lmNudgeSelected(dx,dy);
      timer=setTimeout(function(){ iv=setInterval(function(){ lmNudgeSelected(dx,dy); }, 60); }, 300); }
    function stop(){ clearTimeout(timer); clearInterval(iv); }
    b.addEventListener('mousedown', start); b.addEventListener('touchstart', start, {passive:false});
    ['mouseup','mouseleave','touchend','touchcancel'].forEach(function(e){ b.addEventListener(e, stop); });
  }

  // ── Drag magnifier loupe ──
  // Samples the LIVE GL canvas on every pointermove — no offscreen snapshot.
  // Measured in Chrome on the STAGE-REF 3D-landmark map (GL canvas
  // 5280x6704, 8x supersample behind a 660x838 CSS box): drawImage from the
  // live canvas costs 0.010ms, vs 0.012ms from an offscreen snapshot copy —
  // no measurable win from snapshotting. What actually cost time was the
  // getImageData blank-frame probe (0.645ms, the dominant per-move cost
  // in an earlier version that ran it every move); this version runs that
  // probe exactly ONCE, at dragstart. The snapshot's real price was ~135MB
  // held for the session (5280x6704x4 bytes) on a product with documented
  // iOS Safari WebGL memory pressure, for zero speed benefit — so it's gone.
  // Sampling live also means freshly streamed tiles show up automatically,
  // with nothing to keep fresh.
  /* @lm-test-export:start lmLoupeGeometry */
  // Pure: where the loupe card sits (client px) and which slice of the map
  // canvas to magnify (canvas device px). cx/cy are the marker centre in
  // client px; cRect is the map canvas' bounding rect; canvasW is its backing
  // store width, from which the device-pixel ratio is derived.
  function lmLoupeGeometry(cx, cy, cRect, canvasW, R, Z) {
    var dpr = (cRect.width > 0 ? canvasW / cRect.width : 1) || 1;
    var srcW = (R * 2 / Z) * dpr;
    return {
      left: cx - R - 70,
      top:  cy - R - 90,
      srcX: (cx - cRect.left) * dpr - srcW / 2,
      srcY: (cy - cRect.top) * dpr - srcW / 2,
      srcW: srcW,
      srcH: srcW
    };
  }
  /* @lm-test-export:end lmLoupeGeometry */

  var _loupe = { el:null, cv:null, ctx:null, R:52, Z:2, glCanvas:null,
                 cRect:null, canvasW:0, raf:0, pending:null, on:false };

  function lmLoupeStart(markerEl) {
    try {
      if (!map || !map.getCanvas()) return;
      if (!_loupe.el) {
        _loupe.el = document.createElement('div');
        _loupe.el.className = 'lm-loupe';
        _loupe.el.style.cssText = 'position:fixed;left:0;top:0;width:' + (_loupe.R * 2) +
          'px;height:' + (_loupe.R * 2) + 'px;border-radius:50%;border:3px solid #fff;' +
          'box-shadow:0 4px 16px rgba(0,0,0,.4);overflow:hidden;pointer-events:none;' +
          'z-index:9999;display:none;will-change:transform;';
        _loupe.cv = document.createElement('canvas');
        _loupe.cv.width = _loupe.R * 2;
        _loupe.cv.height = _loupe.R * 2;
        _loupe.el.appendChild(_loupe.cv);
        _loupe.ctx = _loupe.cv.getContext('2d');
        document.body.appendChild(_loupe.el);
      }
      if (!_loupe.ctx) { _loupe.on = false; return; }
      var mapCanvas = map.getCanvas();
      if (!mapCanvas || !mapCanvas.width || !mapCanvas.height) { _loupe.on = false; return; }
      _loupe.cRect = mapCanvas.getBoundingClientRect();
      _loupe.canvasW = mapCanvas.width;
      _loupe.glCanvas = mapCanvas;
      // Blank-frame guard, run ONCE per drag (not per pointermove) against
      // the live GL canvas: draw the marker's current sample region into the
      // small loupe canvas itself and read back its centre pixel. A tainted
      // or lost context is caught by the surrounding try/catch, which
      // suppresses the loupe for this drag.
      var R = _loupe.R;
      var mRect = markerEl ? markerEl.getBoundingClientRect() : _loupe.cRect;
      var g = lmLoupeGeometry(mRect.left + mRect.width / 2, mRect.top + mRect.height / 2,
                              _loupe.cRect, _loupe.canvasW, R, _loupe.Z);
      _loupe.ctx.clearRect(0, 0, R * 2, R * 2);
      _loupe.ctx.drawImage(_loupe.glCanvas, g.srcX, g.srcY, g.srcW, g.srcH, 0, 0, R * 2, R * 2);
      var mid = _loupe.ctx.getImageData(R, R, 1, 1).data;
      if (mid[3] === 0) { _loupe.on = false; return; }
      _loupe.on = true;
      _loupe.el.style.display = 'block';
    } catch (e) { _loupe.on = false; }
  }

  // Coalesce a burst of pointermove-driven drag events into one paint per frame.
  function lmLoupeMove(markerEl, emoji, photoImg) {
    if (!_loupe.on || !_loupe.el) return;
    _loupe.pending = { el: markerEl, emoji: emoji, photo: photoImg || null };
    if (_loupe.raf) return;
    _loupe.raf = requestAnimationFrame(lmLoupePaint);
  }

  function lmLoupePaint() {
    _loupe.raf = 0;
    var p = _loupe.pending;
    _loupe.pending = null;
    if (!p || !_loupe.on) return;
    var R = _loupe.R, ctx = _loupe.ctx;
    var mRect = p.el.getBoundingClientRect();
    var g = lmLoupeGeometry(mRect.left + mRect.width / 2, mRect.top + mRect.height / 2,
                            _loupe.cRect, _loupe.canvasW, R, _loupe.Z);
    _loupe.el.style.transform = 'translate3d(' + g.left + 'px,' + g.top + 'px,0)';
    ctx.clearRect(0, 0, R * 2, R * 2);
    try {
      ctx.drawImage(_loupe.glCanvas, g.srcX, g.srcY, g.srcW, g.srcH, 0, 0, R * 2, R * 2);
    } catch (e) { lmLoupeEnd(); return; }
    ctx.strokeStyle = 'rgba(0,0,0,.45)'; ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(R - 8, R); ctx.lineTo(R + 8, R);
    ctx.moveTo(R, R - 8); ctx.lineTo(R, R + 8);
    ctx.stroke();
    if (p.photo) { lmDrawPhotoMarker(ctx, p.photo, R, R, 30); return; }
    ctx.font = '26px serif'; ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
    ctx.fillText(p.emoji || '📍', R, R - 2);
  }

  function lmLoupeEnd() {
    _loupe.on = false;
    if (_loupe.raf) { cancelAnimationFrame(_loupe.raf); _loupe.raf = 0; }
    _loupe.pending = null;
    _loupe.glCanvas = null;
    if (_loupe.el) _loupe.el.style.display = 'none';
  }

  // ── Supersample size compensation (see lmIconSSMult) ──
  // Re-size every placed marker to its per-icon size × the current SS factor.
  function lmReapplyIconSizes() {
    placedIcons.forEach(function(p) {
      var el = p.marker && p.marker.getElement();
      if (el) el.style.fontSize = ((p.size || iconSize) * lmIconSSMult) + 'px';
      lmApplyLabelToMarker(p); // label font-size carries the SS factor too
    });
  }
  // Wrap a Mapbox text-size expression so labels grow with the SS factor. The
  // add-label functions and buildStyle pass their text-size through this, so
  // labels are baked at the correct size for the current SS scale. (getStyle()
  // throws while 3D-building tiles stream, so we can't post-multiply live
  // layers reliably — we rebuild them instead.)
  // Scale a zoom `interpolate` text-size by lmIconSSMult. Mapbox forbids a
  // zoom expression inside arithmetic (['*', n, interpolate] throws and the
  // layer silently fails to add), so we multiply the interpolate's OUTPUT stops
  // instead and keep `interpolate` at the top level. Shape is always
  // ['interpolate', ['linear'], ['zoom'], in0, out0, in1, out1, ...].
  function lmTS(expr) {
    if (lmIconSSMult === 1 || !Array.isArray(expr) || expr[0] !== 'interpolate') return expr;
    var out = expr.slice();
    for (var i = 4; i < out.length; i += 2) out[i] = out[i] * lmIconSSMult;
    return out;
  }
  // Re-add the active label layers so they pick up the current lmIconSSMult via
  // lmTS. Uses addLayer/removeLayer (robust mid-load), never getStyle().
  function lmReaddLabels() {
    if (!map) return;
    if (state.showLabels)      { try { removeLabelLayers(); } catch (e) {} try { addLabelLayers(); } catch (e) {} }
    if (state.showWaterLabels) { try { removeWaterLabels(); } catch (e) {} try { addWaterLabels(); } catch (e) {} }
    if (state.showRoadLabels)  { try { removeRoadLabels(); } catch (e) {} try { addRoadLabels(); } catch (e) {} }
  }
  function lmApplySSSizes() { lmReapplyIconSizes(); lmReaddLabels(); }

  // Mobile (≤991px): the four helper buttons (info / message / font / save)
  // fly out of a single top-right button instead of sitting permanently down
  // the preview's edge. Ported from starmap-v2.js bindToolbarFab(); the CSS
  // hides the button ≥992px, so everything here is inert on desktop.
  // .lm-main carries both the open flag and the gallery's .sm-gallery-viewing
  // (it is the .app__main starmap-gallery.js targets).
  function bindToolbarFab() {
    var main = document.querySelector('.lm-main');
    var fab = document.getElementById('lm-toolbar-fab');
    var toolbar = document.getElementById('lm-floating-toolbar');
    if (!main || !fab || !toolbar) return;

    function isOpen() { return main.classList.contains('lm-tools-open'); }
    function onOutside(e) {
      if (fab.contains(e.target) || toolbar.contains(e.target)) return;
      setOpen(false);
    }
    function onKey(e) {
      if (e.key !== 'Escape') return;
      setOpen(false);
      fab.focus();
    }
    function setOpen(open) {
      main.classList.toggle('lm-tools-open', open);
      fab.setAttribute('aria-expanded', open ? 'true' : 'false');
      fab.setAttribute('aria-label', open ? 'Hide tools' : 'Show tools');
      if (open) {
        document.addEventListener('pointerdown', onOutside, true);
        document.addEventListener('keydown', onKey);
      } else {
        document.removeEventListener('pointerdown', onOutside, true);
        document.removeEventListener('keydown', onKey);
      }
    }

    fab.addEventListener('click', function () { setOpen(!isOpen()); });

    // Info and Save open a modal, so the flyout has served its purpose. Message
    // and Font cycle through options — those get tapped repeatedly, so leave
    // the flyout up rather than making the customer reopen it every step.
    ['.lm-info-btn', '.lm-save-btn'].forEach(function (sel) {
      var btn = toolbar.querySelector(sel);
      if (btn) btn.addEventListener('click', function () { setOpen(false); });
    });

    // The gallery viewer replaces the print the flyout is anchored to, and the
    // section hides the toolbar outright in that state.
    if (window.MutationObserver) {
      new MutationObserver(function () {
        if (isOpen() && main.classList.contains('sm-gallery-viewing')) setOpen(false);
      }).observe(main, { attributes: true, attributeFilter: ['class'] });
    }
  }

  function bindIcons() {
    var grid = document.getElementById('lm-icons-grid');
    var removeAll = document.getElementById('lm-icons-remove-all');
    if (!grid) return;

    lmBuildPicker(grid, 'place');

    if (removeAll) {
      removeAll.addEventListener('click', function() {
        placedIcons.forEach(function(p) { p.marker.remove(); });
        placedIcons = [];
        selectedIconId = null;
        updatePlacedIconsUI();
        lmSyncIconEditor();
        scheduleLM3DRefresh();
      });
    }

    // Per-icon size — XS/S/M/L/XL steps, edits the currently selected icon only
    var sizeBtns = document.getElementById('lm-icon-size-btns');
    if (sizeBtns) {
      sizeBtns.addEventListener('click', function(e) {
        var btn = e.target.closest('.lm-icon-size-btn');
        if (!btn) return;
        var sel = lmGetSelectedIcon(); if (!sel) return;
        sel.size = parseInt(btn.dataset.size, 10);
        lmApplyIconSize(sel);
        lmSyncIconSizeButtons(sel.size);
        lmSyncCardSizeButtons(sel.size);
        scheduleLM3DRefresh(500);
        if (typeof scheduleLmAutosave === 'function') scheduleLmAutosave();
      });
    }

    // Per-icon label — short caption under the emoji, edits the selected icon
    var labelInput = document.getElementById('lm-icon-label-input');
    if (labelInput) {
      labelInput.addEventListener('input', function() {
        var sel = lmGetSelectedIcon(); if (!sel) return;
        sel.label = labelInput.value.slice(0, LM_ICON_LABEL_MAX);
        lmApplyLabelToMarker(sel);
        scheduleLM3DRefresh(600);
        if (typeof scheduleLmAutosave === 'function') scheduleLmAutosave();
      });
    }

    // Rotation — ±15° step buttons + draggable dial
    var ccw = document.getElementById('lm-icon-rot-ccw');
    var cw  = document.getElementById('lm-icon-rot-cw');
    if (ccw) ccw.addEventListener('click', function(){ var s=lmGetSelectedIcon(); if(s) lmSetIconRotation((s.rotation||0)-15); });
    if (cw)  cw.addEventListener('click',  function(){ var s=lmGetSelectedIcon(); if(s) lmSetIconRotation((s.rotation||0)+15); });
    var dial = document.getElementById('lm-icon-rot-dial');
    if (dial) {
      var dragging = false;
      var angleFrom = function(ev){
        var r = dial.getBoundingClientRect();
        var dcx = r.left + r.width/2, dcy = r.top + r.height/2;
        var pt = ev.touches ? ev.touches[0] : ev;
        return Math.atan2(pt.clientY - dcy, pt.clientX - dcx) * 180/Math.PI + 90;
      };
      var move = function(ev){ if(!dragging) return; ev.preventDefault(); lmSetIconRotation(angleFrom(ev)); };
      var up = function(){ dragging=false; document.removeEventListener('mousemove',move); document.removeEventListener('touchmove',move); document.removeEventListener('mouseup',up); document.removeEventListener('touchend',up); };
      dial.addEventListener('mousedown', function(ev){ dragging=true; lmSetIconRotation(angleFrom(ev)); document.addEventListener('mousemove',move); document.addEventListener('mouseup',up); });
      dial.addEventListener('touchstart', function(ev){ dragging=true; lmSetIconRotation(angleFrom(ev)); document.addEventListener('touchmove',move,{passive:false}); document.addEventListener('touchend',up); }, {passive:true});
    }

    // Position nudge pad (hold-to-repeat)
    lmBindNudge('lm-icon-nudge-up',    0, -LM_NUDGE_PX);
    lmBindNudge('lm-icon-nudge-down',  0,  LM_NUDGE_PX);
    lmBindNudge('lm-icon-nudge-left', -LM_NUDGE_PX, 0);
    lmBindNudge('lm-icon-nudge-right', LM_NUDGE_PX, 0);

    // Arrow keys nudge the selected icon (unless focus is in a text field)
    document.addEventListener('keydown', function(ev){
      if (!selectedIconId) return;
      var t = ev.target, tag = t && t.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || (t && t.isContentEditable)) return;
      var d = { ArrowUp:[0,-LM_NUDGE_PX], ArrowDown:[0,LM_NUDGE_PX], ArrowLeft:[-LM_NUDGE_PX,0], ArrowRight:[LM_NUDGE_PX,0] }[ev.key];
      if (!d) return;
      ev.preventDefault(); lmNudgeSelected(d[0], d[1]);
    });

    // "Done" closes the editor (deselects)
    ['lm-icon-editor-done', 'lm-icon-done-mobile'].forEach(function (id) {
      var doneBtn = document.getElementById(id);
      if (doneBtn) doneBtn.addEventListener('click', function(){ window.lmDeselectIcon(); });
    });

    ['lm-icon-delete', 'lm-icon-delete-mobile'].forEach(function (id) {
      var delBtn = document.getElementById(id);
      if (delBtn) delBtn.addEventListener('click', function () {
        if (selectedIconId) removeIcon(selectedIconId);
      });
    });

    // "Add icon" — opens the picker cloud (lmAddIconPickerOpen); the chosen
    // icon is placed at the map centre and selected, which opens the card to
    // size or label it. Without the cloud's markup it falls back to dropping
    // the generic 'pin' (a real catalogue id, Symbols: only catalogue ids
    // survive a reload and pass tests/lm-emoji-catalogue.test.mjs).
    var addIconBtn = document.getElementById('lm-add-icon-btn');
    if (addIconBtn) addIconBtn.addEventListener('click', function () {
      var pk = document.getElementById('lm-add-icon-picker');
      if (!pk) { addIcon('pin', '📍'); return; }
      lmAddIconPickerOpen(pk.hidden);
    });
    lmBindAddIconPicker();
    lmAddIconAnimInit();

    lmBindIconCard();
  }

  // ── Photo markers ──
  // A customer photo instead of an emoji: picked, framed in a circle (drag +
  // zoom), cropped to a 1024px square JPEG IN THE BROWSER and uploaded to
  // skylit-admin /api/location-marker-upload, which returns an immutable URL.
  // icons_json stores that URL as `photo` (never the image: the CF thumbnail
  // host carries the whole design in its URL). render_locationmap.py draws it
  // with the same ratios as .lm-marker-photo (PHOTO_MARKER_SCALE / _RING).
  var LM_PHOTO_MARKER_MAX = 3;
  // A face has to be far bigger than an emoji to read, so a photo circle is
  // 2.2x the icon size (1.25 at first, which Axel found too small): the same
  // XS-XL steps then give ~6% (M) / ~10% (L, the default) / ~15% (XL) of the
  // print width.
  var LM_PHOTO_MARKER_SCALE = 2.2;   // circle diameter / icon size
  var LM_PHOTO_URL_RE = /^https:\/\/admin\.skylitstudio\.co\.uk\/api\/location-marker-img\/[A-Za-z0-9-]{6,40}\/[a-z0-9]{6,24}$/;
  var LM_PHOTO_OUT = 1024;

  function lmShowRotationControls(on) {
    ['lm-icon-card-ccw', 'lm-icon-card-cw'].forEach(function (id) {
      var b = document.getElementById(id); if (b) b.style.display = on ? '' : 'none';
    });
    var dial = document.getElementById('lm-icon-rot-dial');
    var readout = document.getElementById('lm-icon-rot-readout');
    if (dial && dial.parentElement) dial.parentElement.style.display = on ? '' : 'none';
    if (readout && readout.parentElement) readout.parentElement.style.display = on ? '' : 'none';
  }

  // Photo pin onto a 2D canvas (loupe, 3D mockups): circle with a white ring,
  // pointer under it, soft shadow. (tx, ty) is the pointer's TIP — the spot
  // on the map — and d the circle's diameter, as on the live marker.
  function lmDrawPhotoMarker(ctx, img, tx, ty, d) {
    if (!img || !img.complete || !img.naturalWidth) return false;
    var r = d / 2, ring = Math.max(1.5, d * 0.07);
    var tailH = d * 0.14, tailW = d * 0.1, overlap = d * 0.03;
    var cx = tx, cy = ty - tailH - r + overlap;
    ctx.save();
    ctx.shadowColor = 'rgba(0,0,0,0.35)'; ctx.shadowBlur = d * 0.2; ctx.shadowOffsetY = d * 0.06;
    ctx.fillStyle = '#ffffff';
    ctx.beginPath(); ctx.arc(cx, cy, r, 0, Math.PI * 2); ctx.fill();
    ctx.beginPath(); ctx.moveTo(tx - tailW, cy + r - overlap * 2); ctx.lineTo(tx + tailW, cy + r - overlap * 2); ctx.lineTo(tx, ty); ctx.closePath(); ctx.fill();
    ctx.restore();
    ctx.save();
    ctx.beginPath(); ctx.arc(cx, cy, r - ring, 0, Math.PI * 2); ctx.clip();
    try { ctx.drawImage(img, cx - r + ring, cy - r + ring, (r - ring) * 2, (r - ring) * 2); } catch (e) {}
    ctx.restore();
    return true;
  }
  window.lmDrawPhotoMarker = lmDrawPhotoMarker;

  // The glyph for an icon in the card / editor / "Your icons" chip: the emoji,
  // or a small round thumbnail of the photo.
  function lmSetGlyphEl(el, ic) {
    if (!el) return;
    if (ic && ic.photo) {
      el.textContent = '';
      var im = document.createElement('img');
      im.src = ic.photo; im.alt = 'Your photo';
      im.className = 'lm-glyph-photo';
      el.appendChild(im);
    } else {
      el.textContent = (ic && ic.emoji) || '';
    }
  }

  function lmRandId(n) {
    var a = 'abcdefghijklmnopqrstuvwxyz0123456789', out = '';
    var buf = (window.crypto && crypto.getRandomValues) ? crypto.getRandomValues(new Uint8Array(n)) : null;
    for (var i = 0; i < n; i++) out += a[(buf ? buf[i] : Math.floor(Math.random() * 256)) % 36];
    return out;
  }
  function lmMarkerUploadToken() {
    var k = 'lm_marker_token_' + state.orderId, t = null;
    try { t = localStorage.getItem(k); } catch (e) {}
    if (!t) { t = lmRandId(32); try { localStorage.setItem(k, t); } catch (e) {} }
    return t;
  }

  // replaceSel: the phone card's picker passes the selected icon, which then
  // BECOMES the photo pin (same spot); otherwise a new pin is added.
  function lmPhotoMarkerStart(replaceSel) {
    var count = placedIcons.filter(function (p) { return !!p.photo && p !== replaceSel; }).length;
    if (count >= LM_PHOTO_MARKER_MAX) {
      lmPhotoToast('You can add up to ' + LM_PHOTO_MARKER_MAX + ' photos to a map.');
      return;
    }
    var input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.style.display = 'none';
    input.addEventListener('change', function () {
      var f = input.files && input.files[0];
      input.remove();
      if (f) lmPhotoCropOpen(f, replaceSel);
    });
    document.body.appendChild(input);
    input.click();
  }

  // ── Sample photo pin ──
  // A demo pin (illustrated couple + "Your photo here") shown on the map in
  // the Location step until the customer adds a photo of their own, so they
  // can see what a photo marker looks like before choosing one. Tapping it
  // opens the photo picker. It is NOT in placedIcons, so it never reaches
  // icons_json, the cart or the print; the 3D mockups and the print viewer
  // skip .lm-photo-sample.
  var LM_PHOTO_SAMPLE_SRC = 'data:image/svg+xml;charset=utf-8,' + encodeURIComponent(
    '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100">' +
    '<defs><linearGradient id="g" x1="0" y1="0" x2="0" y2="1"><stop offset="0" stop-color="#f3e6d3"/><stop offset="1" stop-color="#dcc3a1"/></linearGradient></defs>' +
    '<rect width="100" height="100" fill="url(#g)"/>' +
    '<circle cx="37" cy="44" r="13" fill="#a8835a"/><path d="M12 100c0-21 11-34 25-34s25 13 25 34z" fill="#a8835a"/>' +
    '<circle cx="63" cy="40" r="14" fill="#8a6a45"/><path d="M36 100c0-23 12-37 27-37s27 14 27 37z" fill="#8a6a45"/>' +
    '</svg>');
  var _lmPhotoSample = null;   // the mapboxgl.Marker while shown
  var _lmPhotoSampleAt = null; // where the customer dragged it, if they did

  // Off until asked for: the first tap on "Add your photo" turns it on, its x
  // or a placed photo turns it off.
  var _lmPhotoSampleOn = false;

  // "Add your photo" tapped: show the sample if it isn't up yet (and there is
  // no photo pin to learn from already). True = shown, so don't open the picker.
  function lmPhotoSampleOffer() {
    if (_lmPhotoSampleOn || !map || placedIcons.some(function (p) { return !!p.photo; })) return false;
    lmPhotoSampleSet(true);
    if (typeof lmRevealMapIfNeeded === 'function') lmRevealMapIfNeeded();
    return !!_lmPhotoSample;
  }

  function lmPhotoSampleSet(on) {
    _lmPhotoSampleOn = on;
    if (!on) _lmPhotoSampleAt = null;
    lmSyncPhotoSample();
    // While the sample is up, the button's next tap is the one that uploads.
    document.querySelectorAll('.lm-photo-marker-btn[data-photo-place] span').forEach(function (sp) {
      sp.textContent = (on && _lmPhotoSample) ? 'Choose your photo' : 'Add your photo';
    });
  }

  function lmPhotoSampleWanted() {
    if (!_lmPhotoSampleOn) return false;
    if (!map || placedIcons.some(function (p) { return !!p.photo; })) return false;
    var panel = document.querySelector('.lm-step-content[data-step-content="2"]');
    return !!(panel && panel.classList.contains('active'));
  }

  function lmSyncPhotoSample() {
    var want = lmPhotoSampleWanted();
    // A marker from a map that recreateMapForLandmarks tore down is dead.
    if (_lmPhotoSample && (!want || _lmPhotoSample._map !== map)) {
      try { _lmPhotoSample.remove(); } catch (e) {}
      _lmPhotoSample = null;
    }
    if (!want || _lmPhotoSample) return;
    var el = document.createElement('div');
    el.className = 'lm-photo-sample';
    el.setAttribute('role', 'button');
    el.setAttribute('aria-label', 'Sample photo pin. Tap to add your own photo');
    // Same pin as a real L-size photo marker (see _attachMarkerForIcon).
    el.style.fontSize = (48 * lmIconSSMult) + 'px';
    el.innerHTML =
      '<div class="lm-photo-sample__pin">' +
        '<img class="lm-photo-sample__img" alt="" draggable="false" src="' + LM_PHOTO_SAMPLE_SRC + '">' +
        '<div class="lm-photo-sample__tail"></div>' +
        '<button type="button" class="lm-photo-sample__x" aria-label="Hide the sample photo"><svg viewBox="0 0 10 10" aria-hidden="true"><path d="M2.6 2.6l4.8 4.8M7.4 2.6l-4.8 4.8"/></svg></button>' +
      '</div>' +
      '<span class="lm-photo-sample__label">Your photo here</span>';
    // Draggable like a real pin, so it can be tried in place; the photo the
    // customer then adds lands where they left it (addIcon). A drag must not
    // also count as the tap that opens the photo picker.
    var dragged = false;
    var closeBtn = el.querySelector('.lm-photo-sample__x');
    // Its own press must not start a marker drag (Mapbox listens on the element).
    ['mousedown', 'touchstart', 'pointerdown'].forEach(function (t) {
      closeBtn.addEventListener(t, function (ev) { ev.stopPropagation(); }, { passive: true });
    });
    closeBtn.addEventListener('click', function (ev) {
      ev.stopPropagation();
      lmPhotoSampleSet(false);
    });
    el.addEventListener('click', function (ev) {
      ev.stopPropagation();
      if (dragged) { dragged = false; return; }
      lmPhotoMarkerStart(null);
    });
    _lmPhotoSample = new mapboxgl.Marker({ element: el, draggable: true })
      .setLngLat(_lmPhotoSampleAt || lmPhotoSampleSpot()).addTo(map);
    el.__marker = _lmPhotoSample;
    _lmPhotoSample.on('dragstart', function () { dragged = true; });
    _lmPhotoSample.on('dragend', function () {
      _lmPhotoSampleAt = _lmPhotoSample && _lmPhotoSample.getLngLat();
      // Some browsers fire no click after a drag; clear the flag regardless.
      setTimeout(function () { dragged = false; }, 50);
    });
    // A new location search flies the map away: bring the sample back.
    if (!map.__lmPhotoSampleBound) {
      map.__lmPhotoSampleBound = true;
      map.on('moveend', function () {
        if (!_lmPhotoSample || _lmPhotoSample._map !== map) return;
        var c = map.getContainer(), pt = map.project(_lmPhotoSample.getLngLat());
        if (pt.x < 0 || pt.y < 0 || pt.x > c.clientWidth || pt.y > c.clientHeight) {
          _lmPhotoSample.setLngLat(lmPhotoSampleSpot());
          _lmPhotoSampleAt = null;
        }
      });
    }
  }

  // Up and right of centre, clear of the default heart and the title.
  function lmPhotoSampleSpot() {
    var c = map.getContainer();
    return map.unproject([c.clientWidth * 0.7, c.clientHeight * 0.3]);
  }

  function lmPhotoToast(msg) {
    var t = document.createElement('div');
    t.className = 'lm-photo-toast';
    t.textContent = msg;
    document.body.appendChild(t);
    setTimeout(function () { t.remove(); }, 3200);
  }

  // Framing modal: a square stage with a circular window. The photo covers the
  // stage (cover-fit, like the custom print's renderImage), drag moves it,
  // the slider zooms 1-4x around the centre, and the offset is clamped so the
  // circle is always full. Output: the stage's square at LM_PHOTO_OUT px.
  function lmPhotoCropOpen(file, replaceSel) {
    var url = URL.createObjectURL(file);
    var img = new Image();
    img.onload = function () { build(); };
    img.onerror = function () {
      URL.revokeObjectURL(url);
      lmPhotoToast("We couldn't read that photo. Please try a JPG or PNG.");
    };
    img.src = url;

    function build() {
      var ov = document.createElement('div');
      ov.className = 'lm-photo-crop';
      ov.innerHTML =
        '<div class="lm-photo-crop__card" role="dialog" aria-modal="true" aria-label="Position your photo">' +
          '<p class="lm-photo-crop__title">Position your photo</p>' +
          '<div class="lm-photo-crop__stage"><img alt="" draggable="false"><div class="lm-photo-crop__ring"></div></div>' +
          '<label class="lm-photo-crop__zoom"><span>Zoom</span><input type="range" min="1" max="4" step="0.01" value="1"></label>' +
          '<p class="lm-photo-crop__hint">Drag to move, pinch to zoom. The circle is what goes on your map.</p>' +
          '<div class="lm-photo-crop__btns"><button type="button" class="lm-photo-crop__cancel">Cancel</button>' +
          '<button type="button" class="lm-photo-crop__ok">Add to map</button></div>' +
        '</div>';
      document.body.appendChild(ov);
      var stage = ov.querySelector('.lm-photo-crop__stage');
      var pic = stage.querySelector('img');
      var zoomIn = ov.querySelector('input[type=range]');
      var okBtn = ov.querySelector('.lm-photo-crop__ok');
      pic.src = url;

      var W = img.naturalWidth, H = img.naturalHeight;
      var S = 0, base = 1, zoom = 1, ox = 0, oy = 0;
      function scale() { return base * zoom; }
      function clamp() {
        var sw = W * scale(), sh = H * scale();
        ox = Math.min(0, Math.max(S - sw, ox));
        oy = Math.min(0, Math.max(S - sh, oy));
      }
      function paint() {
        pic.style.width = (W * scale()) + 'px';
        pic.style.height = (H * scale()) + 'px';
        pic.style.transform = 'translate(' + ox + 'px,' + oy + 'px)';
      }
      function layout() {
        S = stage.clientWidth;
        base = S / Math.min(W, H);
        ox = (S - W * scale()) / 2; oy = (S - H * scale()) / 2;
        clamp(); paint();
      }
      layout();

      // Zoom to z keeping the photo point under stage point (px, py) still.
      function zoomAbout(z, px, py) {
        var cx = (px - ox) / scale(), cy = (py - oy) / scale();
        zoom = Math.min(4, Math.max(1, z));
        ox = px - cx * scale(); oy = py - cy * scale();
        clamp(); paint();
      }
      zoomIn.addEventListener('input', function () {
        zoomAbout(parseFloat(zoomIn.value) || 1, S / 2, S / 2);
      });

      // One pointer drags; two pinch (zoom about the midpoint, and the
      // midpoint moving pans). Re-anchored whenever a finger lands or lifts,
      // so going from two fingers back to one carries on without a jump.
      var pts = {};
      var gest = null;
      function stagePt(e) {
        var r = stage.getBoundingClientRect();
        return { x: e.clientX - r.left, y: e.clientY - r.top };
      }
      function anchor() {
        var ids = Object.keys(pts);
        if (ids.length >= 2) {
          var a = pts[ids[0]], b = pts[ids[1]];
          gest = { pinch: true, d: Math.hypot(b.x - a.x, b.y - a.y) || 1,
            mx: (a.x + b.x) / 2, my: (a.y + b.y) / 2, zoom: zoom, ox: ox, oy: oy };
        } else if (ids.length === 1) {
          var p0 = pts[ids[0]];
          gest = { pinch: false, x: p0.x, y: p0.y, ox: ox, oy: oy };
        } else {
          gest = null;
        }
      }
      stage.addEventListener('pointerdown', function (e) {
        pts[e.pointerId] = stagePt(e);
        try { stage.setPointerCapture(e.pointerId); } catch (err) {}
        anchor();
        e.preventDefault();
      });
      stage.addEventListener('pointermove', function (e) {
        if (!gest || !pts[e.pointerId]) return;
        pts[e.pointerId] = stagePt(e);
        if (gest.pinch) {
          var ids = Object.keys(pts), a = pts[ids[0]], b = pts[ids[1]];
          var mx = (a.x + b.x) / 2, my = (a.y + b.y) / 2;
          // Start from the anchored state, pan by the midpoint's travel, then
          // zoom about the new midpoint.
          zoom = gest.zoom;
          ox = gest.ox + (mx - gest.mx); oy = gest.oy + (my - gest.my);
          zoomAbout(gest.zoom * (Math.hypot(b.x - a.x, b.y - a.y) / gest.d), mx, my);
          zoomIn.value = zoom;
        } else {
          var p1 = pts[e.pointerId];
          ox = gest.ox + (p1.x - gest.x); oy = gest.oy + (p1.y - gest.y);
          clamp(); paint();
        }
      });
      function endPointer(e) {
        if (!pts[e.pointerId]) return;
        delete pts[e.pointerId];
        anchor();
      }
      stage.addEventListener('pointerup', endPointer);
      stage.addEventListener('pointercancel', endPointer);
      // Trackpad pinch (ctrl+wheel) and mouse wheel on desktop.
      stage.addEventListener('wheel', function (e) {
        e.preventDefault();
        var p2 = stagePt(e);
        zoomAbout(zoom * Math.exp(-e.deltaY * (e.ctrlKey ? 0.01 : 0.002)), p2.x, p2.y);
        zoomIn.value = zoom;
      }, { passive: false });

      function close() { ov.remove(); URL.revokeObjectURL(url); }
      ov.querySelector('.lm-photo-crop__cancel').addEventListener('click', close);
      ov.addEventListener('click', function (e) { if (e.target === ov) close(); });

      okBtn.addEventListener('click', function () {
        okBtn.disabled = true;
        okBtn.textContent = 'Adding…';
        var c = document.createElement('canvas');
        c.width = c.height = LM_PHOTO_OUT;
        var cx2 = c.getContext('2d');
        cx2.fillStyle = '#ffffff'; cx2.fillRect(0, 0, LM_PHOTO_OUT, LM_PHOTO_OUT);
        var sc = scale();
        cx2.drawImage(img, -ox / sc, -oy / sc, S / sc, S / sc, 0, 0, LM_PHOTO_OUT, LM_PHOTO_OUT);
        c.toBlob(function (blob) {
          if (!blob) { okBtn.disabled = false; okBtn.textContent = 'Add to map'; lmPhotoToast('Something went wrong. Please try again.'); return; }
          var fd = new FormData();
          var markerId = lmRandId(12);
          fd.append('image', blob, 'marker.jpg');
          fd.append('order_id', state.orderId);
          fd.append('marker_id', markerId);
          fd.append('upload_token', lmMarkerUploadToken());
          fetch(ADMIN_BASE + 'api/location-marker-upload', { method: 'POST', body: fd })
            .then(function (r) { return r.json().then(function (j) { return { ok: r.ok, j: j }; }); })
            .then(function (res) {
              if (!res.ok || !res.j || !LM_PHOTO_URL_RE.test(res.j.url || '')) throw new Error((res.j && res.j.error) || 'upload failed');
              close();
              if (replaceSel && placedIcons.indexOf(replaceSel) !== -1) lmMakePhotoPin(replaceSel, res.j.url);
              else addIcon('photo', '📷', res.j.url);
            })
            .catch(function (err) {
              console.warn('[lm.photo] upload failed', err);
              okBtn.disabled = false; okBtn.textContent = 'Add to map';
              lmPhotoToast("Your photo didn't upload. Please check your connection and try again.");
            });
        }, 'image/jpeg', 0.88);
      });
    }
  }

  // Turn an existing icon into a photo pin at the same spot.
  function lmMakePhotoPin(sel, url) {
    sel.icon = 'photo'; sel.emoji = '📷'; sel.photo = url;
    lmPhotoSampleSet(false);
    sel.rotation = 0;
    sel.size = Math.max(sel.size || iconSize, 48);
    try { sel.marker && sel.marker.remove(); } catch (e) {}
    _attachMarkerForIcon(sel);
    var pk = document.getElementById('lm-icon-card-picker');
    if (pk) pk.hidden = true;
    updatePlacedIconsUI();
    window.lmSelectIcon(sel.id);
    scheduleLM3DRefresh(300);
    if (typeof scheduleLmAutosave === 'function') scheduleLmAutosave();
  }

  // ── Emoji catalogue ──
  // Drives BOTH the "tap to place on map" palette and the per-icon emoji
  // switcher, so a customer can never switch to a glyph they cannot place.
  // `id` is the stable slug persisted in icons_json — the twelve original ids
  // keep their original glyphs, because changing one would silently alter
  // every saved design that uses it. See tests/lm-emoji-catalogue.test.mjs for
  // the render-safety rules these glyphs have to satisfy.
  /* @lm-test-export:start LM_EMOJI_CATEGORIES */
  var LM_EMOJI_CATEGORIES = [
    { key: 'love', label: 'Love', icons: [
      { id: 'heart', emoji: '❤️' }, { id: 'hearts', emoji: '💕' },
      { id: 'ring', emoji: '💍' },  { id: 'kiss', emoji: '💋' },
      { id: 'rose', emoji: '🌹' },  { id: 'bouquet', emoji: '💐' },
      { id: 'loveletter', emoji: '💌' }, { id: 'cheers', emoji: '🥂' }
    ] },
    { key: 'home', label: 'Home', icons: [
      { id: 'home', emoji: '🏠' },  { id: 'key', emoji: '🔑' },
      { id: 'sofa', emoji: '🛋️' },  { id: 'candle', emoji: '🕯️' },
      { id: 'coffee', emoji: '☕' }, { id: 'paws', emoji: '🐾' },
      { id: 'frame', emoji: '🖼️' }, { id: 'balloon', emoji: '🎈' }
    ] },
    { key: 'travel', label: 'Travel', icons: [
      { id: 'plane', emoji: '✈️' }, { id: 'map', emoji: '🗺️' },
      { id: 'compass', emoji: '🧭' }, { id: 'mountain', emoji: '⛰️' },
      { id: 'beach', emoji: '🏖️' }, { id: 'sailboat', emoji: '⛵' },
      { id: 'car', emoji: '🚗' },   { id: 'backpack', emoji: '🎒' }
    ] },
    { key: 'milestones', label: 'Milestones', icons: [
      { id: 'cake', emoji: '🎂' },  { id: 'graduation', emoji: '🎓' },
      { id: 'baby', emoji: '👶' },  { id: 'bottle', emoji: '🍼' },
      { id: 'party', emoji: '🎉' }, { id: 'trophy', emoji: '🏆' },
      { id: 'briefcase', emoji: '💼' }, { id: 'medal', emoji: '🥇' }
    ] },
    { key: 'nature', label: 'Nature', icons: [
      { id: 'moon', emoji: '🌙' },  { id: 'star', emoji: '⭐' },
      { id: 'sun', emoji: '☀️' },   { id: 'wave', emoji: '🌊' },
      { id: 'tree', emoji: '🌲' },  { id: 'blossom', emoji: '🌸' },
      { id: 'butterfly', emoji: '🦋' }, { id: 'snowflake', emoji: '❄️' }
    ] },
    { key: 'symbols', label: 'Symbols', icons: [
      { id: 'pin', emoji: '📍' },   { id: 'anchor', emoji: '⚓' },
      { id: 'sparkles', emoji: '✨' }, { id: 'bell', emoji: '🔔' },
      { id: 'music', emoji: '🎵' }, { id: 'camera', emoji: '📷' },
      { id: 'hourglass', emoji: '⏳' }, { id: 'dove', emoji: '🕊️' }
    ] }
  ];
  /* @lm-test-export:end LM_EMOJI_CATEGORIES */

  // ── Icon labels ──
  // Optional short caption under a placed icon (max 24 chars). Rendered as an
  // absolutely-positioned child so the marker anchor stays on the emoji and
  // the label rotates with the icon. Sits in a white pill chip (dark ink) so
  // it stays legible over any theme's map colours.
  var LM_ICON_LABEL_MAX = 24;
  function lmApplyLabelToMarker(iconObj) {
    var el = iconObj && iconObj.marker && iconObj.marker.getElement();
    if (!el) return;
    var lab = el.querySelector('.lm-marker-label');
    var text = (iconObj.label || '').trim().slice(0, LM_ICON_LABEL_MAX);
    if (!text) { if (lab) lab.remove(); return; }
    if (!lab) {
      lab = document.createElement('div');
      lab.className = 'lm-marker-label';
      lab.style.cssText = 'position:absolute;left:50%;transform:translateX(-50%);' +
        "font-family:'Josefin Sans',sans-serif;font-weight:600;" +
        'letter-spacing:0.03em;white-space:nowrap;pointer-events:none;line-height:1.2;' +
        'background:rgba(255,255,255,0.94);color:#2b2b2b;border-radius:999px;' +
        'box-shadow:0 1px 4px rgba(0,0,0,0.28);';
      el.appendChild(lab);
    }
    lab.textContent = text;
    var iconPx = (iconObj.size || iconSize) * lmIconSSMult;
    var fs = Math.max(9, Math.round(iconPx * 0.34));
    lab.style.fontSize = fs + 'px';
    lab.style.padding = Math.max(1, Math.round(fs * 0.18)) + 'px ' + Math.max(4, Math.round(fs * 0.55)) + 'px';
    // Anchor below the EMOJI, not the marker box — on touch screens the box
    // is padded out to a 44px hit target, so top:100% would float the label.
    // A photo pin's tip IS the centre, so its label hangs just under the tip.
    var halfH = iconObj.photo ? 0.12 : 0.55;
    lab.style.top = 'calc(50% + ' + (Math.round(iconPx * halfH) + Math.max(2, Math.round(fs * 0.25))) + 'px)';
  }

  // Shared marker-creation helper used by addIcon (new icon placed by user)
  // AND by the order-hydration loop AND by rebindPlacedIconsToMap (after
  // recreateMapForLandmarks wipes the map). Mutates the iconObj in place by
  // assigning a fresh .marker, so callers that already pushed an iconObj
  // into placedIcons get their reference updated.
  function _attachMarkerForIcon(iconObj) {
    if (!map || !iconObj) return null;
    var el = document.createElement('div');
    el.className = "lm-map-marker text-[28px] cursor-pointer [filter:drop-shadow(0_2px_4px_rgba(0,0,0,.5))] transition-transform select-none leading-none hover:scale-[1.15]";
    // Emoji lives in its own span so the optional label below never leaks
    // into textContent readers (the 3D mockups derive the emoji from it).
    if (iconObj.photo) {
      // Photo marker: circle with a white ring + soft shadow (.lm-marker-photo,
      // sized in em so the size buttons scale it like an emoji). The emoji
      // drop-shadow filter would double the shadow, so it is switched off.
      el.classList.add('lm-map-marker--photo');
      // A pin: the circle with a small white pointer under it whose TIP is the
      // location. The wrapper is lifted by half its own height so the tip sits
      // on the marker's centre, which Mapbox anchors to the lng/lat — this
      // holds however the element is padded (touch screens pad it to 44px).
      var pin = document.createElement('div');
      pin.className = 'lm-photo-pin';
      pin.style.cssText = 'position:relative;display:block;transform:translateY(-50%);' +
        'filter:drop-shadow(0 0.132em 0.22em rgba(0,0,0,0.35));';
      var ph = document.createElement('img');
      ph.className = 'lm-marker-photo';
      ph.crossOrigin = 'anonymous';   // the loupe + 3D mockups draw it to canvas
      ph.alt = '';
      ph.draggable = false;
      // Inline, not CSS: the CF thumbnail host loads only the print-frame
      // stylesheet. em units track the marker's font-size (the icon size).
      // Ratios = render_locationmap.py PHOTO_MARKER_SCALE 2.2, ring 0.07 of
      // the diameter, shadow 0.06/0.2 of it.
      ph.style.cssText = 'display:block;width:2.2em;height:2.2em;border-radius:50%;object-fit:cover;' +
        'box-sizing:border-box;border:0.154em solid #ffffff;background:#ffffff;pointer-events:none;';
      // Pointer: a white triangle tucked under the ring (0.1 x the diameter
      // each side, 0.14 x it tall). Inline display:block also beats Dawn's
      // div:empty { display:none }.
      var tail = document.createElement('div');
      tail.className = 'lm-photo-pin-tail';
      tail.style.cssText = 'display:block;width:0;height:0;margin:-0.066em auto 0;' +
        'border-left:0.22em solid transparent;border-right:0.22em solid transparent;' +
        'border-top:0.308em solid #ffffff;';
      el.style.filter = 'none';
      ph.src = iconObj.photo;
      pin.appendChild(ph);
      pin.appendChild(tail);
      el.appendChild(pin);
    } else {
      var emojiSpan = document.createElement('span');
      emojiSpan.className = 'lm-marker-emoji';
      emojiSpan.textContent = iconObj.emoji || '📍';
      el.appendChild(emojiSpan);
    }
    el.style.fontSize = ((iconObj.size || iconSize) * lmIconSSMult) + 'px';
    // A pin is never rotated (it would stop pointing at its spot).
    if (iconObj.photo) iconObj.rotation = 0;
    var marker = new mapboxgl.Marker({ element: el, draggable: true, rotation: (iconObj.rotation || 0) })
      .setLngLat([iconObj.lng, iconObj.lat])
      .addTo(map);
    el.__marker = marker;
    iconObj.marker = marker;
    lmApplyLabelToMarker(iconObj);
    if (iconObj.id === selectedIconId) el.classList.add('lm-marker-selected');
    var _dragged = false;
    // Dragging selects the icon (so its editor shows) and drives the loupe.
    marker.on('dragstart', function() {
      _dragged = true;
      // Mapbox writes a fresh inline `transform` on every pointermove; the
      // element's `transition-transform` utility animates each of those writes,
      // so the marker visibly trails the finger. Suspend it for the drag.
      el.classList.add('lm-marker-dragging');
      window.lmSelectIcon(iconObj.id);
      lmLoupeStart(el);
    });
    marker.on('drag', function() { lmLoupeMove(el, iconObj.emoji, el.querySelector('img.lm-marker-photo')); });
    marker.on('dragend', function() {
      var ll = marker.getLngLat();
      iconObj.lat = ll.lat;
      iconObj.lng = ll.lng;
      el.classList.remove('lm-marker-dragging');
      lmLoupeEnd();
      scheduleLM3DRefresh();
    });
    // A tap that isn't a drag selects this icon for editing.
    el.addEventListener('click', function(ev) {
      ev.stopPropagation();
      if (_dragged) { _dragged = false; return; }
      window.lmSelectIcon(iconObj.id);
    });
    return marker;
  }

  // Re-attach all placedIcons to whatever map is current. Called after
  // recreateMapForLandmarks wipes the map container (which detaches every
  // marker DOM element). Without this, saved icons appear in the "Your
  // icons" sidebar (because placedIcons still has the entries) but never
  // render on the map.
  function rebindPlacedIconsToMap() {
    if (!map) return;
    // recreateMapForLandmarks calls map.remove() to tear down the old map,
    // which kills marker event handlers without ever firing 'dragend' for an
    // in-flight drag. If the loupe was mid-drag when that happened, it's left
    // on-screen with a detached marker element pending a paint that never
    // finishes. This is the one place every recreate routes through, so tear
    // the loupe down here rather than relying on the drag lifecycle.
    lmLoupeEnd();
    _lmMapDeselectBound = false; // map was recreated; re-bind the deselect click
    placedIcons.forEach(function(iconObj) {
      // Drop the old (now-detached) marker reference and create a fresh one.
      try { iconObj.marker && iconObj.marker.remove && iconObj.marker.remove(); } catch (e) {}
      _attachMarkerForIcon(iconObj);
    });
    lmEnsureMapDeselect();
    lmSyncPhotoSample();
  }
  // Expose so recreateMapForLandmarks can call it without scoping issues.
  window.__lmRebindIcons = rebindPlacedIconsToMap;

  function addIcon(iconId, emoji, photo) {
    if (!map) return;
    var center = map.getCenter();
    // A new photo pin takes the sample pin's spot if the customer moved it.
    if (photo && _lmPhotoSampleAt) center = _lmPhotoSampleAt;
    var id = iconId + '-' + Date.now();
    var iconObj = { id: id, icon: iconId, emoji: emoji, lat: center.lat, lng: center.lng, size: iconSize, rotation: 0, label: '' };
    if (photo) {
      iconObj.photo = photo;
      // Photos start at L (48): ~10% of the print width at the 2.2x scale.
      iconObj.size = Math.max(iconSize, 48);
    }
    placedIcons.push(iconObj);
    _attachMarkerForIcon(iconObj);
    updatePlacedIconsUI();
    lmEnsureMapDeselect();
    window.lmSelectIcon(id); // auto-select the freshly placed icon
    scheduleLM3DRefresh();
    if (photo) lmPhotoSampleSet(false);
    if (photo && typeof scheduleLmAutosave === 'function') scheduleLmAutosave();
  }

  function removeIcon(id) {
    var idx = placedIcons.findIndex(function(p) { return p.id === id; });
    if (idx === -1) return;
    placedIcons[idx].marker.remove();
    placedIcons.splice(idx, 1);
    if (selectedIconId === id) selectedIconId = null;
    updatePlacedIconsUI();
    lmSyncIconEditor();
    scheduleLM3DRefresh();
  }

  function updatePlacedIconsUI() {
    var container = document.getElementById('lm-icons-placed');
    var grid = document.getElementById('lm-icons-placed-grid');
    lmSyncPhotoSample();
    if (!container || !grid) return;

    if (placedIcons.length === 0) {
      // Clear the grid, not just hide the container — otherwise a deleted
      // icon's chip (with a stale click handler closing over its old id)
      // is left orphaned in the DOM, invisible but a latent trap for any
      // future code that reads this grid.
      grid.innerHTML = '';
      container.style.display = 'none';
      return;
    }

    container.style.display = 'block';
    grid.innerHTML = '';
    placedIcons.forEach(function(p) {
      var div = document.createElement('div');
      div.className = "lm-icon-placed w-[36px] h-[36px] flex items-center justify-center text-[18px] border-2 border-[#c39c71] rounded-[8px] bg-[rgba(195,156,113,0.12)] cursor-pointer relative hover:bg-[rgba(195,156,113,0.2)]";
      if (p.id === selectedIconId) div.classList.add('lm-chip-selected');
      lmSetGlyphEl(div, p);
      // Tapping a chip selects that icon for editing; tapping the selected
      // chip again deselects it.
      div.addEventListener('click', function() {
        if (selectedIconId === p.id) { window.lmDeselectIcon(); return; }
        window.lmSelectIcon(p.id);
        lmRevealMapIfNeeded();
      });
      grid.appendChild(div);
    });
  }

  function getIconsData() {
    return placedIcons.map(function(p) {
      var lngLat = p.marker.getLngLat();
      var out = { icon: p.icon, emoji: p.emoji, lat: lngLat.lat, lng: lngLat.lng,
               size: p.size || iconSize, rotation: p.rotation || 0,
               label: (p.label || '').trim().slice(0, LM_ICON_LABEL_MAX) };
      if (p.photo) out.photo = p.photo;
      return out;
    });
  }

  // ── Orientation ──
  // Ribbon overlay assets — different art per orientation. The portrait ribbon
  // is the original .png; landscape uses the .webp variant we ship for the
  // family/h-moon products (matches the wider canvas without distortion).
  var RIBBON_IMG_PORTRAIT  = 'https://cdn.shopify.com/s/files/1/0710/5278/7987/files/skylit-studio-GIFT-WRAP.png?v=1748258725';
  var RIBBON_IMG_LANDSCAPE = 'https://cdn.shopify.com/s/files/1/0710/5278/7987/files/skylit-studio-GIFT-WRAP-landscape.webp?v=1765307379';

  function applyRibbonImageForOrientation() {
    var img = document.getElementById('lm-ribbon-overlay');
    if (!img) return;
    var orient = (state.orientation === 'landscape') ? 'landscape' : 'portrait';
    // Per-colour ribbon art picked by the swatches; legacy red is the fallback.
    var url = window.SkylitGifts
      ? window.SkylitGifts.ribbonImageFor(state.ribbonColor || 'Red', orient)
      : (orient === 'landscape' ? RIBBON_IMG_LANDSCAPE : RIBBON_IMG_PORTRAIT);
    if (img.getAttribute('src') !== url) img.setAttribute('src', url);
  }

  function getEffectiveBg() {
    if (state.customBg === 'theme') return lmThemeData().bg;
    return state.customBg;
  }

  // True when a mat layout's mat has been toggled to white (customBg holds the
  // override colour instead of 'theme').
  function lmWhiteMattActive() {
    return LM_MAT_LAYOUTS.indexOf(state.layout) !== -1 && state.customBg !== 'theme';
  }

  // True when the current composition's text actually sits ON the white mat and
  // therefore needs the dark-ink swap. Circle/Heart cut the map into a shape, so
  // ALL their compositions sit on the surrounding mat. Border (passepartout)
  // only closes the bottom mat for classic/boxed; its other compositions render
  // text over the map, which keeps the theme ink.
  function lmTextOnWhiteMat() {
    if (!lmWhiteMattActive()) return false;
    if (state.layout === 'circle' || state.layout === 'heart') return true;
    return state.textStyle === 'classic' || state.textStyle === 'boxed';
  }

  // Side inset for compositions that float their text OVER the map (place,
  // gradient). On Border (passepartout) the map sits inside a mat — 6% of the
  // short edge portrait, 4.5% of the height landscape, the same numbers as
  // .lm-map-inner.layout-passepartout — so the overlay must inset with it or
  // the text spans the mat and reads wider than the map window. Everything
  // else keeps the full frame width. cq units resolve against #lm-print-frame
  // (a size container), so the inline value tracks the frame like the CSS.
  // Mirrors _add_text() in render_locationmap.py (mat window width) and the
  // caption pass in skylit-admin's location-thumbnail-render.ts.
  function lmOverlaySideInset() {
    if ((state.overlayInsetLayout || state.layout) !== 'passepartout') return '0';
    return state.orientation === 'landscape' ? '4.5cqh' : '6cqmin';
  }

  // Place composition: the title is a city / venue name and must stay on ONE
  // line, shrinking (font AND tracking, floor 65%) until it fits 92% of the
  // overlay's width. The CSS alone let it wrap freely, so a name that fit the
  // 600px cart render broke into two lines on a phone frame, and the print
  // then mirrored the phone (order rpibk7dr4ej, "The Hare & Hounds"). The 92%
  // and the 65% floor match _fit_title / _draw_line in render_locationmap.py
  // so the customer sees the size that prints. Font and tracking both scale
  // by the same factor, so one measured pass lands within a pixel; a second
  // measure nudges any rounding.
  // Large prints (50x70, 60x80) set every caption line at 85% of its usual
  // frame-relative size: type that scales linearly with the paper read heavy on
  // a poster (order 0ueo14ymycq, 2026-10-21). render_locationmap.py prints the
  // same sizes at the same 85% (caption_style_for_size), so preview = print.
  var LM_LARGE_PRINT_SIZES = { '50x70': 1, '60x80': 1 };
  var LM_LARGE_PRINT_TEXT_SCALE = 0.85;
  function lmTextScale() {
    var k = String(state.selectedSize || '').toLowerCase().replace(/\s+/g, '').replace(/cm$/, '');
    return LM_LARGE_PRINT_SIZES[k] ? LM_LARGE_PRINT_TEXT_SCALE : 1;
  }
  // Applies lmTextScale() to the caption lines just built. Their sizes come
  // from CSS rules or inline cq values, so read the rendered px and write it
  // back in cqh of the print frame (the size container), which keeps it
  // tracking the frame on resize. The place/header title is left to
  // lmFitPlaceTitle, which starts from the scaled size itself.
  function lmScaleCaption(overlay, skipTitle) {
    var k = lmTextScale();
    if (k === 1) return;
    var frame = document.getElementById('lm-print-frame');
    if (!frame) return;
    // cq units resolve against the container's CONTENT box; the frame carries a
    // border and padding (the mount), so its outer height would understate cqh.
    var fcs = window.getComputedStyle(frame);
    var fh = frame.clientHeight - parseFloat(fcs.paddingTop) - parseFloat(fcs.paddingBottom);
    if (!(fh > 0)) return;
    var lines = overlay.querySelectorAll('.lm-preview-title, .lm-preview-subtitle, .lm-preview-coords');
    for (var i = 0; i < lines.length; i++) {
      if (skipTitle && lines[i].classList.contains('lm-preview-title')) continue;
      var px = parseFloat(window.getComputedStyle(lines[i]).fontSize);
      if (px) lines[i].style.fontSize = (px / fh * 100 * k).toFixed(3) + 'cqh';
    }
  }

  var lmPlaceFitFontsPending = false;
  // Also fits the Header title into its column (`box`, the left 45cqw):
  // render_locationmap.py keeps it on one line and shrinks it to the same box.
  function lmFitPlaceTitle(overlay, baseCqh, box) {
    var title = overlay.querySelector('.lm-preview-title');
    if (!title) return;
    title.style.whiteSpace = 'nowrap';
    // Large prints: start from the shrunk size (baseCqh already carries it).
    if (lmTextScale() !== 1) title.style.fontSize = baseCqh.toFixed(3) + 'cqh';
    var boxW = (box || overlay).getBoundingClientRect().width;
    if (!boxW) return;
    var avail = box ? boxW : boxW * 0.92;
    var track = box ? 0.15 : 0.9;  // .lm-preview-title vs the place title's tracking
    function measure() {
      // Range width = the glyph run itself (a block div's own width is the
      // overlay's, and scrollWidth only exceeds it once the text overflows).
      try {
        var r = document.createRange();
        r.selectNodeContents(title);
        return r.getBoundingClientRect().width;
      } catch (e) {
        return title.scrollWidth > title.clientWidth ? title.scrollWidth : 0;
      }
    }
    var factor = 1;
    for (var pass = 0; pass < 2; pass++) {
      var need = measure();
      if (!need || need <= avail) break;
      factor = Math.max(0.65, factor * (avail / need));
      title.style.fontSize = (baseCqh * factor).toFixed(3) + 'cqh';
      title.style.letterSpacing = (track * factor).toFixed(3) + 'cqh';
      if (factor <= 0.65) break;
    }
    // A web font that is not in yet measures with the fallback face (wider
    // for Prata & co.), so the fit lands too small. document.fonts.status is
    // 'loaded' until something REQUESTS the face — this very overlay is
    // often the first — so check the face itself and re-fit once it lands.
    if (document.fonts && document.fonts.check && !lmPlaceFitFontsPending) {
      var spec = "600 16px '" + state.font + "'";
      var have = false;
      try { have = document.fonts.check(spec); } catch (e) { have = true; }
      if (!have) {
        lmPlaceFitFontsPending = true;
        var refit = function () {
          lmPlaceFitFontsPending = false;
          if (state.textStyle === 'place' || state.textStyle === 'header') updateTextOverlay();
        };
        try { document.fonts.load(spec).then(refit, refit); } catch (e) { lmPlaceFitFontsPending = false; }
      }
    }
  }

  // WCAG relative luminance (0 black → 1 white) of a #rgb / #rrggbb colour.
  function lmRelLuminance(hex) {
    var c = String(hex || '').trim().replace('#', '');
    if (c.length === 3) c = c[0] + c[0] + c[1] + c[1] + c[2] + c[2];
    if (c.length !== 6) return 1;
    function chan(h) {
      var v = parseInt(h, 16) / 255;
      return v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4);
    }
    return 0.2126 * chan(c.slice(0, 2)) + 0.7152 * chan(c.slice(2, 4)) + 0.0722 * chan(c.slice(4, 6));
  }
  // WCAG contrast ratio of a colour against white.
  function lmContrastOnWhite(hex) { return 1.05 / (lmRelLuminance(hex) + 0.05); }

  // Ink for text sitting on a WHITE mat: on-theme but readable. Walk the theme's
  // palette in identity order — its own text ink first, then the road / water
  // accents, then the base colours — and take the first that clears a solid
  // contrast bar on white. If none do (rare, very light palettes) use the
  // darkest palette colour, falling back to a neutral dark only if even that
  // reads poorly.
  function lmInkOnWhite(t) {
    // Pick from the theme's OWN palette, never the blue-water variant: Ruby's
    // text and road are too pale for white, so the pick falls through to
    // water, and with blue water on the caption turned blue. The water toggle
    // recolours the map only; the text keeps the theme's ink.
    t = THEMES[state.theme] || t;
    // Mono's alternate mat is BLACK: its own inverse (the white theme bg) is
    // the natural ink there, so it goes second instead of the grey accents.
    var dark = state.customBg === LM_MATT_BLACK;
    var order = dark
      ? [t.text, t.bg, t.road, t.water, t.roadMinor, t.building, t.park]
      : [t.text, t.road, t.water, t.roadMinor, t.bg, t.building, t.park];
    var best = null, bestC = 0;
    for (var i = 0; i < order.length; i++) {
      var col = order[i];
      if (!col) continue;
      var cr = dark ? (lmRelLuminance(col) + 0.05) / 0.05 : lmContrastOnWhite(col);
      if (cr >= 4.5) return col;
      if (cr > bestC) { bestC = cr; best = col; }
    }
    return bestC >= 3 ? best : (dark ? '#f2f2f2' : '#2b2b2b');
  }

  // Text ink for the preview overlay and the render payload. When the mat is
  // white AND the composition puts its text band ON the mat (classic/boxed —
  // the styles for which the renderer closes the bottom mat), pick a readable
  // on-theme ink via lmInkOnWhite; otherwise use the theme's own text colour.
  function lmEffectiveTextColor() {
    var t = lmThemeData();
    if (lmTextOnWhiteMat()) return lmInkOnWhite(t);
    return t.text;
  }

  // Reflect the current finish onto the form so CSS can conditionally show
  // print-specific UI (e.g. the Ribbon-wrapping tooltip explaining roll-shipping).
  function applyFinishAttr() {
    var form = document.getElementById('lm-product-form');
    if (form) form.setAttribute('data-finish', String(state.selectedType || 'print').toLowerCase());
  }


  // ── Gift Options Picker ──
  // Gift selection mirrored onto state.selectedGift so Alpine $store.lm
  // can drive the picker UI reactively. Default 'none' set in state above.
  if (state.selectedGift === undefined) state.selectedGift = 'none';
  if (!state.giftPaper) state.giftPaper = (window.SkylitGifts && window.SkylitGifts.recallPaperFor('lm_' + (state.orderId || ''))) || 'dots';
  if (!state.ribbonColor) state.ribbonColor = (window.SkylitGifts && window.SkylitGifts.recallRibbonFor('lm_' + (state.orderId || ''))) || 'Red';
  // Expose for 3D toggle sync
  window.lmSelectedGift = function () { return state.selectedGift; };

  // ── Gift peek (eye icon) — transient view-only toggle ─────────────────
  // lmGiftPeekHidden suppresses the overlay visuals without touching
  // state.selectedGift. Mirrors ACP preview route.ts wrapHidden pattern.
  var lmGiftPeekHidden = false;
  // Expose read-only peek state so the 3D mockup's init can respect it.
  window.lmGiftPeekIsHidden = function () { return lmGiftPeekHidden; };
  function lmRefreshPeekBtn() {
    var btn = document.getElementById('lm-gift-peek-btn');
    if (!btn) return;
    var gift = state.selectedGift;
    btn.hidden = !(gift && gift !== 'none');
    var label = lmGiftPeekHidden ? 'Show gift wrap' : 'Hide gift wrap';
    btn.setAttribute('aria-label', label);
    btn.setAttribute('title', label);
    btn.setAttribute('aria-pressed', lmGiftPeekHidden ? 'true' : 'false');
  }
  document.addEventListener('DOMContentLoaded', function () {
    var btn = document.getElementById('lm-gift-peek-btn');
    if (btn) {
      btn.addEventListener('click', function () {
        lmGiftPeekHidden = !lmGiftPeekHidden;
        // Re-apply overlays with the new peek state
        lmApplyGiftOverlays(state.selectedGift);
        // Mirror the peek state into the 3D mockups: peeking hides the 3D
        // ribbon + wrap parcel too; un-peeking restores the selection.
        lmApplyPeekTo3D();
        lmRefreshPeekBtn();
      });
    }
  });

  function lmApplyGiftOverlays(gift) {
    var g = lmGiftPeekHidden ? 'none' : gift;
    applyRibbonImageForOrientation();
    var ribbonOn = (g === 'ribbon' || g === 'bundle');
    var giftWrapOn = (g === 'giftwrap' || g === 'bundle');
    var ribbonOverlay = document.getElementById('lm-ribbon-overlay');
    var giftWrapOverlay = document.getElementById('lm-giftwrap-overlay');
    if (ribbonOverlay) ribbonOverlay.classList.toggle('visible', ribbonOn);
    if (giftWrapOverlay) giftWrapOverlay.classList.toggle('visible', giftWrapOn);
    if (giftWrapOverlay && window.SkylitGifts) window.SkylitGifts.applyPaperTexture(giftWrapOverlay, state.giftPaper);
    var giftCardOverlay = document.getElementById('lm-giftcard-overlay');
    if (giftCardOverlay) giftCardOverlay.classList.toggle('visible', ribbonOn);
  }

  function lmApplyPeekTo3D() {
    var gift = lmGiftPeekHidden ? 'none' : state.selectedGift;
    var ribbonOn = (gift === 'ribbon' || gift === 'bundle');
    var giftWrapOn = (gift === 'giftwrap' || gift === 'bundle');
    [window.LM3DFramedMockup, window.LM3DMockup].forEach(function (api) {
      if (!api) return;
      if (typeof api.showRibbon === 'function') {
        if (ribbonOn) api.showRibbon(); else api.hideRibbon();
      }
      // Only the framed mockup has the wrap parcel; the typeof guard
      // makes this a no-op on the canvas mockup.
      if (typeof api.showGiftWrap === 'function') {
        if (giftWrapOn) api.showGiftWrap(); else api.hideGiftWrap();
      }
      if (typeof api.setWrapDesign === 'function') api.setWrapDesign(state.giftPaper || 'dots');
    });
  }

  window.lmSetSelectedGift = function (gift) {
    if (state.selectedGift === gift) return;
    lmStoreSet('selectedGift', gift);
    if (typeof updateVariantAndPrice === 'function') updateVariantAndPrice();
    // Reset peek when the customer changes their wrap selection
    lmGiftPeekHidden = false;
    lmApplyGiftOverlays(gift);
    lmRefreshPeekBtn();
    // Sync ribbon + wrap parcel to whichever 3D mockups are loaded
    lmApplyPeekTo3D();
  };

  var lmPaperPicker = window.SkylitGifts
    ? window.SkylitGifts.bindPaperPicker(document.getElementById('lm-gift-paper'), function (id) {
        lmStoreSet('giftPaper', id);
        state.giftPaper = id;
        if (window.SkylitGifts) window.SkylitGifts.rememberPaper('lm_' + (state.orderId || ''), id);
        lmApplyGiftOverlays(state.selectedGift);
        lmApplyPeekTo3D();
      })
    : null;
  if (lmPaperPicker) lmPaperPicker.set(state.giftPaper || 'dots');

  var lmRibbonPicker = window.SkylitGifts
    ? window.SkylitGifts.bindRibbonPicker(document.getElementById('lm-gift-ribbon'), function (name) {
        state.ribbonColor = name;
        if (window.SkylitGifts) window.SkylitGifts.rememberRibbon('lm_' + (state.orderId || ''), name);
        lmApplyGiftOverlays(state.selectedGift);
      })
    : null;
  if (lmRibbonPicker) lmRibbonPicker.set(state.ribbonColor || 'Red');

  function updateGiftVisibility() {
    var isPrint = state.selectedType === 'Print';
    document.querySelectorAll('.lm-gift-picker [data-hide-for-print]').forEach(function (btn) {
      btn.style.display = isPrint ? 'none' : '';
    });
    // If print selected and a hidden option was active, reset to none
    if (isPrint && (state.selectedGift === 'giftwrap' || state.selectedGift === 'bundle')) {
      window.lmSetSelectedGift('none');
    }
  }

  // Legacy name: some references still read the local var. Keep a getter
  // so external scripts that read `selectedGift` indirectly still work.
  Object.defineProperty(window, 'selectedGift', { get: function () { return state.selectedGift; } });

  // Gift product variant IDs (same as starmap)
  var GIFT_IDS = {
    ribbon: '55259137802617',
    giftwrap: '56160831930745',
    bundle: '56160845988217'
  };

  // Gift wrap and the bundle are size-tiered by the print being wrapped;
  // SkylitGifts maps the selected size to the right variant, with GIFT_IDS
  // (the smallest tier) as the fallback.
  function lmGiftVariantId(kind) {
    try {
      var tiered = window.SkylitGifts && window.SkylitGifts.variantFor(kind, state.selectedSize, state.giftPaper);
      if (tiered) return String(tiered);
    } catch (e) {}
    return GIFT_IDS[kind];
  }

  // Prices in pence, parallel to GIFT_IDS. Used by updateVariantAndPrice so
  // the PDP "Total" row reflects the wrapping add-on too — the wrapping is
  // still a separate cart line item via addGiftToCart; this is display-only.
  var GIFT_PRICES = {
    none: 0,
    ribbon: 249,
    giftwrap: 369,
    bundle: 499
  };

  function readLmGiftDom() {
    var out = { note: '', design: 'editorial', font: 'handwritten', enabled: false };
    try {
      var ta = document.getElementById('Gift-note-locationmap');
      if (ta) out.note = ta.value || '';
      var root = document.querySelector('.gnc[data-gnc="locationmap"]');
      if (root) {
        var d = root.getAttribute('data-design');
        if (d === 'editorial' || d === 'minimal') out.design = d;
        var f = root.getAttribute('data-font');
        if (f === 'handwritten' || f === 'classic') out.font = f;
        out.enabled = root.getAttribute('data-enabled') === 'true';
      }
    } catch (_) {}
    return out;
  }

  function addGiftToCart() {
    if (state.selectedGift === 'none' || !GIFT_IDS[state.selectedGift]) return Promise.resolve();
    var giftProps = {};
    var dom = readLmGiftDom();
    var useLegacy = false;
    try { useLegacy = new URLSearchParams(window.location.search).get('legacy_gift') === '1'; } catch (_) {}

    if (state.selectedGift === 'ribbon' || state.selectedGift === 'bundle') {
      var lmRibbon = window.SkylitGifts ? window.SkylitGifts.ribbonColorKey(state.ribbonColor) : (state.ribbonColor || 'Red');
      if (window.SkylitGifts) window.SkylitGifts.rememberRibbon('lm_' + (state.orderId || ''), lmRibbon);
      giftProps['Ribbon Colour'] = lmRibbon;
      giftProps._ribbon_color = lmRibbon;
    }
    var paperKey = 'regular';
    if (state.selectedGift === 'giftwrap' || state.selectedGift === 'bundle') {
      paperKey = window.SkylitGifts ? window.SkylitGifts.designKey(state.giftPaper) : 'regular';
      giftProps['Gift Wrap Theme'] = window.SkylitGifts ? window.SkylitGifts.labelFor(paperKey) : 'Regular';
      giftProps._gift_wrap_theme = paperKey;
    }
    if (state && state.orderId) giftProps._paired_with_order_id = state.orderId;
    // Copy reference stamped on the print at add-to-cart time. _OrderID is
    // shared between copies of one design, so the cart/drawer controls pair
    // add-ons on this instead.
    if (state && state.cartLineRef) giftProps._paired_with_line_ref = state.cartLineRef;
    // Attach gift note + design as line-item properties (not cart attrs).
    if (!useLegacy && (state.selectedGift === 'ribbon' || state.selectedGift === 'bundle')
        && dom.enabled && dom.note.trim()) {
      giftProps._gift_note = dom.note;
      giftProps._gift_card_design = dom.design;
      giftProps._gift_card_font = dom.font;
    }

    var attrs = { _custom_bundle_active: state.selectedGift === 'bundle' ? 'true' : 'false' };
    if (state.selectedGift === 'bundle') attrs._custom_bundle_quantity = '1';
    if (useLegacy) {
      if (state.selectedGift === 'bundle') {
        attrs._addon_ribbon_color = 'red';
        attrs._addon_gift_wrap_theme = paperKey;
        attrs._addon_gift_note = dom.note;
      } else if (state.selectedGift === 'ribbon') {
        attrs._addon_ribbon_color = 'red';
        attrs._addon_gift_note = dom.note;
      } else if (state.selectedGift === 'giftwrap') {
        attrs._addon_gift_wrap_theme = paperKey;
      }
    }
    return fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ quantity: 1, id: lmGiftVariantId(state.selectedGift), properties: giftProps })
    }).then(function() {
      return fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ attributes: attrs })
      });
    });
  }

  // ── Filter sizes based on selected type + availability ──
  function filterSizesForType() {
    var variants = window.lmProductVariants || [];
    var sizeExists = {};
    var sizeAvailable = {};
    variants.forEach(function(v) {
      if (v.option1 === state.selectedType) {
        sizeExists[v.option2] = true;
        if (v.available) sizeAvailable[v.option2] = true;
      }
    });

    var anyActive = false;
    var firstAvailable = null;
    document.querySelectorAll('.lm-size-picker [data-variant-option]').forEach(function(btn) {
      var sizeVal = btn.dataset.variantOption;
      if (sizeExists[sizeVal]) {
        btn.style.display = '';
        if (sizeAvailable[sizeVal]) {
          btn.classList.remove('sold-out');
          btn.style.opacity = '';
          btn.style.pointerEvents = '';
          if (!firstAvailable) firstAvailable = btn;
          if (btn.classList.contains('active')) anyActive = true;
        } else {
          btn.classList.add('sold-out');
          // Made in this finish but out of stock: CSS badges it "Sold out"
          // and fades the contents, so no inline tile opacity.
          lmMarkSoldOut(btn, true);
          btn.style.opacity = '';
          btn.style.pointerEvents = 'none';
          if (btn.classList.contains('active')) btn.classList.remove('active');
        }
      } else {
        btn.style.display = 'none';
        btn.classList.remove('active');
      }
      if (!sizeExists[sizeVal] || sizeAvailable[sizeVal]) lmMarkSoldOut(btn, false);
    });

    // If the active size is now sold-out or hidden, select the first available one
    if (!anyActive && firstAvailable) {
      firstAvailable.classList.add('active');
      state.selectedSize = firstAvailable.dataset.variantOption;
      applyFrameRatio();
    }
    updateFrameAvailability();
  }

  // "Sold out" badge (CSS: .lm-soldout) for an option that is made in this
  // combination but out of stock, as opposed to one that isn't made at all.
  function lmMarkSoldOut(btn, soldOut) {
    btn.classList.toggle('lm-soldout', soldOut);
    if (soldOut) btn.setAttribute('aria-label', (btn.dataset.variantOption || btn.textContent.trim()) + ', sold out');
    else btn.removeAttribute('aria-label');
  }

  // Finish tiles: a finish made at the chosen size but sold out there (e.g.
  // Framed at 60x80 with every frame colour gone) can't be picked.
  function updateTypeAvailability() {
    if (lmClaimActive()) return;
    var variants = window.lmProductVariants || [];
    document.querySelectorAll('.lm-type-picker [data-variant-option]').forEach(function(btn) {
      var type = btn.dataset.variantOption;
      var made = false, inStock = false;
      variants.forEach(function(v) {
        if (v.option1 === type && v.option2 === state.selectedSize) {
          made = true;
          if (v.available) inStock = true;
        }
      });
      var soldOut = made && !inStock;
      btn.classList.toggle('sold-out', soldOut);
      btn.style.pointerEvents = soldOut ? 'none' : '';
      lmMarkSoldOut(btn, soldOut);
    });
  }

  // ── Update frame colour availability based on selected type + size ──
  function updateFrameAvailability() {
    updateTypeAvailability();
    if (state.selectedType !== 'Framed') return;
    var variants = window.lmProductVariants || [];
    var frameAvailable = {};
    var frameMade = {};
    variants.forEach(function(v) {
      if (v.option1 === state.selectedType && v.option2 === state.selectedSize) {
        frameMade[v.option3] = true;
        if (v.available) frameAvailable[v.option3] = true;
      }
    });

    var anyActive = false;
    var firstAvailable = null;
    var darkBrownBtn = null;
    document.querySelectorAll('.lm-frame-picker [data-variant-option]').forEach(function(btn) {
      var frameVal = btn.dataset.variantOption;
      if (frameAvailable[frameVal]) {
        btn.classList.remove('sold-out');
        lmMarkSoldOut(btn, false);
        btn.style.opacity = '';
        btn.style.pointerEvents = '';
        if (!firstAvailable) firstAvailable = btn;
        if (frameVal === 'Dark Brown') darkBrownBtn = btn;
        if (btn.classList.contains('active')) anyActive = true;
      } else {
        btn.classList.add('sold-out');
        lmMarkSoldOut(btn, !!frameMade[frameVal]);
        btn.style.opacity = frameMade[frameVal] ? '' : '0.45';
        btn.style.pointerEvents = 'none';
        if (btn.classList.contains('active')) btn.classList.remove('active');
      }
    });

    if (!anyActive) {
      var fallback = darkBrownBtn || firstAvailable;
      if (fallback) {
        fallback.classList.add('active');
        state.selectedFrame = fallback.dataset.variantOption;
        updateFrame();
      }
    }
  }


  // ── UPDATE: Frame Preview ──
  function updateFrame() {
    var frameEl = document.getElementById('lm-print-frame');
    if (!frameEl) return;

    if (state.selectedType !== 'Framed') {
      // Print/Canvas: remove border entirely so map fills edge to edge
      frameEl.style.borderWidth = '0';
      frameEl.style.borderImage = 'none';
      frameEl.style.padding = '0';
      frameEl.style.boxShadow = '0 4px 20px rgba(0,0,0,.3)';
      // Resize map after CSS transition completes (300ms) + multiple passes for safety
      if (map) {
        setTimeout(function() { map.resize(); }, 50);
        setTimeout(function() { map.resize(); }, 350);
      }
      return;
    }

    // Framed: show frame, remove clip
    frameEl.style.clipPath = 'none';
    frameEl.style.webkitClipPath = 'none';
    frameEl.style.boxShadow = '0 8px 40px rgba(0,0,0,.5)';

    var tex = FRAME_TEXTURES[state.selectedFrame] || FRAME_TEXTURES.Black;
    frameEl.style.borderWidth = '25px';
    frameEl.style.borderStyle = 'solid';
    frameEl.style.borderColor = tex.color;
    var texImage = tex.image;
    // Landscape: swap in the 90°-rotated texture (window.lmFrameTexLandscape,
    // set by the section) so the long rails come from the photo's long rails
    // instead of stretching the short ones. Slice params are kept.
    if (state.orientation === 'landscape' && window.lmFrameTexLandscape && window.lmFrameTexLandscape[state.selectedFrame]) {
      texImage = texImage.replace(/url\([^)]*\)/, 'url(' + window.lmFrameTexLandscape[state.selectedFrame] + ')');
    }
    frameEl.style.borderImage = texImage;
    frameEl.style.padding = '10px';

    // Resize map after frame border is restored
    if (map) {
      setTimeout(function() { map.resize(); }, 50);
      setTimeout(function() { map.resize(); }, 350);
    }

    // Update 3D frame colour if active
    if (window.LM3DFramedMockup && window.LM3DFramedMockup.isActive()) {
      var colorMap = { 'Black': 'black', 'White': 'white', 'Natural': 'brown', 'Walnut': 'darkbrown', 'Dark Brown': 'darkbrown' };
      var colorKey = colorMap[state.selectedFrame] || 'black';
      window.LM3DFramedMockup.setFrameColor(colorKey);
    }
    scheduleLM3DRefresh();
  }

  // ── UPDATE: Layout ──
  function updateLayout() {
    var inner = document.getElementById('lm-map-inner');
    var bgEl = document.getElementById('lm-layout-bg');
    var fadeEl = document.getElementById('lm-fade-overlay');
    var bgSection = document.getElementById('lm-bg-section');
    if (!inner) return;
    // Warm the viewport-size cache while the frame is visible so a save
    // fired during .sm-gallery-viewing still reports the designed-against
    // slot size instead of 0.
    lmViewportW(); lmViewportH();

    var bg = getEffectiveBg();
    // Keep the Border-tile badge's theme swatch in sync (runs on init + every
    // theme/layout change, the only things that move the theme bg).
    var tBg = lmThemeData().bg;
    if (state.themeBg !== tBg) lmStoreSet('themeBg', tBg);
    if (state.altMat !== lmAltMat()) lmStoreSet('altMat', lmAltMat());
    // Recolour the Layout/Composition mini-map tiles to match the theme.
    lmApplyIconTheme();
    // Same "covers every load path" reasoning as lmApplyIconTheme above: if a
    // draft/edit/preset hydrated onto a theme outside the curated six, open the
    // Theme list so its active tile is actually on screen.
    lmSyncThemeDisclosure();

    // Remove all layout classes
    inner.className = 'lm-map-inner layout-' + state.layout;

    // Layout-dependent composition availability. The wrapper attribute
    // drives CSS that hides composition tiles that don't make sense for
    // the active layout (see "Composition availability" rules in
    // sections/locationmap.liquid). If the current composition just got
    // hidden, hop to that layout's default so state never points at an
    // invisible tile.
    var lmWrap = document.querySelector('.lm-wrapper');
    if (lmWrap) lmWrap.setAttribute('data-layout', state.layout);
    var hiddenStyles = {
      circle: ['gradient', 'place'],
      heart: ['gradient', 'place'],
      passepartout: ['boxed', 'panel', 'header'],
      fade: ['classic']
    };
    var defaultStyles = { fade: 'gradient' };
    var hiddenNow = hiddenStyles[state.layout] || [];
    if (hiddenNow.indexOf(state.textStyle) !== -1 && typeof window.lmSetTextStyle === 'function') {
      window.lmSetTextStyle(defaultStyles[state.layout] || 'classic');
    }

    // Smart circle border colour based on bg luminance
    if (state.layout === 'circle') {
      var hex = bg || '#ffffff';
      var rr = parseInt(hex.slice(1,3)||'ff',16), gg = parseInt(hex.slice(3,5)||'ff',16), bb = parseInt(hex.slice(5,7)||'ff',16);
      inner.style.borderColor = (0.299*rr + 0.587*gg + 0.114*bb) < 128 ? '#ffffff' : '#000000';
    } else {
      inner.style.borderColor = '';
    }

    // Heart border overlay — same smart colour logic
    var heartBorder = document.getElementById('lm-heart-border');
    if (heartBorder) {
      if (state.layout === 'heart') {
        heartBorder.style.display = 'block';
        var hex2 = bg || '#ffffff';
        var r2 = parseInt(hex2.slice(1,3)||'ff',16), g2 = parseInt(hex2.slice(3,5)||'ff',16), b2 = parseInt(hex2.slice(5,7)||'ff',16);
        var strokeColor = (0.299*r2 + 0.587*g2 + 0.114*b2) < 128 ? '#ffffff' : '#000000';
        var svgPaths = heartBorder.querySelectorAll('path');
        for (var pi = 0; pi < svgPaths.length; pi++) svgPaths[pi].setAttribute('stroke', strokeColor);
      } else {
        heartBorder.style.display = 'none';
      }
    }

    // Show/hide bg colour picker (only useful for fade/circle/heart)
    if (bgSection) {
      bgSection.style.display = (state.layout === 'full') ? 'none' : 'block';
    }

    // Background fill — acts as overlay with shape cutout
    if (bgEl) {
      bgEl.style.backgroundColor = 'transparent';
      bgEl.style.background = '';

      if (state.layout !== 'full') {
        bgEl.style.backgroundColor = bg;
      }
    }

    // Fade overlay gradient — all 4 edges
    if (fadeEl) {
      if (state.layout === 'fade') {
        fadeEl.style.display = 'block';
        fadeEl.style.background =
          'linear-gradient(to bottom, ' + bg + ' 0%, ' + bg + ' 3%, transparent 20%), ' +
          'linear-gradient(to top, ' + bg + ' 0%, ' + bg + ' 5%, transparent 30%), ' +
          'linear-gradient(to right, ' + bg + ' 0%, ' + bg + ' 3%, transparent 18%), ' +
          'linear-gradient(to left, ' + bg + ' 0%, ' + bg + ' 3%, transparent 18%)';
      } else {
        fadeEl.style.display = 'none';
      }
    }


    // Mapbox needs resize after layout change
    if (map) setTimeout(function() { map.resize(); }, 50);

    scheduleLM3DRefresh(400);
  }

  // ── Handwriting helpers ──
  // Fixed viewBox keeps strokes resolution-independent. ~4.17:1 matches the
  // boxed plaque proportions; renderer scales into the actual print plaque.
  var HW_VBW = 1000, HW_VBH = 240;
  // Sample de-noising, mirrored by render_common.smooth_handwriting_points and
  // by starmap-v2.js / moon-handwriting.js. The pad captures in whole CSS
  // pixels mapped into the viewBox, so every point lands on a coarse lattice —
  // invisible in the small preview, a visible zig-zag in the expanded pad and
  // at print resolution. Endpoints pinned; strokes shorter than the kernel
  // (dots, accents) pass through untouched. DRAW time only —
  // state.handwritingStrokes keeps the raw samples.
  var HW_KERNEL = [1, 2, 3, 2, 1];
  function hwSmoothPts(pts) {
    if (!pts || !pts.length) return pts || [];
    var clean = [pts[0]];
    for (var i = 1; i < pts.length; i++) {
      var last = clean[clean.length - 1];
      if (Math.abs(pts[i][0] - last[0]) > 1e-6 || Math.abs(pts[i][1] - last[1]) > 1e-6) clean.push(pts[i]);
    }
    if (clean.length < HW_KERNEL.length) return clean;
    var half = HW_KERNEL.length >> 1;
    var out = [clean[0]];
    for (var k = 1; k < clean.length - 1; k++) {
      var sx = 0, sy = 0, ws = 0;
      for (var j = 0; j < HW_KERNEL.length; j++) {
        var idx = k + j - half;
        if (idx >= 0 && idx < clean.length) {
          sx += clean[idx][0] * HW_KERNEL[j];
          sy += clean[idx][1] * HW_KERNEL[j];
          ws += HW_KERNEL[j];
        }
      }
      out.push([sx / ws, sy / ws]);
    }
    out.push(clean[clean.length - 1]);
    return out;
  }

  function hwStrokesToPathD(strokes) {
    // Build one combined SVG path "d" string per stroke is overkill — we want
    // per-stroke <path> so width can vary. Returns array of {d, w}.
    var out = [];
    for (var i = 0; i < strokes.length; i++) {
      var s = strokes[i];
      if (!s.pts || s.pts.length < 1) continue;
      s = { pts: hwSmoothPts(s.pts), w: s.w };
      var d = 'M ' + s.pts[0][0].toFixed(1) + ' ' + s.pts[0][1].toFixed(1);
      if (s.pts.length === 1) {
        // A lone moveto is NOT stroked, so single-point strokes — the dot on
        // an i or j, a full stop, any quick tap — vanished from this preview
        // and from the print. A zero-length segment makes the round linecap
        // paint them. Both canvas redraws below always drew them, which is why
        // they looked right on the pad and were missing everywhere else.
        d += ' L ' + s.pts[0][0].toFixed(1) + ' ' + s.pts[0][1].toFixed(1);
      } else if (s.pts.length <= 2) {
        for (var j = 1; j < s.pts.length; j++) d += ' L ' + s.pts[j][0].toFixed(1) + ' ' + s.pts[j][1].toFixed(1);
      } else {
        // Midpoint quadratic smoothing — mirrors the pad/thumbnail/renderer.
        for (var j = 1; j < s.pts.length - 1; j++) {
          var cpt = s.pts[j], npt = s.pts[j + 1];
          d += ' Q ' + cpt[0].toFixed(1) + ' ' + cpt[1].toFixed(1) + ' ' +
               ((cpt[0] + npt[0]) / 2).toFixed(1) + ' ' + ((cpt[1] + npt[1]) / 2).toFixed(1);
        }
        d += ' L ' + s.pts[s.pts.length - 1][0].toFixed(1) + ' ' + s.pts[s.pts.length - 1][1].toFixed(1);
      }
      out.push({ d: d, w: s.w || 4 });
    }
    return out;
  }
  function hwBuildPreviewSVG(color) {
    var paths = hwStrokesToPathD(state.handwritingStrokes);
    if (!paths.length) return '';
    var inner = '';
    for (var i = 0; i < paths.length; i++) {
      inner += '<path d="' + paths[i].d + '" stroke="' + color + '" stroke-width="' + paths[i].w +
        '" stroke-linecap="round" stroke-linejoin="round" fill="none"/>';
    }
    // preserveAspectRatio: fit width, keep aspect — prevents stroke distortion.
    return '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ' + HW_VBW + ' ' + HW_VBH +
      '" preserveAspectRatio="xMidYMid meet" style="width:100%;height:auto;display:block;">' + inner + '</svg>';
  }
  function hwHasContent() {
    return state.handwritingEnabled && state.handwritingStrokes && state.handwritingStrokes.length > 0;
  }
  function hwHTML(color, anchor) {
    // anchor: 'center' (default) or 'left'
    // Print size scales the SVG width (controls how big the strokes look).
    // Title height controls vertical slot reservation only — adds/removes
    // bottom margin so the surrounding plaque grows/shrinks without changing
    // stroke size.
    var pct = Math.round((state.handwritingSize || 1) * 100);
    var hFactor = Math.max(0.4, Math.min(1.4, state.handwritingHeight || 1));
    // 100% → 4px; lower shrinks toward -10px (negative margin pulls subtitle
    // up, mimicking the renderer's smaller slot_h on the print).
    var marginBottomPx = Math.round(4 + (hFactor - 1) * 28);
    var marginRule = (anchor === 'left')
      ? 'margin:0 0 ' + marginBottomPx + 'px'
      : 'margin:0 auto ' + marginBottomPx + 'px';
    return '<div class="lm-preview-handwriting" style="color:' + color + ';' + marginRule + ';width:' + pct + '%">' + hwBuildPreviewSVG(color) + '</div>';
  }

  // ── UPDATE: Text Overlay ──
  function updateTextOverlay() {
    var el = document.getElementById('lm-text-overlay');
    if (!el) return;

    var t = lmThemeData();
    lmSyncCoordsInput();
    var hasText = state.title || state.subtitle || state.dateLine || lmCoordsLine() || hwHasContent();

    if (!hasText) { el.style.display = 'none'; return; }
    el.style.display = 'block';

    // Reset styles
    el.style.cssText = 'display:block;pointer-events:none;z-index:6;';
    el.className = '';

    var ts = state.textStyle || 'classic';
    // White mat: where the composition's text sits on the mat, paint its band
    // white and switch the ink to a readable on-theme tone. Shadow a fresh copy
    // of `t` so every t.bg / t.text reference below follows suit without mutating
    // the shared THEMES table.
    if (lmTextOnWhiteMat()) {
      t = Object.assign({}, t, { bg: state.customBg, text: lmInkOnWhite(t) });
    }
    // Expose textStyle on the print frame so layout CSS can nudge circle/heart
    // shapes down when the composition is 'header' (title at top of frame).
    var frameEl = document.getElementById('lm-print-frame');
    if (frameEl) frameEl.setAttribute('data-textstyle', ts);
    var html = '';
    // The place line, then the optional date line as a second line of the
    // same style: render_locationmap.py prints "place\ndate" the same way.
    function lmSubHTML(open) {
      return (state.subtitle ? open + esc(state.subtitle) + '</div>' : '') +
             (state.dateLine ? open + esc(state.dateLine) + '</div>' : '');
    }
    // panel/header used to size their title in FIXED px (15/16px desktop,
    // 6/6.5px mobile via a matchMedia switch) while their subtitle + coords
    // took the cqh sizes from .lm-preview-subtitle/.lm-preview-coords. Fixed px
    // does not scale with the print frame, so the title's size RELATIVE to the
    // print depended on whoever rendered it: ~1.7cqh in a 665px-wide desktop
    // customiser, ~0.9cqh in the 1200px CF cart thumbnail (title came out at
    // half size next to a correctly-sized subtitle), and the 300dpi print +
    // generateThumbnail() both already draw the panel/header title at the
    // classic size (3.5% short side / 2.8cqh). Everything is cq-sized now so
    // all consumers agree; the mobile branch is redundant because cqh already
    // tracks the smaller frame. The subtitle letter-spacing (2px desktop) and
    // the small px paddings below are converted at the same ~890px desktop
    // frame height they were tuned on (2px ~ 0.22cqh, 10px ~ 1.1cqh, 4px ~ 0.45cqh).
    var panelSubLetter = '0.22cqh';
    var headerSubLetter = '0.22cqh';

    // Two-font support (mirrors the star map): the message uses state.font, the
    // details (subtitle + coords) use state.detailsFont. Script/handwritten
    // message faces get a size bump so they don't read tiny — kept in sync with
    // SCRIPT_BUMP in starmap-v2.js and _MSG_SCRIPT_BUMP in render_locationmap.py.
    var LM_SCRIPT_BUMP = {
      'Sacramento': 1.25, 'Great Vibes': 1.25, 'Allura': 1.25,
      'Aguafina Script': 1.25, 'Alex Brush': 1.2, 'Parisienne': 1.2,
      'Dancing Script': 1.15, 'Caveat': 1.15, 'Yellowtail': 1.15,
      'Kaushan Script': 1.1, 'Satisfy': 1.1
    };
    var msgBump = LM_SCRIPT_BUMP[state.font] || 1;
    var detFam = state.detailsFont || 'Josefin Sans';
    // Title inline style. baseCqh = the composition's CSS title size; only when a
    // script bump applies do we override font-size (else the CSS rule stands).
    function lmTitleStyle(color, baseCqh, extra) {
      var fs = (msgBump !== 1 && baseCqh) ? (';font-size:' + (baseCqh * msgBump).toFixed(3) + 'cqh') : '';
      return 'color:' + color + ";font-family:'" + state.font + "',serif" + fs + (extra || '');
    }
    function lmDetFam() { return ";font-family:'" + detFam + "',sans-serif"; }

    if (ts === 'classic') {
      var lmShaped = (state.layout === 'circle' || state.layout === 'heart');
      if (lmShaped) {
        // Circle/Heart classic: float the text into the band BELOW the shape
        // (star-map treatment) instead of an opaque bottom strip. The shape
        // leaves the surrounding canvas as uniform theme bg, so no strip is
        // painted. Title pins just under the shape (padding-top clears it);
        // the details group is pushed to the foot via margin-top:auto, lifted
        // off the print edge by bottom. Container query units resolve against
        // #lm-print-frame (a size container). Geometry mirrors the shape's
        // top:8%/3% + width-relative diameter and render_locationmap.py.
        // Landscape sizes the shapes in cqh (see ".landscape
        // .lm-map-inner.layout-circle/heart" in locationmap-section.css);
        // portrait still sizes them off the frame WIDTH, hence the cqw terms.
        // Each expression is `shape top + shape height` = the shape's bottom
        // edge; paddingTop below is the clearance under it (renderer parity:
        // gap_below_shape = 3.5% of h in render_locationmap.py::_add_text).
        var isLand = state.orientation === 'landscape';
        var bandTop = (state.layout === 'heart')
          ? (isLand ? 'calc(6cqh + 65.8cqh)' : 'calc(8cqh + 85cqw)')
          : (isLand ? 'calc(4cqh + 72cqh)' : 'calc(8cqh + 84cqw)');
        el.style.position = 'absolute'; el.style.left = '0'; el.style.right = '0';
        el.style.top = bandTop; el.style.bottom = '4.5cqh';
        el.style.display = 'flex'; el.style.flexDirection = 'column';
        el.style.alignItems = 'stretch';
        el.style.paddingTop = '3.5cqh'; el.style.paddingBottom = '0';
        // Horizontal inset in cqw (not fixed px) so the caption's text-to-box
        // width ratio is frame-size-independent — the customizer preview and the
        // 600px CF headless thumbnail then wrap identically. 2.1cqw ≈ 14px at the
        // ~665px desktop frame where fixed 14px already fit on one line.
        el.style.paddingLeft = '2.1cqw'; el.style.paddingRight = '2.1cqw';
        el.style.textAlign = 'center'; el.style.backgroundColor = 'transparent';
        // Title zone fills the gap between the shape and the details and
        // centres the message within it; details stay pinned at the foot.
        var lmTitleInner = '';
        if (hwHasContent()) lmTitleInner = hwHTML(t.text, 'center');
        else if (state.title) lmTitleInner = '<div class="lm-preview-title" style="' + lmTitleStyle(t.text, 3.42) + '">' + esc(state.title) + '</div>';
        html += '<div class="lm-preview-title-zone" style="flex:1;display:flex;align-items:center;justify-content:center">' + lmTitleInner + '</div>';
        var lmDetails = '';
        lmDetails += lmSubHTML('<div class="lm-preview-subtitle" style="color:' + t.text + lmDetFam() + '">');
        if (lmCoordsLine()) lmDetails += '<div class="lm-preview-coords" style="color:' + t.text + lmDetFam() + '">' + esc(lmCoordsLine()) + '</div>';
        if (lmDetails) html += '<div class="lm-preview-details" style="display:flex;flex-direction:column;align-items:center">' + lmDetails + '</div>';
      } else {
        // Caption band geometry — the ONE source of truth for the classic band,
        // mirrored by caption_band_rect()/_add_text() in render_locationmap.py:
        //
        //   band height = 8% of the print's LONG edge + the bottom lift
        //                 (6cqmin portrait / 4.5cqh landscape),
        //   and the text block sits VERTICALLY CENTRED inside it.
        //
        // It lives here (inline, in JS) rather than in the stylesheet for two
        // reasons: updateTextOverlay's inline styles win over CSS anyway, and
        // the CF thumbnail host loads this file live from the theme — so one
        // edit covers the customiser, the cart thumbnail and the ACP preview
        // without hand-syncing the host's copied <style> block.
        //
        // Before this, the band was content-sized (12px top padding, a
        // 6cqmin !important bottom lift from locationmap-section.css) which
        // (a) left the text hugging the top of the band with a fat gap under
        // it, and (b) made the band ~12% shorter than the printed one, because
        // that 12px top pad is a FIXED px value that does not scale with the
        // frame — so the customiser, the 1200px thumbnail and the 300dpi print
        // all composed the caption slightly differently.
        //
        // min-height (not height) so an unusually long title still grows the
        // band instead of overflowing it; the symmetric padding keeps the text
        // centred and off the print edge in that case too.
        var lmIsLand = state.orientation === 'landscape';
        var lmLift = lmIsLand ? '4.5cqh' : '6cqmin';
        el.style.position = 'absolute'; el.style.bottom = '0'; el.style.left = '0'; el.style.right = '0';
        // border-box explicitly: min-height must include the padding, or the
        // band comes out padding-taller than the print's. The storefront gets
        // this from a global `*{box-sizing:border-box}`, but the CF thumbnail
        // host embeds only the print-frame CSS and has no such reset — so the
        // same code produced a 14.7% band in the customiser and an 18.9% one in
        // the cart thumbnail. Setting it here keeps every consumer identical.
        el.style.boxSizing = 'border-box';
        // 8cq of the LONG edge (was 10.5): the block is centred in the band, so
        // this constant sets the air above and below it together. 10.5 left an
        // A4 print with 13.5mm of beige above the title, which read as a gap
        // rather than a margin. Mirrors caption_band_rect() in
        // render_locationmap.py — change both together.
        el.style.minHeight = 'calc(' + (lmIsLand ? '8cqw' : '8cqh') + ' + ' + lmLift + ')';
        el.style.display = 'flex'; el.style.flexDirection = 'column'; el.style.justifyContent = 'center';
        el.style.padding = 'calc(' + lmLift + ' / 2) 2.1cqw';
        el.style.textAlign = 'center';
        el.style.backgroundColor = t.bg; // opaque band (was t.bg+'cc'); boxed keeps its frosted look
        if (hwHasContent()) html += hwHTML(t.text, 'center');
        else if (state.title) html += '<div class="lm-preview-title" style="' + lmTitleStyle(t.text, 2.8) + '">' + esc(state.title) + '</div>';
        html += lmSubHTML('<div class="lm-preview-subtitle" style="color:' + t.text + lmDetFam() + '">');
        if (lmCoordsLine()) {
          html += '<div class="lm-preview-coords" style="color:' + t.text + lmDetFam() + '">' + esc(lmCoordsLine()) + '</div>';
        }
      }

    } else if (ts === 'boxed') {
      el.style.position = 'absolute'; el.style.bottom = '5%'; el.style.left = '12%'; el.style.right = '12%';
      el.style.padding = '10px 2.4cqw 8px'; el.style.textAlign = 'center';
      el.style.backgroundColor = t.bg + 'cc';
      el.style.border = '1px solid ' + t.text;
      if (hwHasContent()) html += hwHTML(t.text, 'center');
      else if (state.title) html += '<div class="lm-preview-title" style="' + lmTitleStyle(t.text, 2.8) + '">' + esc(state.title) + '</div>';
      html += lmSubHTML('<div class="lm-preview-subtitle" style="color:' + t.text + lmDetFam() + '">');
      if (lmCoordsLine()) {
        html += '<div class="lm-preview-coords" style="color:' + t.text + lmDetFam() + '">' + esc(lmCoordsLine()) + '</div>';
      }

    } else if (ts === 'panel') {
      el.style.position = 'absolute'; el.style.bottom = '0'; el.style.left = '0'; el.style.right = '0';
      el.style.padding = '1.1cqh 2.1cqw 0.9cqh'; el.style.textAlign = 'left';
      el.style.backgroundColor = '#ffffff';
      var panelTextColor = '#111111';
      if (hwHasContent()) html += hwHTML(panelTextColor, 'left');
      else if (state.title) html += '<div class="lm-preview-title" style="' + lmTitleStyle(panelTextColor, 2.8) + '">' + esc(state.title) + '</div>';
      html += lmSubHTML('<div class="lm-preview-subtitle" style="color:#666;text-transform:uppercase;letter-spacing:' + panelSubLetter + lmDetFam() + '">');
      if (lmCoordsLine()) {
        html += '<div class="lm-preview-coords" style="color:#999;margin-top:0.45cqh' + lmDetFam() + '">' + esc(lmCoordsLine()) + '</div>';
      }

    } else if (ts === 'header') {
      // White bar across the top: title left, details (place, date, coords)
      // stacked right, both blocks centred on the bar's middle line. This is
      // the VISIBLE design: bar = 8% of the long edge (the classic band
      // constant), growing so the text keeps 2.4cqmin of air above and below;
      // 5% side margins. Details are sized in cqmin (1.8 / 1.3, line-height
      // 1.4), the print's own units (1.8% / 1.3% of the short side, 1.4
      // step): at the shared 1.5/1.15cqh sizes three lines filled the whole
      // bar and the place line sat ~1% from the top edge. render_locationmap.py
      // prints the same bar plus the frame lip (the bar grows by it, the
      // margins inset by it), so what shows inside the frame matches this.
      // The bar used to be content-sized with 1.1cqh of top padding, and the
      // print drew the title 7mm from the top edge, under the 9mm lip.
      // box-sizing inline for the CF thumbnail host (no global reset there,
      // see the classic band below).
      el.style.position = 'absolute'; el.style.top = '0'; el.style.left = '0'; el.style.right = '0'; el.style.bottom = 'auto';
      el.style.boxSizing = 'border-box';
      el.style.minHeight = state.orientation === 'landscape' ? '8cqw' : '8cqh';
      el.style.display = 'flex'; el.style.alignItems = 'center';
      el.style.padding = '2.4cqmin 5cqw'; el.style.textAlign = 'left';
      el.style.backgroundColor = '#ffffff';
      var headerTextColor = '#111111';
      html += '<div style="display:flex;justify-content:space-between;align-items:center;gap:3cqw;width:100%">';
      html += '<div class="lm-header-title-col" style="flex:0 0 auto;width:45cqw;max-width:45cqw;min-width:0">';
      if (hwHasContent()) html += hwHTML(headerTextColor, 'left');
      else if (state.title) html += '<div class="lm-preview-title" style="' + lmTitleStyle(headerTextColor, 2.8, ';margin-bottom:0;white-space:nowrap') + '">' + esc(state.title) + '</div>';
      html += '</div>';
      html += '<div style="text-align:right;min-width:0">';
      html += lmSubHTML('<div class="lm-preview-subtitle" style="color:#666;text-transform:uppercase;letter-spacing:' + headerSubLetter + ';font-size:1.8cqmin;line-height:1.4;margin:0' + lmDetFam() + '">');
      if (lmCoordsLine()) html += '<div class="lm-preview-coords" style="color:#999;font-size:1.3cqmin;line-height:1.4;margin:0' + lmDetFam() + '">' + esc(lmCoordsLine()) + '</div>';
      html += '</div>';
      html += '</div>';

    } else if (ts === 'gradient') {
      el.style.position = 'absolute'; el.style.bottom = '0';
      el.style.left = lmOverlaySideInset(); el.style.right = lmOverlaySideInset();
      el.style.padding = '40px 2.1cqw 10px'; el.style.textAlign = 'center';
      el.style.background = 'linear-gradient(to top, ' + t.bg + ' 0%, ' + t.bg + 'ee 40%, transparent 100%)';
      if (hwHasContent()) html += hwHTML(t.text, 'center');
      else if (state.title) html += '<div class="lm-preview-title" style="' + lmTitleStyle(t.text, 2.8) + '">' + esc(state.title) + '</div>';
      html += lmSubHTML('<div class="lm-preview-subtitle" style="color:' + t.text + lmDetFam() + '">');
      if (lmCoordsLine()) {
        html += '<div class="lm-preview-coords" style="color:' + t.text + lmDetFam() + '">' + esc(lmCoordsLine()) + '</div>';
      }

    } else if (ts === 'place') {
      // City (large, tracked) + country (small) + coords, centered over the map
      // with a soft theme-tinted scrim for legibility. Size/tracking come from
      // the [data-textstyle="place"] CSS rules; here we set position + scrim and
      // the per-font colours. City uses state.font, details use detailsFont.
      el.style.position = 'absolute'; el.style.bottom = '0';
      el.style.left = lmOverlaySideInset(); el.style.right = lmOverlaySideInset();
      el.style.padding = '44px 2.1cqw 16px'; el.style.textAlign = 'center';
      el.style.background = 'linear-gradient(to top, ' + t.bg + 'f2 0%, ' + t.bg + 'a8 42%, transparent 100%)';
      // A handwritten message takes the city's place, as it takes the title's
      // in every other composition (it was ignored here, so the drawn message
      // never showed on Place). Mirrors the place branch of render_locationmap.py.
      if (hwHasContent()) {
        html += hwHTML(t.text, 'center');
        html += '<div class="lm-place-rule" style="background:' + t.text + '"></div>';
      } else if (state.title) {
        html += '<div class="lm-preview-title" style="' + lmTitleStyle(t.text, 4.4) + '">' + esc(state.title) + '</div>';
        // Short centred rule under the city for a refined map-poster look.
        html += '<div class="lm-place-rule" style="background:' + t.text + '"></div>';
      }
      html += lmSubHTML('<div class="lm-preview-subtitle" style="color:' + t.text + lmDetFam() + '">');
      if (lmCoordsLine()) {
        html += '<div class="lm-preview-coords" style="color:' + t.text + lmDetFam() + '">' + esc(lmCoordsLine()) + '</div>';
      }
    }

    el.innerHTML = html;
    lmScaleCaption(el, ts === 'place' || ts === 'header');
    if (ts === 'place') lmFitPlaceTitle(el, 4.4 * msgBump * lmTextScale());
    if (ts === 'header') lmFitPlaceTitle(el, 2.8 * msgBump * lmTextScale(), el.querySelector('.lm-header-title-col'));
    scheduleLM3DRefresh();
  }

  function fmtCoords() {
    var latD = Math.abs(state.lat).toFixed(4) + '°' + (state.lat >= 0 ? 'N' : 'S');
    var lngD = Math.abs(state.lng).toFixed(4) + '°' + (state.lng >= 0 ? 'E' : 'W');
    return latD + ' / ' + lngD;
  }

  // ── UPDATE: Variant + Price ──
  function updateVariantAndPrice() {
    var variants = window.lmProductVariants || [];
    var match = null;

    for (var i = 0; i < variants.length; i++) {
      var v = variants[i];
      if (v.option1 === state.selectedType && v.option2 === state.selectedSize) {
        if (state.selectedType === 'Framed') {
          if (v.option3 === state.selectedFrame) { match = v; break; }
        } else {
          match = v; break;
        }
      }
    }

    // The wrapping pills render from the gift product's FIRST variant (the
    // smallest tier), so re-stamp them for the size selected now.
    try { if (window.SkylitGifts) window.SkylitGifts.refreshLabels(state.selectedSize); } catch (e) {}

    var addBtn = document.querySelector('.lm-add-to-cart-btn');
    if (match) {
      document.getElementById('lm-variant-id').value = match.id;
      var priceEl = document.getElementById('lm-price');
      if (priceEl) {
        // Include the selected wrapping in the displayed Total so the label
        // matches what the customer actually pays at checkout.
        var giftPence = GIFT_PRICES[state.selectedGift] || 0;
        // Size-tiered wrap/bundle price when the shared map is loaded.
        try {
          if (state.selectedGift !== 'none' && window.SkylitGifts) {
            giftPence = window.SkylitGifts.priceFor(state.selectedGift, state.selectedSize) || giftPence;
          }
        } catch (e) {}
        var amt = ((parseInt(match.price) + giftPence) / 100).toFixed(2);
        priceEl.textContent = '£' + amt;
      }
      if (addBtn) {
        // Claim mode owns this button's label/disabled state completely —
        // design-claim.js's setCtaLabel() already collapsed the button's
        // markup to plain text (wiping the <span>/<img> this function
        // otherwise reads via addBtn.querySelector('span')), and this
        // function runs on every option change/hydration pass, so without
        // this branch the very next call here would throw on a null
        // querySelector('span') and/or silently re-enable a blocked/loading
        // claim CTA with the normal "Add To Cart" copy. Must never let the
        // normal availability copy win while a claim is active.
        if (lmClaimActive()) {
          var claimUsable = !!(window.SkylitClaim.usable && window.SkylitClaim.claim);
          // Mirror aria-disabled with .disabled — design-claim.js's
          // setCtaLabel() only ever sets [data-claim-cta] to a disabled
          // state; this is a re-enable path, so it must clear
          // aria-disabled too or assistive tech keeps announcing the
          // button as unavailable. Keep this pair moving together.
          addBtn.disabled = !claimUsable;
          addBtn.setAttribute('aria-disabled', String(!claimUsable));
          addBtn.textContent = claimUsable ? 'Confirm my design' : (window.SkylitClaim.message || 'Unavailable');
        } else if (match.available) {
          addBtn.disabled = false;
          addBtn.querySelector('span').textContent = state.editMode ? 'Confirm edits' : 'Add To Cart';
        } else {
          addBtn.disabled = true;
          addBtn.querySelector('span').textContent = 'Sold Out';
        }
      }
    } else if (addBtn) {
      if (lmClaimActive()) {
        addBtn.disabled = true;
        addBtn.setAttribute('aria-disabled', 'true');
        addBtn.textContent = window.SkylitClaim.message || 'Unavailable';
      } else {
        addBtn.disabled = true;
        addBtn.querySelector('span').textContent = 'Unavailable';
      }
    }
  }

  // ── Generate Thumbnail from Mapbox canvas ──
  function hexToRgba(hex, a) {
    var r = parseInt(hex.slice(1,3),16), g = parseInt(hex.slice(3,5),16), b = parseInt(hex.slice(5,7),16);
    return 'rgba(' + r + ',' + g + ',' + b + ',' + (a === undefined ? 1 : a) + ')';
  }
  function hexToRgba0(hex) { return hexToRgba(hex, 0); }

  // ── Mockup compositing ──────────────────────────────────────────────
  // Portrait mockup: 800×600 canvas, frame cutout coords below.
  // Landscape mockup: 900×600 canvas, different cutout (horizontal frame).
  var _lmMockupOverlayPortrait = null;
  var _lmMockupOverlayLandscape = null;
  // Fractions measured from the overlay PNGs' transparent windows (soft
  // alpha<250 bbox + 2px bleed) — keep in sync with skylit-admin
  // app/lib/location-thumbnail-render.ts. The old values sat inside the soft
  // window edge, leaving a hairline of bare wall around the print.
  var MF_PORTRAIT = { left: 0.3375, top: 0.2117, right: 0.6462, bottom: 0.795, mw: 800, mh: 600 };
  var MF_LANDSCAPE = { left: 0.2756, top: 0.2167, right: 0.7233, bottom: 0.735, mw: 900, mh: 600 };
  (function preloadMockups() {
    var el = document.querySelector('[data-mockup-overlay]');
    if (!el) return;
    var srcP = el.getAttribute('data-mockup-overlay');
    if (srcP) {
      var ip = new Image();
      ip.crossOrigin = 'anonymous';
      ip.onload = function() { _lmMockupOverlayPortrait = ip; };
      ip.src = srcP;
    }
    var srcL = el.getAttribute('data-mockup-overlay-landscape');
    if (srcL) {
      var il = new Image();
      il.crossOrigin = 'anonymous';
      il.onload = function() { _lmMockupOverlayLandscape = il; };
      il.src = srcL;
    }
  })();

  function _compositeIntoMockup(printCanvas) {
    var isLandscape = state.orientation === 'landscape';
    var overlay = isLandscape ? _lmMockupOverlayLandscape : _lmMockupOverlayPortrait;
    var MF = isLandscape ? MF_LANDSCAPE : MF_PORTRAIT;
    if (!overlay) {
      try { return printCanvas.toDataURL('image/jpeg', 0.85); }
      catch (e) { return null; }
    }
    var mw = MF.mw, mh = MF.mh;
    var mc = document.createElement('canvas');
    mc.width = mw; mc.height = mh;
    var mx = mc.getContext('2d');
    var fx = Math.round(MF.left * mw), fy = Math.round(MF.top * mh);
    var fw = Math.round((MF.right - MF.left) * mw);
    var fh = Math.round((MF.bottom - MF.top) * mh);
    var srcAr = printCanvas.width / printCanvas.height;
    var dstAr = fw / fh;
    var dx, dy, dw, dh;
    if (srcAr > dstAr) { dh = fh; dw = fh * srcAr; dx = fx + (fw - dw) / 2; dy = fy; }
    else { dw = fw; dh = fw / srcAr; dx = fx; dy = fy + (fh - dh) / 2; }
    mx.save();
    mx.beginPath(); mx.rect(fx, fy, fw, fh); mx.clip();
    try { mx.drawImage(printCanvas, dx, dy, dw, dh); } catch (e) {}
    mx.restore();
    try { mx.drawImage(overlay, 0, 0, mw, mh); } catch (e) {}
    try { return mc.toDataURL('image/jpeg', 0.85); }
    catch (e) { return null; }
  }

  function generateThumbnail() {
    // Flat canvas fallback (3D-landmark carts only): the optional date line
    // rides on the place line here rather than re-flowing every layout below.
    var lmThumbSub = (state.subtitle || '') + (state.dateLine ? (state.subtitle ? ' · ' : '') + state.dateLine : '');
    try {
      // Pre-flight: the Mapbox `map` reference must exist + carry a canvas.
      // If we're somehow here before map init, beacon the skip reason
      // before bailing so the order-detail page has a paper trail rather
      // than a silent null.
      if (!map || typeof map.getCanvas !== 'function') {
        if (window.skylitBeaconThumbnailSkip) {
          try { window.skylitBeaconThumbnailSkip(state.orderId || '', 'location_map', 'no_custom_frame', { reason: 'map_not_ready' }); } catch (e) {}
        }
        return null;
      }
      var mapCanvas = map.getCanvas();
      // Source AR comes from the selected paper size, not the live map
      // element. On some mobile browsers `aspect-ratio` on #lm-print-frame
      // doesn't resolve cleanly and offsetWidth/offsetHeight collapses to
      // ~square — that fed a wrong AR into the cover-fit and produced
      // edge-clipped circles in the cart thumbnail.
      var sizeKey = (state.selectedSize || '').toLowerCase().trim();
      var portraitAR = sizeRatios[sizeKey];
      if (portraitAR == null) {
        portraitAR = sizeRatios[sizeKey + ' cm'];   // tolerate "30 x 40" vs "30 x 40 cm"
      }
      var srcAR;
      if (portraitAR != null) {
        srcAR = (state.orientation === 'landscape') ? (1 / portraitAR) : portraitAR;
      } else {
        // Fall back to the live element when the size isn't in the map.
        var mapEl = map.getContainer();
        var srcW = (mapEl && mapEl.offsetWidth)  || mapCanvas.width;
        var srcH = (mapEl && mapEl.offsetHeight) || mapCanvas.height;
        srcAR = srcW / srcH;
      }
      var thumbW, thumbH;
      if (state.orientation === 'landscape') {
        thumbW = 400;
        thumbH = Math.round(thumbW / srcAR);
      } else {
        thumbH = 500;
        thumbW = Math.round(thumbH * srcAR);
      }
      var t = lmThemeData();
      var bg = (state.customBg === 'theme') ? t.bg : state.customBg;

      var canvas = document.createElement('canvas');
      canvas.width = thumbW;
      canvas.height = thumbH;
      var ctx = canvas.getContext('2d');

      // Fill background first (visible for circle/heart/fade).
      // Full layout draws the map edge-to-edge and never uses bg — skip the
      // fill so a stale customBg from a previous layout choice can't show.
      if (state.layout !== 'full') {
        ctx.fillStyle = bg;
        ctx.fillRect(0, 0, thumbW, thumbH);
      }

      // Apply layout clipping before drawing map
      if (state.layout === 'circle') {
        ctx.save();
        var cr = Math.min(thumbW, thumbH) * 0.38;
        var cx = thumbW / 2;
        var cy = thumbH * 0.40;
        ctx.beginPath();
        ctx.arc(cx, cy, cr, 0, Math.PI * 2);
        ctx.clip();
        ctx.drawImage(mapCanvas, 0, 0, thumbW, thumbH);
        ctx.restore();
        var borderBg = bg || '#ffffff';
        var r = parseInt(borderBg.slice(1,3)||'ff',16), g = parseInt(borderBg.slice(3,5)||'ff',16), b2 = parseInt(borderBg.slice(5,7)||'ff',16);
        var lum = 0.299*r + 0.587*g + 0.114*b2;
        ctx.strokeStyle = lum < 128 ? '#ffffff' : '#000000';
        ctx.lineWidth = Math.max(1, cr * 0.016);
        ctx.beginPath();
        ctx.arc(cx, cy, cr, 0, Math.PI * 2);
        ctx.stroke();
      } else if (state.layout === 'heart') {
        // Match the live customizer's heart clip exactly — same SVG path
        // data as sections/locationmap.liquid:1067 (clipPath#lm-heart-clip),
        // applied via Path2D inside a 0..1 unit-space transform so it
        // scales cleanly to the thumbnail aspect. The previous hand-rolled
        // bezier with hs*2.5 control offsets produced a squat, asymmetric
        // heart that didn't match the customizer (see order mho8k47eioh).
        // Starmap-v2 uses the same technique in its cart thumbnail.
        var HEART_PATH = 'M0.5,0.98 C0.25,0.7 0.02,0.55 0.02,0.3 C0.02,0.15 0.09,0.02 0.24,0.02 C0.37,0.02 0.46,0.1 0.5,0.18 C0.54,0.1 0.63,0.02 0.76,0.02 C0.91,0.02 0.98,0.15 0.98,0.3 C0.98,0.55 0.75,0.7 0.5,0.98Z';
        ctx.save();
        ctx.scale(thumbW, thumbH);
        try { ctx.clip(new Path2D(HEART_PATH)); } catch (e) {}
        // Undo the unit-space transform so the subsequent drawImage uses
        // the same coords as the other branches (0..thumbW, 0..thumbH).
        ctx.scale(1 / thumbW, 1 / thumbH);
        ctx.drawImage(mapCanvas, 0, 0, thumbW, thumbH);
        ctx.restore();

        // Stroke the heart outline so the print silhouette reads cleanly
        // against the matte — matches the customizer's .lm-heart-border
        // SVG overlay. Same luminance-aware color the circle branch uses.
        var hbg = bg || '#ffffff';
        var hr = parseInt(hbg.slice(1,3) || 'ff', 16);
        var hg = parseInt(hbg.slice(3,5) || 'ff', 16);
        var hb = parseInt(hbg.slice(5,7) || 'ff', 16);
        ctx.save();
        ctx.scale(thumbW, thumbH);
        ctx.strokeStyle = (0.299 * hr + 0.587 * hg + 0.114 * hb) < 128 ? '#ffffff' : '#000000';
        ctx.lineWidth = 0.005;
        ctx.lineCap = 'round';
        ctx.stroke(new Path2D(HEART_PATH));
        ctx.restore();
      } else {
        ctx.drawImage(mapCanvas, 0, 0, thumbW, thumbH);
      }

      // Fade overlay — use bg with alpha 0 instead of 'transparent' (which is rgba(0,0,0,0) and causes dark banding)
      if (state.layout === 'fade') {
        var bgT = hexToRgba0(bg);
        var gTop = ctx.createLinearGradient(0, 0, 0, thumbH * 0.15);
        gTop.addColorStop(0, bg); gTop.addColorStop(1, bgT);
        ctx.fillStyle = gTop; ctx.fillRect(0, 0, thumbW, thumbH * 0.15);
        var gBot = ctx.createLinearGradient(0, thumbH * 0.75, 0, thumbH);
        gBot.addColorStop(0, bgT); gBot.addColorStop(1, bg);
        ctx.fillStyle = gBot; ctx.fillRect(0, thumbH * 0.75, thumbW, thumbH * 0.25);
        var gL = ctx.createLinearGradient(0, 0, thumbW * 0.12, 0);
        gL.addColorStop(0, bg); gL.addColorStop(1, bgT);
        ctx.fillStyle = gL; ctx.fillRect(0, 0, thumbW * 0.12, thumbH);
        var gR = ctx.createLinearGradient(thumbW * 0.88, 0, thumbW, 0);
        gR.addColorStop(0, bgT); gR.addColorStop(1, bg);
        ctx.fillStyle = gR; ctx.fillRect(thumbW * 0.88, 0, thumbW * 0.12, thumbH);
      }

      // Draw icons — scale emoji size by the map→thumbnail ratio so the
      // customer's chosen icon size (iconSize slider, 16–48px in the editor)
      // is preserved visually in the cart thumbnail. Falls back to the old
      // hard-coded heuristic if the map element width can't be read.
      if (placedIcons.length > 0) {
        var mapEl = map.getContainer();
        var mapW = mapEl && mapEl.offsetWidth ? mapEl.offsetWidth : 0;
        var scale = mapW ? (thumbW / mapW) : null;
        placedIcons.forEach(function(p) {
          try {
            var lngLat = p.marker.getLngLat();
            var point = map.project([lngLat.lng, lngLat.lat]);
            var px = mapW ? (point.x / mapW) * thumbW : point.x;
            var py = mapEl.offsetHeight ? (point.y / mapEl.offsetHeight) * thumbH : point.y;
            if (px >= 0 && px <= thumbW && py >= 0 && py <= thumbH) {
              // Per-icon size + rotation.
              var fs = scale
                ? Math.max(8, Math.round((p.size || iconSize) * scale))
                : Math.round(thumbW * 0.08);
              ctx.save();
              ctx.translate(px, py);
              if (p.rotation) ctx.rotate(p.rotation * Math.PI / 180);
              ctx.font = fs + 'px serif';
              ctx.textAlign = 'center';
              ctx.textBaseline = 'middle';
              ctx.fillText(p.emoji, 0, 0);
              // Optional caption below the emoji — mirrors the marker label
              // (white pill chip, dark ink, 0.34× the icon size).
              var labText = (p.label || '').trim();
              if (labText) {
                var labFS = Math.max(5, Math.round(fs * 0.34));
                ctx.font = '600 ' + labFS + "px 'Josefin Sans', sans-serif";
                var padX = Math.max(3, Math.round(labFS * 0.55));
                var padY = Math.max(1, Math.round(labFS * 0.18));
                var tw = ctx.measureText(labText).width;
                var by = Math.round(fs * 0.62);
                var bh = labFS + padY * 2;
                ctx.fillStyle = 'rgba(255,255,255,0.94)';
                if (ctx.roundRect) {
                  ctx.beginPath();
                  ctx.roundRect(-tw / 2 - padX, by, tw + padX * 2, bh, bh / 2);
                  ctx.fill();
                } else {
                  ctx.fillRect(-tw / 2 - padX, by, tw + padX * 2, bh);
                }
                ctx.fillStyle = '#2b2b2b';
                ctx.textBaseline = 'top';
                ctx.fillText(labText, 0, by + padY);
              }
              ctx.restore();
            }
          } catch(e) {}
        });
      }

      // Draw text overlay — respects composition style
      // Font sizes mirror the customizer's cqh units: the #lm-print-frame is
      // container-type:size, so 1cqh == 1% of frame height. In the thumbnail
      // the "frame" is the canvas itself (pre-mockup), so cqh ≡ 1% of thumbH.
      // Matches: .lm-preview-title 2.8cqh, .lm-preview-subtitle 1.5cqh,
      // .lm-preview-coords 1.15cqh.
      var ts = state.textStyle || 'classic';
      var titleFS = Math.max(10, Math.round(thumbH * 0.028));
      var subFS   = Math.max(7,  Math.round(thumbH * 0.015));
      var coordFS = Math.max(6,  Math.round(thumbH * 0.0115));
      var lineGap = Math.max(1,  Math.round(thumbH * 0.003));   // 0.3cqh
      // Text-driven band height (padding + 3 lines) so each style mirrors
      // the customizer instead of using a fixed 15% slab.
      var padTop  = Math.round(thumbH * 0.024);                 // ~12px on 500
      var padBot  = Math.round(thumbH * 0.020);                 // ~10px on 500
      // Handwriting slot — when active, the title row grows to a taller
      // band scaled by handwritingHeight so the rasterised strokes fit.
      var hwActive = state.handwritingEnabled && state.handwritingStrokes && state.handwritingStrokes.length > 0;
      var hwHeightFactor = Math.max(0.4, Math.min(1.4, state.handwritingHeight || 1));
      var hwSlotH = hwActive ? Math.round(thumbH * 0.085 * hwHeightFactor) : 0;
      var titleSlotH = hwActive ? hwSlotH : titleFS;
      var textH = padTop + titleSlotH + lineGap + subFS + lineGap + coordFS + padBot;
      function paintHwBlock(cx, cyMid, maxW, slotH, color) {
        var vbw = 1000, vbh = 240;
        var sizeF = Math.max(0.2, Math.min(1.1, state.handwritingSize || 0.75));
        var availW = maxW * sizeF;
        var scale = Math.min(availW / vbw, slotH / vbh);
        var dw = vbw * scale, dh = vbh * scale;
        var ox = cx - dw / 2, oy = cyMid - dh / 2;
        ctx.save();
        ctx.strokeStyle = color || '#111';
        ctx.lineCap = 'round'; ctx.lineJoin = 'round';
        for (var i = 0; i < state.handwritingStrokes.length; i++) {
          var s = state.handwritingStrokes[i];
          if (!s.pts || !s.pts.length) continue;
          s = { pts: hwSmoothPts(s.pts), w: s.w };
          ctx.lineWidth = Math.max(1, (s.w || 4) * scale);
          ctx.beginPath();
          ctx.moveTo(ox + s.pts[0][0] * scale, oy + s.pts[0][1] * scale);
          if (s.pts.length === 1) {
            ctx.lineTo(ox + s.pts[0][0] * scale + 0.01, oy + s.pts[0][1] * scale + 0.01);
          } else if (s.pts.length === 2) {
            ctx.lineTo(ox + s.pts[1][0] * scale, oy + s.pts[1][1] * scale);
          } else {
            // Midpoint quadratic smoothing — mirrors pad/preview/renderer.
            for (var j = 1; j < s.pts.length - 1; j++) {
              var cpx = ox + s.pts[j][0] * scale, cpy = oy + s.pts[j][1] * scale;
              var nxx = ox + s.pts[j + 1][0] * scale, nxy = oy + s.pts[j + 1][1] * scale;
              ctx.quadraticCurveTo(cpx, cpy, (cpx + nxx) / 2, (cpy + nxy) / 2);
            }
            ctx.lineTo(ox + s.pts[s.pts.length - 1][0] * scale, oy + s.pts[s.pts.length - 1][1] * scale);
          }
          ctx.stroke();
        }
        ctx.restore();
      }
      // ── Title wrapping ──────────────────────────────────────────────
      // The customizer's title is an HTML div, so it wraps; canvas fillText
      // doesn't, and long titles used to run straight off both edges of the
      // cart image. Wrap to at most maxLines, ellipsise whatever still
      // overflows, and grow the band so the extra line has somewhere to sit.
      function lmWrapText(text, fontSpec, maxW, maxLines, ls) {
        ctx.save();
        ctx.font = fontSpec;
        if (ls && ('letterSpacing' in ctx)) ctx.letterSpacing = ls + 'px';
        var words = String(text || '').split(/\s+/);
        var lines = [], cur = '', truncated = false;
        for (var i = 0; i < words.length; i++) {
          if (!words[i]) continue;
          var probe = cur ? cur + ' ' + words[i] : words[i];
          // `!cur` forces the first word onto the line even if it alone is too
          // wide — the ellipsis pass below trims it.
          if (!cur || ctx.measureText(probe).width <= maxW) { cur = probe; continue; }
          if (lines.length + 1 >= maxLines) { truncated = true; break; }
          lines.push(cur); cur = words[i];
        }
        if (cur) lines.push(cur);
        var last = lines.length ? lines[lines.length - 1] : '';
        if (last && (truncated || ctx.measureText(last).width > maxW)) {
          if (truncated) last += '…';
          while (last.length > 1 && ctx.measureText(last).width > maxW) {
            last = last.slice(0, -2) + '…';
          }
          lines[lines.length - 1] = last;
        }
        ctx.restore();
        return lines;
      }
      // slotH is measured from the slot top to the LAST line's baseline, so a
      // single-line title keeps the existing `padTop + titleSlotH` geometry
      // untouched and extra lines grow the band upward.
      // `text` overrides state.title — pass it when the branch transforms the
      // string before drawing (place uppercases, which measures wider) or when
      // the branch renders the typed title even with handwriting active.
      function lmTitleSlot(fontSpec, maxW, maxLines, fs, ls, text) {
        var step = Math.round(fs * 1.15);
        if (text === undefined) {
          if (hwActive) return { lines: [], slotH: hwSlotH, step: step };
          text = state.title;
        }
        if (!text) return { lines: [], slotH: fs, step: step };
        var lines = lmWrapText(text, fontSpec, maxW, maxLines, ls);
        return { lines: lines, slotH: fs + (lines.length - 1) * step, step: step };
      }
      // `lastBaseline` is where the FINAL line sits; earlier lines stack above
      // it, which is what keeps the details below the title in place.
      function lmDrawTitleLines(lines, x, lastBaseline, step) {
        var top = lastBaseline - (lines.length - 1) * step;
        for (var i = 0; i < lines.length; i++) ctx.fillText(lines[i], x, top + i * step);
      }
      // One-line fit for the place title: shrink font size and tracking
      // together (tracking stays lsRatio of the size, floor 65%) until the
      // text fits maxW. Mirrors lmFitPlaceTitle() in updateTextOverlay and
      // _fit_title(max_lines=1) in render_locationmap.py.
      function lmFitOneLine(text, fontFor, fs, lsRatio, maxW) {
        var min = Math.max(6, Math.round(fs * 0.65)), cur = fs, ls = Math.round(fs * lsRatio);
        ctx.save();
        while (text) {
          ls = Math.round(cur * lsRatio);
          ctx.font = fontFor(cur);
          if ('letterSpacing' in ctx) ctx.letterSpacing = ls + 'px';
          if (cur <= min || ctx.measureText(text).width <= maxW) break;
          cur = Math.max(min, Math.floor(cur * 0.95));
        }
        ctx.restore();
        return { fs: cur, ls: ls };
      }

      // Buffer from the canvas bottom for bottom-anchored bands so the
      // mockup's cover-fit trim (~2% top/bottom in landscape) doesn't clip
      // the last line of text. Landscape cutout trims ~5px off a 320px
      // thumbnail, so ~4% keeps a visible breathing gap afterwards.
      var bottomSafety = Math.round(thumbH * 0.04);
      var latD = Math.abs(state.lat).toFixed(4) + '\u00B0' + (state.lat >= 0 ? 'N' : 'S');
      var lngD = Math.abs(state.lng).toFixed(4) + '\u00B0' + (state.lng >= 0 ? 'E' : 'W');
      var coordStr = lmCoordsLine();  // the coordinates field: live lat/lng or the customer's own line
      var lmThumbDet = state.detailsFont || 'Josefin Sans';  // details (subtitle+coords) font

      if (ts === 'header') {
        // Mirrors the header branch of updateTextOverlay: bar = 8% of the long
        // edge, grown to keep 2.4% of the short side above and below the
        // text; 5% margins; details at 1.8% / 1.3% of the short side with a
        // 1.4 line step; title one line shrunk into 45%; both blocks centred
        // on the bar's middle line.
        var hMin = Math.min(thumbW, thumbH);
        var mx = Math.round(thumbW * 0.05);
        var hSubFS = Math.max(6, Math.round(hMin * 0.018)), hCoordFS = Math.max(5, Math.round(hMin * 0.013));
        var hdrLines = [];
        if (state.subtitle) hdrLines.push({ t: state.subtitle.toUpperCase(), fs: hSubFS, c: '#666666' });
        if (state.dateLine) hdrLines.push({ t: state.dateLine.toUpperCase(), fs: hSubFS, c: '#666666' });
        if (coordStr) hdrLines.push({ t: coordStr, fs: hCoordFS, c: '#999999' });
        var hdrStep = function (ln) { return Math.round(ln.fs * 1.4); };
        var hdrBlockH = 0;
        for (var hi = 0; hi < hdrLines.length; hi++) hdrBlockH += hdrStep(hdrLines[hi]);
        var hdrPad = Math.round(hMin * 0.024);
        var barH = Math.max(Math.round(Math.max(thumbW, thumbH) * 0.08),
                            Math.max(hdrBlockH, hwActive ? hwSlotH : titleFS * 1.2) + hdrPad * 2);
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, thumbW, barH);
        var vc = Math.round(barH / 2);
        ctx.textBaseline = 'middle';
        if (hwActive) {
          paintHwBlock(mx + thumbW * 0.225, vc, thumbW * 0.45, hwSlotH, '#111111');
        } else if (state.title) {
          var hFit = lmFitOneLine(state.title, function (fs) { return 'bold ' + fs + 'px ' + state.font; },
                                  titleFS, 0.15 / 2.8, thumbW * 0.45);
          ctx.save();
          ctx.fillStyle = '#111111'; ctx.font = 'bold ' + hFit.fs + 'px ' + state.font; ctx.textAlign = 'left';
          if ('letterSpacing' in ctx) ctx.letterSpacing = hFit.ls + 'px';
          ctx.fillText(state.title, mx, vc);
          ctx.restore();
        }
        var hy = vc - hdrBlockH / 2;
        ctx.textAlign = 'right';
        for (var hj = 0; hj < hdrLines.length; hj++) {
          var hl = hdrLines[hj], hs = hdrStep(hl);
          ctx.fillStyle = hl.c; ctx.font = hl.fs + 'px ' + lmThumbDet;
          ctx.fillText(hl.t, thumbW - mx, Math.round(hy + hs / 2));
          hy += hs;
        }
        ctx.textBaseline = 'alphabetic';
      } else if (ts === 'panel') {
        // Shift the band up by bottomSafety and extend the fill all the way
        // down to the canvas edge so the panel still looks flush with the
        // mockup frame after cover-fit (no white-to-customBg seam).
        var mx2 = Math.round(thumbW * 0.04);
        var pTL = lmTitleSlot('bold ' + titleFS + 'px ' + state.font, thumbW - mx2 * 2, 2, titleFS);
        titleSlotH = pTL.slotH;
        textH = padTop + titleSlotH + lineGap + subFS + lineGap + coordFS + padBot;
        var pBandTop = thumbH - textH - bottomSafety;
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, pBandTop, thumbW, textH + bottomSafety);
        ctx.textAlign = 'left';
        var py = pBandTop + padTop + titleSlotH;  // last title baseline (or hw slot bottom)
        if (hwActive) {
          paintHwBlock(thumbW * 0.5, pBandTop + padTop + titleSlotH / 2, thumbW - mx2 * 2, hwSlotH, '#111111');
        } else if (pTL.lines.length) { ctx.fillStyle = '#111111'; ctx.font = 'bold ' + titleFS + 'px ' + state.font; lmDrawTitleLines(pTL.lines, mx2, py, pTL.step); }
        py += lineGap + subFS;
        if (lmThumbSub) { ctx.fillStyle = '#666666'; ctx.font = subFS + 'px ' + lmThumbDet; ctx.fillText(lmThumbSub.toUpperCase(), mx2, py); }
        py += lineGap + coordFS;
        if (coordStr) { ctx.fillStyle = '#999999'; ctx.font = coordFS + 'px ' + lmThumbDet; ctx.fillText(coordStr, mx2, py); }
      } else if (ts === 'boxed') {
        var bxm = Math.round(thumbW * 0.12); var bym = Math.round(thumbH * 0.05);
        var bTL = lmTitleSlot('bold ' + titleFS + 'px ' + state.font,
                              thumbW - bxm * 2 - Math.round(thumbW * 0.02), 2, titleFS);
        titleSlotH = bTL.slotH;
        textH = padTop + titleSlotH + lineGap + subFS + lineGap + coordFS + padBot;
        // Match customizer: el.style.backgroundColor = t.bg + 'cc' (theme bg,
        // not the outer canvas bg — which may be a customBg the user picked).
        ctx.fillStyle = t.bg; ctx.globalAlpha = 0.8;
        ctx.fillRect(bxm, thumbH - textH - bym, thumbW - bxm * 2, textH);
        ctx.globalAlpha = 1.0; ctx.strokeStyle = t.text; ctx.lineWidth = 1;
        ctx.strokeRect(bxm, thumbH - textH - bym, thumbW - bxm * 2, textH);
        ctx.textAlign = 'center';
        // Keep the original single-line anchor (0.35 of the box) and push the
        // baseline down by the extra lines so the first one still clears the
        // box's top edge.
        var yb = thumbH - textH - bym + Math.round(textH * 0.35)
               + (bTL.lines.length > 1 ? (bTL.lines.length - 1) * bTL.step : 0);
        if (hwActive) {
          paintHwBlock(thumbW / 2, yb - titleSlotH / 2, thumbW - bxm * 2, hwSlotH, t.text);
        } else if (bTL.lines.length) { ctx.fillStyle = t.text; ctx.font = 'bold ' + titleFS + 'px ' + state.font; lmDrawTitleLines(bTL.lines, thumbW / 2, yb, bTL.step); }
        var subYOffset = hwActive ? Math.round(titleSlotH * 0.6) : Math.round(titleFS * 1.0);
        if (lmThumbSub) { ctx.fillStyle = t.text; ctx.font = subFS + 'px ' + lmThumbDet; ctx.fillText(lmThumbSub.toUpperCase(), thumbW / 2, yb + subYOffset); }
        if (coordStr) { ctx.fillStyle = t.text; ctx.globalAlpha = 0.6; ctx.font = coordFS + 'px ' + lmThumbDet; ctx.fillText(coordStr, thumbW / 2, yb + subYOffset + Math.round(subFS * 1.4)); ctx.globalAlpha = 1.0; }
      } else if (ts === 'gradient') {
        // Match customizer: el.style.padding '40px 14px 10px' + gradient
        // 'linear-gradient(to top, bg 0%, bg+ee 40%, transparent 100%)'.
        // Customizer's top padding is 40px on a ~500px frame ≈ 8% of height.
        // Lift the band off the very bottom by gradPad so cover-fit into the
        // mockup cutout (which trims ~2% top/bottom) doesn't clip coords.
        var gPadTop = Math.round(thumbH * 0.08);
        var gTL = lmTitleSlot('bold ' + titleFS + 'px ' + state.font, thumbW * 0.9, 2, titleFS);
        titleSlotH = gTL.slotH;
        var bandH = gPadTop + titleSlotH + lineGap + subFS + lineGap + coordFS + padBot;
        var gradPad = Math.round(thumbH * 0.03);
        var bandTop = thumbH - bandH - gradPad;
        // Fade to theme bg (t.bg), not the outer canvas bg — mirrors the
        // customizer which uses `linear-gradient(to top, t.bg, t.bg+ee, transparent)`.
        var grd = ctx.createLinearGradient(0, bandTop, 0, thumbH - gradPad);
        grd.addColorStop(0, hexToRgba0(t.bg));
        grd.addColorStop(0.6, hexToRgba(t.bg, 0.93));   // ~t.bg+ee at 40% from bottom
        grd.addColorStop(1, t.bg);
        ctx.fillStyle = grd; ctx.fillRect(0, bandTop, thumbW, bandH);
        // Tail the gradient out to the very bottom so there's no hard seam.
        ctx.fillStyle = t.bg; ctx.fillRect(0, thumbH - gradPad, thumbW, gradPad);
        ctx.textAlign = 'center';
        var gy = bandTop + gPadTop + titleSlotH;  // last title baseline (or hw slot bottom)
        if (hwActive) {
          paintHwBlock(thumbW / 2, bandTop + gPadTop + titleSlotH / 2, thumbW * 0.9, hwSlotH, t.text);
        } else if (gTL.lines.length) { ctx.fillStyle = t.text; ctx.font = 'bold ' + titleFS + 'px ' + state.font; lmDrawTitleLines(gTL.lines, thumbW / 2, gy, gTL.step); }
        gy += lineGap + subFS;
        if (lmThumbSub) { ctx.fillStyle = t.text; ctx.font = subFS + 'px ' + lmThumbDet; ctx.fillText(lmThumbSub.toUpperCase(), thumbW / 2, gy); }
        gy += lineGap + coordFS;
        if (coordStr) { ctx.fillStyle = t.text; ctx.globalAlpha = 0.6; ctx.font = coordFS + 'px ' + lmThumbDet; ctx.fillText(coordStr, thumbW / 2, gy); ctx.globalAlpha = 1.0; }
      } else if (ts === 'place') {
        // 'place' (the DEFAULT composition) — soft theme scrim from the bottom,
        // large tracked uppercase city, short centred rule, details below.
        // Mirrors updateTextOverlay's place branch + the
        // [data-textstyle="place"] cqh rules in sections/locationmap.liquid
        // (title 4.4cqh/600/ls 0.9cqh, rule 7cqh × 0.22cqh @ 0.55 alpha,
        // subtitle 1.55cqh/ls 0.55cqh, coords 1.2cqh @ 0.78) and the matching
        // branch in locationmap-3d-framed-mockup.js capturePrint(). Without it
        // place fell through to the classic `else` and the cart image showed an
        // opaque band with a mixed-case title — a composition the customer
        // never picked.
        // Handwriting is deliberately NOT painted here: the customizer's place
        // branch doesn't render it either, so the capture matches the preview.
        // Font floors mirror the sibling branches — at thumbnail scale the raw
        // cqh values fall to 4/3px and stop being legible in the cart.
        var plTitleFS = Math.max(10, Math.round(thumbH * 0.044));
        var plSubFS   = Math.max(7,  Math.round(thumbH * 0.0155));
        var plCoordFS = Math.max(6,  Math.round(thumbH * 0.012));
        // Tracking stays proportional to the (floored) size, not to thumbH:
        // 0.9/4.4 and 0.55/1.55 of the CSS font sizes.
        // The city is ONE line: shrink size + tracking to fit 92% of the width
        // (same rule as the overlay and the print) instead of wrapping.
        var plTitleText = (state.title || '').toUpperCase();
        var plFit = lmFitOneLine(plTitleText, function (fs) { return '600 ' + fs + 'px ' + state.font; },
                                 plTitleFS, 0.205, thumbW * 0.92);
        plTitleFS = plFit.fs;
        var plTitleLS = plFit.ls;
        var plSubLS   = Math.round(plSubFS * 0.355);
        // Overlay padding measured off the live overlay: 44px top / 16px bottom
        // on a 747px cq container. The bottom keeps the classic branch's
        // bottomSafety floor so the mockup cover-fit trim can't clip the coords.
        var plPadTop  = Math.round(thumbH * 0.059);
        var plPadBot  = Math.max(Math.round(thumbH * 0.0214), bottomSafety);
        var plRuleH   = Math.max(1, Math.round(thumbH * 0.0022));
        var plRuleGap = Math.round(thumbH * 0.01);   // title/rule margin-bottom 1cqh
        // Baseline → line-box bottom for the title, so the rule clears the
        // descenders instead of striking through them (CSS gets this from
        // line-height 1.05 + the font's descent; ≈0.24em for Prata).
        var plTitleDesc = Math.round(plTitleFS * 0.24);
        var plSubGap  = Math.round(thumbH * 0.006);  // subtitle margin-bottom 0.6cqh
        var plSub     = lmThumbSub;
        var plCoords  = coordStr ? coordStr : '';
        // Already fitted to one line above (plFit). Place renders the typed
        // title even when handwriting is on, matching updateTextOverlay.
        var plTL = { lines: plTitleText ? [plTitleText] : [], slotH: plTitleFS, step: Math.round(plTitleFS * 1.15) };
        var plBandH = plPadTop
          + (plTL.lines.length ? plTL.slotH + plTitleDesc + plRuleGap + plRuleH + plRuleGap : 0)
          + (plSub ? plSubFS + plSubGap : 0)
          + (plCoords ? plCoordFS : 0)
          + plPadBot;
        var plTop = thumbH - plBandH;
        var plGrad = ctx.createLinearGradient(0, plTop, 0, thumbH);
        plGrad.addColorStop(0, hexToRgba0(t.bg));
        plGrad.addColorStop(0.58, hexToRgba(t.bg, 0.66));   // a8 at 42% from bottom
        plGrad.addColorStop(1, hexToRgba(t.bg, 0.95));      // f2
        ctx.fillStyle = plGrad;
        ctx.fillRect(0, plTop, thumbW, plBandH);
        ctx.textAlign = 'center';
        var ply = plTop + plPadTop + plTL.slotH;
        if (plTL.lines.length) {
          ctx.save();
          ctx.font = '600 ' + plTitleFS + 'px ' + state.font;
          if ('letterSpacing' in ctx) ctx.letterSpacing = plTitleLS + 'px';
          ctx.fillStyle = t.text;
          lmDrawTitleLines(plTL.lines, thumbW / 2, ply, plTL.step);
          ctx.restore();
          // Short centred rule under the city
          ply += plTitleDesc + plRuleGap;
          ctx.save();
          ctx.globalAlpha = 0.55;
          ctx.fillStyle = t.text;
          ctx.fillRect(thumbW / 2 - (thumbH * 0.07) / 2, ply, thumbH * 0.07, plRuleH);
          ctx.restore();
          ply += plRuleH + plRuleGap;
        }
        if (plSub) {
          ply += plSubFS;
          ctx.save();
          ctx.font = plSubFS + 'px ' + lmThumbDet;
          if ('letterSpacing' in ctx) ctx.letterSpacing = plSubLS + 'px';
          ctx.fillStyle = t.text;
          ctx.fillText(plSub, thumbW / 2, ply);
          ctx.restore();
          ply += plSubGap;
        }
        if (plCoords) {
          ply += plCoordFS;
          ctx.font = plCoordFS + 'px ' + lmThumbDet;
          ctx.fillStyle = t.text; ctx.globalAlpha = 0.78;
          ctx.fillText(plCoords, thumbW / 2, ply);
          ctx.globalAlpha = 1.0;
        }
      } else {
        // 'classic' — match customizer: opaque band (el.style.backgroundColor = t.bg).
        var cTL = lmTitleSlot('bold ' + titleFS + 'px ' + state.font, thumbW * 0.958, 2, titleFS);
        titleSlotH = cTL.slotH;
        textH = padTop + titleSlotH + lineGap + subFS + lineGap + coordFS + padBot;
        var cBandTop = thumbH - textH - bottomSafety;
        ctx.fillStyle = t.bg; ctx.globalAlpha = 1.0;
        // Extend fill to canvas bottom so the band stays flush after cover-fit.
        ctx.fillRect(0, cBandTop, thumbW, textH + bottomSafety);
        ctx.globalAlpha = 1.0; ctx.textAlign = 'center';
        var cy = cBandTop + padTop + titleSlotH;
        if (hwActive) {
          paintHwBlock(thumbW / 2, cBandTop + padTop + titleSlotH / 2, thumbW * 0.9, hwSlotH, t.text);
        } else if (cTL.lines.length) { ctx.fillStyle = t.text; ctx.font = 'bold ' + titleFS + 'px ' + state.font; lmDrawTitleLines(cTL.lines, thumbW / 2, cy, cTL.step); }
        cy += lineGap + subFS;
        if (lmThumbSub) { ctx.fillStyle = t.text; ctx.font = subFS + 'px ' + lmThumbDet; ctx.fillText(lmThumbSub.toUpperCase(), thumbW / 2, cy); }
        cy += lineGap + coordFS;
        if (coordStr) { ctx.fillStyle = t.text; ctx.globalAlpha = 0.6; ctx.font = coordFS + 'px ' + lmThumbDet; ctx.fillText(coordStr, thumbW / 2, cy); ctx.globalAlpha = 1.0; }
      }

      return _compositeIntoMockup(canvas);
    } catch (e) {
      console.warn('[LocationMap] Thumbnail generation failed:', e);
      if (window.skylitBeaconThumbnailSkip) {
        try { window.skylitBeaconThumbnailSkip(state.orderId || '', 'location_map', 'generator_error', { error: String(e).slice(0, 200) }); } catch (_e) {}
      }
      return null;
    }
  }

  // ── Add to Cart ──
  function bindAddToCart() {
    var form = document.getElementById('lm-product-form');
    if (!form) return;
    form.addEventListener('submit', function(e) {
      e.preventDefault();
      // Frame adopted from the cart add-on (see the pairedFrame hydration in
      // the edit branch): keep the two-line structure. The selection flips
      // back to Print before the variant id is read, and the frame add-on
      // line is synced — or removed if the customer deselected the frame —
      // right before the redirect.
      var lmAddonFrameOwned = lmFrameFromAddon;
      var lmAddonFrameColour = null;
      if (lmAddonFrameOwned && state.selectedType === 'Framed') {
        lmAddonFrameColour = state.selectedFrame || 'Black';
        window.lmSetSelectedType('Print');
      }
      var variantId = document.getElementById('lm-variant-id').value;
      if (!variantId) { alert('Please select a size and type'); return; }

      // Claim mode: this design order was already paid for through another
      // channel (Shop app / TikTok / draft order) — there is no cart to add
      // to. Gated on `active`, never `claim`/`usable` alone: a customer must
      // never fall through to a normal checkout while `?claim=` is present,
      // whether the claim is still loading, already used, expired, or the
      // lookup failed outright. Blocks before ANY of the save/cart logic
      // below runs.
      if (window.SkylitClaim && window.SkylitClaim.active) {
        if (!(window.SkylitClaim.usable && window.SkylitClaim.claim)) {
          alert(window.SkylitClaim.message || 'This design link cannot be used right now. Please contact hello@skylitstudio.co.uk.');
          return;
        }
      }

      var btn = document.querySelector('.lm-add-to-cart-btn');
      var inClaimMode = !!(window.SkylitClaim && window.SkylitClaim.active);
      if (btn) { btn.disabled = true; btn.textContent = inClaimMode ? 'Confirming…' : 'Adding...'; }
      // A star flies from the button into the basket while the cart requests
      // run (lm-stage.js); the redirect waits for it to land.
      if (!inClaimMode && window.LMStage) window.LMStage.launchCartFlight(btn, !!state.editMode);

      var sizeBtn = document.querySelector('.lm-size-picker [data-variant-option].active');
      var sizeCode = sizeBtn ? sizeBtn.dataset.sizeCode : 'A4';

      // Wait for the map to finish rendering before capturing the cart
      // thumbnail. Without this, customers on slower GPUs can hit ATC during
      // the 1–3s window when Mapbox Standard's photoreal landmarks are still
      // streaming in, producing a thumbnail of half-rendered low-LOD tiles
      // (no London Eye, etc.). 3s timeout so a stuck idle event can't hang
      // the submit forever — the captured frame is no worse than today.
      // Floor 1500ms for non-3D, 4000ms for 3D-landmark renders. The
      // previous 800ms timeout was too tight for mobile Safari on slower
      // connections (e.g. iPhone OS 26, viewport 440x871) — see
      // mho8k47eioh, where the captured Mapbox canvas was almost empty
      // despite no skip-log entry (validation passed because the heart-
      // clipped cream bg + sparse rural roads cleared the inner-range
      // floor). The fallback timeout was firing before Mapbox finished
      // its tile stream, so the cart thumbnail showed less map detail
      // than the customer actually designed.
      var idleWaitMs = state.show3dLandmarks ? 4000 : 1500;
      waitForMapIdle(idleWaitMs, function() { proceedWithSubmit(); });

      function proceedWithSubmit() {
      // Cart thumbnail. Two paths, mirroring how star maps do it:
      //   • Landmark orders — CF Browser Rendering can't draw glTF landmarks, so
      //     the client canvas IS the cart image. Keep the instant localStorage
      //     preview and upload that canvas to R2 (Windows RTX fallback path).
      //   • Every other order — the server-side CF render (with the passe-partout
      //     mat + wall mockup, no device-canvas divergence) is the source of
      //     truth. Star's add-to-cart writes NO localStorage so the cart's
      //     upgrade loop (upgradeCartBackendThumbnails) polls /api/cart-thumbnail-img
      //     and swaps the CF render in. Location must do the same: a stored
      //     thumbnail makes that loop treat localStorage as authoritative and
      //     short-circuit, so the CF render (mat included) never appears. We
      //     therefore DON'T write localStorage here and actively clear any stale
      //     entry from an earlier design session of this order.
      // Resolved before the /cart redirect so an in-flight thumbnail upload
      // isn't cancelled by the navigation (fire-and-forget lost the race on
      // slower devices — the cart then kept serving the PREVIOUS design's
      // thumbnail while D1 had the edited text; see order 2l0tsa803n7).
      var lmThumbUpload = Promise.resolve();
      if (state.show3dLandmarks) {
        var thumbnail = generateThumbnail();
        if (thumbnail) {
          // Save with LRU eviction — if localStorage is full of old thumbnails
          // (common in dev carts), drop oldest thumbnail_* entries and retry.
          (function saveWithEviction(key, value) {
            for (var i = 0; i < 20; i++) {
              try { localStorage.setItem(key, value); return; }
              catch(e) {
                // Quota hit — find the oldest thumbnail_* key and drop it.
                var oldest = null;
                for (var j = 0; j < localStorage.length; j++) {
                  var k = localStorage.key(j);
                  if (k && k.indexOf('thumbnail_') === 0 && k !== key) { oldest = k; break; }
                }
                if (!oldest) return; // nothing left to evict
                try { localStorage.removeItem(oldest); } catch(e2) { return; }
              }
            }
          })('thumbnail_' + state.orderId, thumbnail);
          if (window.skylitUploadCartThumbnail) {
            lmThumbUpload = window.skylitUploadCartThumbnail({ order: state.orderId, productType: 'location_map', dataUrl: thumbnail }) || Promise.resolve();
          }
        }
      } else {
        // Non-landmark: clear any stale localStorage now; the CF render is
        // fired AFTER the location-map-data save below (it reads the D1 row,
        // so triggering it here — before the edited design is persisted —
        // would render the PREVIOUS design and the cart would show a stale
        // preview on edits).
        try { localStorage.removeItem('thumbnail_' + state.orderId); } catch(e) {}
      }

      var icons = getIconsData();
      var postData = buildLmPostData(sizeCode, icons, 'cart');

      // Check if editing an existing cart item. state.editMode is set by
      // lmHydrateFromData from actual cart membership — the old URL-param
      // check also fired for save-for-later / resumed-draft sessions and
      // logged edit_personalisations_confirmed for designs never in cart.
      var isEditMode = !!state.editMode;

      var adminOpts = { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(postData) };
      fetch(API_BASE + 'api/location-map-data/', adminOpts)
      .then(function(r) { if (!r.ok) throw new Error(r.status); return r.json(); })
      .catch(function() {
        // Retry once on failure (CF Worker cold-start / transient error)
        return fetch(API_BASE + 'api/location-map-data/', adminOpts)
          .then(function(r) { return r.json(); })
          .catch(function(e) { console.warn('[LocationMap] Admin POST failed after retry', e); return {}; });
      })
      .then(function() {
        // Claim mode: attach this design to the already-paid Shopify order
        // instead of adding to cart. Already validated usable at submit
        // time above (SkylitClaim.active && .usable && .claim) — none of
        // the pairing/purge, doAdd, or addGiftToCart logic below applies,
        // since there is no cart line to create; complete() navigates to /pages/design-confirmed itself on
        // success. The design (including the locked type/size/frame) was
        // just saved to D1 by the location-map-data POST above, so the
        // order id it attaches is state.orderId — the same module-level id
        // used throughout this file, generated once per page load and
        // already stamped into that saved row via buildLmPostData().
        if (inClaimMode) {
          return window.SkylitClaim.complete(state.orderId).then(function () {
            return { __claimHandled: true };
          });
        }
        // The cart thumbnail is rendered server-side, triggered inside the
        // location-map-data save above (which is why it renders the CURRENT
        // design, not the previous one). It used to be a separate
        // fire-and-forget POST to /api/location-cart-thumbnail-cf from here,
        // which raced this page's navigation to /cart and was sometimes
        // dropped before it left the device even with keepalive set. Landmark
        // orders are still skipped server-side: they upload their client
        // canvas earlier, and renderLocationCartThumbnail returns early on
        // show_3d_landmarks = 1.

        // One fresh copy reference per add-to-cart, shared by the print and
        // its paired add-on so cart/drawer controls bind to this exact copy.
        state.cartLineRef = window.SkylitPairing
          ? window.SkylitPairing.makeLineRef()
          : state.orderId;

        // Capitalise the first letter of the design attributes shown in the
        // cart (theme/layout/orientation/composition are stored lowercase).
        // Preserves the rest of the string so multi-word/proper values stay
        // intact. Backend reads the D1 order record, not these display props.
        var lmCap = function (s) {
          s = String(s || '');
          return s ? s.charAt(0).toUpperCase() + s.slice(1) : s;
        };
        var properties = {
          '_OrderID': state.orderId,
          '_cart_line_ref': state.cartLineRef,
          'Location': state.locationName,
          'Theme': lmCap(state.theme), 'Layout': lmCap(state.layout),
          'Composition': lmCap(state.textStyle),
          '_print_url': API_BASE + 'api/location-download/?orderId=' + state.orderId,
          // Cart line-item image. Point at the CF browser-render cart thumbnail
          // (cart_thumbnail_r2_key) — the same endpoint star uses — NOT
          // api/location-thumbnail (the PRINT thumbnail r2_thumb_key, which
          // doesn't exist until the order is paid+rendered). The shared cart
          // upgrade loop (upgradeCartBackendThumbnails in main-cart-items.liquid)
          // polls this with a "Generating preview…" loader until the CF render
          // lands, then swaps it in over the instant localStorage capture.
          '_thumbnail_final': API_BASE + 'api/cart-thumbnail-img/' + state.orderId,
          // The BARE print (no room, no frame). The cart composes the scene
          // live in CSS around this, so a frame bought from the cart's own
          // upsell appears instantly instead of needing a re-render. Falls
          // back to _thumbnail_final's baked composite when this 404s.
          '_thumbnail_print': API_BASE + 'api/cart-print-img/' + state.orderId,
          '_selected_type': state.selectedType,
          // Canonical size token ('A4', '30x40') — the variant option
          // ('30 x 40 cm') stripped of spaces/cm, matching the admin
          // webhook / renderer keys.
          '_selected_size': String(state.selectedSize || '').replace(/cm/gi, '').replace(/\s+/g, ''),
          '_selected_frame': state.selectedFrame
        };
        if (state.title) properties['Title'] = state.title;
        if (state.subtitle) properties['Subtitle'] = state.subtitle;
        if (state.dateLine) properties['Date'] = state.dateLine;
        properties['Orientation'] = lmCap(state.orientation);
        // Everything the customer switched on, spelled out for the cart row
        // and the order confirmation (mirrors the star map's line). Uses the
        // toggle names from the designer so the list reads back the same way.
        properties['Included on print'] = (function () {
          var on = [];
          if (state.show3dLandmarks) on.push('3D landmarks');
          else if (state.show3dBuildings) on.push('3D buildings');
          if (state.show3dTerrain) on.push('3D terrain');
          if (state.showLabels) on.push('Place names');
          if (state.showRoadLabels) on.push('Road names');
          if (state.showWaterLabels) on.push('Water names');
          if (state.showLandBorders) on.push('Land borders');
          if (state.showMaritimeBorders) on.push('Maritime borders');
          on.push(typeof state.coordsText === 'string' && state.coordsText ? 'Own line: ' + state.coordsText : (lmCoordsLine() ? 'Coordinates' : 'No coordinates'));
          return on.join(', ');
        })();
        if (icons.length) properties['Icons'] = icons.map(function(i) { return i.photo ? '📷' : i.emoji; }).join(' ');
        if (window.SkylitGalleryWall) window.SkylitGalleryWall.applyProperties(properties);

        var cartPayload = { id: parseInt(variantId), quantity: 1, properties: properties };

        var doAdd = function() {
          return fetch('/cart/add.js', {
            method: 'POST', headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(cartPayload)
          });
        };

        // Drop anything this design already owns in the cart (print + paired
        // ribbon/wrap/bundle/wall strips) before adding. Always driven by a
        // LIVE /cart.js read: state.editMode comes from a page-load snapshot,
        // and a page restored from the back-forward cache still believes the
        // cart is empty — skipping this is what produced duplicate paid gift
        // lines.
        if (window.SkylitPairing) {
          return window.SkylitPairing.purgeDesign(state.orderId).then(doAdd);
        }

        if (isEditMode) {
          // Helper missing (asset blocked) — old snapshot path.
          window.__skylitCartEditInFlight = true;
          return fetch('/cart.js')
            .then(function(r) { return r.json(); })
            .then(function(cart) {
              var keysToRemove = [];
              cart.items.forEach(function(item) {
                var p = item.properties || {};
                if (p._OrderID === state.orderId) keysToRemove.push(item.key);
                if (p._paired_with_order_id === state.orderId) keysToRemove.push(item.key);
              });
              var chain = Promise.resolve();
              keysToRemove.forEach(function(k) {
                chain = chain.then(function() {
                  return fetch('/cart/change.js', {
                    method: 'POST', headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ id: k, quantity: 0 })
                  });
                });
              });
              return chain.then(doAdd);
            });
        }

        return doAdd();
      })
      .then(function(r) {
        if (r && r.__claimHandled) return r;
        if (!r.ok) throw new Error('Cart error');
        return r.json();
      })
      .then(function(r) {
        if (r && r.__claimHandled) return r;
        return addGiftToCart();
      })
      .then(function(r) {
        // complete() already redirected to /pages/design-confirmed —
        // nothing left to log or navigate for here.
        if (r && r.__claimHandled) return;
        // Log confirm in edit mode so the activity log shows whether a
        // customer who opened edit personalisations actually confirmed.
        if (isEditMode) {
          try {
            // keepalive: survives the /cart navigation below — without it the
            // confirm event was silently dropped whenever the redirect won.
            fetch(API_BASE + 'api/order-event', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              keepalive: true,
              body: JSON.stringify({
                order_id: state.orderId,
                event: 'edit_personalisations_confirmed',
                detail: window.location.href.slice(0, 200)
              })
            }).catch(function() {});
          } catch (e) {}
        }
        // Landmark orders: the client canvas IS the cart image, so wait for
        // its R2 upload (bounded at 6s) before leaving the page — the body is
        // too large for fetch keepalive. localStorage was already written, so
        // a timeout only affects cross-device carts, same as before.
        var lmNavCap = new Promise(function(res) { setTimeout(res, 6000); });
        var lmFrameSync = (lmAddonFrameOwned && window.SkylitPairing && window.SkylitPairing.syncFrameAddon)
          ? window.SkylitPairing.syncFrameAddon(state.orderId, lmAddonFrameColour)
          : Promise.resolve();
        var lmLanded = function() { return window.LMStage ? window.LMStage.landCartFlight() : Promise.resolve(); };
        var lmToCart = function() { window.location.href = window.SkylitGalleryWall ? window.SkylitGalleryWall.destination() : '/cart'; };
        var lmGoCart = function() {
          lmFrameSync.then(lmLanded, lmLanded).then(lmToCart, lmToCart);
        };
        return Promise.race([lmThumbUpload, lmNavCap]).then(lmGoCart, lmGoCart);
      })
      .catch(function(e) {
        console.error('[LocationMap] Error:', e);
        if (window.LMStage) window.LMStage.cancelCartFlight();
        if (inClaimMode) {
          if (btn) { btn.disabled = false; btn.textContent = 'Confirm my design'; }
          alert('We could not confirm your design. Please try again or contact hello@skylitstudio.co.uk.');
          return;
        }
        if (btn) { btn.disabled = false; btn.textContent = 'Add To Cart'; }
        alert('Something went wrong. Please try again.');
      });
      } // proceedWithSubmit
    });
  }

  // Resolve once the map has finished its current render pass (tiles loaded,
  // no pending repaints) or after `timeoutMs` — whichever is first. If
  // tiles are already idle synchronously, fires the callback on the next
  // microtask so behaviour stays predictable.
  function waitForMapIdle(timeoutMs, cb) {
    if (!map) { cb(); return; }
    var done = false;
    function finish() { if (done) return; done = true; try { map.off('idle', finish); } catch(e) {} cb(); }
    try {
      if (map.areTilesLoaded && map.areTilesLoaded() && !map.isMoving() && !map.isZooming() && !map.isRotating()) {
        // Already idle — but Standard import landmarks finish drawing in a
        // later frame than tiles, so still wait one idle tick if landmarks
        // are on. Cheap insurance against the same race.
        if (!state.show3dLandmarks) { setTimeout(finish, 0); return; }
      }
      map.once('idle', finish);
    } catch (e) { finish(); return; }
    setTimeout(finish, timeoutMs);
  }

  // ── Build admin POST payload (shared by add-to-cart and autosave) ──
  function buildLmPostData(sizeCode, icons, funnelStatus) {
    return {
      order: state.orderId, lat: state.lat, lng: state.lng, zoom: state.zoom,
      bearing: state.bearing, pitch: state.pitch,
      theme: state.theme, layout: state.layout, text_style: state.textStyle, passepartout: state.passepartout,
      title: state.title, subtitle: lmJoinSubtitle(),
      // Same four fields as lmGetSaveData. They went missing when this shared
      // builder replaced the per-path payloads (Apr 2026), so a drawn title
      // was never saved and the order printed the typed title instead.
      handwriting_enabled: state.handwritingEnabled ? 1 : 0,
      handwriting_strokes: state.handwritingEnabled && state.handwritingStrokes && state.handwritingStrokes.length
        ? JSON.stringify({ vbw: 1000, vbh: 240, strokes: state.handwritingStrokes })
        : null,
      handwriting_size: state.handwritingSize,
      handwriting_height: state.handwritingHeight,
      show_coordinates: !!lmCoordsLine(), coords_text: (typeof state.coordsText === 'string' && state.coordsText.trim()) ? state.coordsText.trim() : null, water_tint: state.waterTint === 'blue' ? 'blue' : null, show_labels: state.showLabels, show_water_labels: state.showWaterLabels, show_road_labels: state.showRoadLabels, show_poi_labels: state.showPOILabels, show_land_borders: state.showLandBorders, show_maritime_borders: state.showMaritimeBorders, show_3d_buildings: state.show3dBuildings, show_3d_terrain: state.show3dTerrain, building_detail: state.buildingDetail, show_3d_landmarks: state.show3dLandmarks, lm_mode: (window.SKYLIT_LM_MODE === 'landmarks' ? 'landmarks' : 'flat'), light_preset: state.lightPreset, font: state.font, details_font: state.detailsFont,
      text_color: lmEffectiveTextColor(),
      location_name: state.locationName, size: sizeCode,
      selected_type: state.selectedType, selected_frame: state.selectedFrame, gift_option: state.selectedGift || 'none',
      orientation: state.orientation, custom_bg: state.customBg, icons_json: JSON.stringify(icons), icon_size: iconSize,
      bounds: map ? JSON.stringify(map.getBounds().toArray()) : null,
      preview_viewport_w: lmViewportW(),
      preview_viewport_h: lmViewportH(),
      device: (function(){
        var uad = null;
        try {
          if (navigator.userAgentData) {
            uad = {
              brands: navigator.userAgentData.brands || null,
              mobile: !!navigator.userAgentData.mobile,
              platform: navigator.userAgentData.platform || ''
            };
          }
        } catch (e) {}
        return {
          userAgent: (navigator && navigator.userAgent) || '',
          userAgentData: uad,
          viewport: { w: window.innerWidth, h: window.innerHeight }
        };
      })(),
      funnel_status: funnelStatus || 'draft'
    };
  }

  // ── Autosave (draft funnel_status) + Resume modal ──
  // Pattern matches starmap-v2 / moon-myjs / hor-moon-myjs:
  //  - Debounced (2500ms) POST with funnel_status='draft'
  //  - Plain URL revisit shows resume modal pointing at last_draft orderId
  //  - Preset URL overwrites the pointer immediately
  //  - Single pointer per product family — Save for Later is the durable
  //    multi-design escape hatch
  // Namespace the draft pointer per mode so the regular flat location map
  // and the 3D landmark map don't share a "Continue your design?" pointer
  // (both products mount this same section, set window.SKYLIT_LM_MODE to
  // 'flat' or 'landmarks'). Without the namespace, opening the 3D landmark
  // page resumes a draft from the flat location map and vice versa.
  var LM_DRAFT_KEY = (window.SKYLIT_LM_MODE === 'landmarks')
    ? 'location_map_landmarks_last_draft'
    : 'location_map_last_draft';
  var LM_DRAFT_TTL_MS = 30 * 86400000; // 30 days
  var lmAutosavePrimed = false;
  var lmAutosaveTimer = null;

  // Arm autosave on the customer's FIRST genuine interaction, not at boot.
  //
  // bindLmAutosave() wires scheduleLmAutosave to Mapbox 'moveend'/'zoomend'/
  // 'rotateend'/'pitchend'. Hydration and first paint move the map themselves
  // (setCenter/flyTo to the preset's or the default coordinates), so priming
  // synchronously meant a POST fired ~2.5s after load with nobody having
  // touched anything — creating a stub draft row and firing the "New
  // customisation" push on a mere page view. Deferring to a real pointer/key
  // event is what the "prime-only" comments on the preset + fresh-load paths
  // always intended: arming does NOT schedule a save, so the interaction that
  // follows (drag → moveend, tile click, typing) schedules its own.
  function lmArmAutosaveOnFirstInteraction() {
    if (lmAutosavePrimed) return;
    var arm = function () {
      lmAutosavePrimed = true;
      document.removeEventListener('pointerdown', arm, true);
      document.removeEventListener('keydown', arm, true);
    };
    document.addEventListener('pointerdown', arm, true);
    document.addEventListener('keydown', arm, true);
  }

  // Live-cart _OrderID list, fetched once per boot path that needs it.
  // null = unknown (cart fetch failed / not attempted) — callers treat
  // unknown conservatively (edit mode ON for ?order_id= boots, so an
  // in-cart edit never accidentally duplicates its line).
  var lmCartOrderIds = null;
  function lmFetchCartOrderIds() {
    return fetch('/cart.js')
      .then(function(r) { return r.json(); })
      .then(function(cart) {
        lmCartOrderIds = (cart.items || []).map(function(it) {
          return (it.properties && (it.properties._OrderID || it.properties.OrderID)) || null;
        }).filter(Boolean);
        return lmCartOrderIds;
      })
      .catch(function() { return null; });
  }

  // Add-to-cart navigates to /cart, so pressing Back restores this page from
  // the back-forward cache with the pre-add cart snapshot still in memory — it
  // would offer "Add To Cart" for a design already in the cart. Re-read the
  // live cart on restore so edit mode and the label stay honest (the add path
  // purges by live cart state regardless).
  window.addEventListener('pageshow', function(event) {
    if (!event.persisted) return;
    lmFetchCartOrderIds().then(function() {
      if (lmCartOrderIds === null || !state.orderId) return;
      state.editMode = lmCartOrderIds.indexOf(state.orderId) !== -1;
      if (state.editMode) {
        var btn = document.querySelector('.lm-add-to-cart-btn span');
        if (btn) btn.textContent = 'Confirm edits';
      }
      console.log('[LocationMap] bfcache restore — cart re-read, editMode=' + state.editMode);
    });
  });

  function lmWriteDraftPointer(orderId) {
    try {
      localStorage.setItem(LM_DRAFT_KEY, JSON.stringify({ orderId: orderId, ts: Date.now() }));
    } catch (e) {}
  }
  function lmReadDraftPointer() {
    try {
      var raw = localStorage.getItem(LM_DRAFT_KEY);
      if (!raw) return null;
      var obj = JSON.parse(raw);
      if (!obj || !obj.orderId) return null;
      if (Date.now() - (obj.ts || 0) > LM_DRAFT_TTL_MS) {
        try { localStorage.removeItem(LM_DRAFT_KEY); } catch (e2) {}
        return null;
      }
      return obj.orderId;
    } catch (e) { return null; }
  }

  function lmAutosavePost() {
    if (state.editMode) return;
    var sizeBtn = document.querySelector('.lm-size-picker [data-variant-option].active');
    var sizeCode = sizeBtn ? sizeBtn.dataset.sizeCode : 'A4';
    var icons;
    try { icons = getIconsData(); } catch (e) { icons = []; }
    var data = buildLmPostData(sizeCode, icons, 'draft');
    fetch(API_BASE + 'api/location-map-data/', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data)
    }).then(function(r) {
      if (r && r.ok) lmWriteDraftPointer(state.orderId);
    }).catch(function(){});
  }

  // Edit mode only: the options tried on a carted design go to the admin's
  // cart-edit log, never location-map-data, so the design that prints is
  // left alone. Fire-and-forget.
  var lmCartEditTimer = null;
  function scheduleLmCartEditLog() {
    if (!state.orderId) return;
    clearTimeout(lmCartEditTimer);
    lmCartEditTimer = setTimeout(function() {
      try {
        var sizeBtn = document.querySelector('.lm-size-picker [data-variant-option].active');
        var icons;
        try { icons = getIconsData(); } catch (e) { icons = []; }
        var data = buildLmPostData(sizeBtn ? sizeBtn.dataset.sizeCode : 'A4', icons, 'draft');
        fetch(API_BASE + 'api/cart-edit', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ product_type: 'location_map', data: data }, function (k, v) { return (typeof v === 'string' && v.length > 1000 && v.indexOf('data:') === 0) ? undefined : v; })
        }).catch(function(){});
      } catch (e) {}
    }, 2500);
  }

  function scheduleLmAutosave() {
    if (!lmAutosavePrimed) return;
    if (state.editMode) { scheduleLmCartEditLog(); return; }
    clearTimeout(lmAutosaveTimer);
    lmAutosaveTimer = setTimeout(lmAutosavePost, 2500);
  }

  function showLmResumeModal(opts) {
    var existing = document.getElementById('lm-resume-modal');
    if (existing) existing.remove();
    var wrap = document.createElement('div');
    wrap.id = 'lm-resume-modal';
    // Styled in locationmap-section.css (light) and customizer-theme.css
    // (dark). Every colour is set there explicitly: inline styles left the
    // title and "Start fresh" to inherit, which went light-on-white in dark.
    wrap.setAttribute('role', 'dialog');
    wrap.setAttribute('aria-modal', 'true');
    wrap.setAttribute('aria-labelledby', 'lm-resume-title');
    wrap.innerHTML =
      '<div class="lm-resume-card">' +
        '<h3 class="lm-resume-title" id="lm-resume-title">' + (opts.title || 'Continue your design?') + '</h3>' +
        '<p class="lm-resume-body">' + (opts.body || '') + '</p>' +
        '<div class="lm-resume-actions">' +
          '<button type="button" id="lm-resume-secondary" class="lm-resume-btn lm-resume-btn--secondary">' + (opts.secondaryLabel || 'Start fresh') + '</button>' +
          '<button type="button" id="lm-resume-primary" class="lm-resume-btn lm-resume-btn--primary">' + (opts.primaryLabel || 'Continue') + '</button>' +
        '</div>' +
      '</div>';
    document.body.appendChild(wrap);
    document.getElementById('lm-resume-primary').addEventListener('click', function() {
      wrap.remove();
      try { opts.onPrimary && opts.onPrimary(); } catch (e) {}
    });
    document.getElementById('lm-resume-secondary').addEventListener('click', function() {
      wrap.remove();
      try { opts.onSecondary && opts.onSecondary(); } catch (e) {}
    });
    // Escape = dismiss, NOT "Start fresh" — keep the draft pointer so a
    // refresh re-offers it; just let this session proceed fresh.
    document.addEventListener('keydown', function onEsc(e) {
      if (e.key === 'Escape') {
        document.removeEventListener('keydown', onEsc, true);
        if (wrap.parentNode) {
          wrap.remove();
          try { opts.onDismiss && opts.onDismiss(); } catch (e2) {}
        }
      }
    }, true);
  }

  // Open a saved design in place — no full page reload. Mirrors the
  // ?order_id= boot path: loadExistingOrder (without preserveOrderId) sets
  // editMode=true, swaps the ATC label to "Confirm edits", and logs the
  // revisit. replaceState so a refresh re-enters the same session instead of
  // re-prompting.
  function lmResumeInPlace(orderId) {
    var loader = document.querySelector('.lm-loader');
    if (loader) loader.style.display = '';
    try {
      var u = new URL(window.location.href);
      u.searchParams.set('order_id', orderId);
      history.replaceState(null, '', u.toString());
    } catch (e) {}
    loadExistingOrder(orderId, function() {
      applyFrameRatio();
      updateTextOverlay();
      updateFrame();
      updateLayout();
      updateVariantAndPrice();
      updateGiftVisibility();
      applyRibbonImageForOrientation();
      applyFinishAttr();
      if (loader) loader.style.display = 'none';
    });
  }

  function maybeShowLmResumeModal() {
    // Boot withholds autosave priming whenever a draft pointer exists (so a
    // pending modal isn't raced by a stub autosave). Every path that ends
    // without a modal must therefore prime autosave itself, or the session
    // runs with autosave dead.
    var params = new URLSearchParams(window.location.search);
    if (params.get('order_id') || params.get('saved') || params.get('preset') ||
        params.get('prefill_date')) {
      lmAutosavePrimed = true;
      return;
    }
    var draftId = lmReadDraftPointer();
    if (draftId === state.orderId) draftId = null;
    // Resolve the saved order's status before offering to resume. A paid
    // order's draft pointer is stale — clear it silently so the customer
    // starts fresh on revisit.
    Promise.all([draftId ? lmFetchOrder(draftId) : null, lmFetchCartOrderIds()]).then(function(res) {
      var row = res[0], cartIds = res[1] || [];
      if (draftId && !row) {
        // Draft row is gone server-side (GC'd, never autosaved, or API
        // unreachable). Offering "Continue" would hydrate nothing — drop
        // the pointer instead of looping the modal on every visit.
        console.log('[lm.cart] last draft has no server row, clearing localStorage', draftId);
        try { localStorage.removeItem(LM_DRAFT_KEY); } catch (e) {}
      } else if (row && isPaidLocked(row, draftId)) {
        console.log('[lm.cart] last draft is a paid order, clearing localStorage', draftId);
        try { localStorage.removeItem(LM_DRAFT_KEY); } catch (e) {}
        row = null;
      }
      var picker = window.SkylitResumePicker;
      var draft = row ? {
        orderId: draftId,
        primary: String(row.title || row.location_name || '').split('\n')[0].trim(),
        secondary: row.title && row.location_name ? row.location_name : ''
      } : null;
      return (picker ? picker.gather('location', draft) : Promise.resolve([])).then(function(items) {
        // Two or more location maps between the cart and the last draft:
        // list them all instead of offering only the last one.
        if (items.length >= 2) {
          picker.show({
            family: 'location',
            items: items,
            onPick: function(item) { lmResumeInPlace(item.orderId); },
            onNew: function() {
              try { localStorage.removeItem(LM_DRAFT_KEY); } catch (e) {}
              lmAutosavePrimed = true;
            },
            onDismiss: function() { lmAutosavePrimed = true; }
          });
          return;
        }
        if (!row) { lmAutosavePrimed = true; return; }
        // A design already in the live cart isn't a dangling draft — the
        // cart's "Edit personalisations" covers it. Cart-aware copy so
        // "start a new one" doesn't read like losing the carted design.
        var inCart = cartIds.indexOf(draftId) !== -1;
        showLmResumeModal({
          title: inCart ? 'This design is in your cart' : 'Continue your design?',
          body: inCart
            ? 'Your last design is already in your cart, safe and sound. Edit it here, or start a brand-new design.'
            : "We saved your last design. Pick up where you left off, or start fresh.",
          primaryLabel: inCart ? 'Edit cart design' : 'Continue',
          secondaryLabel: inCart ? 'Start a new design' : 'Start fresh',
          onPrimary: function() { lmResumeInPlace(draftId); },
          onSecondary: function() {
            try { localStorage.removeItem(LM_DRAFT_KEY); } catch (e) {}
            lmAutosavePrimed = true;
          },
          // Escape: keep the pointer so a refresh re-offers the draft.
          onDismiss: function() {
            lmAutosavePrimed = true;
          }
        });
      });
    }).catch(function() { lmAutosavePrimed = true; });
  }

  function bindLmAutosave() {
    // Map interactions (lat/lng/zoom/bearing/pitch)
    if (map) {
      map.on('moveend', scheduleLmAutosave);
      map.on('zoomend', scheduleLmAutosave);
      map.on('rotateend', scheduleLmAutosave);
      map.on('pitchend', scheduleLmAutosave);
    }
    // Inputs
    document.addEventListener('input', function(e) {
      var id = e.target && e.target.id;
      if (id === 'lm-title' || id === 'lm-subtitle' || id === 'lm-date' || id === 'lm-zoom' ||
          id === 'lm-rotate' || id === 'lm-pitch') {
        // lm-location-input removed — autosave on location change is now
        // triggered directly from the gmp-select handler in bindPlacesAutocomplete.
        scheduleLmAutosave();
      }
    });
    // Clicks on pickers / toggles
    document.addEventListener('click', function(e) {
      if (!e.target) return;
      var t = e.target.closest('.lm-theme-grid > *, .lm-layout-grid > *, ' +
        '.lm-orientation-grid > *, .lm-bg-grid > *, .lm-toggle-switch, ' +
        '.lm-icons-grid > *, .lm-icons-placed-grid > *, ' +
        '.lm-frame-picker [data-variant-option], .lm-type-picker [data-variant-option], ' +
        '.lm-size-picker [data-variant-option], .lm-landmark-chips > *, ' +
        '.lm-light-preset > *, .lm-bd-detail > *, .lm-font-picker > *');
      if (t) setTimeout(scheduleLmAutosave, 50);
    });
  }

  function esc(s) { var d = document.createElement('div'); d.textContent = s; return d.innerHTML; }

  // ── Headless boot entry (CF Browser Rendering preview) ──────────────────
  // The preview-host page sets window.__LM_HEADLESS = true BEFORE the script
  // loads, so init() creates the Mapbox map then returns early. The host then
  // calls window.LM.bootHeadless(state) to hydrate and wait for a stable paint.
  window.LM = window.LM || {};
  window.LM.bootHeadless = function (state) {
    return new Promise(function (resolve, reject) {
      try {
        function whenMapReady(cb) {
          if (map && map.isStyleLoaded && map.isStyleLoaded()) return cb();
          if (map) return map.once('load', cb);
          // map created in init() — poll briefly in case init hasn't run yet
          var mapWaitDeadline = Date.now() + 18000;
          var t = setInterval(function () {
            if (map) { clearInterval(t); (map.isStyleLoaded && map.isStyleLoaded()) ? cb() : map.once('load', cb); }
            else if (Date.now() > mapWaitDeadline) { clearInterval(t); reject(new Error('LM.bootHeadless: map never initialised')); }
          }, 50);
        }
        whenMapReady(function () {
          // Reuse the customizer's own hydration — guarantees 1:1 parity.
          lmHydrateFromData(state);
          // Hydration applies the layout-dependent composition (passepartout/
          // fade/circle/heart insets, the frame, the text overlay) only via a
          // DEFERRED 'styledata' handler / 2s fallback. Headless can reach map
          // 'idle' before that fires and screenshot a pre-layout (full-bleed)
          // frame — that was the missing passepartout mat. Apply it up front so
          // the inset exists before tiles settle, then resize the canvas into it.
          try { updateLayout(); updateFrame(); updateTextOverlay(); } catch (e) {}
          if (map) map.resize();
          var MAX = 18000, start = Date.now();
          function done(stable) { window.__lmPreviewReady = true; resolve({ stable: stable, elapsed: Date.now() - start }); }
          map.once('idle', function () {
            // Re-assert after the style/tiles settle, resize the map into the
            // inset, then wait for the resize-induced repaint (or a bounded
            // fallback if resize was a no-op) before signalling ready.
            try { updateLayout(); updateFrame(); } catch (e) {}
            var settled = false;
            function finish() {
              if (settled) return; settled = true;
              // Photo markers are network images: a screenshot before they
              // decode captures empty rings. Wait (bounded) for every one.
              var photos = Array.prototype.slice.call(document.querySelectorAll('img.lm-marker-photo'));
              var photoWait = Promise.race([
                Promise.all(photos.map(function (im) { return im.decode ? im.decode().catch(function () {}) : null; })),
                new Promise(function (r) { setTimeout(r, 8000); })
              ]);
              Promise.all([Promise.resolve(document.fonts && document.fonts.ready), photoWait])
                .then(function () { requestAnimationFrame(function () { setTimeout(function () { done(true); }, 150); }); });
            }
            if (map) { map.once('idle', finish); map.resize(); }
            setTimeout(finish, 800);
          });
          setTimeout(function () { if (!window.__lmPreviewReady) done(false); }, MAX);
        });
      } catch (e) { try { window.__lmPreviewReady = true; } catch (_e) {} reject(e); }
    });
  };

  // Headless only: apply a drawn title after bootHeadless. The strokes are too
  // big for the preview host's ?state= URL (Browser Rendering returns HTTP 431
  // past ~16KB), so skylit-admin sends them here through page.evaluate.
  // Mirrors the handwriting block in lmHydrateFromData. Returns true once the
  // caption has been redrawn.
  window.LM.applyHandwriting = function (data) {
    if (!data) return false;
    state.handwritingEnabled = !!data.handwriting_enabled && data.handwriting_enabled !== '0';
    try {
      var hw = typeof data.handwriting_strokes === 'string' ? JSON.parse(data.handwriting_strokes) : data.handwriting_strokes;
      state.handwritingStrokes = (hw && hw.strokes) || [];
    } catch (e) { state.handwritingStrokes = []; }
    var hs = parseFloat(data.handwriting_size);
    if (!isNaN(hs)) state.handwritingSize = Math.max(0.2, Math.min(1.1, hs));
    var hh = parseFloat(data.handwriting_height);
    if (!isNaN(hh)) state.handwritingHeight = Math.max(0.4, Math.min(1.4, hh));
    updateTextOverlay();
    return true;
  };

  // ── Temp debug: ?lm_debug=1 shows a floating toggle for the always-on
  // Skylit building layers (urban fabric + own low-zoom footprints) so the
  // visual delta is easy to inspect. Param-gated, linked nowhere — safe on
  // live; remove once no longer useful. Note: switching THEME rebuilds the
  // style, which resets the layers to visible (the button re-syncs its label
  // on next click).
  (function lmDebugLayerToggle() {
    try {
      if (new URLSearchParams(location.search).get('lm_debug') !== '1') return;
    } catch (e) { return; }
    var IDS = ['lm-urban-fabric', 'lm-lowzoom-buildings'];
    var poll = setInterval(function () {
      var m = window.__lmMapInst;
      if (!m) return;
      clearInterval(poll);
      var btn = document.createElement('button');
      btn.type = 'button';
      btn.style.cssText = 'position:fixed;bottom:90px;left:12px;z-index:99999;padding:9px 14px;background:#141414;color:#fff;border:none;border-radius:8px;font-size:13px;font-family:inherit;cursor:pointer;opacity:0.92;box-shadow:0 4px 14px rgba(0,0,0,0.3)';
      function isOn() {
        try {
          return !IDS.some(function (id) {
            return m.getLayer(id) && m.getLayoutProperty(id, 'visibility') === 'none';
          });
        } catch (e) { return true; }
      }
      function sync() { btn.textContent = 'Skylit buildings: ' + (isOn() ? 'ON' : 'OFF'); }
      btn.addEventListener('click', function () {
        var next = !isOn();
        IDS.forEach(function (id) {
          try { if (m.getLayer(id)) m.setLayoutProperty(id, 'visibility', next ? 'visible' : 'none'); } catch (e) {}
        });
        sync();
      });
      sync();
      document.body.appendChild(btn);
    }, 400);
  })();

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
// v3.0 — canvas variants + print fill fix
